import { useState, useEffect, useRef, useCallback, Fragment } from 'react';
import api, {
  pickupSSOToken, isAuthenticated, redirectToSSO, clearToken,
  providers as providersApi, promptLibrary as promptLibApi,
  builder as builderApi,
  fieldLinks as fieldLinksApi,
} from './lib/api.js';

const C = {
  bg:'#0A0C10', surface:'#10141C', surfaceHover:'#161B26',
  border:'#1E2535', borderHover:'#2A3450',
  accent:'#00D4FF', accentDim:'#00D4FF22', accentMid:'#00D4FF55',
  success:'#00E5A0', successDim:'#00E5A022',
  warning:'#FFB800', warningDim:'#FFB80022',
  danger:'#FF4466',  dangerDim:'#FF446622',
  purple:'#A855F7',  purpleDim:'#A855F722',
  text:'#E8EDF5', textSec:'#7A8BA0', textMuted:'#3D4F65',
};

const Badge = ({ c, label }) => (
  <span style={{ display:'inline-flex', alignItems:'center', padding:'2px 8px', borderRadius:4,
    fontSize:11, fontWeight:600, letterSpacing:'0.05em', textTransform:'uppercase',
    background:c+'22', color:c, border:`1px solid ${c}44`, fontFamily:"'DM Mono',monospace" }}>
    {label}
  </span>
);

const Btn = ({ children, variant='primary', onClick, style={}, size='md', disabled=false }) => {
  const v = {
    primary:  { background:C.accent,     color:'#000',   border:'none' },
    secondary:{ background:'transparent',color:C.accent, border:`1px solid ${C.accent}44` },
    ghost:    { background:'transparent',color:C.textSec,border:`1px solid ${C.border}` },
    danger:   { background:C.dangerDim,  color:C.danger, border:`1px solid ${C.danger}44` },
    success:  { background:C.successDim, color:C.success,border:`1px solid ${C.success}44` },
    purple:   { background:C.purpleDim,  color:C.purple, border:`1px solid ${C.purple}44` },
  }[variant] || { background:'transparent', color:C.textSec, border:`1px solid ${C.border}` };
  return (
    <button onClick={onClick} disabled={disabled} style={{
      ...v, borderRadius:8, cursor:disabled?'not-allowed':'pointer',
      padding:size==='sm'?'5px 12px':size==='lg'?'10px 24px':'7px 16px',
      fontSize:size==='sm'?12:13, fontWeight:600, fontFamily:"'DM Sans',sans-serif",
      transition:'all 0.15s', opacity:disabled?0.4:1, whiteSpace:'nowrap', ...style }}
      onMouseEnter={e=>!disabled&&(e.currentTarget.style.opacity='0.8')}
      onMouseLeave={e=>!disabled&&(e.currentTarget.style.opacity='1')}>
      {children}
    </button>
  );
};

const Card = ({ children, style={}, onClick }) => (
  <div onClick={onClick} style={{
    background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, padding:20,
    transition:'all 0.2s', cursor:onClick?'pointer':'default', ...style }}
    onMouseEnter={e=>onClick&&(e.currentTarget.style.borderColor=C.accent)}
    onMouseLeave={e=>onClick&&(e.currentTarget.style.borderColor=C.border)}>
    {children}
  </div>
);

const Input = ({ label, value, onChange, placeholder, type='text', style={} }) => (
  <div style={{ marginBottom:14 }}>
    {label&&<div style={{ fontSize:11, fontWeight:600, color:C.textSec, marginBottom:6,
      letterSpacing:'0.08em', textTransform:'uppercase', fontFamily:"'DM Mono',monospace" }}>{label}</div>}
    <input type={type} value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder}
      style={{ width:'100%', background:C.bg, border:`1px solid ${C.border}`, borderRadius:8,
        padding:'9px 12px', color:C.text, fontSize:13, fontFamily:"'DM Sans',sans-serif",
        outline:'none', boxSizing:'border-box', ...style }}
      onFocus={e=>e.target.style.borderColor=C.accent}
      onBlur={e=>e.target.style.borderColor=C.border} />
  </div>
);

const Select = ({ label, value, onChange, options }) => (
  <div style={{ marginBottom:14 }}>
    {label&&<div style={{ fontSize:11, fontWeight:600, color:C.textSec, marginBottom:6,
      letterSpacing:'0.08em', textTransform:'uppercase', fontFamily:"'DM Mono',monospace" }}>{label}</div>}
    <select value={value} onChange={e=>onChange(e.target.value)} style={{
      width:'100%', background:C.bg, border:`1px solid ${C.border}`, borderRadius:8,
      padding:'9px 12px', color:C.text, fontSize:13, fontFamily:"'DM Sans',sans-serif",
      outline:'none', boxSizing:'border-box', cursor:'pointer' }}
      onFocus={e=>e.target.style.borderColor=C.accent}
      onBlur={e=>e.target.style.borderColor=C.border}>
      {options.map(o=><option key={o.value} value={o.value} style={{background:C.surface}}>{o.label}</option>)}
    </select>
  </div>
);

const Textarea = ({ label, value, onChange, rows=4, mono=false, placeholder='' }) => (
  <div style={{ marginBottom:14 }}>
    {label&&<div style={{ fontSize:11, fontWeight:600, color:C.textSec, marginBottom:6,
      letterSpacing:'0.08em', textTransform:'uppercase', fontFamily:"'DM Mono',monospace" }}>{label}</div>}
    <textarea value={value} onChange={e=>onChange(e.target.value)} rows={rows} placeholder={placeholder} style={{
      width:'100%', background:C.bg, border:`1px solid ${C.border}`, borderRadius:8,
      padding:'9px 12px', color:C.text, fontSize:13, resize:'vertical', boxSizing:'border-box',
      fontFamily:mono?"'DM Mono',monospace":"'DM Sans',sans-serif", outline:'none', lineHeight:1.6 }}
      onFocus={e=>e.target.style.borderColor=C.accent}
      onBlur={e=>e.target.style.borderColor=C.border} />
  </div>
);

// ── JsonEditor — editable JSON with syntax validation + format button ─────────
// Used wherever JSON schema/config values are edited or displayed.
// Shows green border when valid, red when invalid. Format button pretty-prints.
const JsonEditor = ({ label, value, onChange, rows=4, readOnly=false, placeholder='{}' }) => {
  const [error, setError] = useState('');

  const validate = (v) => {
    if (!v || v.trim() === '') { setError(''); return; }
    try { JSON.parse(v); setError(''); }
    catch (e) { setError(e.message); }
  };

  const format = () => {
    try {
      const formatted = JSON.stringify(JSON.parse(value), null, 2);
      onChange(formatted);
      setError('');
    } catch(e) {
      if (e.tokenBudget || e.status === 429) {
        setError('🪙 ' + e.message);
      } else {
        setError(e.message);
      }
    }
  };

  const borderColor = error ? C.danger : value && !error ? C.success : C.border;

  return (
    <div style={{ marginBottom:14 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
        {label && <div style={{ fontSize:11, fontWeight:600, color:C.textSec, letterSpacing:'0.08em', textTransform:'uppercase', fontFamily:"'DM Mono',monospace" }}>{label}</div>}
        {!readOnly && (
          <button onClick={format} style={{ background:'none', border:'none', cursor:'pointer', fontSize:11, color:C.textSec, fontFamily:"'DM Sans',sans-serif", padding:'1px 6px', borderRadius:4, border:`1px solid ${C.border}` }}
            title="Format / pretty-print JSON">⌗ Format</button>
        )}
      </div>
      <textarea
        value={value}
        onChange={e=>{ onChange(e.target.value); validate(e.target.value); }}
        onBlur={e=>validate(e.target.value)}
        rows={rows}
        placeholder={placeholder}
        readOnly={readOnly}
        style={{
          width:'100%', background:readOnly?C.bg+'88':C.bg,
          border:`1px solid ${borderColor}`,
          borderRadius:8, padding:'9px 12px', color:readOnly?C.textSec:C.text,
          fontSize:12, resize:'vertical', boxSizing:'border-box',
          fontFamily:"'DM Mono',monospace", outline:'none', lineHeight:1.7,
          transition:'border-color 0.15s',
        }}
        onFocus={e=>!error&&(e.target.style.borderColor=C.accent)}
        // onBlur={e=>e.target.style.borderColor=borderColor}
      />
      {error && (
        <div style={{ fontSize:10, color:C.danger, marginTop:4, fontFamily:"'DM Mono',monospace", lineHeight:1.4 }}>
          ✗ {error}
        </div>
      )}
      {!error && value && !readOnly && (
        <div style={{ fontSize:10, color:C.success, marginTop:4 }}>✓ valid JSON</div>
      )}
    </div>
  );
};

const Modal = ({ open, onClose, title, children, width=620, confirmClose=false, isDirty=false }) => {
  if (!open) return null;

  const handleClose = () => {
    if (confirmClose && isDirty) {
      if (!window.confirm('You have unsaved changes. Close anyway?')) return;
    }
    onClose();
  };

  return (
    // FIX: backdrop click is disabled (no onClick on outer div) — modal only closes via × button or Cancel
    <div style={{ position:'fixed', inset:0, background:'#000A', zIndex:1000,
      display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
      <div style={{
        background:C.surface, border:`1px solid ${C.border}`, borderRadius:16,
        width:'100%', maxWidth:width, maxHeight:'92vh', overflow:'auto', boxShadow:'0 32px 80px #000C' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
          padding:'18px 24px', borderBottom:`1px solid ${C.border}`, position:'sticky', top:0, background:C.surface, zIndex:1 }}>
          <div style={{ fontSize:16, fontWeight:700, color:C.text, fontFamily:"'DM Serif Display',serif" }}>{title}</div>
          <button onClick={handleClose} style={{ background:'none', border:'none', color:C.textSec, cursor:'pointer', fontSize:22 }}>×</button>
        </div>
        <div style={{ padding:24 }}>{children}</div>
      </div>
    </div>
  );
};

const Spinner = () => (
  <div style={{ display:'flex', alignItems:'center', justifyContent:'center', padding:40 }}>
    <div style={{ width:32, height:32, border:`3px solid ${C.border}`, borderTop:`3px solid ${C.accent}`,
      borderRadius:'50%', animation:'spin 0.8s linear infinite' }} />
  </div>
);

const CATS = {
  ppe:     {label:'PPE Check',  color:C.accent,  icon:'🦺'},
  site:    {label:'Site Image', color:C.purple,  icon:'🏗️'},
  selfie:  {label:'Selfie',     color:C.success, icon:'🤳'},
  serial:  {label:'Serial No.', color:C.warning, icon:'🔢'},
  odometer:{label:'Odometer',   color:'#FF6B35', icon:'🚗'},
  custom:  {label:'Custom',     color:C.textSec, icon:'⚙️'},
};

const PROVIDER_ICONS = { anthropic:'🟣', openai:'🟢', gemini:'🔵', ollama:'🟠', azure_openai:'🔷' };

// ── Branding — reads from runtime env injected by docker-entrypoint.sh ────────
const getAppName    = () => window.__ENV__?.VITE_APP_NAME    || import.meta.env?.VITE_APP_NAME    || 'VisionAI';
const getAppTagline = () => window.__ENV__?.VITE_APP_TAGLINE || import.meta.env?.VITE_APP_TAGLINE || 'Visual Intelligence Platform';
// Split name into accent+rest for coloured logo: "VisionAI" → <accent>Vision</accent>AI
const AppLogo = ({ size=17 }) => {
  const name = getAppName();
  const half = Math.ceil(name.length / 2);
  const a = name.slice(0, half), b = name.slice(half);
  return <span style={{ fontSize:size, fontWeight:700, fontFamily:"'DM Serif Display',serif" }}>
    <span style={{color:C.accent}}>{a}</span>{b}
  </span>;
};

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-GB', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' });
}
function timeAgo(d) {
  const m = Math.floor((Date.now()-new Date(d))/60000);
  if (m<1) return 'just now'; if (m<60) return `${m}m ago`;
  if (m<1440) return `${Math.floor(m/60)}h ago`; return `${Math.floor(m/1440)}d ago`;
}

// ── DateRangePicker ───────────────────────────────────────────────────────────
function DateRangePicker({ dateFrom, dateTo, onFrom, onTo, label='Date Range' }) {
  return (
    <div style={{ display:'flex', gap:8, alignItems:'flex-end' }}>
      <Input label={label ? `${label} From` : 'From'} type="date" value={dateFrom} onChange={onFrom} style={{ marginBottom:0 }} />
      <Input label={label ? `${label} To` : 'To'}   type="date" value={dateTo}   onChange={onTo}   style={{ marginBottom:0 }} />
    </div>
  );
}


// ── ImageZoom — clickable image with fullscreen overlay ─────────────────────
function ImageZoom({ src }) {
  const [zoomed, setZoomed] = useState(false);
  return (
    <>
      <div style={{ position:'relative', cursor:'zoom-in' }} onClick={() => setZoomed(true)}>
        <img src={src} alt="validation input"
          style={{ width:'100%', maxHeight:220, objectFit:'contain', borderRadius:8,
            border:`1px solid ${C.border}`, display:'block', background:C.bg }}
          onError={e=>{ e.target.style.display='none'; }} />
        <div style={{ position:'absolute', bottom:6, right:6, background:'#000A', borderRadius:6,
          padding:'3px 8px', fontSize:10, color:'#fff', pointerEvents:'none' }}>
          🔍 Click to zoom
        </div>
      </div>
      {zoomed && (
        <div onClick={() => setZoomed(false)} style={{
          position:'fixed', inset:0, background:'#000D', zIndex:9999,
          display:'flex', alignItems:'center', justifyContent:'center',
          cursor:'zoom-out', padding:24 }}>
          <img src={src} alt="zoomed"
            style={{ maxWidth:'95vw', maxHeight:'95vh', objectFit:'contain',
              borderRadius:12, border:`2px solid ${C.border}`, boxShadow:'0 32px 80px #000' }}
            onClick={e => e.stopPropagation()} />
          <button onClick={() => setZoomed(false)} style={{
            position:'absolute', top:20, right:24, background:'#fff3', border:'none',
            color:'#fff', fontSize:28, cursor:'pointer', borderRadius:'50%',
            width:44, height:44, display:'flex', alignItems:'center', justifyContent:'center' }}>×</button>
        </div>
      )}
    </>
  );
}

// ── ExpandableResultRows — handles objects, arrays, long strings ─────────────
function ExpandableResultRows({ data }) {
  const [expanded, setExpanded] = useState({});
  const toggle = k => setExpanded(prev => ({ ...prev, [k]: !prev[k] }));

  return (
    <>
      {Object.entries(data).map(([k, v]) => {
        const isObj = v !== null && typeof v === 'object';
        const isLongStr = typeof v === 'string' && v.length > 60;
        const needsExpand = isObj || isLongStr;
        const exp = expanded[k];

        return (
          <div key={k} style={{ borderBottom:`1px solid ${C.border}` }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'5px 0', gap:8 }}>
              <span style={{ fontSize:12, color:C.textSec, fontFamily:"'DM Mono',monospace", flexShrink:0 }}>{k}</span>
              <div style={{ display:'flex', alignItems:'center', gap:6, minWidth:0 }}>
                {typeof v === 'boolean' ? (
                  <span style={{ fontSize:12, fontFamily:"'DM Mono',monospace", color:v?C.success:C.danger }}>
                    {v ? '✓ true' : '✗ false'}
                  </span>
                ) : isObj ? (
                  <span style={{ fontSize:11, color:C.warning, fontFamily:"'DM Mono',monospace" }}>
                    {Array.isArray(v) ? `[${v.length} items]` : `{${Object.keys(v).length} keys}`}
                  </span>
                ) : isLongStr ? (
                  <span style={{ fontSize:12, color:C.textMuted, maxWidth:200, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {v.slice(0, 40)}…
                  </span>
                ) : (
                  <span style={{ fontSize:12, fontFamily:"'DM Mono',monospace", color:typeof v==='number'?C.accent:C.text }}>
                    {String(v)}
                  </span>
                )}
                {needsExpand && (
                  <button onClick={() => toggle(k)} style={{ background:'none', border:`1px solid ${C.border}`,
                    borderRadius:4, color:C.textSec, cursor:'pointer', fontSize:10, padding:'1px 6px',
                    fontFamily:"'DM Sans',sans-serif", flexShrink:0 }}>
                    {exp ? '▲ Hide' : '▼ Show'}
                  </button>
                )}
              </div>
            </div>
            {needsExpand && exp && (
              <div style={{ padding:'6px 0 8px', paddingLeft:12, marginBottom:4 }}>
                {isObj ? (
                  <pre style={{ fontFamily:"'DM Mono',monospace", fontSize:11, color:C.text,
                    background:C.bg, padding:10, borderRadius:8, border:`1px solid ${C.border}`,
                    whiteSpace:'pre-wrap', overflowX:'auto', maxHeight:200, overflowY:'auto', margin:0 }}>
                    {JSON.stringify(v, null, 2)}
                  </pre>
                ) : (
                  <div style={{ fontSize:12, color:C.text, fontFamily:"'DM Mono',monospace",
                    background:C.bg, padding:10, borderRadius:8, border:`1px solid ${C.border}`,
                    whiteSpace:'pre-wrap', lineHeight:1.6 }}>
                    {String(v)}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}

// ── RunDetailModal ────────────────────────────────────────────────────────────
const SOURCE_MODE_LABELS = {
  api:       { label:'UI Test',    color:'#00D4FF', icon:'🖥' },
  ui_test:   { label:'UI Test',    color:'#00D4FF', icon:'🖥' },
  field:     { label:'Field App',  color:'#00E5A0', icon:'📱' },
  external:  { label:'API / M2M',  color:'#A855F7', icon:'⚡' },
};
const IMAGE_SOURCE_LABELS = { url:'🔗 URL', upload:'📤 Upload' };
const FEEDBACK_REASONS = [
  { value:'false_positive',   label:'False positive (passed but should fail)' },
  { value:'false_negative',   label:'False negative (failed but should pass)' },
  { value:'wrong_extraction', label:'Wrong data extracted' },
  { value:'low_confidence',   label:'Confidence too low' },
  { value:'image_quality',    label:'Image quality issue' },
  { value:'other',            label:'Other' },
];

function FeedbackWidget({ runId, tenantId }) {
  const [fb,         setFb]         = useState(null);   // existing feedback
  const [rating,     setRating]     = useState(null);   // 1 or -1
  const [reason,     setReason]     = useState('');
  const [notes,      setNotes]      = useState('');
  const [saving,     setSaving]     = useState(false);
  const [saved,      setSaved]      = useState(false);
  const [showForm,   setShowForm]   = useState(false);

  useEffect(() => {
    if (!runId) return;
    api.feedback.getForRun(runId)
      .then(r => {
        if (r?.data) {
          setFb(r.data); setRating(r.data.rating);
          setReason(r.data.reason||''); setNotes(r.data.notes||'');
        }
      }).catch(() => {});
  }, [runId]);

  const submit = async () => {
    if (!rating) return;
    setSaving(true);
    try {
      const r = await api.feedback.submit({ runId, rating, reason: reason||undefined, notes: notes||undefined });
      setFb(r.data); setSaved(true); setShowForm(false);
      setTimeout(() => setSaved(false), 2000);
    } catch(e) { alert(e.message); }
    setSaving(false);
  };

  const ratingColor = rating === 1 ? C.success : rating === -1 ? C.danger : C.textSec;

  return (
    <div style={{ marginTop:16 }}>
      <div style={{ fontSize:11, color:C.textSec, textTransform:'uppercase', letterSpacing:'0.07em', fontFamily:"'DM Mono',monospace", marginBottom:8 }}>
        Feedback
      </div>
      <div style={{ display:'flex', gap:8, alignItems:'center' }}>
        <button onClick={() => { setRating(1); setShowForm(true); }} style={{
          padding:'7px 14px', borderRadius:8, border:`2px solid ${rating===1?C.success+'66':C.border}`,
          background:rating===1?C.successDim:'transparent', color:rating===1?C.success:C.textSec,
          cursor:'pointer', fontSize:16, transition:'all 0.15s' }} title="Thumbs up">
          👍
        </button>
        <button onClick={() => { setRating(-1); setShowForm(true); }} style={{
          padding:'7px 14px', borderRadius:8, border:`2px solid ${rating===-1?C.danger+'66':C.border}`,
          background:rating===-1?C.dangerDim:'transparent', color:rating===-1?C.danger:C.textSec,
          cursor:'pointer', fontSize:16, transition:'all 0.15s' }} title="Thumbs down">
          👎
        </button>
        {fb && <span style={{ fontSize:12, color:ratingColor }}>{rating===1?'Marked correct':'Marked incorrect'}</span>}
        {saved && <span style={{ fontSize:12, color:C.success }}>✓ Saved</span>}
        {!showForm && !fb && <span style={{ fontSize:11, color:C.textMuted }}>Rate this result</span>}
        {!showForm && (rating!==null) && <Btn size="sm" variant="ghost" onClick={() => setShowForm(true)}>Edit</Btn>}
      </div>

      {showForm && (
        <div style={{ marginTop:10, background:C.bg, borderRadius:8, padding:14, border:`1px solid ${rating===-1?C.danger+'33':C.border}` }}>
          {rating === -1 && (
            <div style={{ marginBottom:10 }}>
              <div style={{ fontSize:11, color:C.textSec, marginBottom:6, textTransform:'uppercase', letterSpacing:'0.07em', fontFamily:"'DM Mono',monospace" }}>Reason</div>
              <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                {FEEDBACK_REASONS.map(r => (
                  <button key={r.value} onClick={() => setReason(r.value)} style={{
                    padding:'4px 10px', borderRadius:6, border:`1px solid ${reason===r.value?C.danger+'55':C.border}`,
                    background:reason===r.value?C.dangerDim:'transparent', color:reason===r.value?C.danger:C.textSec,
                    cursor:'pointer', fontSize:11, fontFamily:"'DM Sans',sans-serif", transition:'all 0.15s' }}>
                    {r.label}
                  </button>
                ))}
              </div>
            </div>
          )}
          <Textarea label="Notes (optional)" value={notes} onChange={setNotes} rows={2} placeholder="Add context about why this result is correct or incorrect…" />
          <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
            <Btn variant="ghost" size="sm" onClick={() => setShowForm(false)}>Cancel</Btn>
            <Btn size="sm" onClick={submit} disabled={saving}>{saving?'Saving…':'Save Feedback'}</Btn>
          </div>
        </div>
      )}
    </div>
  );
}

function RunDetailModal({ run, onClose }) {
  const [full, setFull] = useState(null);

  useEffect(() => {
    if (!run) return;
    setFull(null);
    api.runs.get(run.id).then(r => setFull(r.data)).catch(() => setFull(run));
  }, [run?.id]);

  if (!run) return null;
  const r   = full || run;
  const sc  = r.validation_status==='pass'?C.success:r.validation_status==='fail'?C.danger:C.warning;
  const cat = CATS[r.workflow_category]||CATS.custom;
  const sm  = SOURCE_MODE_LABELS[r.source_mode] || SOURCE_MODE_LABELS.api;

  return (
    <Modal open={!!run} onClose={onClose} title="Validation Run Detail" width={900}>
      {!full ? <Spinner /> : (
        <div>
          {/* Header badges */}
          <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:20, alignItems:'center' }}>
            <Badge c={sc} label={r.validation_status} />
            {r.confidence!=null && <Badge c={C.purple} label={`${(r.confidence*100).toFixed(0)}% conf`} />}
            <Badge c={sm.color} label={`${sm.icon} ${sm.label}`} />
            {r.image_source && <Badge c={C.textSec} label={IMAGE_SOURCE_LABELS[r.image_source]||r.image_source} />}
            {run?.role !== 'viewer' && r.ai_provider_type && <Badge c={C.accent} label={`${PROVIDER_ICONS[r.ai_provider_type]||''} ${r.provider_name||r.ai_provider_type}`} />}
            {r.action_taken && <Badge c={C.warning} label={r.action_taken} />}
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
            {/* Left col */}
            <div>
              {/* Image */}
              {(r.image_url || r.image_hash) && (
                <div style={{ marginBottom:14 }}>
                  <div style={{ fontSize:11, color:C.textSec, textTransform:'uppercase', letterSpacing:'0.07em', fontFamily:"'DM Mono',monospace", marginBottom:6 }}>
                    Image {r.image_source && <span style={{ color:C.textMuted }}>+ {IMAGE_SOURCE_LABELS[r.image_source]||r.image_source}</span>}
                  </div>
                  {r.image_url ? (
                    <ImageZoom src={r.image_url} />
                  ) : (
                    <div style={{ background:C.bg, borderRadius:8, padding:12, border:`1px solid ${C.border}`, fontSize:12, color:C.textMuted, fontFamily:"'DM Mono',monospace" }}>
                      {r.image_hash ? `sha256:${r.image_hash.slice(0,16)}…` : 'uploaded image (URL not stored)'}
                    </div>
                  )}
                  {r.image_meta && Object.keys(r.image_meta).length>0 && (
                    <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginTop:6 }}>
                      {Object.entries(r.image_meta).filter(([k])=>k!=='url').map(([k,v])=>(
                        <span key={k} style={{ fontSize:10, color:C.textMuted, fontFamily:"'DM Mono',monospace" }}>{k}: {String(v)}</span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Parsed result */}
              <div style={{ fontSize:11, color:C.textSec, textTransform:'uppercase', letterSpacing:'0.07em', fontFamily:"'DM Mono',monospace", marginBottom:6 }}>Result</div>
              <div style={{ background:C.bg, borderRadius:8, padding:12, border:`1px solid ${sc}44`, marginBottom:14 }}>
                {r.parsed_result && <ExpandableResultRows data={r.parsed_result} />}
              </div>

              {/* References */}
              <div style={{ fontSize:11, color:C.textSec, textTransform:'uppercase', letterSpacing:'0.07em', fontFamily:"'DM Mono',monospace", marginBottom:6 }}>References</div>
              <div style={{ background:C.bg, borderRadius:8, padding:12, border:`1px solid ${C.border}`, fontSize:12, fontFamily:"'DM Mono',monospace", color:C.text, lineHeight:2 }}>
                {r.job_id        && <div>job_id: <span style={{color:C.accent}}>{r.job_id}</span></div>}
                {r.external_ref_id && <div>ext_ref: <span style={{color:C.accent}}>{r.external_ref_id}</span></div>}
                <div>run_id: {r.id}</div>
                <div>param: {r.param_name}</div>
                <div>trigger: {r.status_trigger}</div>
              </div>

              {/* Feedback */}
              <FeedbackWidget runId={r.id} />
            </div>

            {/* Right col */}
            <div>
              {/* Execution stats */}
              <div style={{ fontSize:11, color:C.textSec, textTransform:'uppercase', letterSpacing:'0.07em', fontFamily:"'DM Mono',monospace", marginBottom:6 }}>Execution</div>
              <div style={{ background:C.bg, borderRadius:8, padding:12, border:`1px solid ${C.border}`, marginBottom:14 }}>
                {[
                  ['Date / Time',  fmtDate(r.created_at)],
                  ['Source',       `${sm.icon} ${sm.label}`],
                  ['Processing',   r.processing_ms ? `${r.processing_ms}ms` : '—'],
                  ...(run?.role !== 'viewer' ? [
                    ['Tokens In',  r.tokens_input  != null ? r.tokens_input.toLocaleString()  : '—'],
                    ['Tokens Out', r.tokens_output != null ? r.tokens_output.toLocaleString() : '—'],
                    ['Model',      r.ai_model || '—'],
                    ['Provider',   r.provider_name || r.ai_provider_type || '—'],
                  ] : []),
                  ['Workflow',     r.workflow_name || '—'],
                  ['Tenant',       r.tenant_name || '—'],
                ].map(([label, val]) => (
                  <div key={label} style={{ display:'flex', justifyContent:'space-between', padding:'5px 0', borderBottom:`1px solid ${C.border}` }}>
                    <span style={{ fontSize:12, color:C.textSec }}>{label}</span>
                    <span style={{ fontSize:12, color:C.text, fontFamily:"'DM Mono',monospace", maxWidth:180, textAlign:'right', wordBreak:'break-all' }}>{val}</span>
                  </div>
                ))}
              </div>

              {/* Job metadata */}
              {r.job_meta && Object.keys(r.job_meta).length>0 && (
                <>
                  <div style={{ fontSize:11, color:C.textSec, textTransform:'uppercase', letterSpacing:'0.07em', fontFamily:"'DM Mono',monospace", marginBottom:6 }}>Job Metadata</div>
                  <div style={{ background:C.bg, borderRadius:8, padding:12, border:`1px solid ${C.border}`, marginBottom:14 }}>
                    {Object.entries(r.job_meta).map(([k,v])=>(
                      <div key={k} style={{ display:'flex', justifyContent:'space-between', padding:'4px 0', borderBottom:`1px solid ${C.border}` }}>
                        <span style={{ fontSize:12, color:C.textSec, fontFamily:"'DM Mono',monospace" }}>{k}</span>
                        <span style={{ fontSize:12, color:C.text }}>{String(v)}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Prompt used */}
              <div style={{ fontSize:11, color:C.textSec, textTransform:'uppercase', letterSpacing:'0.07em', fontFamily:"'DM Mono',monospace", marginBottom:6 }}>
                Prompt Used <span style={{color:C.textMuted}}>v{r.prompt_version}</span>
              </div>
              <div style={{ background:C.bg, borderRadius:8, padding:12, border:`1px solid ${C.border}`,
                fontFamily:"'DM Mono',monospace", fontSize:11, color:C.textMuted, lineHeight:1.6,
                maxHeight:160, overflowY:'auto', whiteSpace:'pre-wrap' }}>
                {r.prompt_used || r.config_prompt || '—'}
              </div>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

// ── Login Screen ──────────────────────────────────────────────────────────────
function ChangePasswordScreen({ onDone }) {
  const [current, setCurrent] = useState('');
  const [next,    setNext]    = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  const submit = async () => {
    if (next !== confirm) return setError('Passwords do not match');
    if (next.length < 8)  return setError('Password must be at least 8 characters');
    setLoading(true); setError('');
    try {
      await api.users.changePassword({ currentPassword: current, newPassword: next });
      onDone();
    } catch(e) { setError(e.message); }
    setLoading(false);
  };

  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:'100vh', background:C.bg }}>
      <div style={{ width:'100%', maxWidth:400, padding:20 }}>
        <div style={{ textAlign:'center', marginBottom:28 }}>
          <div style={{ fontSize:32, marginBottom:10, color:C.warning }}>🔐</div>
          <AppLogo size={22} />
          <div style={{ color:C.textSec, fontSize:13, marginTop:6 }}>Change your password to continue</div>
        </div>
        <Card>
          <div style={{ background:C.warningDim, border:`1px solid ${C.warning}44`, borderRadius:8, padding:12, marginBottom:20, fontSize:12, color:C.warning, lineHeight:1.6 }}>
            You are logging in with a temporary password. Please set a new permanent password before continuing.
          </div>
          <Input label="Current (Temporary) Password" value={current} onChange={setCurrent} placeholder="Enter temporary password" />
          <Input label="New Password (min 8 chars)" value={next} onChange={setNext} placeholder="New password" />
          <Input label="Confirm New Password" value={confirm} onChange={setConfirm} placeholder="Confirm new password" />
          {error && <div style={{ background:C.dangerDim, border:`1px solid ${C.danger}44`, borderRadius:8, padding:'10px 14px', marginBottom:14, fontSize:12, color:C.danger }}>✗ {error}</div>}
          <Btn onClick={submit} disabled={loading||!current||!next||!confirm} style={{ width:'100%' }} size="lg">
            {loading ? 'Updating…' : 'Set New Password →'}
          </Btn>
        </Card>
      </div>
    </div>
  );
}

function ForgotPasswordScreen({ onBack }) {
  const [email,   setEmail]   = useState('');
  const [loading, setLoading] = useState(false);
  const [sent,    setSent]    = useState(false);
  const [error,   setError]   = useState('');

  const submit = async () => {
    if (!email.trim()) return setError('Email is required');
    setLoading(true); setError('');
    try {
      await api.auth.forgotPassword({ email: email.trim() });
      setSent(true);
    } catch(e) { setError(e.message); }
    setLoading(false);
  };

  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center',
      minHeight:'100vh', background:C.bg }}>
      <div style={{ width:'100%', maxWidth:400, padding:20 }}>
        <div style={{ textAlign:'center', marginBottom:28 }}>
          <div style={{ fontSize:36, marginBottom:10 }}>🔑</div>
          <AppLogo size={22} />
          <div style={{ color:C.textSec, fontSize:13, marginTop:6 }}>
            {sent ? 'Check your email' : 'Reset your password'}
          </div>
        </div>
        <Card>
          {sent ? (
            <div style={{ textAlign:'center' }}>
              <div style={{ fontSize:40, marginBottom:14 }}>✉️</div>
              <div style={{ fontSize:14, fontWeight:600, color:C.text, marginBottom:8 }}>
                Password sent
              </div>
              <div style={{ fontSize:13, color:C.textSec, marginBottom:20, lineHeight:1.6 }}>
                If <strong style={{color:C.text}}>{email}</strong> is registered, a temporary
                password has been sent. Check your inbox and spam folder.
              </div>
              <div style={{ background:C.warningDim, border:`1px solid ${C.warning}44`,
                borderRadius:8, padding:'10px 14px', marginBottom:20, fontSize:12,
                color:C.warning, lineHeight:1.6 }}>
                You will be asked to set a new permanent password on first login.
              </div>
              <Btn onClick={onBack} style={{ width:'100%' }} size="lg">Back to Sign In →</Btn>
            </div>
          ) : (
            <div>
              <div style={{ fontSize:13, color:C.textSec, marginBottom:18, lineHeight:1.6 }}>
                Enter your email address and we will send you a temporary password.
              </div>
              <Input label="Email address" value={email} onChange={setEmail}
                placeholder="your@email.com" />
              {error && (
                <div style={{ background:C.dangerDim, border:`1px solid ${C.danger}44`,
                  borderRadius:8, padding:'10px 14px', marginBottom:14, fontSize:12,
                  color:C.danger }}>✗ {error}</div>
              )}
              <Btn onClick={submit} disabled={loading || !email} style={{ width:'100%' }}
                size="lg">{loading ? 'Sending…' : 'Send Reset Password →'}</Btn>
              <div style={{ textAlign:'center', marginTop:14 }}>
                <button onClick={onBack} style={{ background:'none', border:'none',
                  color:C.textSec, cursor:'pointer', fontSize:12 }}>
                  ← Back to Sign In
                </button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

function LoginCard() {
  const ssoUrl   = window.__ENV__?.VITE_SSO_LOGIN_URL || import.meta.env?.VITE_SSO_LOGIN_URL;
  const appEnv   = window.__ENV__?.VITE_APP_ENV || import.meta.env?.VITE_APP_ENV || 'production';
  const SHOW_DEV = appEnv !== 'production';
  const [devEmail, setDevEmail] = useState('admin@visionai.local');
  const [devName,  setDevName]  = useState('Dev Admin');
  const [devRole,  setDevRole]  = useState('superadmin');
  const [manEmail, setManEmail] = useState('');
  const [manPwd,   setManPwd]   = useState('');
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');
  const [mustChange, setMustChange] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const defaultTab = ssoUrl ? 'sso' : 'manual';
  const [tab, setTab] = useState(defaultTab);

  const devLogin = async () => {
    setLoading(true); setError('');
    try {
      const r = await fetch('/api/v1/auth/dev-login', {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ email:devEmail, name:devName, role:devRole }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.message || `HTTP ${r.status}`);
      localStorage.setItem('vai_token', d.data.token);
      window.location.reload();
    } catch(e) { setError(e.message); }
    setLoading(false);
  };

  const manualLogin = async () => {
    setLoading(true); setError('');
    try {
      const r = await api.auth.login({ email: manEmail.trim(), password: manPwd });
      if (r?.data?.token) {
        localStorage.setItem('vai_token', r.data.token);
        if (r.data.mustChangePassword) {
          setMustChange(true);
        } else {
          window.location.reload();
        }
      }
    } catch(e) { setError(e.message); }
    setLoading(false);
  };

  if (mustChange) return <ChangePasswordScreen onDone={() => window.location.reload()} />;
  if (forgotMode) return <ForgotPasswordScreen onBack={() => setForgotMode(false)} />;

  return (
    <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:14,
      overflow:'hidden', boxShadow:'0 20px 60px #0008' }}>
      <div style={{ display:'flex', borderBottom:`1px solid ${C.border}` }}>
        {[['manual','Password Login'],
          ...(ssoUrl ? [['sso','SSO']] : []),
          ...(SHOW_DEV ? [['dev','Dev']] : []),
          ].map(([id, label]) => (
          <button key={id} onClick={() => { setTab(id); setError(''); }} style={{
            flex:1, padding:'11px', border:'none', cursor:'pointer', fontSize:13, fontWeight:600,
            background: tab===id ? C.surface : C.bg, color: tab===id ? C.accent : C.textSec,
            borderBottom: `2px solid ${tab===id ? C.accent : 'transparent'}`,
            fontFamily:"'DM Sans',sans-serif",
          }}>
            {label}
          </button>
        ))}
      </div>
      <div style={{ padding:28 }}>
        {tab==='sso' && ssoUrl && (
          <div style={{ textAlign:'center' }}>
            <div style={{ color:C.textSec, fontSize:13, marginBottom:24, lineHeight:1.6 }}>
              Sign in using your workflow engine account.
            </div>
            <Btn size="lg" onClick={redirectToSSO} style={{ width:'100%' }}>
              Sign in with SSO →
            </Btn>
          </div>
        )}
        {tab==='manual' && (
          <div>
            <Input label="Email" value={manEmail} onChange={setManEmail}
              placeholder="your@email.com" />
            <div style={{ marginBottom:14 }}>
              <div style={{ fontSize:11, fontWeight:600, color:C.textSec, marginBottom:6,
                letterSpacing:'0.08em', textTransform:'uppercase',
                fontFamily:"'DM Mono',monospace" }}>Password</div>
              <input type="password" value={manPwd}
                onChange={e => setManPwd(e.target.value)}
                onKeyDown={e => e.key==='Enter' && manualLogin()}
                placeholder="Enter your password"
                style={{ width:'100%', background:C.bg, border:`1px solid ${C.border}`,
                  borderRadius:8, padding:'9px 12px', color:C.text, fontSize:13,
                  outline:'none', boxSizing:'border-box' }} />
            </div>
            {error && (
              <div style={{ background:C.dangerDim, border:`1px solid ${C.danger}44`,
                borderRadius:8, padding:'10px 14px', marginBottom:14, fontSize:12,
                color:C.danger }}>✗ {error}</div>
            )}
            <Btn onClick={manualLogin} disabled={loading || !manEmail || !manPwd}
              style={{ width:'100%' }} size="lg">
              {loading ? 'Signing in...' : 'Sign in →'}
            </Btn>
            <div style={{ textAlign:'center', marginTop:14 }}>
              <button onClick={() => { setForgotMode(true); setError(''); }}
                style={{ background:'none', border:'none', color:C.textSec,
                  cursor:'pointer', fontSize:12, textDecoration:'underline' }}>
                Forgot your password?
              </button>
            </div>
          </div>
        )}
        {tab==='dev' && (
          <div>
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:20,
              padding:'10px 14px', background:C.warningDim,
              border:`1px solid ${C.warning}44`, borderRadius:8 }}>
              <span>⚠️</span>
              <span style={{ fontSize:12, color:C.warning }}>Dev mode — disabled in production.</span>
            </div>
            <Input label="Email" value={devEmail} onChange={setDevEmail}
              placeholder="admin@visionai.local" />
            <Input label="Display Name" value={devName} onChange={setDevName}
              placeholder="Dev Admin" />
            <Select label="Role" value={devRole} onChange={setDevRole} options={[
              { value:'superadmin',      label:'Super Admin (all tenants)' },
              { value:'account_admin',   label:'Account Admin' },
              { value:'account_manager', label:'Account Manager' },
              { value:'viewer',          label:'Viewer (read only)' },
            ]} />
            {error && (
              <div style={{ background:C.dangerDim, border:`1px solid ${C.danger}44`,
                borderRadius:8, padding:'10px 14px', marginBottom:14, fontSize:12,
                color:C.danger }}>✗ {error}</div>
            )}
            <Btn onClick={devLogin} disabled={loading} style={{ width:'100%' }} size="lg">
              {loading ? 'Signing in...' : 'Sign in (Dev)'}
            </Btn>
          </div>
        )}
      </div>
    </div>
  );
}

function LoginScreen() {
  const [showLogin, setShowLogin] = useState(false);

  if (showLogin) {
    return (
      <div style={{ display:'flex', alignItems:'center', justifyContent:'center',
        minHeight:'100vh', background:C.bg }}>
        <div style={{ width:'100%', maxWidth:420, padding:20 }}>
          <div style={{ textAlign:'center', marginBottom:36 }}>
            <div style={{ fontSize:44, marginBottom:12, color:C.accent }}>◈</div>
            <div style={{ fontSize:30, fontWeight:700, fontFamily:"'DM Serif Display',serif",
              color:C.text }}>
              <AppLogo size={28} />
            </div>
            <div style={{ color:C.textSec, fontSize:13, marginTop:6 }}>{getAppTagline()}</div>
          </div>
          <LoginCard />
          <div style={{ textAlign:'center', marginTop:16 }}>
            <button onClick={() => setShowLogin(false)}
              style={{ background:'none', border:'none', color:C.textMuted,
                cursor:'pointer', fontSize:12 }}>
              ← Back to overview
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Landing page ────────────────────────────────────────────────────────────
  return <LandingPage onSignIn={() => setShowLogin(true)} />;
}

// ── DemoForm — standalone component to prevent input defocus on keystroke ───────
// Note: must be defined OUTSIDE LandingPage. Defining it inline caused React to
// recreate the component identity on every LandingPage render, unmounting and
// remounting inputs, causing the cursor to defocus after each character typed.
function DemoForm({ contactTo }) {
  // form state
  const [demoForm,    setDemoForm]    = useState({ name:'', email:'', phone:'', industry:'', useCase:'', note:'' });
  const [demoSending, setDemoSending] = useState(false);
  const [demoSent,    setDemoSent]    = useState(false);
  const [demoError,   setDemoError]   = useState('');
  // Simple math captcha
  const [captchaQ,    setCaptchaQ]    = useState(() => { const a = Math.ceil(Math.random()*9), b = Math.ceil(Math.random()*9); return { a, b, ans: '' }; });
  const refreshCaptcha = () => setCaptchaQ(prev => { const a = Math.ceil(Math.random()*9), b = Math.ceil(Math.random()*9); return { a, b, ans: '' }; });

  const submitDemo = async () => {
    if (!demoForm.name.trim() || !demoForm.email.trim()) return setDemoError('Name and email are required.');
    if (!demoForm.email.includes('@')) return setDemoError('Enter a valid email address.');
    if (parseInt(captchaQ.ans) !== captchaQ.a + captchaQ.b) {
      setDemoError('Captcha answer incorrect — please try again.');
      refreshCaptcha();
      return;
    }
    setDemoSending(true); setDemoError('');
    try {
      await api.auth.demoRequest({
        name:       demoForm.name.trim(),
        email:      demoForm.email.trim(),
        phone:      demoForm.phone || '',
        industry:   demoForm.industry || '',
        useCase:    demoForm.useCase || '',
        note:       demoForm.note || '',
        captchaA:   captchaQ.a,
        captchaB:   captchaQ.b,
        captchaAns: parseInt(captchaQ.ans),
      });
      setDemoSent(true);
    } catch(e) {
      setDemoError(e.message || 'Failed to send request. Please email ' + contactTo + ' directly.');
      refreshCaptcha();
    }
    setDemoSending(false);
  };

  const INDUSTRIES = ['Field Service','Utilities & Energy','Telecommunications','Construction','Transport & Logistics','Manufacturing','Oil & Gas','Facilities Management','Other'];
  const USE_CASES  = ['PPE / Safety Compliance','Equipment Inspection','Asset Serial Numbers','Meter Reading','Site Documentation','Vehicle Pre-Start','Before / After Audits','Other'];

  return (
    <div id="demo-form" style={{ background:C.surface,
        borderTop:`1px solid ${C.border}`, padding:'72px 32px' }}>
        <div style={{ maxWidth:640, margin:'0 auto' }}>
          <div style={{ textAlign:'center', marginBottom:40 }}>
            <div style={{ fontSize:11, fontWeight:700, color:C.accent,
              textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:12 }}>
              Get started
            </div>
            <h2 style={{ fontSize:28, fontWeight:700, color:C.text, marginBottom:12,
              fontFamily:"'DM Serif Display',serif" }}>
              Request a demo
            </h2>
            <p style={{ color:C.textSec, fontSize:14, lineHeight:1.7 }}>
              Tell us about your use case and we'll set up a personalised walkthrough
              with your workflows and field scenarios.
            </p>
          </div>
          {demoSent ? (
            <div style={{ background:C.accentDim, border:`1px solid ${C.accent}44`,
              borderRadius:12, padding:'32px', textAlign:'center' }}>
              <div style={{ fontSize:40, marginBottom:16 }}>✉️</div>
              <div style={{ fontSize:16, fontWeight:700, color:C.text,
                marginBottom:8 }}>Request sent!</div>
              <div style={{ fontSize:13, color:C.textSec, lineHeight:1.6 }}>
                Your request has been received.
                We'll be in touch within one business day.
              </div>
              <button onClick={() => { setDemoSent(false); setDemoForm({ name:'', email:'', phone:'', industry:'', useCase:'', note:'' }); refreshCaptcha(); }}
                style={{ marginTop:20, background:'transparent', border:`1px solid ${C.border}`,
                  color:C.textSec, borderRadius:8, padding:'8px 20px',
                  cursor:'pointer', fontSize:13 }}>
                Submit another request
              </button>
            </div>
          ) : (
            <div style={{ background:C.bg, border:`1px solid ${C.border}`,
              borderRadius:12, padding:'32px' }}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div style={{ marginBottom:12 }}>
                <label style={{ display:'block', fontSize:11, fontWeight:700, color:C.textSec,
                  textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:5,
                  fontFamily:"'DM Mono',monospace" }}>Full Name *</label>
                <input type="text" value={demoForm.name}
                  onChange={e => setDemoForm(f=>({...f,name:e.target.value}))}
                  placeholder="Jane Smith"
                  style={{ width:'100%', background:C.bg, border:`1px solid ${C.border}`,
                    borderRadius:8, padding:'9px 12px', color:C.text, fontSize:13,
                    fontFamily:"'DM Sans',sans-serif", outline:'none', boxSizing:'border-box' }} />
              </div>
                <div style={{ marginBottom:12 }}>
                <label style={{ display:'block', fontSize:11, fontWeight:700, color:C.textSec,
                  textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:5,
                  fontFamily:"'DM Mono',monospace" }}>Work Email *</label>
                <input type="email" value={demoForm.email}
                  onChange={e => setDemoForm(f=>({...f,email:e.target.value}))}
                  placeholder="jane@company.com"
                  style={{ width:'100%', background:C.bg, border:`1px solid ${C.border}`,
                    borderRadius:8, padding:'9px 12px', color:C.text, fontSize:13,
                    fontFamily:"'DM Sans',sans-serif", outline:'none', boxSizing:'border-box' }} />
              </div>
                <div style={{ marginBottom:12 }}>
                <label style={{ display:'block', fontSize:11, fontWeight:700, color:C.textSec,
                  textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:5,
                  fontFamily:"'DM Mono',monospace" }}>Phone</label>
                <input type="tel" value={demoForm.phone}
                  onChange={e => setDemoForm(f=>({...f,phone:e.target.value}))}
                  placeholder="+1 555 000 0000"
                  style={{ width:'100%', background:C.bg, border:`1px solid ${C.border}`,
                    borderRadius:8, padding:'9px 12px', color:C.text, fontSize:13,
                    fontFamily:"'DM Sans',sans-serif", outline:'none', boxSizing:'border-box' }} />
              </div>
                <div style={{ marginBottom:12 }}>
                  <label style={{ display:'block', fontSize:11, fontWeight:700,
                    color:C.textSec, textTransform:'uppercase', letterSpacing:'0.07em',
                    marginBottom:5, fontFamily:"'DM Mono',monospace" }}>Industry</label>
                  <select value={demoForm.industry}
                    onChange={e=>setDemoForm(f=>({...f,industry:e.target.value}))}
                    style={{ width:'100%', background:C.surface, border:`1px solid ${C.border}`,
                      borderRadius:8, padding:'9px 12px',
                      color: demoForm.industry ? C.text : C.textMuted,
                      fontSize:13, fontFamily:"'DM Sans',sans-serif",
                      outline:'none', boxSizing:'border-box' }}>
                    <option value="">Select…</option>
                    {INDUSTRIES.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ marginBottom:12 }}>
                <label style={{ display:'block', fontSize:11, fontWeight:700,
                  color:C.textSec, textTransform:'uppercase', letterSpacing:'0.07em',
                  marginBottom:5, fontFamily:"'DM Mono',monospace" }}>Primary Use Case</label>
                <select value={demoForm.useCase}
                  onChange={e=>setDemoForm(f=>({...f,useCase:e.target.value}))}
                  style={{ width:'100%', background:C.surface, border:`1px solid ${C.border}`,
                    borderRadius:8, padding:'9px 12px',
                    color: demoForm.useCase ? C.text : C.textMuted,
                    fontSize:13, fontFamily:"'DM Sans',sans-serif",
                    outline:'none', boxSizing:'border-box' }}>
                  <option value="">Select…</option>
                  {USE_CASES.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div style={{ marginBottom:12 }}>
              <label style={{ display:'block', fontSize:11, fontWeight:700, color:C.textSec,
                textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:5,
                fontFamily:"'DM Mono',monospace" }}>Tell us about your workflow (optional)</label>
              <textarea value={demoForm.note} rows={3}
                onChange={e => setDemoForm(f=>({...f,note:e.target.value}))}
                placeholder="e.g. We need to validate PPE at 3 stages of a maintenance workflow across 500 field technicians…"
                style={{ width:'100%', background:C.bg, border:`1px solid ${C.border}`,
                  borderRadius:8, padding:'9px 12px', color:C.text, fontSize:13, resize:'vertical',
                  fontFamily:"'DM Sans',sans-serif", outline:'none', boxSizing:'border-box' }} />
            </div>
              {/* Captcha */}
              <div style={{ background:C.surface, border:`1px solid ${C.border}`,
                borderRadius:8, padding:'12px 16px', marginBottom:14,
                display:'flex', alignItems:'center', gap:12 }}>
                <span style={{ fontSize:13, color:C.textSec }}>
                  What is {captchaQ.a} + {captchaQ.b}?
                </span>
                <input type="number" value={captchaQ.ans}
                  onChange={e => setCaptchaQ(q => ({...q, ans: e.target.value}))}
                  style={{ width:70, background:C.bg, border:`1px solid ${C.border}`,
                    borderRadius:6, padding:'6px 10px', color:C.text, fontSize:13,
                    fontFamily:"'DM Mono',monospace", outline:'none',
                    textAlign:'center' }} />
                <button onClick={refreshCaptcha}
                  style={{ background:'none', border:'none', color:C.textMuted,
                    cursor:'pointer', fontSize:11 }}>↺ Refresh</button>
              </div>
              {demoError && (
                <div style={{ background:C.dangerDim, border:`1px solid ${C.danger}44`,
                  borderRadius:8, padding:'10px 14px', marginBottom:14, fontSize:12,
                  color:C.danger }}>✗ {demoError}</div>
              )}
              <button onClick={submitDemo} disabled={demoSending}
                style={{ width:'100%', background:C.accent, color:'#000', border:'none',
                  borderRadius:10, padding:'14px', fontSize:15, fontWeight:700,
                  cursor: demoSending ? 'not-allowed' : 'pointer',
                  opacity: demoSending ? 0.7 : 1 }}>
                {demoSending ? 'Sending…' : 'Request Demo →'}
              </button>
              <p style={{ fontSize:11, color:C.textMuted, textAlign:'center',
                marginTop:12, lineHeight:1.5 }}>
                Or email us directly at{' '}
                <a href={`mailto:${contactTo}`}
                  style={{ color:C.accent, textDecoration:'none' }}>{contactTo}</a>.
                We respond within one business day.
              </p>
            </div>
          )}
        </div>
      </div>
  );
}

function LandingPage({ onSignIn }) {
  const docsUrl    = window.__ENV__?.VITE_DOCS_URL    || '/docs';
  const contactTo  = window.__ENV__?.VITE_CONTACT_EMAIL || 'visionaidemo@vitamap.com';
  const appURL  = window.__ENV__?.UI_URL || 'ctvisionai.cloudtrac.app';



  return (
    <div style={{ minHeight:'100vh', background:C.bg, color:C.text,
      fontFamily:"'DM Sans',sans-serif" }}>

      {/* ── Sticky Navbar ───────────────────────────────────────────────── */}
      <nav style={{ borderBottom:`1px solid ${C.border}`, padding:'0 32px',
        display:'flex', alignItems:'center', justifyContent:'space-between',
        background:C.surface, height:58, position:'sticky', top:0, zIndex:100 }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <span style={{ fontSize:20, color:C.accent }}>◈</span>
          <span style={{ fontSize:16, fontWeight:800, color:C.text,
            letterSpacing:'-0.02em' }}>VisionAI</span>
          <span style={{ fontSize:10, color:C.textMuted, background:C.accentDim,
            border:`1px solid ${C.accent}33`, padding:'2px 8px', borderRadius:10,
            fontWeight:700, letterSpacing:'0.06em', textTransform:'uppercase' }}>Beta</span>
        </div>
        <div style={{ display:'flex', gap:12, alignItems:'center' }}>
          <a href={docsUrl} target="_blank" rel="noreferrer"
            style={{ color:C.textSec, textDecoration:'none', fontSize:13,
              fontWeight:600 }}>Docs</a>
          <a href="#demo-form"
            style={{ color:C.textSec, textDecoration:'none', fontSize:13,
              fontWeight:600 }}>Request Demo</a>
          <button onClick={onSignIn} style={{
            background:C.accent, color:'#000', border:'none', borderRadius:8,
            padding:'7px 20px', fontSize:13, fontWeight:700, cursor:'pointer' }}>
            Sign In →
          </button>
        </div>
      </nav>

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <div style={{ padding:'80px 32px 64px', textAlign:'center', maxWidth:860,
        margin:'0 auto', position:'relative' }}>
        {/* Background accent */}
        <div style={{ position:'absolute', top:0, left:'50%', transform:'translateX(-50%)',
          width:600, height:300, background:`radial-gradient(ellipse at center, ${C.accent}15 0%, transparent 70%)`,
          pointerEvents:'none' }} />
        <div style={{ position:'relative' }}>
          <div style={{ display:'inline-flex', alignItems:'center', gap:8,
            background:C.accentDim, border:`1px solid ${C.accent}44`,
            borderRadius:20, padding:'5px 16px', marginBottom:28 }}>
            <span style={{ width:7, height:7, borderRadius:'50%', background:'#22c55e',
              boxShadow:'0 0 8px #22c55e', display:'inline-block',
              animation:'pulse 2s infinite' }} />
            <span style={{ fontSize:12, fontWeight:700, color:C.accent,
              letterSpacing:'0.07em', textTransform:'uppercase' }}>
              Enterprise Beta — ${appURL}
            </span>
          </div>
          <h1 style={{ fontSize:'clamp(2.2rem,5vw,3.6rem)', fontWeight:800,
            margin:'0 0 22px', fontFamily:"'DM Serif Display',serif", lineHeight:1.12,
            color:C.text }}>
            Visual intelligence at every<br />
            <span style={{ color:C.accent }}>step of your workflow</span>
          </h1>
          <p style={{ fontSize:18, color:C.textSec, lineHeight:1.75, marginBottom:38,
            maxWidth:640, margin:'0 auto 38px' }}>
            Connect AI-powered visual checks directly to your workflow engine.
            Validate compliance, extract data, and make automated decisions —
            in near real time, at any scale. Seamlessly integrate with Cloudtrac platform or your existing systems via API.
          </p>
          <div style={{ display:'flex', gap:14, justifyContent:'center', flexWrap:'wrap',
            marginBottom:32 }}>
            <button onClick={onSignIn} style={{
              background:C.accent, color:'#000', border:'none', borderRadius:10,
              padding:'15px 36px', fontSize:15, fontWeight:700, cursor:'pointer',
              boxShadow:`0 4px 32px ${C.accent}44` }}>
              Sign In to Platform →
            </button>
            <a href="#demo-form" style={{
              background:'transparent', color:C.text,
              border:`1px solid ${C.border}`, borderRadius:10,
              padding:'15px 28px', fontSize:15, fontWeight:600,
              cursor:'pointer', textDecoration:'none',
              display:'inline-block' }}>
              Request a Demo
            </a>
          </div>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'center',
            gap:8, color:C.textMuted, fontSize:12 }}>
            <span style={{ width:7, height:7, borderRadius:'50%', background:'#22c55e',
              display:'inline-block' }} />
            Seamless integration with workflow engines &nbsp;|&nbsp;
            Works with any media capture process &nbsp;|&nbsp;
            Enterprise-ready, built on Cloudtrac platform
          </div>
        </div>
      </div>

      {/* ── Social proof / metrics strip ─────────────────────────────────── */}
      <div style={{ borderTop:`1px solid ${C.border}`, borderBottom:`1px solid ${C.border}`,
        background:C.surface, padding:'28px 32px' }}>
        <div style={{ display:'grid',
          gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))',
          gap:28, maxWidth:900, margin:'0 auto', textAlign:'center' }}>
          {[
            ['Near real-time','AI validation response'],
            ['95%+','Accuracy on configured checks'],
            ['Any AI model','Top cloud & open-source providers supported'],
            ['Zero','Extra apps for field teams'],
            ['1 API call','To integrate any workflow status'],
          ].map(([v,l]) => (
            <div key={l}>
              <div style={{ fontSize:26, fontWeight:800, color:C.accent,
                fontFamily:"'DM Serif Display',serif", marginBottom:4 }}>{v}</div>
              <div style={{ fontSize:11, color:C.textSec, lineHeight:1.5 }}>{l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Problem / Solution ───────────────────────────────────────────── */}
      <div style={{ padding:'72px 32px', maxWidth:1100, margin:'0 auto' }}>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:48,
          alignItems:'start' }}>
          <div>
            <div style={{ fontSize:11, fontWeight:700, color:C.danger,
              textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:12 }}>
              The Problem
            </div>
            <h2 style={{ fontSize:28, fontWeight:700, color:C.text, marginBottom:20,
              fontFamily:"'DM Serif Display',serif", lineHeight:1.2 }}>
              Manual photo review doesn't scale
            </h2>
            {[
              'Supervisors spend hours reviewing compliance photos — one by one',
              'No structured data from images means no automation or audit trail',
              'Inconsistent enforcement leads to safety incidents and rework',
              'Workflow bottlenecks as jobs wait for manual sign-off',
            ].map(t => (
              <div key={t} style={{ display:'flex', gap:10, marginBottom:12 }}>
                <span style={{ color:C.danger, fontSize:16, flexShrink:0, marginTop:1 }}>✗</span>
                <span style={{ fontSize:14, color:C.textSec, lineHeight:1.6 }}>{t}</span>
              </div>
            ))}
          </div>
          <div>
            <div style={{ fontSize:11, fontWeight:700, color:C.success,
              textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:12 }}>
              VisionAI Solution
            </div>
            <h2 style={{ fontSize:28, fontWeight:700, color:C.text, marginBottom:20,
              fontFamily:"'DM Serif Display',serif", lineHeight:1.2 }}>
              Instant AI validation built into your workflow
            </h2>
            {[
              'AI validates photos in under 4 seconds — pass, fail, or review',
              'Returns structured JSON your engine can act on immediately',
              'Every check is evidence-stored with image, result, and timestamp',
              'One REST API call per status transition — no architecture change',
            ].map(t => (
              <div key={t} style={{ display:'flex', gap:10, marginBottom:12 }}>
                <span style={{ color:C.success, fontSize:16, flexShrink:0, marginTop:1 }}>✓</span>
                <span style={{ fontSize:14, color:C.textSec, lineHeight:1.6 }}>{t}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── How it works ─────────────────────────────────────────────────── */}
      <div style={{ background:C.surface, borderTop:`1px solid ${C.border}`,
        borderBottom:`1px solid ${C.border}`, padding:'72px 32px' }}>
        <div style={{ maxWidth:900, margin:'0 auto' }}>
          <div style={{ textAlign:'center', marginBottom:52 }}>
            <div style={{ fontSize:11, fontWeight:700, color:C.accent,
              textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:12 }}>
              Integration in hours, not weeks
            </div>
            <h2 style={{ fontSize:30, fontWeight:700, color:C.text,
              fontFamily:"'DM Serif Display',serif", margin:0 }}>
              How VisionAI fits into your workflow
            </h2>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12 }}>
            {[
              ['1', '⚙', 'Configure',
                'Map workflow statuses to AI validation configs. Use the builder wizard or choose from industry templates.'],
              ['2', '📷', 'Capture',
                'Technicians submit photos from any device — your app, the field app, or direct API call.'],
              ['3', '🤖', 'Validate',
                'VisionAI calls your chosen AI provider, extracts structured JSON, and applies your pass/fail logic.'],
              ['4', '✅', 'Decide',
                'Your workflow engine receives the result synchronously and advances, blocks, or routes — automatically.'],
            ].map(([n, icon, title, desc]) => (
              <div key={n} style={{ background:C.bg, border:`1px solid ${C.border}`,
                borderRadius:12, padding:'22px 18px', textAlign:'center',
                position:'relative' }}>
                <div style={{ position:'absolute', top:-14, left:'50%',
                  transform:'translateX(-50%)',
                  width:28, height:28, borderRadius:'50%',
                  background:C.accent, color:'#000', fontWeight:800, fontSize:13,
                  display:'flex', alignItems:'center', justifyContent:'center' }}>{n}</div>
                <div style={{ fontSize:28, marginBottom:10, marginTop:8 }}>{icon}</div>
                <div style={{ fontSize:14, fontWeight:700, color:C.text,
                  marginBottom:8 }}>{title}</div>
                <div style={{ fontSize:12, color:C.textSec, lineHeight:1.6 }}>{desc}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Key Features ─────────────────────────────────────────────────── */}
      <div style={{ padding:'72px 32px', maxWidth:1100, margin:'0 auto' }}>
        <div style={{ textAlign:'center', marginBottom:48 }}>
          <div style={{ fontSize:11, fontWeight:700, color:C.accent,
            textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:12 }}>
            Platform capabilities
          </div>
          <h2 style={{ fontSize:30, fontWeight:700, color:C.text,
            fontFamily:"'DM Serif Display',serif", margin:'0 0 12px' }}>
            Everything you need for enterprise-scale visual validation
          </h2>
          <p style={{ color:C.textSec, fontSize:15, maxWidth:600, margin:'0 auto' }}>
            From single-photo compliance checks to multi-step composite validations
            — fully configurable, fully auditable.
          </p>
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))',
          gap:16 }}>
          {[
            ['🔗','Multi-Step Solutions',
              'Chain multiple photo validations into a single composite result. Odometer in/out, before/after, multi-zone PPE — one API call, one decision.',
              C.accent],
            ['🏢','Multi-Tenant Architecture',
              'Fully isolated data, configs, and users per organisation. Token budgets, role-based access, and API keys managed per account.',
              C.purple],
            ['🤖','Any AI Provider',
              'Anthropic Claude, OpenAI GPT-4o, Google Gemini, Azure OpenAI, or a private on-premise Ollama model. Switch without code changes.',
              C.success],
            ['📱','Zero-Install Field App',
              'Mobile-optimised browser app for technicians. Camera capture, upload, or URL submission — results display on-screen immediately.',
              C.warning],
            ['🔌','One-Call Integration',
              'POST /external/validate returns pass/fail/review synchronously in the same HTTP response. No polling, no webhooks required.',
              C.accent],
            ['📊','Full Audit Trail & Analytics',
              'Every validation stored permanently with image, AI result, confidence score, and timestamp. Exportable reports and live analytics.',
              C.success],
            ['🪙','Token Budget Management',
              'Per-tenant daily and weekly token limits with top-up pools. Prevent runaway costs and plan AI spend across your enterprise accounts.',
              C.warning],
            ['🖼','Embeddable Results',
              'Drop validation evidence directly into your existing task UI with a single iframe tag. No extra login, no popup windows.',
              C.purple],
            ['📚','Industry Templates',
              'Start fast with a growing library of field-service prompt templates — PPE, meters, serial numbers, site audits, and more. Fully customisable.',
              C.accent],
          ].map(([icon, title, desc, col]) => (
            <div key={title} style={{ background:C.surface,
              border:`1px solid ${C.border}`, borderRadius:12, padding:'22px 24px',
              borderLeft:`3px solid ${col}44`, transition:'border-color 0.15s' }}>
              <div style={{ fontSize:26, marginBottom:12 }}>{icon}</div>
              <div style={{ fontSize:14, fontWeight:700, color:C.text,
                marginBottom:8 }}>{title}</div>
              <div style={{ fontSize:12.5, color:C.textSec, lineHeight:1.7 }}>{desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── API / Integration section ────────────────────────────────────── */}
      <div style={{ background:C.surface, borderTop:`1px solid ${C.border}`,
        borderBottom:`1px solid ${C.border}`, padding:'72px 32px' }}>
        <div style={{ maxWidth:1000, margin:'0 auto', display:'grid',
          gridTemplateColumns:'1fr 1fr', gap:48, alignItems:'center' }}>
          <div>
            <div style={{ fontSize:11, fontWeight:700, color:C.accent,
              textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:12 }}>
              Developer-first integration
            </div>
            <h2 style={{ fontSize:28, fontWeight:700, color:C.text, marginBottom:16,
              fontFamily:"'DM Serif Display',serif", lineHeight:1.2 }}>
              One API call. Result in 4 seconds. Zero architecture change.
            </h2>
            <p style={{ color:C.textSec, fontSize:14, lineHeight:1.7, marginBottom:20 }}>
              Add photo validation to any workflow status transition with a single
              POST request. Synchronous response — no polling, no event bus.
              Your existing integration developer can add it in an afternoon.
            </p>
            {[
              'REST API with API key authentication',
              'Synchronous response — result in same HTTP call',
              'Batch endpoint for multiple photos in one request',
              'Webhook callbacks for async flows',
              'Embeddable iframe results view',
              'Full API docs at '+docsUrl,
            ].map(t => (
              <div key={t} style={{ display:'flex', gap:8, marginBottom:8,
                alignItems:'flex-start' }}>
                <span style={{ color:C.accent, fontSize:12, flexShrink:0 }}>→</span>
                <span style={{ fontSize:13, color:C.textSec }}>{t}</span>
              </div>
            ))}
          </div>
          <div>
            <div style={{ background:'#0d1117', border:`1px solid ${C.border}`,
              borderRadius:12, padding:'20px 22px',
              fontFamily:"'DM Mono',monospace", fontSize:12,
              lineHeight:1.7, overflow:'auto' }}>
              <div style={{ color:'#7A8BA0', marginBottom:6 }}>
                {'// One call per workflow status transition'}
              </div>
              <div>
                <span style={{ color:'#f97583' }}>POST </span>
                <span style={{ color:'#e8edf5' }}>/api/v1/external/validate</span>
              </div>
              <div style={{ color:'#7A8BA0', margin:'10px 0 4px' }}>
                {'// Request'}
              </div>
              <pre style={{ margin:0, color:'#e8edf5', fontSize:11 }}>{`{
  "engineWorkflowId": "ENG-WF-001",
  "statusTrigger":    "pre_work_check",
  "paramName":        "ppe_photo",
  "imageUrl":         "https://...",
  "jobId":            "WO-2025-001"
}`}</pre>
              <div style={{ color:'#7A8BA0', margin:'10px 0 4px' }}>
                {'// Response in < 4 seconds'}
              </div>
              <pre style={{ margin:0, fontSize:11 }}><span style={{ color:'#79c0ff' }}>{`{
  `}</span><span style={{ color:'#7ee787' }}>"validationStatus"</span><span style={{ color:'#e8edf5' }}>{`: "pass",
  `}</span><span style={{ color:'#7ee787' }}>"confidence"</span><span style={{ color:'#e8edf5' }}>{`: 0.96,
  `}</span><span style={{ color:'#7ee787' }}>"parsedResult"</span><span style={{ color:'#e8edf5' }}>{`: {
    "hard_hat": true,
    "safety_score": 0.96
  }
}`}</span></pre>
            </div>
          </div>
        </div>
      </div>

      {/* ── Industry Templates & Playground ──────────────────────────────────── */}
      <div style={{ background:C.surface, borderTop:`1px solid ${C.border}`,
        borderBottom:`1px solid ${C.border}`, padding:'72px 32px' }}>
        <div style={{ maxWidth:1100, margin:'0 auto' }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:56,
            alignItems:'center' }}>
            <div>
              <div style={{ fontSize:11, fontWeight:700, color:C.accent,
                textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:12 }}>
                Industry-standard templates
              </div>
              <h2 style={{ fontSize:28, fontWeight:700, color:C.text, marginBottom:16,
                fontFamily:"'DM Serif Display',serif", lineHeight:1.2 }}>
                Start validating in minutes,<br />not months
              </h2>
              <p style={{ color:C.textSec, fontSize:14, lineHeight:1.75, marginBottom:20 }}>
                VisionAI ships with a growing library of ready-to-deploy field service
                validation templates — from PPE compliance to odometer checks, serial number
                capture, and multi-zone site audits. Each template is production-tested,
                fully customisable, and directly extensible with your own prompts and schemas.
              </p>
              {[
                ['🦺', 'PPE Compliance',       'Hard hat, hi-vis, boots, gloves — configurable per environment'],
                ['🚗', 'Fleet Odometer',        'Check-in / check-out meter reading with distance calculation'],
                ['🔢', 'Asset Serial Numbers',  'Extract serial, model, and asset tag from equipment nameplates'],
                ['🏗', 'Site Before / After',   'AI-compares before and after photos for documentation'],
                ['📋', 'Inspection Checklist',  'Multi-item visual audit with individual pass/fail per item'],
                ['+ Many more', '',             'New templates added regularly — or build your own in minutes'],
              ].map(([icon, title, desc]) => (
                <div key={icon} style={{ display:'flex', gap:12, marginBottom:10,
                  alignItems:'flex-start' }}>
                  <span style={{ fontSize:16, flexShrink:0, marginTop:2 }}>{icon}</span>
                  <div>
                    {title && <span style={{ fontSize:13, fontWeight:700, color:C.text }}>
                      {title}{desc ? ' — ' : ''}</span>}
                    <span style={{ fontSize:13, color:C.textSec }}>{desc}</span>
                  </div>
                </div>
              ))}
            </div>
            <div>
              {/* Playground teaser card */}
              <div style={{ background:C.bg, border:`2px solid ${C.accent}44`,
                borderRadius:16, padding:'32px 28px', textAlign:'center',
                position:'relative', overflow:'hidden' }}>
                <div style={{ position:'absolute', top:0, left:'50%',
                  transform:'translateX(-50%)',
                  width:300, height:200,
                  background:`radial-gradient(ellipse at top, ${C.accent}18 0%, transparent 70%)`,
                  pointerEvents:'none' }} />
                <div style={{ fontSize:40, marginBottom:12 }}>🎮</div>
                <div style={{ fontSize:20, fontWeight:700, color:C.text, marginBottom:8,
                  fontFamily:"'DM Serif Display',serif" }}>
                  AI Validation Playground
                </div>
                <div style={{ fontSize:13, color:C.textSec, lineHeight:1.7, marginBottom:20 }}>
                  Try VisionAI live — no account required.
                  Pick a template, upload a photo, and see the AI validate it in real time.
                  <br /><br />
                  <span style={{ color:C.accent, fontWeight:600 }}>
                    Coming soon for selected templates.
                  </span>
                </div>
                <div style={{ display:'flex', gap:10, justifyContent:'center', flexWrap:'wrap' }}>
                  <a href="#demo-form" style={{
                    background:C.accent, color:'#000', border:'none', borderRadius:8,
                    padding:'10px 22px', fontSize:13, fontWeight:700,
                    cursor:'pointer', textDecoration:'none', display:'inline-block' }}>
                    Request Early Access →
                  </a>
                </div>
                <div style={{ marginTop:16, fontSize:11, color:C.textMuted, lineHeight:1.5 }}>
                  Available to demo participants and Cloudtrac enterprise users.<br />
                  No credit card, no installation required.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

            {/* ── Cloudtrac Integration ──────────────────────────────────────────── */}
      <div style={{ padding:'72px 32px', maxWidth:1100, margin:'0 auto' }}>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:48,
          alignItems:'center' }}>
          {/* Left: Cloudtrac feature */}
          <div>
            <div style={{ display:'inline-flex', alignItems:'center', gap:8,
              background:'#7C3AED22', border:'1px solid #7C3AED44',
              borderRadius:20, padding:'4px 14px', marginBottom:20 }}>
              <span style={{ fontSize:12, fontWeight:700, color:'#A78BFA',
                letterSpacing:'0.06em' }}>CLOUDTRAC WORKFLOW AUTOMATION</span>
            </div>
            <h2 style={{ fontSize:28, fontWeight:700, color:C.text, marginBottom:16,
              fontFamily:"'DM Serif Display',serif", lineHeight:1.2 }}>
              Integrated with Cloudtrac NCLC field operations
            </h2>
            <p style={{ color:C.textSec, fontSize:14, lineHeight:1.75, marginBottom:20 }}>
              Combine the power of{' '}
              <strong style={{ color:C.text }}>Cloudtrac's workflow automation and reporting</strong>
              {' '}with the vision intelligence of{' '}
              <strong style={{ color:'#A78BFA' }}>CTVisionAI</strong>
              {' '}to transform how you deliver and govern field operations.
            </p>
            {[
              'Breakdown response — validate technician arrival evidence and PPE before work begins',
              'Preventive maintenance — capture meter readings, before/after site state, serial numbers',
              'Inspection & commissioning — photo evidence at every sign-off stage, automatically structured',
              'Patrolling & compliance — route verification, site condition audits, defect documentation',
            ].map(t => (
              <div key={t} style={{ display:'flex', gap:10, marginBottom:12,
                alignItems:'flex-start' }}>
                <span style={{ color:'#A78BFA', fontSize:14, flexShrink:0 }}>◈</span>
                <span style={{ fontSize:13, color:C.textSec, lineHeight:1.6 }}>{t}</span>
              </div>
            ))}
          </div>

          {/* Right: Integration flow card */}
          <div style={{ background:C.surface, border:`1px solid #7C3AED44`,
            borderRadius:16, padding:'28px 28px', position:'relative',
            overflow:'hidden' }}>
            <div style={{ position:'absolute', top:0, right:0, width:200, height:200,
              background:'radial-gradient(circle at top right, #7C3AED18 0%, transparent 70%)',
              pointerEvents:'none' }} />
            <div style={{ fontSize:12, fontWeight:700, color:'#A78BFA',
              textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:16 }}>
              How it connects
            </div>
            {[
              ['Cloudtrac Job', 'Field technician reaches a workflow status requiring photo evidence'],
              ['CTVisionAI',    'Receives the image via one API call — validates in near real-time'],
              ['Structured Result', 'Pass / fail / review returned with JSON data to Cloudtrac'],
              ['Workflow Advances', 'Cloudtrac auto-proceeds, blocks, or routes based on AI decision'],
              ['Full Audit Trail', 'Image + result stored in CTVisionAI, linked to the Cloudtrac job ID'],
            ].map(([step, desc], i) => (
              <div key={step} style={{ display:'flex', gap:12, marginBottom:14,
                paddingBottom:14,
                borderBottom: i < 4 ? `1px solid ${C.border}` : 'none' }}>
                <div style={{ width:26, height:26, borderRadius:'50%', background:'#7C3AED22',
                  border:'1px solid #7C3AED44', display:'flex', alignItems:'center',
                  justifyContent:'center', fontSize:11, fontWeight:700, color:'#A78BFA',
                  flexShrink:0, marginTop:1 }}>{i+1}</div>
                <div>
                  <div style={{ fontSize:12, fontWeight:700, color:C.text,
                    marginBottom:2 }}>{step}</div>
                  <div style={{ fontSize:11, color:C.textSec, lineHeight:1.5 }}>{desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Extends to any platform ──────────────────────────────────────────── */}
      <div style={{ background:C.surface, borderTop:`1px solid ${C.border}`,
        borderBottom:`1px solid ${C.border}`, padding:'48px 32px' }}>
        <div style={{ maxWidth:900, margin:'0 auto', textAlign:'center' }}>
          <div style={{ fontSize:11, fontWeight:700, color:C.textSec,
            textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:16 }}>
            Not just Cloudtrac
          </div>
          <h2 style={{ fontSize:24, fontWeight:700, color:C.text, marginBottom:12,
            fontFamily:"'DM Serif Display',serif" }}>
            Integrates with any workflow platform or CRM
          </h2>
          <p style={{ color:C.textSec, fontSize:14, lineHeight:1.7, marginBottom:32,
            maxWidth:600, margin:'0 auto 32px' }}>
            CTVisionAI exposes a standard REST API. Connect it to your existing workflow engine,
            ERP, or field service management platform with a single API call per status transition.
          </p>
          <div style={{ display:'flex', gap:12, justifyContent:'center', flexWrap:'wrap' }}>
            {['Cloudtrac NCLC','ServiceNow','SAP Field Service','Salesforce FSM',
              'Custom Workflow Engine','Any REST API-enabled platform'].map(p => (
              <div key={p} style={{ background:C.bg, border:`1px solid ${C.border}`,
                borderRadius:20, padding:'6px 16px', fontSize:12, color:C.textSec,
                fontWeight:600 }}>
                {p}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Enterprise section ───────────────────────────────────────────── */}
      <div style={{ padding:'72px 32px', maxWidth:1000, margin:'0 auto',
        textAlign:'center' }}>
        <div style={{ fontSize:11, fontWeight:700, color:C.purple,
          textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:12 }}>
          Built for enterprise operations
        </div>
        <h2 style={{ fontSize:28, fontWeight:700, color:C.text, marginBottom:40,
          fontFamily:"'DM Serif Display',serif" }}>
          The management layer your operations team needs
        </h2>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:16 }}>
          {[
            ['🏢','Multi-Account Management',
              'Superadmins oversee all tenants. Account managers access multiple mapped organisations. Per-tenant data isolation is enforced at the database layer.'],
            ['🪙','Token Budget Control',
              'Set daily and weekly token limits per tenant. Add top-up pools for burst capacity. Block or warn at configurable thresholds — no surprise AI bills.'],
            ['👥','Role-Based Access',
              'Five roles from superadmin to field viewer. SSO integration with your existing identity provider — no separate password management required.'],
            ['📋','Workflow Governance',
              'Validation configs are versioned. Every change is audited. Roll back any prompt to a previous version and compare results.'],
            ['☁️','Cloud-Delivered, Always On',
              'Fully managed, cloud-delivered platform. Your images are processed by the AI provider you configure. Available wherever your field teams operate.'],
            ['📈','Reporting & Compliance',
              'Aggregate reports by workflow, tenant, or status. CSV export for external compliance tools. Per-field analytics to track trends over time.'],
          ].map(([icon, title, desc]) => (
            <div key={title} style={{ background:C.surface, border:`1px solid ${C.border}`,
              borderRadius:12, padding:'22px 20px', textAlign:'left' }}>
              <div style={{ fontSize:24, marginBottom:10 }}>{icon}</div>
              <div style={{ fontSize:13, fontWeight:700, color:C.text,
                marginBottom:8 }}>{title}</div>
              <div style={{ fontSize:12, color:C.textSec, lineHeight:1.65 }}>{desc}</div>
            </div>
          ))}
        </div>
      </div>

            {/* ── Request Demo / Contact form ──────────────────────────────────── */}
      <DemoForm contactTo={contactTo} />

      {/* ── Footer ───────────────────────────────────────────────────────── */}
      <div style={{ borderTop:`1px solid ${C.border}`, padding:'24px 32px',
        display:'flex', alignItems:'center', justifyContent:'space-between',
        flexWrap:'wrap', gap:12, color:C.textMuted, fontSize:12 }}>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <span style={{ color:C.accent, fontSize:16 }}>◈</span>
          <span>{getAppName()} by Vitamap &nbsp;|&nbsp; {(getAppTagline())}</span>
        </div>
        <div style={{ display:'flex', gap:20, flexWrap:'wrap' }}>
          <button onClick={onSignIn}
            style={{ background:'none', border:'none', color:C.textMuted,
              cursor:'pointer', fontSize:12 }}>Sign In</button>
          <a href={docsUrl} target="_blank" rel="noreferrer"
            style={{ color:C.textMuted, textDecoration:'none' }}>Documentation</a>
          <a href="#demo-form"
            style={{ color:C.textMuted, textDecoration:'none' }}>Request Demo</a>
          <a href={`mailto:${contactTo}`}
            style={{ color:C.textMuted, textDecoration:'none' }}>Contact</a>
        </div>
      </div>
    </div>
  );
}


// ── Dashboard ─────────────────────────────────────────────────────────────────
function Dashboard({ user, onNavigate }) {
  const [stats,      setStats]      = useState(null);
  const [recent,     setRecent]     = useState([]);
  const [topData,    setTopData]    = useState({ configs:[], solutions:[] });
  const [cfgCount,   setCfgCount]   = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [dateFrom,   setDateFrom]   = useState('');
  const [dateTo,     setDateTo]     = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const p = {};
    if (dateFrom) p.dateFrom = dateFrom;
    if (dateTo)   p.dateTo   = dateTo;
    try {
      const [s, r, top, cfgs] = await Promise.all([
        api.runs.summary(p),
        api.runs.list({ limit:8, ...p }),
        api.runs.topConfigs({ limit:5 }),
        api.configs.list({ limit:1 }),
      ]);
      setStats(s.data);
      setRecent(r.data || []);
      setTopData(top.data || { configs:[], solutions:[] });
      setCfgCount(cfgs.pagination?.total ?? null);
    } catch(e) { console.error(e); }
    setLoading(false);
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const s    = stats?.summary || {};
  const isViewer = user.role === 'viewer';
  const isSA = user.role === 'superadmin';

  const sc = v => v==='pass' ? C.success : v==='fail' ? C.danger : C.warning;

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom:20, display:'flex', justifyContent:'space-between',
        alignItems:'center', flexWrap:'wrap', gap:12 }}>
        <div>
          <div style={{ fontSize:24, fontWeight:700, fontFamily:"'DM Serif Display',serif",
            color:C.text, marginBottom:4 }}>
            Vision AI Control Center
          </div>
          <div style={{ color:C.textSec, fontSize:13 }}>
            Welcome back, {user.name}
            {user.tenant_name && (
              <span style={{ color:C.textMuted }}> &middot; {user.tenant_name}</span>
            )}
          </div>
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
          <Input label="From" type="date" value={dateFrom} onChange={setDateFrom}
            style={{ marginBottom:0, width:140 }} />
          <Input label="To"   type="date" value={dateTo}   onChange={setDateTo}
            style={{ marginBottom:0, width:140 }} />
          <Btn variant="ghost" size="sm" style={{ marginTop:18 }}
            onClick={() => { setDateFrom(''); setDateTo(''); }}>Clear</Btn>
          <Btn variant="ghost" size="sm" style={{ marginTop:18 }} onClick={load}>
            ↺ Refresh
          </Btn>
        </div>
      </div>

      {loading ? <Spinner /> : (
        <>
          {/* ── KPI tiles ─────────────────────────────────────────────── */}
          <div style={{ display:'flex', gap:12, flexWrap:'wrap', marginBottom:20 }}>
            {[
              ['Total Validations', parseInt(s.total||0).toLocaleString(),        C.accent],
              ['Pass Rate',         `${s.pass_rate_pct||0}%`,                     C.success],
              ['Avg Confidence',    `${s.avg_confidence_pct||0}%`,                C.purple],
              ['Avg Processing',    s.avg_processing_ms ? `${s.avg_processing_ms}ms` : '—', C.warning],
              ...(user.role!=='viewer' ? [['Tokens (In/Out)',
                `${parseInt(s.total_tokens_input||0).toLocaleString()} / ${parseInt(s.total_tokens_output||0).toLocaleString()}`,
                C.textSec, true]] : []),
            ].map(([l,v,c,clickable]) => (
              <Card key={l}
                onClick={clickable ? () => onNavigate && onNavigate('settings', { tab:'tokens' }) : undefined}
                style={{ flex:1, minWidth:120, cursor: clickable ? 'pointer' : 'default',
                  transition:'border-color 0.15s' }}
                onMouseEnter={clickable ? e=>e.currentTarget.style.borderColor=C.accent+'66' : undefined}
                onMouseLeave={clickable ? e=>e.currentTarget.style.borderColor=C.border : undefined}>
                <div style={{ fontSize:11, color:C.textSec, textTransform:'uppercase',
                  letterSpacing:'0.08em', marginBottom:8,
                  fontFamily:"'DM Mono',monospace" }}>
                  {l}{clickable && <span style={{ color:C.textMuted, fontSize:9 }}> ↗</span>}
                </div>
                <div style={{ fontSize:24, fontWeight:700, color:c,
                  fontFamily:"'DM Serif Display',serif", lineHeight:1 }}>{v}</div>
              </Card>
            ))}
          </div>

          {/* ── Config summary + shortcut ─────────────────────────────── */}
          {!isViewer && (
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12,
              marginBottom:16 }}>
              <Card style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                padding:'14px 18px' }}>
                <div>
                  <div style={{ fontSize:11, color:C.textSec, textTransform:'uppercase',
                    letterSpacing:'0.07em', fontFamily:"'DM Mono',monospace",
                    marginBottom:4 }}>Active Configs</div>
                  <div style={{ fontSize:28, fontWeight:700, color:C.accent,
                    fontFamily:"'DM Serif Display',serif" }}>
                    {cfgCount !== null ? cfgCount : '—'}
                  </div>
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                  {!isViewer && (
                    <Btn size="sm" onClick={() => onNavigate && onNavigate('workflow-config')}>
                      ⚙ Manage Configs
                    </Btn>
                  )}
                  {!isViewer && !['account_manager','viewer'].includes(user.role) && (
                    <Btn size="sm" variant="ghost"
                      onClick={() => onNavigate && onNavigate('config-builder')}>
                      + New Config
                    </Btn>
                  )}
                </div>
              </Card>
              <Card style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                padding:'14px 18px' }}>
                <div>
                  <div style={{ fontSize:11, color:C.textSec, textTransform:'uppercase',
                    letterSpacing:'0.07em', fontFamily:"'DM Mono',monospace",
                    marginBottom:4 }}>Active Solutions</div>
                  <div style={{ fontSize:28, fontWeight:700, color:C.purple,
                    fontFamily:"'DM Serif Display',serif" }}>
                    {topData.solutions.length > 0 ? `${topData.solutions.length}+` : '—'}
                  </div>
                </div>
                <Btn size="sm" onClick={() => onNavigate && onNavigate('solutions')}>
                  🔗 Solutions
                </Btn>
              </Card>
            </div>
          )}

          {/* ── Top 5 configs/solutions + Recent runs ────────────────── */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 320px', gap:16 }}>

            {/* Left: recent runs — clickable */}
            <Card>
              <div style={{ fontSize:14, fontWeight:700, color:C.text, marginBottom:16,
                display:'flex', alignItems:'center', gap:8 }}>
                <span style={{ width:8, height:8, borderRadius:'50%', background:C.success,
                  boxShadow:`0 0 8px ${C.success}` }} />
                Recent Validations
                <span style={{ marginLeft:'auto', fontSize:11, color:C.textMuted,
                  fontWeight:400 }}>
                  Click any row to view results
                </span>
              </div>
              {recent.length === 0 ? (
                <div style={{ color:C.textMuted, fontSize:13, textAlign:'center',
                  padding:20 }}>No validations yet</div>
              ) : recent.map(v => {
                const cat = CATS[v.workflow_category] || CATS.custom;
                const sc2 = v.validation_status==='pass' ? C.success
                          : v.validation_status==='fail' ? C.danger : C.warning;
                return (
                  <div key={v.id}
                    onClick={() => onNavigate && onNavigate('results', { runId: v.id })}
                    style={{ display:'flex', alignItems:'center', gap:12,
                      padding:'10px 14px', background:C.bg, borderRadius:10,
                      border:`1px solid ${C.border}`, marginBottom:8,
                      cursor:'pointer', transition:'border-color 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor=C.accent+'66'}
                    onMouseLeave={e => e.currentTarget.style.borderColor=C.border}>
                    <span style={{ fontSize:20 }}>{cat.icon}</span>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:13, fontWeight:600, color:C.text,
                        overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                        {v.workflow_name}
                      </div>
                      <div style={{ fontSize:11, color:C.textSec }}>
                        {v.param_name} | {timeAgo(v.created_at)}
                        {v.job_id && (
                          <span style={{ color:C.textMuted,
                            fontFamily:"'DM Mono',monospace" }}> | {v.job_id}</span>
                        )}
                      </div>
                    </div>
                    <span style={{ fontSize:11, color:C.textSec,
                      fontFamily:"'DM Mono',monospace", flexShrink:0 }}>
                      {v.confidence ? `${(v.confidence*100).toFixed(0)}%` : '—'}
                    </span>
                    <Badge c={sc2} label={v.validation_status} />
                    <span style={{ color:C.textMuted, fontSize:14 }}>›</span>
                  </div>
                );
              })}
            </Card>

            {/* Right: by-status + top configs/solutions */}
            <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
              <Card>
                <div style={{ fontSize:13, fontWeight:700, color:C.text, marginBottom:14 }}>
                  By Status
                </div>
                {[['Passed',s.passed,C.success],['Failed',s.failed,C.danger],
                  ['Review',s.review,C.warning],['Errors',s.errors,C.textSec]].map(([l,v,c]) => (
                  <div key={l} style={{ display:'flex', justifyContent:'space-between',
                    padding:'6px 0', borderBottom:`1px solid ${C.border}` }}>
                    <span style={{ fontSize:13, color:C.textSec }}>{l}</span>
                    <span style={{ fontSize:13, fontWeight:700, color:c,
                      fontFamily:"'DM Mono',monospace" }}>
                      {parseInt(v||0).toLocaleString()}
                    </span>
                  </div>
                ))}
              </Card>

              {/* Top configs */}
              {topData.configs.length > 0 && (
                <Card>
                  <div style={{ fontSize:13, fontWeight:700, color:C.text, marginBottom:10,
                    display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <span>Top Configs</span>
                    <span style={{ fontSize:10, color:C.textMuted, fontWeight:400 }}>
                      by run count
                    </span>
                  </div>
                  {topData.configs.map((c, i) => (
                    <div key={c.config_id}
                      onClick={() => c.last_run_id && onNavigate && onNavigate('results', { runId: c.last_run_id })}
                      style={{ display:'flex', alignItems:'center', gap:8,
                        padding:'6px 0', borderBottom:`1px solid ${C.border}`,
                        cursor: c.last_run_id ? 'pointer' : 'default' }}
                      onMouseEnter={e => c.last_run_id && (e.currentTarget.style.opacity='0.8')}
                      onMouseLeave={e => (e.currentTarget.style.opacity='1')}>
                      <span style={{ fontSize:10, color:C.textMuted, width:14,
                        fontFamily:"'DM Mono',monospace", flexShrink:0 }}>
                        {i+1}
                      </span>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:11, fontWeight:600, color:C.text,
                          overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                          {c.param_name}
                        </div>
                        <div style={{ fontSize:10, color:C.textMuted }}>
                          {c.workflow_name}
                        </div>
                      </div>
                      <span style={{ fontSize:10, color:C.accent,
                        fontFamily:"'DM Mono',monospace", flexShrink:0 }}>
                        {parseInt(c.total_runs||0)}
                      </span>
                    </div>
                  ))}
                </Card>
              )}

              {/* Top solutions */}
              {topData.solutions.length > 0 && (
                <Card>
                  <div style={{ fontSize:13, fontWeight:700, color:C.text, marginBottom:10,
                    display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <span>Top Solutions</span>
                    <span style={{ fontSize:10, color:C.textMuted, fontWeight:400 }}>
                      by run count
                    </span>
                  </div>
                  {topData.solutions.map((s2, i) => (
                    <div key={s2.solution_id}
                      onClick={() => s2.last_run_id && onNavigate &&
                        onNavigate('solution-runs', { solRunId: s2.last_run_id })}
                      style={{ display:'flex', alignItems:'center', gap:8,
                        padding:'6px 0', borderBottom:`1px solid ${C.border}`,
                        cursor: s2.last_run_id ? 'pointer' : 'default' }}
                      onMouseEnter={e => s2.last_run_id && (e.currentTarget.style.opacity='0.8')}
                      onMouseLeave={e => (e.currentTarget.style.opacity='1')}>
                      <span style={{ fontSize:10, color:C.textMuted, width:14,
                        fontFamily:"'DM Mono',monospace", flexShrink:0 }}>
                        {i+1}
                      </span>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:11, fontWeight:600, color:C.text,
                          overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                          {s2.solution_name}
                        </div>
                        <div style={{ fontSize:10, color:C.textMuted }}>
                          {s2.workflow_name}
                        </div>
                      </div>
                      <span style={{ fontSize:10, color:C.purple,
                        fontFamily:"'DM Mono',monospace", flexShrink:0 }}>
                        {parseInt(s2.total_runs||0)}
                      </span>
                    </div>
                  ))}
                </Card>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}


// ── ConfigBuilder — AI-assisted prompt + schema design ────────────────────────
// 3-phase wizard:
//   Phase 1: Context   — describe photo purpose, what to validate/extract
//   Phase 2: Images    — upload 1-5 sample images for AI analysis
//   Phase 3: Refine    — AI generates prompt+schema → test on samples → save
//
// Output: a prompt_library entry + optional direct mapping to a workflow config param
// ─────────────────────────────────────────────────────────────────────────────
function ConfigBuilder({ user, onSaved, initialTemplate }) {
  const PHASES = ['context','images','generate','map'];

  const [phase,        setPhase]        = useState(initialTemplate ? 'generate' : 'context');
  const [ctx,          setCtx]          = useState({
    purpose:'', photoSubject:'', validateItems:'', extractItems:'',
    involvesPeople:false, industry:'field service', notes:'',
  });
  const [images,       setImages]       = useState([]); // [{base64?,url?,label?,mediaType?,size_kb?}]
  const [urlInput,     setUrlInput]     = useState('');
  const [generating,   setGenerating]   = useState(false);
  const [generated,    setGenerated]    = useState(initialTemplate ? {
    prompt:           initialTemplate.prompt_text,
    resultSchema:     initialTemplate.result_schema,
    suggestedName:    initialTemplate.name,
    suggestedCategory:initialTemplate.category,
    explanation:      initialTemplate.description || '',
  } : null);
  const [templateName, setTemplateName] = useState(initialTemplate?.name || '');
  const [templateDesc, setTemplateDesc] = useState(initialTemplate?.description || '');
  const [editPrompt,   setEditPrompt]   = useState(initialTemplate?.prompt_text || '');
  const [editSchema,   setEditSchema]   = useState(initialTemplate ? JSON.stringify(initialTemplate.result_schema, null, 2) : '');
  const [testing,      setTesting]      = useState(false);
  const [testResults,  setTestResults]  = useState([]);
  const [saving,       setSaving]       = useState(false);
  const [savedTemplate,setSavedTemplate]= useState(null);

  // Map-to-param state
  const [workflows,    setWorkflows]    = useState([]);
  const [selWorkflow,  setSelWorkflow]  = useState('');
  const [selStatus,    setSelStatus]    = useState('');
  const [paramName,    setParamName]    = useState('');
  const [mapAction,    setMapAction]    = useState('flag_fail');
  const [mapConf,      setMapConf]      = useState('0.85');
  const [provList,     setProvList]     = useState([]);
  const [selProvider,  setSelProvider]  = useState('');
  const [mapping,      setMapping]      = useState(false);
  const [mapped,       setMapped]       = useState(false);

  const fileRef = useRef(null);

  useEffect(() => {
    if (phase === 'map') {
      Promise.all([api.workflows.list(), providersApi.list()])
        .then(([wR, pR]) => { setWorkflows(wR.data||[]); setProvList((pR.data||[]).filter(p=>p.is_active)); })
        .catch(console.error);
    }
  }, [phase]);

  // Sync generated → edit fields whenever generated changes
  useEffect(() => {
    if (generated) {
      setEditPrompt(generated.prompt);
      setEditSchema(JSON.stringify(generated.resultSchema, null, 2));
      if (!templateName) setTemplateName(generated.suggestedName || '');
      if (!templateDesc) setTemplateDesc(generated.explanation || '');
    }
  }, [generated]);

  // ── Image helpers ──────────────────────────────────────────────────────────
  const addFile = (file) => {
    if (images.length >= 5) return;
    const reader = new FileReader();
    reader.onload = ev => {
      const b64 = ev.target.result.split(',')[1];
      const sizeKb = Math.round(file.size / 1024);
      setImages(prev => [...prev, {
        base64: b64, mediaType: file.type || 'image/jpeg',
        label: file.name, size_kb: sizeKb,
      }]);
    };
    reader.readAsDataURL(file);
  };

  const addUrl = () => {
    if (!urlInput.trim() || images.length >= 5) return;
    setImages(prev => [...prev, { url: urlInput.trim(), label: urlInput.trim().split('/').pop() }]);
    setUrlInput('');
  };

  const removeImage = (i) => setImages(prev => prev.filter((_,idx) => idx !== i));

  // ── Phase 3: Generate ──────────────────────────────────────────────────────
  const generate = async () => {
    setGenerating(true); setGenerated(null); setTestResults([]);
    try {
      const r = await builderApi.generate({
        context: ctx,
        images:  images.map(({ base64, url, mediaType, label }) => ({ base64, url, mediaType, label })),
      });
      setGenerated(r.data);
    } catch (e) { alert('Generation failed: ' + e.message); }
    setGenerating(false);
  };

  // ── Phase 3: Test on samples ───────────────────────────────────────────────
  const testOnSamples = async () => {
    if (!editPrompt) return;
    let schema;
    try { schema = JSON.parse(editSchema); }
    catch { return alert('Result schema is not valid JSON'); }

    setTesting(true); setTestResults([]);
    try {
      const imgsToTest = images.filter(i => i.base64 || i.url);
      if (imgsToTest.length === 0) {
        alert('Add at least one sample image first'); setTesting(false); return;
      }
      const r = await builderApi.testOnSamples({
        prompt: editPrompt, resultSchema: schema,
        images: imgsToTest.map(({ base64, url, mediaType, label }) => ({ base64, url, mediaType, label })),
      });
      setTestResults(r.data?.results || []);
    } catch (e) { alert('Test failed: ' + e.message); }
    setTesting(false);
  };

  // ── Save template ──────────────────────────────────────────────────────────
  const saveTemplate = async () => {
    if (!templateName.trim()) return alert('Enter a template name');
    let schema;
    try { schema = JSON.parse(editSchema); }
    catch { return alert('Result schema is not valid JSON'); }

    setSaving(true);
    try {
      const sampleImageRefs = images.map(({ url, size_kb, label }) => ({ url, size_kb, label }));
      const r = await promptLibApi.create({
        name:           templateName,
        description:    templateDesc,
        promptText:     editPrompt,
        resultSchema:   schema,
        category:       generated?.suggestedCategory || 'custom',
        tags:           [ctx.industry, ctx.purpose].filter(Boolean).map(s => s.toLowerCase().replace(/\s+/g, '_')).slice(0, 3),
        builderContext: ctx,
        sampleImages:   sampleImageRefs,
      });
      setSavedTemplate(r.data);
      setPhase('map');
    } catch (e) { alert('Save failed: ' + e.message); }
    setSaving(false);
  };

  // ── Map to param ───────────────────────────────────────────────────────────
  const mapToParam = async () => {
    if (!selWorkflow || !selStatus || !paramName.trim()) {
      return alert('Select workflow, status trigger and enter a param name');
    }
    const template = savedTemplate || initialTemplate;
    let schema;
    try { schema = JSON.parse(editSchema); }
    catch { return alert('Result schema is not valid JSON'); }

    setMapping(true);
    try {
      await api.configs.create({
        workflowId:    selWorkflow,
        statusTrigger: selStatus,
        paramName:     paramName.trim(),
        prompt:        editPrompt,
        resultSchema:  schema,
        action:        mapAction,
        minConfidence: parseFloat(mapConf),
        aiProviderId:  selProvider || undefined,
        libraryId:     template?.id || undefined,
      });
      setMapped(true);
      if (onSaved) onSaved();
    } catch (e) { alert('Mapping failed: ' + e.message); }
    setMapping(false);
  };

  const selWf = workflows.find(w => w.id === selWorkflow);
  const statusOptions = selWf ? selWf.statuses.map(s => ({ value: s, label: s })) : [];

  // ── Phase indicators ───────────────────────────────────────────────────────
  const phaseLabels = [
    { id:'context',  num:1, label:'Context' },
    { id:'images',   num:2, label:'Sample Images' },
    { id:'generate', num:3, label:'Generate & Refine' },
    { id:'map',      num:4, label:'Map to Workflow' },
  ];

  const PhaseBar = () => (
    <div style={{ display:'flex', gap:0, marginBottom:28 }}>
      {phaseLabels.map((p, i) => {
        const idx = phaseLabels.findIndex(pl => pl.id === phase);
        const done = i < idx;
        const active = p.id === phase;
        const color = done ? C.success : active ? C.accent : C.textMuted;
        return (
          <div key={p.id} style={{ display:'flex', alignItems:'center', flex:1 }}>
            <div onClick={() => { if (done || active) setPhase(p.id); }} style={{
              display:'flex', alignItems:'center', gap:8, cursor:(done||active)?'pointer':'default',
              padding:'8px 12px', borderRadius:8, background:active?C.accentDim:'transparent',
              border:`1px solid ${active?C.accent+'44':done?C.success+'33':'transparent'}`, flex:1, transition:'all 0.2s',
            }}>
              <div style={{ width:22, height:22, borderRadius:'50%', background:color+'22', border:`2px solid ${color}`,
                display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:700, color, flexShrink:0 }}>
                {done ? '✓' : p.num}
              </div>
              <span style={{ fontSize:12, fontWeight:active?600:400, color, whiteSpace:'nowrap' }}>{p.label}</span>
            </div>
            {i < phaseLabels.length - 1 && (
              <div style={{ flex:'0 0 20px', height:1, background:done?C.success+'44':C.border, margin:'0 4px' }} />
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <div style={{ maxWidth:1100, margin:'0 auto' }}>
      <div style={{ marginBottom:24 }}>
        <div style={{ fontSize:20, fontWeight:700, fontFamily:"'DM Serif Display',serif", color:C.text, marginBottom:4 }}>
          🔬 Config Builder
        </div>
        <div style={{ color:C.textSec, fontSize:13 }}>
          AI-assisted prompt & schema design from sample images and context
        </div>
      </div>

      <PhaseBar />

      {/* ── Phase 1: Context ─────────────────────────────────────────────── */}
      {phase === 'context' && (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
          <Card>
            <div style={{ fontSize:14, fontWeight:700, color:C.text, marginBottom:16 }}>📋 What is this photo for?</div>
            <Select label="Industry / Context" value={ctx.industry} onChange={v=>setCtx(c=>({...c,industry:v}))}
              options={[
                {value:'field service',label:'Field Service / Maintenance'},
                {value:'construction',label:'Construction & Civil'},
                {value:'utilities',label:'Utilities / Grid / Power'},
                {value:'oil & gas',label:'Oil & Gas'},
                {value:'manufacturing',label:'Manufacturing'},
                {value:'logistics',label:'Logistics / Delivery'},
                {value:'healthcare',label:'Healthcare / Clinical'},
                {value:'solar & renewables',label:'Solar & Renewables'},
                {value:'hvac',label:'HVAC & Refrigeration'},
                {value:'other',label:'Other'},
              ]} />
            <Input label="Purpose of this photo"
              value={ctx.purpose} onChange={v=>setCtx(c=>({...c,purpose:v}))}
              placeholder="e.g. Safety check before work begins, delivery confirmation, meter reading" />
            <Input label="What/who is in the photo?"
              value={ctx.photoSubject} onChange={v=>setCtx(c=>({...c,photoSubject:v}))}
              placeholder="e.g. Field technician in PPE, equipment nameplate, vehicle dashboard" />
            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14, padding:'10px 14px',
              background:C.bg, borderRadius:8, border:`1px solid ${C.border}`, cursor:'pointer' }}
              onClick={()=>setCtx(c=>({...c,involvesPeople:!c.involvesPeople}))}>
              <div style={{ width:18, height:18, borderRadius:4, background:ctx.involvesPeople?C.accent:C.bg,
                border:`2px solid ${ctx.involvesPeople?C.accent:C.border}`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                {ctx.involvesPeople && <span style={{ color:'#000', fontSize:12, fontWeight:700 }}>✓</span>}
              </div>
              <div>
                <div style={{ fontSize:13, color:C.text }}>Photo involves people</div>
                <div style={{ fontSize:11, color:C.textSec }}>Affects privacy handling and PPE-style checks</div>
              </div>
            </div>
          </Card>

          <Card>
            <div style={{ fontSize:14, fontWeight:700, color:C.text, marginBottom:16 }}>✅ What needs to be checked or extracted?</div>
            <Textarea label="Validation checks (pass/fail)" value={ctx.validateItems} onChange={v=>setCtx(c=>({...c,validateItems:v}))} rows={3}
              placeholder="e.g. Hard hat worn, hi-vis vest present, area demarcated, scaffold tag in-date&#10;Leave empty if this is extraction-only" />
            <Textarea label="Data to extract" value={ctx.extractItems} onChange={v=>setCtx(c=>({...c,extractItems:v}))} rows={3}
              placeholder="e.g. Serial number, model number, odometer reading, expiry date&#10;Leave empty if this is validation-only" />
            <Textarea label="Additional notes / edge cases" value={ctx.notes} onChange={v=>setCtx(c=>({...c,notes:v}))} rows={2}
              placeholder="e.g. Sometimes image is blurry, component may be partially hidden, use UK English" />

            <div style={{ marginTop:8, background:ctx.validateItems&&ctx.extractItems ? C.warningDim : C.accentDim,
              border:`1px solid ${ctx.validateItems&&ctx.extractItems?C.warning+'44':C.accent+'44'}`, borderRadius:8, padding:12 }}>
              <div style={{ fontSize:12, color:ctx.validateItems&&ctx.extractItems?C.warning:C.accent, lineHeight:1.6 }}>
                {ctx.validateItems && ctx.extractItems
                  ? '⚠ Mixed validation + extraction prompts can be complex. The AI will handle it, but consider splitting into two separate configs.'
                  : ctx.validateItems
                    ? '✓ Pure validation — result will have boolean fields and a score'
                    : ctx.extractItems
                      ? '✓ Pure extraction — result will have string/numeric fields and confidence'
                      : 'Fill in at least one of: validation checks or data to extract'}
              </div>
            </div>
          </Card>

          <div style={{ gridColumn:'1/-1', display:'flex', justifyContent:'flex-end' }}>
            <Btn onClick={() => setPhase('images')} disabled={!ctx.purpose && !ctx.validateItems && !ctx.extractItems}>
              Next: Add Sample Images →
            </Btn>
          </div>
        </div>
      )}

      {/* ── Phase 2: Images ──────────────────────────────────────────────── */}
      {phase === 'images' && (
        <div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>
            <Card>
              <div style={{ fontSize:14, fontWeight:700, color:C.text, marginBottom:8 }}>📷 Add Sample Images</div>
              <div style={{ fontSize:13, color:C.textSec, marginBottom:16, lineHeight:1.6 }}>
                Add 1–5 real images from this workflow step. The AI will analyse them to understand exactly
                what your photos look like and design the most accurate prompt and schema.
              </div>

              {/* Upload */}
              <div onClick={() => images.length < 5 && fileRef.current?.click()} style={{
                border:`2px dashed ${images.length>=5?C.border:C.accent+'55'}`, borderRadius:10, padding:20,
                textAlign:'center', cursor:images.length>=5?'not-allowed':'pointer', marginBottom:12,
                background:images.length>=5?C.bg:C.accentDim, transition:'all 0.2s' }}>
                <div style={{ fontSize:28, marginBottom:8 }}>📤</div>
                <div style={{ fontSize:13, color:images.length>=5?C.textMuted:C.accent }}>
                  {images.length>=5 ? 'Maximum 5 images' : 'Click to upload image file'}
                </div>
                <div style={{ fontSize:11, color:C.textMuted, marginTop:4 }}>JPG, PNG, WebP + max 5MB each</div>
              </div>
              <input ref={fileRef} type="file" accept="image/*" style={{ display:'none' }}
                onChange={e => { const f=e.target.files[0]; if(f) addFile(f); e.target.value=''; }} />

              {/* URL input */}
              <div style={{ display:'flex', gap:8, marginBottom:8 }}>
                <input value={urlInput} onChange={e=>setUrlInput(e.target.value)}
                  onKeyDown={e=>e.key==='Enter'&&addUrl()}
                  placeholder="Or paste image URL and press Enter"
                  style={{ flex:1, background:C.bg, border:`1px solid ${C.border}`, borderRadius:8,
                    padding:'9px 12px', color:C.text, fontSize:13, outline:'none' }}
                  onFocus={e=>e.target.style.borderColor=C.accent}
                  onBlur={e=>e.target.style.borderColor=C.border} />
                <Btn variant="secondary" onClick={addUrl} disabled={!urlInput.trim()||images.length>=5}>Add</Btn>
              </div>

              <div style={{ fontSize:12, color:C.textMuted }}>
                {images.length}/5 images + <span style={{ color:images.length>0?C.success:C.warning }}>
                  {images.length===0?'Add at least 1 image for best results':images.length===1?'Add more for better accuracy':'Good sample set'}
                </span>
              </div>
            </Card>

            {/* Image preview grid */}
            <Card style={{ padding:12 }}>
              <div style={{ fontSize:13, fontWeight:700, color:C.text, marginBottom:12 }}>Sample Images ({images.length}/5)</div>
              {images.length === 0 ? (
                <div style={{ textAlign:'center', padding:40, color:C.textMuted, fontSize:13 }}>
                  <div style={{ fontSize:32, marginBottom:8 }}>🖼️</div>
                  No images yet — add some from the left
                </div>
              ) : (
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                  {images.map((img, i) => (
                    <div key={i} style={{ position:'relative', borderRadius:8, overflow:'hidden',
                      border:`1px solid ${C.border}`, background:C.bg }}>
                      {img.base64 ? (
                        <img src={`data:${img.mediaType||'image/jpeg'};base64,${img.base64}`} alt={img.label}
                          style={{ width:'100%', height:100, objectFit:'cover', display:'block' }} />
                      ) : img.url ? (
                        <img src={img.url} alt={img.label}
                          style={{ width:'100%', height:100, objectFit:'cover', display:'block' }}
                          onError={e=>{ e.target.style.display='none'; }} />
                      ) : null}
                      <div style={{ padding:'6px 8px', borderTop:`1px solid ${C.border}` }}>
                        <div style={{ fontSize:10, color:C.textSec, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                          {img.label || `Image ${i+1}`}
                        </div>
                        {img.size_kb && <div style={{ fontSize:9, color:C.textMuted }}>{img.size_kb}KB</div>}
                      </div>
                      <button onClick={() => removeImage(i)} style={{
                        position:'absolute', top:4, right:4, background:'#000A', border:'none', color:'#fff',
                        borderRadius:'50%', width:20, height:20, cursor:'pointer', fontSize:13, lineHeight:1 }}>×</button>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>

          <div style={{ display:'flex', justifyContent:'space-between' }}>
            <Btn variant="ghost" onClick={() => setPhase('context')}>← Back</Btn>
            <div style={{ display:'flex', gap:10 }}>
              {images.length === 0 && (
                <Btn variant="ghost" onClick={() => setPhase('generate')}>Skip images (context only) →</Btn>
              )}
              <Btn onClick={() => setPhase('generate')} disabled={images.length === 0}>
                Next: Generate Schema →
              </Btn>
            </div>
          </div>
        </div>
      )}

      {/* ── Phase 3: Generate & Refine ───────────────────────────────────── */}
      {phase === 'generate' && (
        <div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
            {/* Left: Generate controls + result */}
            <div>
              <Card style={{ marginBottom:16 }}>
                <div style={{ fontSize:14, fontWeight:700, color:C.text, marginBottom:12 }}>🤖 AI Generation</div>
                {!generated && !generating && (
                  <div style={{ background:C.accentDim, border:`1px solid ${C.accent}44`, borderRadius:8, padding:14, marginBottom:16 }}>
                    <div style={{ fontSize:13, color:C.accent, fontWeight:600, marginBottom:6 }}>Ready to generate</div>
                    <div style={{ fontSize:12, color:C.textSec, lineHeight:1.6 }}>
                      The AI will analyse your {images.length > 0 ? `${images.length} sample image(s) and` : ''} context
                      to design an optimised prompt and response schema for this exact use case.
                    </div>
                  </div>
                )}
                {generating && (
                  <div style={{ textAlign:'center', padding:24 }}>
                    <Spinner />
                    <div style={{ color:C.textSec, fontSize:13, marginTop:8 }}>Analysing images and context…</div>
                  </div>
                )}
                {generated && !generating && (
                  <div style={{ background:C.successDim, border:`1px solid ${C.success}44`, borderRadius:8, padding:12, marginBottom:12 }}>
                    <div style={{ fontSize:12, color:C.success, fontWeight:600, marginBottom:4 }}>✓ Generated</div>
                    <div style={{ fontSize:12, color:C.textSec, lineHeight:1.5 }}>{generated.explanation}</div>
                  </div>
                )}
                <Btn onClick={generate} disabled={generating} style={{ width:'100%' }}>
                  {generating ? '⏳ Generating…' : generated ? '↻ Regenerate' : '✨ Generate Prompt & Schema'}
                </Btn>
                {generated && (
                  <Btn variant="ghost" onClick={() => setPhase('images')} style={{ width:'100%', marginTop:8 }} size="sm">
                    ← Change images or context
                  </Btn>
                )}
              </Card>

              {/* Template save card */}
              {generated && (
                <Card>
                  <div style={{ fontSize:14, fontWeight:700, color:C.text, marginBottom:14 }}>💾 Save as Template</div>
                  <Input label="Template Name" value={templateName} onChange={setTemplateName}
                    placeholder={generated.suggestedName || 'e.g. PPE Full Body Check'} />
                  <Input label="Description (optional)" value={templateDesc} onChange={setTemplateDesc}
                    placeholder={generated.explanation || 'Brief description'} />
                  <Btn onClick={saveTemplate} disabled={saving||!templateName.trim()} style={{ width:'100%' }}>
                    {saving ? '⏳ Saving…' : savedTemplate ? '✓ Saved — go to Map →' : '💾 Save Template'}
                  </Btn>
                  {savedTemplate && (
                    <Btn onClick={() => setPhase('map')} style={{ width:'100%', marginTop:8 }} variant="secondary">
                      Map to Workflow Param →
                    </Btn>
                  )}
                </Card>
              )}
            </div>

            {/* Right: Edit + Test */}
            <div>
              {generated && (
                <>
                  <Card style={{ marginBottom:16 }}>
                    <div style={{ fontSize:14, fontWeight:700, color:C.text, marginBottom:14 }}>✏️ Edit Generated Prompt</div>
                    <Textarea value={editPrompt} onChange={setEditPrompt} rows={7} mono />
                    <div style={{ fontSize:11, color:C.textMuted }}>
                      Words: {editPrompt.split(' ').length} + ~{Math.ceil(editPrompt.length/4)} tokens
                    </div>
                  </Card>

                  <Card style={{ marginBottom:16 }}>
                    <div style={{ fontSize:14, fontWeight:700, color:C.text, marginBottom:14 }}>📐 Edit Result Schema</div>
                    <JsonEditor value={editSchema} onChange={setEditSchema} rows={5} />
                    <div style={{ fontSize:11, color:C.textMuted, marginTop:4 }}>
                      Always include "confidence": 0.0 — booleans for pass/fail, numbers for scores, strings for extracted values
                    </div>
                  </Card>

                  {/* Test on samples */}
                  <Card>
                    <div style={{ fontSize:14, fontWeight:700, color:C.text, marginBottom:12 }}>🧪 Test on Sample Images</div>
                    {images.length === 0 ? (
                      <div style={{ color:C.textMuted, fontSize:13, padding:'8px 0' }}>
                        Add sample images in step 2 to test here
                      </div>
                    ) : (
                      <>
                        <Btn onClick={testOnSamples} disabled={testing||!editPrompt} style={{ width:'100%', marginBottom:14 }}>
                          {testing ? '⏳ Testing on all samples…' : `▶ Test on ${images.filter(i=>i.base64||i.url).length} Sample(s)`}
                        </Btn>
                        {testing && <Spinner />}
                        {testResults.map((tr, i) => {
                          const conf = tr.result?.confidence;
                          const sc   = conf != null ? (conf >= 0.85 ? C.success : conf >= 0.7 ? C.warning : C.danger) : C.textSec;
                          return (
                            <div key={i} style={{ background:C.bg, borderRadius:8, border:`1px solid ${C.border}`,
                              overflow:'hidden', marginBottom:10 }}>
                              <div style={{ padding:'8px 12px', borderBottom:`1px solid ${C.border}`,
                                display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                                <span style={{ fontSize:12, fontWeight:600, color:C.text }}>
                                  {images[tr.index]?.base64 ? (
                                    <img src={`data:${images[tr.index].mediaType||'image/jpeg'};base64,${images[tr.index].base64}`}
                                      alt="" style={{ height:20, width:28, objectFit:'cover', borderRadius:3, marginRight:6, verticalAlign:'middle' }} />
                                  ) : null}
                                  {tr.label}
                                </span>
                                <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                                  {conf != null && <span style={{ fontSize:11, fontFamily:"'DM Mono',monospace", color:sc }}>{(conf*100).toFixed(0)}%</span>}
                                  {tr.processingMs && <span style={{ fontSize:10, color:C.textMuted }}>{tr.processingMs}ms</span>}
                                  {tr.error && <Badge c={C.danger} label="error" />}
                                </div>
                              </div>
                              {tr.error ? (
                                <div style={{ padding:'8px 12px', fontSize:11, color:C.danger, fontFamily:"'DM Mono',monospace" }}>{tr.error}</div>
                              ) : (
                                <div style={{ padding:'8px 12px', display:'flex', flexWrap:'wrap', gap:6 }}>
                                  {tr.result && Object.entries(tr.result).map(([k,v]) => (
                                    <div key={k} style={{ background:C.surface, borderRadius:4, padding:'3px 8px',
                                      border:`1px solid ${typeof v==='boolean'?(v?C.success+'44':C.danger+'44'):C.border}` }}>
                                      <span style={{ fontSize:10, color:C.textMuted, textTransform:'uppercase' }}>{k}: </span>
                                      <span style={{ fontSize:10, fontFamily:"'DM Mono',monospace",
                                        color:typeof v==='boolean'?(v?C.success:C.danger):C.text }}>
                                        {typeof v==='boolean'?(v?'✓':'✗'):String(v).slice(0,30)}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </>
                    )}
                  </Card>
                </>
              )}
              {!generated && (
                <Card style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:300 }}>
                  <div style={{ textAlign:'center', color:C.textMuted }}>
                    <div style={{ fontSize:48, marginBottom:12 }}>✨</div>
                    <div style={{ fontSize:14, fontWeight:600, color:C.text, marginBottom:8 }}>Click Generate to start</div>
                    <div style={{ fontSize:13, lineHeight:1.6 }}>The AI will design your<br/>prompt and response schema</div>
                  </div>
                </Card>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Phase 4: Map to Workflow Param ───────────────────────────────── */}
      {phase === 'map' && (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
          <Card>
            <div style={{ fontSize:14, fontWeight:700, color:C.text, marginBottom:16 }}>🔗 Map to Workflow Param</div>
            {mapped ? (
              <div style={{ textAlign:'center', padding:32 }}>
                <div style={{ fontSize:48, marginBottom:12 }}>✅</div>
                <div style={{ fontSize:16, fontWeight:700, color:C.success, marginBottom:8 }}>Config mapped successfully!</div>
                <div style={{ fontSize:13, color:C.textSec, marginBottom:20 }}>
                  The template is now active on <strong style={{color:C.text}}>{selWf?.name}</strong> → <strong style={{color:C.accent}}>{selStatus}</strong> → <strong style={{color:C.accent}}>{paramName}</strong>
                </div>
                <Btn onClick={() => { if(onSaved) onSaved(); }}>← Back to Workflow Config</Btn>
              </div>
            ) : (
              <>
                <div style={{ background:C.successDim, border:`1px solid ${C.success}44`, borderRadius:8, padding:12, marginBottom:16 }}>
                  <div style={{ fontSize:12, color:C.success }}>
                    ✓ Template saved: <strong>{savedTemplate?.name || initialTemplate?.name}</strong>
                  </div>
                </div>
                <Select label="Workflow" value={selWorkflow} onChange={v=>{ setSelWorkflow(v); setSelStatus(''); }}
                  options={[{value:'',label:'-- select workflow --'},...workflows.map(w=>({value:w.id,label:`${CATS[w.category]?.icon||'⚙️'} ${w.name}`}))]} />
                {selWorkflow && (
                  <Select label="Status Trigger (when is this photo taken?)" value={selStatus} onChange={setSelStatus}
                    options={[{value:'',label:'-- select status --'},...statusOptions]} />
                )}
                <Input label="Param Name (machine identifier)" value={paramName} onChange={setParamName}
                  placeholder="e.g. technician_ppe_photo, asset_nameplate_photo" />
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                  <Select label="Action on Result" value={mapAction} onChange={setMapAction}
                    options={[{value:'flag_fail',label:'Flag Fail (boolean checks)'},{value:'extract_data',label:'Extract Data'},{value:'auto_approve',label:'Auto Approve'},{value:'notify',label:'Notify'}]} />
                  <Input label="Min Confidence" type="number" value={mapConf} onChange={setMapConf} />
                </div>
                {provList.length > 0 && (
                  <Select label="AI Provider (optional)" value={selProvider} onChange={setSelProvider}
                    options={[{value:'',label:'Auto (active provider)'},...provList.map(p=>({value:p.id,label:`${PROVIDER_ICONS[p.provider_type]||''} ${p.name}`}))]} />
                )}
                <Btn onClick={mapToParam} disabled={mapping||!selWorkflow||!selStatus||!paramName.trim()} style={{ width:'100%' }}>
                  {mapping ? '⏳ Mapping…' : '✓ Create Workflow Config'}
                </Btn>
                <Btn variant="ghost" onClick={() => { if(onSaved) onSaved(); }} style={{ width:'100%', marginTop:8 }} size="sm">
                  Skip — map later from Workflow Config
                </Btn>
              </>
            )}
          </Card>

          {/* Preview */}
          <Card>
            <div style={{ fontSize:14, fontWeight:700, color:C.text, marginBottom:14 }}>📄 Template Preview</div>
            <div style={{ fontSize:11, color:C.textSec, textTransform:'uppercase', letterSpacing:'0.07em', fontFamily:"'DM Mono',monospace", marginBottom:6 }}>Prompt</div>
            <div style={{ background:C.bg, borderRadius:8, padding:12, border:`1px solid ${C.border}`,
              fontFamily:"'DM Mono',monospace", fontSize:11, color:C.text, lineHeight:1.7,
              whiteSpace:'pre-wrap', maxHeight:200, overflowY:'auto', marginBottom:14 }}>
              {editPrompt}
            </div>
            <div style={{ fontSize:11, color:C.textSec, textTransform:'uppercase', letterSpacing:'0.07em', fontFamily:"'DM Mono',monospace", marginBottom:6 }}>Result Schema</div>
            <div style={{ background:C.bg, borderRadius:8, padding:12, border:`1px solid ${C.border}`,
              fontFamily:"'DM Mono',monospace", fontSize:11, color:C.success, lineHeight:1.8,
              maxHeight:200, overflowY:'auto' }}>
              {editSchema}
            </div>
            {images.length > 0 && (
              <>
                <div style={{ fontSize:11, color:C.textSec, textTransform:'uppercase', letterSpacing:'0.07em', fontFamily:"'DM Mono',monospace", margin:'14px 0 6px' }}>Reference Images</div>
                <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                  {images.map((img, i) => img.base64 ? (
                    <img key={i} src={`data:${img.mediaType||'image/jpeg'};base64,${img.base64}`} alt={img.label}
                      style={{ height:48, width:64, objectFit:'cover', borderRadius:6, border:`1px solid ${C.border}` }} />
                  ) : img.url ? (
                    <img key={i} src={img.url} alt={img.label}
                      style={{ height:48, width:64, objectFit:'cover', borderRadius:6, border:`1px solid ${C.border}` }}
                      onError={e=>e.target.style.display='none'} />
                  ) : null)}
                </div>
              </>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}


// ── FieldLinksManager — Settings tab for managing signed field links ──────────
function FieldLinksManager({ user }) {
  const isSuperAdmin   = user.role === 'superadmin';
  const isAccountMgr   = user.role === 'account_manager';
  const isAccountAdmin = user.role === 'account_admin';

  const [links,       setLinks]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [modal,       setModal]       = useState(false);
  const [form,        setForm]        = useState({});
  const [saving,      setSaving]      = useState(false);
  const [newToken,    setNewToken]    = useState(null);
  const [tenants,     setTenants]     = useState([]);
  const [workflows,   setWorkflows]   = useState([]);
  const [configs,     setConfigs]     = useState([]);
  const [solutions,   setSolutions]   = useState([]);

  // Load tenants for roles that can pick one
  useEffect(() => {
    load();
    if (isSuperAdmin) {
      api.tenants.list().then(r => setTenants(r.data||[])).catch(()=>{});
    } else if (isAccountMgr) {
      // Self-service: account_manager fetches their own mapped tenants
      api.users.myManagedTenants()
        .then(r => setTenants(r.data||[]))
        .catch(()=>{});
    }
  }, []);

  // When selectedTenantId changes in the form, reload target options
  useEffect(() => {
    const tid = form.tenantId;
    if (!tid) { setWorkflows([]); setConfigs([]); setSolutions([]); return; }
    const p = isSuperAdmin || isAccountMgr ? { tenantId: tid } : {};
    api.workflows.list({ limit:200, ...p }).then(r => setWorkflows(r.data||[])).catch(()=>{});
    api.configs.list({ limit:200, ...p }).then(r => setConfigs(r.data||[])).catch(()=>{});
    api.solutions.list({ limit:200, ...p }).then(r => setSolutions(r.data||[])).catch(()=>{});
  }, [form.tenantId]);

  const load = () => {
    setLoading(true);
    fieldLinksApi.list()
      .then(r => setLinks(r.data||[]))
      .catch(()=>{})
      .finally(()=>setLoading(false));
  };

  const openCreate = () => {
    // Default tenantId = user's own tenant (for admin/account_admin)
    // For superadmin/account_manager, leave blank so user must pick
    const defaultTid = (isSuperAdmin || isAccountMgr) ? '' : user.tenantId;
    setForm({
      name:'', targetType:'config', targetId:'',
      expiry:'48h', comment:'', emailsTo:'',
      tenantId: defaultTid,
    });
    setNewToken(null);
    setModal(true);
  };

  const save = async () => {
    if (!form.name.trim())  return alert('Name is required');
    if (!form.tenantId)     return alert('Select a tenant first');
    if (!form.targetId)     return alert('Select a target config, workflow, or solution');
    setSaving(true);
    try {
      const body = {
        name:       form.name.trim(),
        expiry:     form.expiry,
        tenantId:   form.tenantId,
        comment:    form.comment || undefined,
        emailsTo:   form.emailsTo ? form.emailsTo.split(',').map(e=>e.trim()).filter(Boolean) : [],
        ...(form.targetType === 'config'   ? { configId:   form.targetId } : {}),
        ...(form.targetType === 'workflow' ? { workflowId: form.targetId } : {}),
        ...(form.targetType === 'solution' ? { solutionId: form.targetId } : {}),
      };
      const r = await fieldLinksApi.create(body);
      setNewToken(r.data?.token || r.token);
      load();
    } catch(e) { alert(e.message); }
    setSaving(false);
  };

  const revoke = async (id, name) => {
    if (!confirm(`Revoke link "${name}"? It will immediately stop working.`)) return;
    try { await fieldLinksApi.revoke(id); load(); } catch(e) { alert(e.message); }
  };

  const copyLink = (token) => {
    const url = `${window.location.origin}/field?token=${token}`;
    navigator.clipboard?.writeText(url)
      .then(() => alert('Link copied to clipboard!'))
      .catch(() => { prompt('Copy this link:', url); });
  };

  const EXPIRY_LABELS = {
    '1h':'1 hour','8h':'8 hours','24h':'24 hours','48h':'48 hours (default)',
    '7d':'7 days','30d':'30 days','never':'Never expires',
  };

  const targetOptions = form.targetType === 'config'
    ? configs.map(c => ({ value:c.id, label:`${c.param_name} — ${c.workflow_name||''}` }))
    : form.targetType === 'workflow'
    ? workflows.map(w => ({ value:w.id, label:w.name }))
    : solutions.map(s => ({ value:s.id, label:`${s.name} — ${s.workflow_name||''}` }));

  const isExpired = (l) => l.expires_at && new Date(l.expires_at) < new Date();

  // Tenant options for picker
  const tenantOptions = isSuperAdmin || isAccountMgr
    ? tenants
    : [{ id: user.tenantId, name: user.tenant_name || 'My Account' }];

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
        marginBottom:16 }}>
        <div>
          <div style={{ fontSize:14, fontWeight:700, color:C.text }}>Field Links</div>
          <div style={{ fontSize:12, color:C.textSec, marginTop:2 }}>
            Signed, expiring URLs for the field tester — share without exposing API keys
          </div>
        </div>
        <Btn onClick={openCreate}>+ New Link</Btn>
      </div>

      {loading ? <Spinner /> : links.length === 0 ? (
        <Card style={{ textAlign:'center', padding:40, color:C.textMuted }}>
          No field links yet. Create one to share the field tester with specific configs.
        </Card>
      ) : (
        <div style={{ overflowX:'auto', border:`1px solid ${C.border}`,
          borderRadius:10, background:C.surface }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
            <thead>
              <tr style={{ borderBottom:`1px solid ${C.border}` }}>
                {[...(isSuperAdmin||isAccountMgr?['Account']:[]),'Name','Target','Expiry','Uses','Created','Status',''].map(h => (
                  <th key={h} style={{ padding:'8px 14px', textAlign:'left', fontSize:10,
                    color:C.textSec, fontWeight:700, textTransform:'uppercase',
                    letterSpacing:'0.06em', fontFamily:"'DM Mono',monospace" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {links.map(l => {
                const expired  = isExpired(l);
                const inactive = l.is_revoked || expired;
                const target   = l.param_name || l.workflow_name || l.solution_name || '—';
                return (
                  <tr key={l.id}
                    style={{ borderBottom:`1px solid ${C.border}`, opacity: inactive ? 0.6 : 1 }}>
                    {(isSuperAdmin||isAccountMgr) && (
                      <td style={{ padding:'9px 14px', fontSize:11, color:C.textSec,
                        fontFamily:"'DM Mono',monospace" }}>
                        {l.tenant_name || '—'}
                      </td>
                    )}
                    <td style={{ padding:'9px 14px', fontWeight:600, color:C.text }}>
                      {l.name}
                      {l.comment && (
                        <div style={{ fontSize:10, color:C.textMuted, marginTop:1 }}>
                          {l.comment}
                        </div>
                      )}
                    </td>
                    <td style={{ padding:'9px 14px', color:C.textSec, fontSize:11 }}>{target}</td>
                    <td style={{ padding:'9px 14px', color:expired?C.danger:C.textSec,
                      fontFamily:"'DM Mono',monospace", fontSize:10 }}>
                      {l.expires_at ? new Date(l.expires_at).toLocaleDateString() : 'Never'}
                    </td>
                    <td style={{ padding:'9px 14px', color:C.textMuted,
                      fontFamily:"'DM Mono',monospace" }}>{l.use_count}</td>
                    <td style={{ padding:'9px 14px', color:C.textMuted, fontSize:10,
                      fontFamily:"'DM Mono',monospace" }}>
                      {new Date(l.created_at).toLocaleDateString()}
                    </td>
                    <td style={{ padding:'9px 14px' }}>
                      {l.is_revoked ? <Badge c={C.danger}   label="revoked" />
                        : expired   ? <Badge c={C.warning}  label="expired" />
                        :             <Badge c={C.success}  label="active"  />}
                    </td>
                    <td style={{ padding:'9px 14px' }}>
                      {!inactive && (
                        <Btn size="sm" variant="danger" onClick={() => revoke(l.id, l.name)}>
                          Revoke
                        </Btn>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={modal} onClose={() => { setModal(false); setNewToken(null); }}
        title="Create Field Link" width={560}>
        {newToken ? (
          <div>
            <div style={{ textAlign:'center', marginBottom:20 }}>
              <div style={{ fontSize:36, marginBottom:10 }}>🔗</div>
              <div style={{ fontSize:14, fontWeight:700, color:C.text, marginBottom:6 }}>
                Link created — copy it now
              </div>
              <div style={{ fontSize:12, color:C.textSec, lineHeight:1.6 }}>
                This is the only time the full link will be shown.
                It cannot be retrieved later.
              </div>
            </div>
            <div style={{ background:C.bg, border:`1px solid ${C.accent}44`,
              borderRadius:8, padding:'12px 16px', marginBottom:16,
              fontFamily:"'DM Mono',monospace", fontSize:11, wordBreak:'break-all',
              color:C.accent }}>
              {`${window.location.origin}/field?token=${newToken}`}
            </div>
            <Btn onClick={() => copyLink(newToken)} style={{ width:'100%', marginBottom:10 }}>
              Copy Link to Clipboard
            </Btn>
            <Btn variant="ghost" onClick={() => { setModal(false); setNewToken(null); }}
              style={{ width:'100%' }}>Done</Btn>
          </div>
        ) : (
          <div>
            {/* Tenant picker for superadmin/account_manager */}
            {(isSuperAdmin || isAccountMgr) && (
              <div style={{ marginBottom:14 }}>
                <div style={{ fontSize:11, fontWeight:700, color:C.textSec,
                  textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:5,
                  fontFamily:"'DM Mono',monospace" }}>Account *</div>
                <select value={form.tenantId||''}
                  onChange={e => setForm(f=>({...f, tenantId:e.target.value, targetId:''}))}
                  style={{ width:'100%', background:C.bg, border:`1px solid ${C.border}`,
                    borderRadius:8, padding:'9px 12px',
                    color: form.tenantId ? C.text : C.textMuted,
                    fontSize:13, fontFamily:"'DM Sans',sans-serif",
                    outline:'none', boxSizing:'border-box' }}>
                  <option value="">Select account…</option>
                  {tenantOptions.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
            )}

            <Input label="Link Name *" value={form.name||''}
              onChange={v => setForm(f=>({...f,name:v}))}
              placeholder="e.g. Site Crew — July Shift" />

            {/* Target type selector */}
            <div style={{ marginBottom:14 }}>
              <div style={{ fontSize:11, fontWeight:700, color:C.textSec,
                textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:5,
                fontFamily:"'DM Mono',monospace" }}>Link Target *</div>
              <div style={{ display:'flex', gap:6, marginBottom:8 }}>
                {[['config','Param Config'],['workflow','Workflow'],['solution','Solution']].map(([t,l])=>(
                  <button key={t}
                    onClick={() => setForm(f=>({...f, targetType:t, targetId:''}))}
                    style={{ padding:'4px 12px', borderRadius:16, fontSize:11, cursor:'pointer',
                      fontWeight:600,
                      border:`1px solid ${form.targetType===t ? C.accent : C.border}`,
                      background: form.targetType===t ? C.accentDim : C.bg,
                      color:      form.targetType===t ? C.accent    : C.textSec }}>
                    {l}
                  </button>
                ))}
              </div>
              {!form.tenantId && (isSuperAdmin || isAccountMgr) ? (
                <div style={{ color:C.textMuted, fontSize:12, padding:'8px 12px',
                  background:C.bg, borderRadius:8, border:`1px solid ${C.border}` }}>
                  Select an account first to load options
                </div>
              ) : (
                <select value={form.targetId||''}
                  onChange={e => setForm(f=>({...f,targetId:e.target.value}))}
                  style={{ width:'100%', background:C.bg, border:`1px solid ${C.border}`,
                    borderRadius:8, padding:'9px 12px',
                    color: form.targetId ? C.text : C.textMuted,
                    fontSize:13, fontFamily:"'DM Sans',sans-serif",
                    outline:'none', boxSizing:'border-box' }}>
                  <option value="">Select {form.targetType}…</option>
                  {targetOptions.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              )}
            </div>

            <Select label="Expiry" value={form.expiry||'48h'}
              onChange={v=>setForm(f=>({...f,expiry:v}))}
              options={Object.entries(EXPIRY_LABELS).map(([v,l])=>({value:v,label:l}))} />

            <Input label="Comment (shown in email and field app header)"
              value={form.comment||''}
              onChange={v=>setForm(f=>({...f,comment:v}))}
              placeholder="e.g. July site inspection team" />

            <Input label="Send to Emails (comma-separated, optional)"
              value={form.emailsTo||''}
              onChange={v=>setForm(f=>({...f,emailsTo:v}))}
              placeholder="tech1@company.com, tech2@company.com" />

            <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:4 }}>
              <Btn variant="ghost" onClick={() => setModal(false)}>Cancel</Btn>
              <Btn onClick={save} disabled={saving}>
                {saving ? 'Creating…' : 'Create Link'}
              </Btn>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}


// ── WorkflowConfig ────────────────────────────────────────────────────────────
function WorkflowConfig({ user, onOpenBuilder }) {
  const [workflows, setWorkflows] = useState([]);
  const [configs,   setConfigs]   = useState([]);
  const [tenants,   setTenants]   = useState([]);
  const [templates, setTemplates] = useState([]);
  const [perfData,  setPerfData]  = useState({});  // configId -> perf stats
  const [selected,  setSelected]  = useState(null);
  const [tenantFilter, setTenantFilter] = useState(''); // superadmin filter
  const [selConfig, setSelConfig] = useState(null);
  const [viewCfg,   setViewCfg]   = useState(null); // config detail view (read-only)
  const [modal,     setModal]     = useState(false);
  const [modalDirty,setModalDirty]= useState(false);  // tracks unsaved edits
  const [wfModal,   setWfModal]   = useState(false);  // workflow create/edit
  const [wfModalDirty,setWfModalDirty] = useState(false);
  const [wfForm,    setWfForm]    = useState({});
  const [editWfId,  setEditWfId]  = useState(null);
  const [form,      setForm]      = useState({});
  const [syncing,   setSyncing]   = useState(false);
  const [loading,   setLoading]   = useState(true);
  const [provList,  setProvList]  = useState([]);
  const [fromTpl,   setFromTpl]   = useState('');
  const [cfgSearch, setCfgSearch] = useState('');     // search by param/trigger/workflow
  const [cfgProvFilter,setCfgProvFilter] = useState(''); // filter by provider id
  const [cloneModal,setCloneModal] = useState(false);
  const [cloneSrc,  setCloneSrc]  = useState(null);
  const [cloneForm, setCloneForm] = useState({});
  const [testCfg,   setTestCfg]   = useState(null);
  const [testImageUrl,  setTestImageUrl]  = useState('');
  const [testImageB64,  setTestImageB64]  = useState('');
  const [testImageMode, setTestImageMode] = useState('url');
  const [testRunning,   setTestRunning]   = useState(false);
  const [testResult,    setTestResult]    = useState(null);
  const [testSave,      setTestSave]      = useState(true);
  const testFileRef = useRef(null);

  const isSuperAdmin  = user.role === 'superadmin';
  const isViewer      = user.role === 'viewer';
  const isAccountMgr  = user.role === 'account_manager';
  const canManage     = isSuperAdmin || isAccountMgr;  // can see tenant filter
  const canWrite     = isSuperAdmin || user.role === 'account_admin' || user.role === 'account_manager';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const cfgParams = {};
      if (tenantFilter) cfgParams.tenantId = tenantFilter;
      const calls = [
        api.workflows.list(tenantFilter ? { tenantId: tenantFilter } : {}),
        api.configs.list({ limit:500, ...cfgParams }),
        providersApi.list(),
        promptLibApi.list(),
      ];
      if (isSuperAdmin || isAccountMgr) calls.push(api.tenants.list());
      const [wfR,cfgR,prR,tplR,tR] = await Promise.all(calls);
      setWorkflows(wfR.data||[]); setConfigs(cfgR.data||[]); setProvList(prR.data||[]);
      setTemplates(tplR.data||[]);
      if (tR) setTenants(tR.data||[]);
      // load perf for all configs
      const cfgs = cfgR.data||[];
      if (cfgs.length > 0 && cfgs.length <= 50) {
        const perfResults = await Promise.allSettled(cfgs.map(c => api.feedback.statsForConfig(c.id)));
        const perfMap = {};
        cfgs.forEach((c,i) => {
          if (perfResults[i].status==='fulfilled') perfMap[c.id] = perfResults[i].value?.data;
        });
        setPerfData(perfMap);
      }
    } catch(e) { console.error(e); }
    setLoading(false);
  }, [isSuperAdmin, isAccountMgr, tenantFilter]);

  useEffect(()=>{ load(); },[load]);

  const visibleConfigs = configs.filter(c => {
    if (selected && c.workflow_id !== selected.id) return false;
    if (cfgProvFilter && c.ai_provider_id !== cfgProvFilter) return false;
    if (cfgSearch) {
      const q = cfgSearch.toLowerCase();
      return (c.param_name||'').toLowerCase().includes(q) ||
             (c.status_trigger||'').toLowerCase().includes(q) ||
             (c.workflow_name||'').toLowerCase().includes(q);
    }
    return true;
  });

  const applyTemplate = (tplId) => {
    const tpl = templates.find(t => t.id === tplId);
    if (!tpl) return;
    setForm(f => ({ ...f, prompt: tpl.prompt_text, resultSchema: JSON.stringify(tpl.result_schema,null,2), libraryId: tpl.id }));
    setFromTpl(tplId);
  };

  const openNew = () => {
    setFromTpl('');
    setForm({
      workflowId:selected?.id||'', statusTrigger:'', paramName:'',
      prompt:'', action:'flag_fail', minConfidence:'0.85', resultSchema:'{}',
      aiModel:'', aiProviderId:'', tenantId:user.tenantId||tenantFilter||'', libraryId:'',
      imageResolution:'original', imageCustomWidth:'', imageCustomHeight:'',
      activeForTesting:true, activeForApi:true, variableSchema:'[]',
    });
    setModalDirty(false);
    setModal(true);
  };

  const openEdit = (cfg) => {
    setFromTpl('');
    setForm({
      workflowId:cfg.workflow_id, statusTrigger:cfg.status_trigger,
      paramName:cfg.param_name, displayName:cfg.display_name||'', description:cfg.description||'',
      prompt:cfg.prompt, action:cfg.action,
      minConfidence:cfg.min_confidence, resultSchema:JSON.stringify(cfg.result_schema,null,2),
      aiModel:cfg.ai_model||'', aiProviderId:cfg.ai_provider_id||'',
      tenantId:cfg.tenant_id||'', id:cfg.id, libraryId:cfg.library_id||'',
      imageResolution:cfg.image_resolution||'original',
      imageCustomWidth:cfg.image_custom_width||'', imageCustomHeight:cfg.image_custom_height||'',
      activeForTesting:cfg.active_for_testing!==false,
      activeForApi:cfg.active_for_api!==false,
      variableSchema:JSON.stringify(cfg.variable_schema||[],null,2),
    });
    setModalDirty(false);
    setModal(true);
  };

  // Wrap setForm to mark dirty on any change
  const updateForm = (updater) => { setForm(updater); setModalDirty(true); };

  const closeModal = () => {
    if (modalDirty && !window.confirm('You have unsaved changes. Close without saving?')) return;
    setModal(false); setModalDirty(false);
  };

  const save = async () => {
    try {
      let varSchema; try { varSchema = JSON.parse(form.variableSchema||'[]'); } catch { varSchema = []; }
      const isEdit = !!form.id;
      const body = {
        prompt:form.prompt, action:form.action,
        minConfidence:parseFloat(form.minConfidence),
        resultSchema:JSON.parse(form.resultSchema||'{}'),
        aiModel:form.aiModel||undefined, aiProviderId:form.aiProviderId||undefined,
        libraryId:form.libraryId||undefined,
        imageResolution:form.imageResolution||'original',
        displayName:form.displayName||undefined, description:form.description||undefined,
        imageCustomWidth:form.imageResolution==='custom'&&form.imageCustomWidth?parseInt(form.imageCustomWidth):undefined,
        imageCustomHeight:form.imageResolution==='custom'&&form.imageCustomHeight?parseInt(form.imageCustomHeight):undefined,
        activeForTesting:form.activeForTesting!==false, activeForApi:form.activeForApi!==false,
        variableSchema:varSchema,
      };
      if (isEdit) { await api.configs.update(form.id, body); }
      else {
        body.workflowId = form.workflowId; body.statusTrigger = form.statusTrigger;
        body.paramName = form.paramName;
        if (form.tenantId) body.tenantId = form.tenantId;
        await api.configs.create(body);
      }
      setModal(false); setModalDirty(false); load();
    } catch(e) { alert(e.message); }
  };

  // Workflow CRUD
  const openNewWf = () => {
    setEditWfId(null);
    setWfForm({
      name:'', category:'custom', statuses:'', description:'',
      workflowContext:'', statusContexts:'{}', useContextPrefix:false,
      is_active:true,
      tenantId: tenantFilter || user.tenantId || '',
    });
    setWfModalDirty(false);
    setWfModal(true);
  };
  const openEditWf = (wf) => {
    setEditWfId(wf.id);
    setWfForm({
      name:wf.name, category:wf.category, description:wf.description||'',
      statuses:(wf.statuses||[]).join('\n'),
      workflowContext:wf.workflow_context||'',
      statusContexts:JSON.stringify(wf.status_contexts||{},null,2),
      useContextPrefix:wf.use_context_prefix||false,
      is_active:wf.is_active!==false,
      engineWorkflowId:wf.engine_workflow_id||'',
    });
    setWfModalDirty(false);
    setWfModal(true);
  };

  const updateWfForm = (updater) => { setWfForm(updater); setWfModalDirty(true); };

  const closeWfModal = () => {
    if (wfModalDirty && !window.confirm('You have unsaved changes. Close without saving?')) return;
    setWfModal(false); setWfModalDirty(false);
  };

  const saveWf = async () => {
    try {
      const statuses = wfForm.statuses.split(/[\n,]+/).map(s=>s.trim()).filter(Boolean);
      if (!statuses.length) return alert('Add at least one status');
      let sc; try { sc = JSON.parse(wfForm.statusContexts||'{}'); } catch { sc = {}; }
      const body = {
        name:wfForm.name, category:wfForm.category, description:wfForm.description||undefined,
        statuses, workflowContext:wfForm.workflowContext||undefined,
        statusContexts:sc, useContextPrefix:wfForm.useContextPrefix,
        is_active:wfForm.is_active!==false,
        engineWorkflowId:wfForm.engineWorkflowId||undefined,
      };
      if (editWfId) await api.workflows.update(editWfId, body);
      else {
        const tid = wfForm.tenantId || tenantFilter || user.tenantId;
        if (!tid) return alert('Select a tenant first');
        body.tenantId = tid;
        await api.workflows.create(body);
      }
      setWfModal(false); setWfModalDirty(false); load();
    } catch(e) { alert(e.message); }
  };
  const deleteWf = async (id) => {
    if (!confirm('Delete this workflow? All its configs will be deleted too.')) return;
    try { await api.workflows.delete(id); load(); } catch(e) { alert(e.message); }
  };
  const cloneWf = async (wf) => {
    if (!confirm(`Clone workflow "${wf.name}"?`)) return;
    try { await api.workflows.clone(wf.id, { name: wf.name + ' (copy)' }); load(); }
    catch(e) { alert(e.message); }
  };

  const doSync = async () => {
    setSyncing(true);
    try {
      const r = await api.workflows.sync();
      if (r?.data?.warning) {
        // Engine not reachable — show info message, not an error
        alert(`ℹ ${r.data.message}`);
      } else {
        await load();
        if (r?.data?.message) alert(`✓ ${r.data.message}`);
      }
    } catch(e) { alert('Sync failed: ' + e.message); }
    setSyncing(false);
  };

  const openTest = (cfg) => {
    setTestCfg(cfg); setTestImageUrl(''); setTestImageB64('');
    setTestImageMode('url'); setTestResult(null); setTestSave(true);
    setSelConfig(null);
  };

  const [testJobId, setTestJobId] = useState('');
  const runTest = async () => {
    if (!testCfg) return;
    if (!testJobId.trim() && testSave) return alert('Job ID is required when saving the result');
    if (testImageMode==='url' && !testImageUrl) return alert('Enter an image URL');
    if (testImageMode==='upload' && !testImageB64) return alert('Upload an image');
    setTestRunning(true); setTestResult(null);
    try {
      if (!testSave) {
        const r = await api.prompts.test({
          prompt: testCfg.prompt,
          ...(testImageMode==='url'?{imageUrl:testImageUrl}:{imageBase64:testImageB64}),
        });
        setTestResult({ unsaved:true, data:r.data });
      } else {
        const body = { configId: testCfg.id, jobId: testJobId || `test-${Date.now()}` };
        if (testImageMode==='url') body.imageUrl = testImageUrl;
        else body.imageBase64 = testImageB64;
        const r = await api.runs.submit(body);
        setTestResult({ saved:true, data:r.data });
      }
    } catch(e) {
      const msg = e.tokenBudget
        ? '🪙 ' + e.message
        : e.message;
      setTestResult({ error: msg });
    }
    setTestRunning(false);
  };

  if (loading) return <Spinner />;
  const activeProviders = provList.filter(p=>p.is_active);
  const linkedTpl = form.id ? templates.find(t=>t.id===form.libraryId) : null;

  return (
    <div>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20, flexWrap:'wrap', gap:10 }}>
        <div>
          <div style={{ fontSize:20, fontWeight:700, fontFamily:"'DM Serif Display',serif", color:C.text }}>Workflow Configurations</div>
          <div style={{ color:C.textSec, fontSize:13 }}>Map AI validation configs to workflow status params</div>
        </div>
        {/* Search + provider filter row */}
        <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center', marginBottom:0 }}>
          <Input value={cfgSearch} onChange={setCfgSearch} placeholder="Search param / trigger / workflow…"
            style={{ marginBottom:0, minWidth:220 }} />
          <select value={cfgProvFilter} onChange={e=>setCfgProvFilter(e.target.value)} style={{
            background:C.bg, border:`1px solid ${C.border}`, borderRadius:8, padding:'7px 12px',
            color:cfgProvFilter?C.text:C.textMuted, fontSize:13, fontFamily:"'DM Sans',sans-serif" }}>
            <option value="">All providers</option>
            {provList.filter(p=>p.is_active).map(p=><option key={p.id} value={p.id}>{PROVIDER_ICONS[p.provider_type]||''} {p.name}</option>)}
          </select>
          {(cfgSearch||cfgProvFilter) && (
            <button onClick={()=>{setCfgSearch('');setCfgProvFilter('');}} style={{ background:'none', border:`1px solid ${C.border}`, borderRadius:6, color:C.textSec, cursor:'pointer', padding:'5px 10px', fontSize:12 }}>Clear</button>
          )}
        </div>
        <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
          {canManage && (
            <select value={tenantFilter} onChange={e=>{ setTenantFilter(e.target.value); setSelected(null); }} style={{
              background:C.bg, border:`1px solid ${C.border}`, borderRadius:8, padding:'7px 12px',
              color:tenantFilter?C.text:C.textMuted, fontSize:13, fontFamily:"'DM Sans',sans-serif" }}>
              <option value="">{isSuperAdmin ? 'All tenants' : 'All mapped tenants'}</option>
              {tenants.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          )}
          <Btn variant="ghost" onClick={doSync} disabled={syncing}>{syncing?'⏳ Syncing...':'🔄 Sync'}</Btn>
          {!isViewer && <Btn variant="ghost" onClick={openNewWf}>＋ Workflow</Btn>}
          {!isViewer && <Btn variant="secondary" onClick={() => onOpenBuilder && onOpenBuilder()}>🔬 Builder</Btn>}
          {!isViewer && <Btn onClick={openNew}>＋ Config</Btn>}
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'220px 1fr', gap:16 }}>
        {/* Sidebar: workflows with enable/disable */}
        <div>
          <div style={{ fontSize:11, color:C.textSec, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:8, fontFamily:"'DM Mono',monospace" }}>Workflows</div>
          <div onClick={()=>setSelected(null)} style={{ padding:'9px 12px', borderRadius:8, cursor:'pointer', marginBottom:4,
            background:!selected?C.accentDim:C.bg, border:`1px solid ${!selected?C.accent+'44':C.border}`,
            color:!selected?C.accent:C.textSec, fontSize:12, fontWeight:!selected?600:400 }}>
            All <span style={{color:C.textMuted}}>({workflows.length})</span>
          </div>
          {workflows.map(wf=>{ const cat=CATS[wf.category]||CATS.custom, active=selected?.id===wf.id; return (
            <div key={wf.id} style={{ marginBottom:4, borderRadius:8, border:`1px solid ${active?C.accent+'44':wf.is_active===false?C.border:C.border}`,
              background:active?C.accentDim:wf.is_active===false?C.bg+'88':C.bg,
              opacity:wf.is_active===false?0.6:1, overflow:'hidden' }}>
              <div onClick={()=>setSelected(wf)} style={{ padding:'9px 12px', cursor:'pointer' }}>
                <div style={{ display:'flex', alignItems:'center', gap:6, justifyContent:'space-between' }}>
                  <div style={{ fontSize:12, fontWeight:600, color:active?C.accent:wf.is_active===false?C.textMuted:C.text, minWidth:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {cat.icon} {wf.name}
                  </div>
                  <div style={{ display:'flex', gap:4, flexShrink:0 }}>
                    {!isViewer && <button onClick={e=>{ e.stopPropagation(); openEditWf(wf); }} style={{ background:'none', border:'none', color:C.textMuted, cursor:'pointer', fontSize:12, padding:'0 2px' }} title="Edit">✏</button>}
                    {!isViewer && <button onClick={e=>{ e.stopPropagation(); api.workflows.update(wf.id,{is_active:!wf.is_active}).then(load); }} style={{ background:'none', border:'none', cursor:'pointer', fontSize:11, padding:'0 2px', color:wf.is_active?C.success:C.textMuted }} title={wf.is_active?'Disable':'Enable'}>
                      {wf.is_active?'●':'○'}
                    </button>}
                    {!isViewer && <button onClick={e=>{ e.stopPropagation(); deleteWf(wf.id); }} style={{ background:'none', border:'none', color:C.textMuted, cursor:'pointer', fontSize:12, padding:'0 2px' }} title="Delete">✕</button>}
                    {!isViewer && <button onClick={e=>{ e.stopPropagation(); cloneWf(wf); }} style={{ background:'none', border:'none', color:C.textMuted, cursor:'pointer', fontSize:12, padding:'0 2px' }} title="Clone">⎘</button>}
                  </div>
                </div>
                <div style={{ fontSize:10, color:C.textSec, marginTop:2 }}>
                  {(wf.statuses||[]).length} statuses
                  {wf.use_context_prefix && <span style={{ color:C.purple }}> + ctx</span>}
                  {wf.engine_workflow_id && (
                    <span style={{ color:C.accent, fontFamily:"'DM Mono',monospace", fontSize:9 }}>
                      {' '}ref:{wf.engine_workflow_id}
                    </span>
                  )}
                  {isSuperAdmin && wf.tenant_name && <span style={{ color:C.textMuted }}> + {wf.tenant_name}</span>}
                </div>
              </div>
            </div>
          ); })}
        </div>

        {/* Config table */}
        <Card style={{ padding:0, overflow:'hidden' }}>
          <div style={{ padding:'14px 20px', borderBottom:`1px solid ${C.border}`, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <div style={{ fontSize:14, fontWeight:600, color:C.text }}>
              {visibleConfigs.length} configs
              {selected && (
                <span style={{ fontSize:12, color:C.textSec, fontWeight:400, marginLeft:8 }}>
                  in {selected.name}
                  {selected.engine_workflow_id &&
                    <span style={{ fontFamily:"'DM Mono',monospace", fontSize:10,
                      color:C.accent, marginLeft:6 }}>
                      [{selected.engine_workflow_id}]
                    </span>}
                </span>
              )}
            </div>
          </div>
          {visibleConfigs.length===0 ? (
            <div style={{ padding:48, textAlign:'center' }}>
              <div style={{ fontSize:32, marginBottom:12 }}>🔬</div>
              <div style={{ color:C.text, fontSize:14, fontWeight:600, marginBottom:8 }}>No configs yet</div>
              <div style={{ display:'flex', gap:10, justifyContent:'center' }}>
                <Btn onClick={() => onOpenBuilder && onOpenBuilder()}>🔬 Config Builder</Btn>
                <Btn variant="ghost" onClick={openNew}>＋ Manual</Btn>
              </div>
            </div>
          ) : (
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', minWidth:900 }}>
                <thead>
                  <tr style={{ background:C.bg }}>
                    {(isSuperAdmin?['Tenant','Workflow','Trigger','Param','Model','Template','Performance','Active','Created By','']
                                  :         ['Workflow','Trigger','Param','Model','Template','Performance','Active','Created By','']).map(h=>(
                      <th key={h} style={{ padding:'8px 12px', textAlign:'left', fontSize:10, color:C.textSec,
                        fontWeight:600, letterSpacing:'0.06em', textTransform:'uppercase',
                        fontFamily:"'DM Mono',monospace", borderBottom:`1px solid ${C.border}`, whiteSpace:'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visibleConfigs.map(cfg=>{
                    const perf = perfData[cfg.id];
                    const passRate = perf?.total_runs > 0 ? Math.round(perf.passed/perf.total_runs*100) : null;
                    const approvalPct = perf?.feedback_count > 0 ? Math.round(perf.thumbs_up/perf.feedback_count*100) : null;
                    return (
                      <tr key={cfg.id} style={{ borderBottom:`1px solid ${C.border}`, opacity:cfg.active_for_testing===false&&cfg.active_for_api===false?0.5:1, cursor:'pointer' }}
                        onMouseEnter={e=>e.currentTarget.style.background=C.surfaceHover}
                        onMouseLeave={e=>e.currentTarget.style.background='transparent'}
                        onClick={()=>setViewCfg(cfg)}>
                        {isSuperAdmin && <td style={{ padding:'8px 12px', fontSize:11, color:C.textMuted, whiteSpace:'nowrap' }}>{cfg.tenant_name||'—'}</td>}
                        <td style={{ padding:'8px 12px', fontSize:12, color:C.text, whiteSpace:'nowrap' }}>{CATS[cfg.category]?.icon} {cfg.workflow_name}</td>
                        <td style={{ padding:'8px 12px' }}><Badge c={C.purple} label={cfg.status_trigger} /></td>
                        <td style={{ padding:'8px 12px', fontSize:11, color:C.accent, fontFamily:"'DM Mono',monospace", whiteSpace:'nowrap' }}>{cfg.param_name}</td>
                        <td style={{ padding:'8px 12px', fontSize:10, color:C.textSec, fontFamily:"'DM Mono',monospace", whiteSpace:'nowrap' }}>
                          {cfg.ai_model || (provList.find(p=>p.id===cfg.ai_provider_id)?.default_model) || '—'}
                        </td>
                        <td style={{ padding:'8px 12px', fontSize:10 }}>
                          {cfg.library_id
                            ? <span style={{ color:C.success }}>✓ {templates.find(t=>t.id===cfg.library_id)?.name||'tpl'}</span>
                            : <span style={{ color:C.textMuted }}>manual</span>}
                        </td>
                        <td style={{ padding:'8px 12px', minWidth:160 }}>
                          {perf && perf.total_runs > 0 ? (
                            <div>
                              <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                                <div style={{ flex:1, height:5, background:C.border, borderRadius:3 }}>
                                  <div style={{ width:`${passRate}%`, height:'100%', background:passRate>=80?C.success:passRate>=60?C.warning:C.danger, borderRadius:3 }} />
                                </div>
                                <span style={{ fontSize:10, color:C.textSec, fontFamily:"'DM Mono',monospace", width:32 }}>{passRate}%</span>
                              </div>
                              <div style={{ fontSize:10, color:C.textMuted, marginTop:2 }}>
                                {perf.total_runs} runs + {perf.passed||0}✓ {perf.failed||0}✗
                                {approvalPct!==null && ` + 👍${approvalPct}%`}
                              </div>
                            </div>
                          ) : <span style={{ fontSize:10, color:C.textMuted }}>no runs yet</span>}
                        </td>
                        <td style={{ padding:'8px 12px' }}>
                          <div style={{ display:'flex', gap:4 }}>
                            <span style={{ fontSize:10, color:cfg.active_for_testing!==false?C.success:C.textMuted }} title="Testing">🧪</span>
                            <span style={{ fontSize:10, color:cfg.active_for_api!==false?C.success:C.textMuted }} title="API">⚡</span>
                          </div>
                        </td>
                        <td style={{ padding:'8px 12px', fontSize:11, color:C.textMuted, whiteSpace:'nowrap' }}>
                          {cfg.created_by_name || '—'}
                        </td>
                        <td style={{ padding:'8px 12px' }}>
                          <div style={{ display:'flex', gap:4 }}>
                            {!isViewer && <Btn size="sm" variant="ghost" onClick={e=>{e.stopPropagation();openTest(cfg);}}>🧪</Btn>}
                            {!isViewer && <Btn size="sm" variant="secondary" onClick={e=>{e.stopPropagation();openEdit(cfg);}}>Edit</Btn>}
                            {(isSuperAdmin || user.role==='account_admin') && (
                              <Btn size="sm" variant="ghost" onClick={e=>{e.stopPropagation();setCloneSrc(cfg);setCloneForm({workflowId:cfg.workflow_id,statusTrigger:'',paramName:'',tenantId:cfg.tenant_id});setCloneModal(true);}}>⎘ Clone</Btn>
                            )}
                            {!isViewer && <Btn size="sm" variant="danger" onClick={e=>{e.stopPropagation();if(confirm('Delete?'))api.configs.delete(cfg.id).then(load);}}>Del</Btn>}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {/* Inline test panel */}
      {testCfg && (
        <Card style={{ marginTop:16 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
            <div>
              <div style={{ fontSize:14, fontWeight:700, color:C.text }}>🧪 Test — <span style={{ color:C.accent, fontFamily:"'DM Mono',monospace" }}>{testCfg.param_name}</span></div>
              <div style={{ fontSize:12, color:C.textSec }}>{testCfg.workflow_name} → {testCfg.status_trigger}</div>
            </div>
            <Btn variant="ghost" size="sm" onClick={()=>{ setTestCfg(null); setTestResult(null); }}>✕ Close</Btn>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:16 }}>
            <div>
              <div style={{ display:'flex', gap:6, marginBottom:10 }}>
                {[['url','🔗 URL'],['upload','📁 Upload']].map(([m,l])=>(
                  <button key={m} onClick={()=>{ setTestImageMode(m); setTestImageUrl(''); setTestImageB64(''); }} style={{
                    flex:1, padding:'6px', borderRadius:6, border:`1px solid ${testImageMode===m?C.accent+'44':C.border}`,
                    background:testImageMode===m?C.accentDim:'transparent', color:testImageMode===m?C.accent:C.textSec,
                    cursor:'pointer', fontSize:12, fontFamily:"'DM Sans',sans-serif" }}>{l}</button>
                ))}
              </div>
              {testImageMode==='url' && (
                <>
                  <Input value={testImageUrl} onChange={setTestImageUrl} placeholder="https://…" style={{ marginBottom:8 }} />
                  {testImageUrl && <img src={testImageUrl} alt="" style={{ width:'100%', maxHeight:120, objectFit:'cover', borderRadius:8, marginBottom:8, border:`1px solid ${C.border}` }} onError={e=>e.target.style.display='none'} />}
                </>
              )}
              {testImageMode==='upload' && (
                <div style={{ marginBottom:8 }}>
                  <div onClick={()=>testFileRef.current?.click()} style={{ border:`2px dashed ${testImageB64?C.success+'66':C.border}`, borderRadius:8, padding:16, textAlign:'center', cursor:'pointer', background:testImageB64?C.successDim:'transparent' }}>
                    {testImageB64 ? <img src={`data:image/jpeg;base64,${testImageB64}`} alt="" style={{ maxHeight:100, borderRadius:6 }} /> : <><div style={{ fontSize:22, marginBottom:4 }}>📷</div><div style={{ fontSize:12, color:C.textSec }}>Click to upload</div></>}
                  </div>
                  <input ref={testFileRef} type="file" accept="image/*" style={{ display:'none' }}
                    onChange={e=>{ const f=e.target.files[0]; if(f){ const r=new FileReader(); r.onload=ev=>setTestImageB64(ev.target.result.split(',')[1]); r.readAsDataURL(f); }}} />
                </div>
              )}
              <div style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 10px', background:C.bg, borderRadius:8, border:`1px solid ${C.border}`, cursor:'pointer', marginBottom:10 }} onClick={()=>setTestSave(s=>!s)}>
                <div style={{ width:14, height:14, borderRadius:3, background:testSave?C.accent:C.bg, border:`2px solid ${testSave?C.accent:C.border}`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  {testSave && <span style={{ color:'#000', fontSize:9, fontWeight:700 }}>✓</span>}
                </div>
                <span style={{ fontSize:11, color:C.text }}>Save result to DB</span>
              </div>
              <Input value={testJobId} onChange={setTestJobId} placeholder="Job ID (required to save)"
                style={{ marginBottom:8 }} />
              <Btn onClick={runTest} disabled={testRunning} style={{ width:'100%' }}>{testRunning?'⏳ Running…':'▶ Run Test'}</Btn>
            </div>
            <div>
              <div style={{ fontSize:11, color:C.textSec, textTransform:'uppercase', letterSpacing:'0.07em', fontFamily:"'DM Mono',monospace", marginBottom:6 }}>Prompt v{testCfg.prompt_version}</div>
              <div style={{ background:C.bg, borderRadius:8, padding:10, border:`1px solid ${C.border}`, fontFamily:"'DM Mono',monospace", fontSize:11, color:C.text, lineHeight:1.6, whiteSpace:'pre-wrap', height:180, overflowY:'auto' }}>{testCfg.prompt}</div>
            </div>
            <div>
              <div style={{ fontSize:11, color:C.textSec, textTransform:'uppercase', letterSpacing:'0.07em', fontFamily:"'DM Mono',monospace", marginBottom:6 }}>Result</div>
              {testRunning && <Spinner />}
              {!testRunning && testResult && (() => {
                if (testResult.error) return <div style={{ background:C.dangerDim, border:`1px solid ${C.danger}44`, borderRadius:8, padding:10, fontSize:12, color:C.danger }}>{testResult.error}</div>;
                const d = testResult.saved ? testResult.data : testResult.data?.result;
                const status = testResult.saved ? testResult.data?.validation_status : null;
                const sc = status==='pass'?C.success:status==='fail'?C.danger:C.warning;
                const parsed = testResult.saved ? testResult.data?.parsed_result : d;
                return (
                  <div>
                    <div style={{ display:'flex', gap:6, marginBottom:8, flexWrap:'wrap' }}>
                      {status && <Badge c={sc} label={status} />}
                      {testResult.unsaved && <Badge c={C.warning} label="not saved" />}
                      {testResult.saved && <Badge c={C.success} label="saved" />}
                    </div>
                    <div style={{ background:C.bg, borderRadius:8, padding:10, border:`1px solid ${C.border}`, fontFamily:"'DM Mono',monospace", fontSize:11, lineHeight:1.8, maxHeight:200, overflowY:'auto' }}>
                      {parsed && Object.entries(parsed).map(([k,v])=>(
                        <div key={k} style={{ display:'flex', justifyContent:'space-between', padding:'2px 0', borderBottom:`1px solid ${C.border}` }}>
                          <span style={{ color:C.textSec }}>{k}</span>
                          <span style={{ color:typeof v==='boolean'?(v?C.success:C.danger):C.text, fontWeight:600 }}>{typeof v==='boolean'?(v?'✓':'✗'):String(v)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
              {!testRunning && !testResult && <div style={{ background:C.bg, borderRadius:8, padding:20, border:`1px solid ${C.border}`, textAlign:'center', color:C.textMuted, fontSize:12 }}>Add image and run test</div>}
            </div>
          </div>
        </Card>
      )}

      {/* Config detail panel */}
      {selConfig && (
        <Card style={{ marginTop:16 }}>
          <div style={{ fontSize:13, fontWeight:700, color:C.text, marginBottom:12, display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
            <span>Prompt —</span>
            <span style={{ color:C.accent, fontFamily:"'DM Mono',monospace" }}>{selConfig.param_name}</span>
            <span style={{ color:C.textMuted, fontFamily:"'DM Mono',monospace", fontSize:11 }}>v{selConfig.prompt_version}</span>
            {selConfig.library_id && <Badge c={C.success} label={`template: ${templates.find(t=>t.id===selConfig.library_id)?.name||'linked'}`} />}
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
            <div style={{ background:C.bg, borderRadius:8, padding:14, border:`1px solid ${C.border}`, fontFamily:"'DM Mono',monospace", fontSize:12, color:C.text, lineHeight:1.6, whiteSpace:'pre-wrap', maxHeight:200, overflowY:'auto' }}>{selConfig.prompt}</div>
            <JsonEditor value={JSON.stringify(selConfig.result_schema,null,2)} onChange={()=>{}} rows={5} readOnly />
          </div>
        </Card>
      )}

      {/* Workflow create/edit modal */}
      <Modal open={wfModal} onClose={closeWfModal} title={editWfId?'Edit Workflow':'New Workflow'} width={680}>
        {/* FIX: Superadmin needs to pick a tenant when creating a workflow */}
        {isSuperAdmin && !editWfId && (
          <Select label="Tenant *" value={wfForm.tenantId||''} onChange={v=>updateWfForm(f=>({...f,tenantId:v}))}
            options={[{value:'',label:'-- select tenant --'},...tenants.map(t=>({value:t.id,label:`${t.name} (${t.slug})`}))]} />
        )}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
          <Input label="Workflow Name *" value={wfForm.name||''} onChange={v=>updateWfForm(f=>({...f,name:v}))} placeholder="e.g. Breakdown Service" />
          <Select label="Category" value={wfForm.category||'custom'} onChange={v=>updateWfForm(f=>({...f,category:v}))}
            options={Object.entries(CATS).map(([k,v])=>({value:k,label:`${v.icon} ${v.label}`}))} />
        </div>
        <Input label="Description" value={wfForm.description||''} onChange={v=>updateWfForm(f=>({...f,description:v}))} placeholder="Brief description of this workflow" />
        <Input label="Reference Workflow ID (from workflow engine)" value={wfForm.engineWorkflowId||''} onChange={v=>updateWfForm(f=>({...f,engineWorkflowId:v}))} placeholder="e.g. WF-12345 or engine_workflow_id" />
        <Textarea label="Statuses (one per line or comma-separated) *" value={wfForm.statuses||''} onChange={v=>updateWfForm(f=>({...f,statuses:v}))} rows={3} mono
          placeholder={'pre_work_check\non_site_arrival\nwork_complete'} />
        {/* Context fields */}
        <div style={{ background:C.purpleDim, borderRadius:8, padding:14, border:`1px solid ${C.purple}33`, marginBottom:14 }}>
          <div style={{ fontSize:12, color:C.purple, fontWeight:600, marginBottom:10 }}>🧠 Workflow Context (optional — prepended to prompts)</div>
          <Textarea label="Workflow Context" value={wfForm.workflowContext||''} onChange={v=>updateWfForm(f=>({...f,workflowContext:v}))} rows={2}
            placeholder="e.g. This workflow handles air conditioning breakdown repairs in residential properties." />
          <JsonEditor label="Status Contexts (JSON: {status_name: context_text})" value={wfForm.statusContexts||'{}'} onChange={v=>updateWfForm(f=>({...f,statusContexts:v}))} rows={3}
            placeholder={'{\n  "pre_work_check": "Technician is about to start work. Verify safety compliance.",\n  "work_complete": "Work is finished. Verify quality and documentation."\n}'} />
          <div style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', marginTop:8 }} onClick={()=>updateWfForm(f=>({...f,useContextPrefix:!f.useContextPrefix}))}>
            <div style={{ width:16, height:16, borderRadius:3, background:wfForm.useContextPrefix?C.purple:C.bg, border:`2px solid ${wfForm.useContextPrefix?C.purple:C.border}`, display:'flex', alignItems:'center', justifyContent:'center' }}>
              {wfForm.useContextPrefix && <span style={{ color:'#fff', fontSize:10, fontWeight:700 }}>✓</span>}
            </div>
            <span style={{ fontSize:12, color:C.text }}>Prepend workflow + status context to all prompts in this workflow</span>
          </div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', marginBottom:14 }} onClick={()=>updateWfForm(f=>({...f,is_active:f.is_active===false?true:false}))}>
          <div style={{ width:16, height:16, borderRadius:3, background:wfForm.is_active!==false?C.success:C.bg, border:`2px solid ${wfForm.is_active!==false?C.success:C.border}`, display:'flex', alignItems:'center', justifyContent:'center' }}>
            {wfForm.is_active!==false && <span style={{ color:'#000', fontSize:10, fontWeight:700 }}>✓</span>}
          </div>
          <span style={{ fontSize:12, color:C.text }}>Active</span>
        </div>
        <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
          <Btn variant="ghost" onClick={closeWfModal}>Cancel</Btn>
          <Btn onClick={saveWf} disabled={!wfForm.name||!wfForm.statuses}>{editWfId?'Save Changes':'Create Workflow'}</Btn>
        </div>
      </Modal>


      {/* Clone Config Modal */}
      <Modal open={cloneModal} onClose={()=>setCloneModal(false)} title={`Clone Config — ${cloneSrc?.param_name||''}`} width={560}>
        {cloneSrc && (
          <div>
            <div style={{ background:C.bg, borderRadius:8, padding:12, border:`1px solid ${C.border}`, marginBottom:16, fontSize:12 }}>
              <div style={{ color:C.textSec, marginBottom:4 }}>Cloning from:</div>
              <div style={{ color:C.accent, fontFamily:"'DM Mono',monospace" }}>{cloneSrc.workflow_name} → {cloneSrc.status_trigger} → {cloneSrc.param_name}</div>
            </div>
            <Select label="Target Workflow *" value={cloneForm.workflowId||''} onChange={v=>setCloneForm(f=>({...f,workflowId:v,statusTrigger:''}))}
              options={[{value:'',label:'-- select workflow --'},...workflows.map(w=>({value:w.id,label:`${CATS[w.category]?.icon||''} ${w.name}`}))]} />
            {cloneForm.workflowId && (() => {
              const wf = workflows.find(w=>w.id===cloneForm.workflowId);
              return wf?.statuses?.length > 0 ? (
                <Select label="Status Trigger *" value={cloneForm.statusTrigger||''} onChange={v=>setCloneForm(f=>({...f,statusTrigger:v}))}
                  options={[{value:'',label:'-- select status --'},...(wf.statuses||[]).map(s=>({value:s,label:s}))]} />
              ) : (
                <Input label="Status Trigger *" value={cloneForm.statusTrigger||''} onChange={v=>setCloneForm(f=>({...f,statusTrigger:v}))} placeholder="e.g. pre_work_check" />
              );
            })()}
            <Input label="Param Name *" value={cloneForm.paramName||''} onChange={v=>setCloneForm(f=>({...f,paramName:v}))}
              placeholder={cloneSrc.param_name + '_copy'} />
            {isSuperAdmin && (
              <Select label="Target Tenant" value={cloneForm.tenantId||''} onChange={v=>setCloneForm(f=>({...f,tenantId:v}))}
                options={[{value:'',label:'Same tenant'},...tenants.map(t=>({value:t.id,label:t.name}))]} />
            )}
            <div style={{ fontSize:11, color:C.textMuted, marginBottom:16, lineHeight:1.6 }}>
              The prompt, result schema, action, confidence threshold, model, and all advanced settings will be copied exactly. Only the workflow/status/param identity changes.
            </div>
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
              <Btn variant="ghost" onClick={()=>setCloneModal(false)}>Cancel</Btn>
              <Btn onClick={async () => {
                if (!cloneForm.workflowId) return alert('Select a target workflow');
                if (!cloneForm.statusTrigger) return alert('Select a status trigger');
                if (!cloneForm.paramName?.trim()) return alert('Enter a param name');
                try {
                  await api.configs.create({
                    workflowId:     cloneForm.workflowId,
                    statusTrigger:  cloneForm.statusTrigger,
                    paramName:      cloneForm.paramName.trim(),
                    tenantId:       cloneForm.tenantId || cloneSrc.tenant_id,
                    prompt:         cloneSrc.prompt,
                    action:         cloneSrc.action,
                    minConfidence:  cloneSrc.min_confidence,
                    resultSchema:   cloneSrc.result_schema,
                    aiModel:        cloneSrc.ai_model || undefined,
                    aiProviderId:   cloneSrc.ai_provider_id || undefined,
                    imageResolution: cloneSrc.image_resolution,
                    imageCustomWidth: cloneSrc.image_custom_width,
                    imageCustomHeight: cloneSrc.image_custom_height,
                    activeForTesting: cloneSrc.active_for_testing,
                    activeForApi:   cloneSrc.active_for_api,
                    variableSchema: cloneSrc.variable_schema,
                  });
                  setCloneModal(false); load();
                } catch(e) { alert(e.message); }
              }} disabled={!cloneForm.workflowId||!cloneForm.statusTrigger||!cloneForm.paramName?.trim()}>
                ⎘ Create Clone
              </Btn>
            </div>
          </div>
        )}
      </Modal>

      {/* Config create/edit modal */}
      <Modal open={modal} onClose={closeModal} title={form.id?'Edit Config':'New Validation Config'} width={760}>
        {form.id && linkedTpl && (
          <div style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px', marginBottom:16, background:C.successDim, border:`1px solid ${C.success}44`, borderRadius:8 }}>
            <span style={{ fontSize:16 }}>📚</span>
            <div><div style={{ fontSize:12, fontWeight:600, color:C.success }}>Built from template: {linkedTpl.name}</div></div>
          </div>
        )}
        {!form.id && templates.length > 0 && (
          <div style={{ marginBottom:18 }}>
            <div style={{ fontSize:11, fontWeight:600, color:C.textSec, marginBottom:8, letterSpacing:'0.08em', textTransform:'uppercase', fontFamily:"'DM Mono',monospace" }}>Load from Template</div>
            <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:8 }}>
              {templates.slice(0,10).map(t => (
                <button key={t.id} onClick={() => applyTemplate(t.id)} style={{
                  padding:'5px 10px', borderRadius:6, cursor:'pointer', fontSize:11, fontFamily:"'DM Sans',sans-serif",
                  border:`1px solid ${fromTpl===t.id?C.success+'55':C.border}`,
                  background:fromTpl===t.id?C.successDim:C.bg, color:fromTpl===t.id?C.success:C.textSec, transition:'all 0.15s' }}>
                  {CATS[t.category]?.icon||'⚙️'} {t.name}
                </button>
              ))}
              <button onClick={()=>{ setModal(false); onOpenBuilder && onOpenBuilder(); }} style={{
                padding:'5px 10px', borderRadius:6, cursor:'pointer', fontSize:11, fontFamily:"'DM Sans',sans-serif",
                border:`1px solid ${C.accent}44`, background:C.accentDim, color:C.accent }}>🔬 Build new</button>
            </div>
            {fromTpl && <div style={{ fontSize:11, color:C.success, marginBottom:8 }}>✓ Template loaded — edit below if needed</div>}
            <div style={{ height:1, background:C.border, margin:'4px 0 12px' }} />
          </div>
        )}
        {(isSuperAdmin || isAccountMgr) && !form.id && (
          <Select label="Tenant *" value={form.tenantId||''} onChange={v=>updateForm(f=>({...f,tenantId:v}))}
            options={[{value:'',label:'-- select tenant --'},...tenants.map(t=>({value:t.id,label:`${t.name} (${t.slug})`}))]} />
        )}
        {form.id ? (
          <div style={{ background:C.bg, borderRadius:8, padding:'12px 14px', border:`1px solid ${C.border}`, marginBottom:14 }}>
            <div style={{ fontSize:11, color:C.textSec, textTransform:'uppercase', letterSpacing:'0.07em', fontFamily:"'DM Mono',monospace", marginBottom:10 }}>Identity — readonly</div>
            <div style={{ display:'flex', gap:24, flexWrap:'wrap' }}>
              {[['Workflow',workflows.find(w=>w.id===form.workflowId)?.name||form.workflowId],
                ['Status Trigger',form.statusTrigger],['Param Name',form.paramName],
                ...(isSuperAdmin?[['Tenant',tenants.find(t=>t.id===form.tenantId)?.name||form.tenantId]]:[])]
                .map(([label,val])=>(
                <div key={label}>
                  <div style={{ fontSize:10, color:C.textMuted, textTransform:'uppercase', letterSpacing:'0.07em', fontFamily:"'DM Mono',monospace", marginBottom:3 }}>{label}</div>
                  <div style={{ fontSize:13, color:C.text, fontFamily:"'DM Mono',monospace" }}>{val||'—'}</div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <>
            <Select label="Workflow *" value={form.workflowId||''} onChange={v=>updateForm(f=>({...f,workflowId:v}))}
              options={[{value:'',label:'-- select --'},...workflows.map(w=>({value:w.id,label:`${CATS[w.category]?.icon||''} ${w.name}`}))]} />
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <div>
                <div style={{ fontSize:11, fontWeight:600, color:C.textSec, marginBottom:6, letterSpacing:'0.08em', textTransform:'uppercase', fontFamily:"'DM Mono',monospace" }}>Status Trigger *</div>
                {form.workflowId && workflows.find(w=>w.id===form.workflowId)?.statuses?.length > 0 ? (
                  <select value={form.statusTrigger||''} onChange={e=>updateForm(f=>({...f,statusTrigger:e.target.value}))} style={{
                    width:'100%', background:C.bg, border:`1px solid ${C.border}`, borderRadius:8, padding:'9px 12px',
                    color:C.text, fontSize:13, fontFamily:"'DM Sans',sans-serif", outline:'none', boxSizing:'border-box', cursor:'pointer', marginBottom:14 }}
                    onFocus={e=>e.target.style.borderColor=C.accent} onBlur={e=>e.target.style.borderColor=C.border}>
                    <option value="">-- select status --</option>
                    {(workflows.find(w=>w.id===form.workflowId)?.statuses||[]).map(s=><option key={s} value={s}>{s}</option>)}
                  </select>
                ) : <Input value={form.statusTrigger||''} onChange={v=>updateForm(f=>({...f,statusTrigger:v}))} placeholder="e.g. pre_work_check" />}
              </div>
              <Input label="Param Name *" value={form.paramName||''} onChange={v=>updateForm(f=>({...f,paramName:v}))} placeholder="e.g. technician_ppe_photo" />
            </div>
          </>
        )}
        <Input label="Display Name (optional)" value={form.displayName||''}
          onChange={v=>updateForm(f=>({...f,displayName:v}))}
          placeholder="Human-readable label, e.g. PPE Check — Pre-work" />
        <div style={{ marginBottom:14 }}>
          <div style={{ fontSize:11, fontWeight:700, color:C.textSec, textTransform:'uppercase',
            letterSpacing:'0.07em', marginBottom:5, fontFamily:"'DM Mono',monospace" }}>
            Description / Instructions (optional)
          </div>
          <textarea value={form.description||''}
            onChange={e=>updateForm(f=>({...f,description:e.target.value}))}
            placeholder="Tell technicians what to photograph and what the AI will check (shown in field app and results)"
            rows={2} style={{ width:'100%', background:C.bg, border:`1px solid ${C.border}`,
              borderRadius:8, padding:'9px 12px', color:C.text, fontSize:13,
              fontFamily:"'DM Sans',sans-serif", resize:'vertical', outline:'none',
              boxSizing:'border-box' }} />
        </div>
        <Textarea label="Vision Prompt" value={form.prompt||''} onChange={v=>updateForm(f=>({...f,prompt:v}))} rows={5} />
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 }}>
          <Select label="Action" value={form.action||'flag_fail'} onChange={v=>updateForm(f=>({...f,action:v}))}
            options={[{value:'flag_fail',label:'Flag Fail'},{value:'auto_approve',label:'Auto Approve'},{value:'extract_data',label:'Extract Data'},{value:'notify',label:'Notify'}]} />
          <Select label="AI Provider" value={form.aiProviderId||''} onChange={v=>updateForm(f=>({...f,aiProviderId:v,aiModel:''}))}
            options={[{value:'',label:'Auto (active provider)'},...activeProviders.map(p=>({value:p.id,label:`${PROVIDER_ICONS[p.provider_type]||''} ${p.name}`}))]} />
          <Input label="Min Confidence" type="number" value={form.minConfidence||'0.85'} onChange={v=>updateForm(f=>({...f,minConfidence:v}))} />
        </div>
        {form.aiProviderId && (() => {
          const selProv = activeProviders.find(p=>p.id===form.aiProviderId);
          const models  = selProv?.settings?.models || [];
          return models.length > 0 ? (
            <Select label={`Model (${selProv?.name})`} value={form.aiModel||''} onChange={v=>updateForm(f=>({...f,aiModel:v}))}
              options={[{value:'',label:`Default (${selProv?.default_model||''})`},...models.map(m=>({value:m,label:m}))]} />
          ) : (
            <Input label="Model Override" value={form.aiModel||''} onChange={v=>updateForm(f=>({...f,aiModel:v}))} placeholder={selProv?.default_model||''} />
          );
        })()}
        <JsonEditor label="Result Schema (JSON)" value={form.resultSchema||'{}'} onChange={v=>updateForm(f=>({...f,resultSchema:v}))} rows={4} />
        <details style={{ marginBottom:14 }}>
          <summary style={{ fontSize:12, color:C.textSec, cursor:'pointer', userSelect:'none', padding:'6px 0', fontFamily:"'DM Mono',monospace", textTransform:'uppercase', letterSpacing:'0.07em' }}>▸ Advanced Settings</summary>
          <div style={{ marginTop:12, display:'flex', flexDirection:'column', gap:12 }}>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <Select label="Image Resolution" value={form.imageResolution||'original'} onChange={v=>updateForm(f=>({...f,imageResolution:v}))}
                options={[{value:'original',label:'Original'},{value:'512x512',label:'512×512'},{value:'1024x768',label:'1024×768'},{value:'1024x1024',label:'1024×1024'},{value:'custom',label:'Custom'}]} />
              {form.imageResolution==='custom' && (
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                  <Input label="Width px" type="number" value={form.imageCustomWidth||''} onChange={v=>updateForm(f=>({...f,imageCustomWidth:v}))} placeholder="1280" />
                  <Input label="Height px" type="number" value={form.imageCustomHeight||''} onChange={v=>updateForm(f=>({...f,imageCustomHeight:v}))} placeholder="960" />
                </div>
              )}
            </div>
            <div style={{ display:'flex', gap:12 }}>
              {[['activeForTesting','Active for UI Testing'],['activeForApi','Active for API / External']].map(([key,label])=>(
                <div key={key} onClick={()=>updateForm(f=>({...f,[key]:!f[key]}))} style={{
                  display:'flex', alignItems:'center', gap:8, padding:'8px 12px', flex:1,
                  background:C.bg, border:`1px solid ${form[key]===false?C.border:C.success+'44'}`, borderRadius:8, cursor:'pointer' }}>
                  <div style={{ width:16, height:16, borderRadius:3, flexShrink:0, background:form[key]===false?C.bg:C.success, border:`2px solid ${form[key]===false?C.border:C.success}`, display:'flex', alignItems:'center', justifyContent:'center' }}>
                    {form[key]!==false && <span style={{ color:'#000', fontSize:10, fontWeight:700 }}>✓</span>}
                  </div>
                  <span style={{ fontSize:12, color:form[key]===false?C.textSec:C.text }}>{label}</span>
                </div>
              ))}
            </div>
            <div>
              <div style={{ fontSize:11, color:C.textSec, textTransform:'uppercase', letterSpacing:'0.07em', fontFamily:"'DM Mono',monospace", marginBottom:6 }}>Dynamic Prompt Variables</div>
              <div style={{ fontSize:11, color:C.textMuted, marginBottom:6 }}>Use <code style={{fontFamily:"'DM Mono',monospace",background:C.bg,padding:'1px 4px',borderRadius:3}}>{'{{variable_name}}'}</code> in the prompt. Callers supply values via <code style={{fontFamily:"'DM Mono',monospace"}}>variables</code>.</div>
              <JsonEditor value={form.variableSchema||'[]'} onChange={v=>updateForm(f=>({...f,variableSchema:v}))} rows={3} placeholder={'[\n  {"name":"serial","label":"Serial Number","type":"text","required":false}\n]'} />
            </div>
          </div>
        </details>
        <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
          <Btn variant="ghost" onClick={closeModal}>Cancel</Btn>
          <Btn onClick={save}>{form.id?'Save Changes':'Create Config'}</Btn>
        </div>
      </Modal>

      {/* ── Config detail / view modal ─────────────────────────────────────── */}
      <Modal open={!!viewCfg} onClose={()=>setViewCfg(null)}
        title={viewCfg ? `${viewCfg.display_name||viewCfg.param_name} — Config Details` : ''} width={620}>
        {viewCfg && (
          <div>
            {/* Identity */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:14 }}>
              {[
                ['Workflow',    viewCfg.workflow_name],
                ['Param Name',  viewCfg.param_name],
                ['Display Name',viewCfg.display_name || '—'],
                ['Status Trigger', viewCfg.status_trigger],
                ['Action',      viewCfg.action],
                ['Min Confidence', `${Math.round((viewCfg.min_confidence||0.85)*100)}%`],
                ['Prompt Version', `v${viewCfg.prompt_version || 1}`],
                ['Provider',    viewCfg.provider_name || 'System default'],
              ].map(([label, val]) => (
                <div key={label} style={{ background:C.bg, border:`1px solid ${C.border}`,
                  borderRadius:8, padding:'8px 12px' }}>
                  <div style={{ fontSize:10, color:C.textSec, fontWeight:700,
                    textTransform:'uppercase', letterSpacing:'0.07em',
                    fontFamily:"'DM Mono',monospace", marginBottom:3 }}>{label}</div>
                  <div style={{ fontSize:13, color:C.text, fontWeight:600 }}>{val}</div>
                </div>
              ))}
            </div>
            {/* Description */}
            {viewCfg.description && (
              <div style={{ background:C.accentDim, border:`1px solid ${C.accent}33`,
                borderRadius:8, padding:'10px 14px', marginBottom:12 }}>
                <div style={{ fontSize:10, color:C.accent, fontWeight:700,
                  textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:4 }}>
                  Description / Instructions
                </div>
                <div style={{ fontSize:13, color:C.text, lineHeight:1.6 }}>
                  {viewCfg.description}
                </div>
              </div>
            )}
            {/* Prompt */}
            <div style={{ marginBottom:12 }}>
              <div style={{ fontSize:11, fontWeight:700, color:C.textSec,
                textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:6,
                fontFamily:"'DM Mono',monospace" }}>
                AI Prompt (v{viewCfg.prompt_version || 1})
              </div>
              <div style={{ background:C.bg, border:`1px solid ${C.border}`, borderRadius:8,
                padding:'12px', fontSize:12, color:C.text, lineHeight:1.7,
                fontFamily:"'DM Mono',monospace", whiteSpace:'pre-wrap',
                maxHeight:180, overflowY:'auto' }}>
                {viewCfg.prompt}
              </div>
            </div>
            {/* Result schema */}
            <div style={{ marginBottom:16 }}>
              <div style={{ fontSize:11, fontWeight:700, color:C.textSec,
                textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:6,
                fontFamily:"'DM Mono',monospace" }}>Result Schema</div>
              <div style={{ background:C.bg, border:`1px solid ${C.border}`, borderRadius:8,
                padding:'12px', fontSize:11, color:C.accent, lineHeight:1.7,
                fontFamily:"'DM Mono',monospace", whiteSpace:'pre-wrap' }}>
                {JSON.stringify(viewCfg.result_schema, null, 2)}
              </div>
            </div>
            <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
              <Btn variant="ghost" onClick={()=>setViewCfg(null)}>Close</Btn>
              {canWrite && (
                <Btn onClick={()=>{ setViewCfg(null); openEdit(viewCfg); }}>
                  ✏ Edit Config
                </Btn>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}


// ── Image Tester ──────────────────────────────────────────────────────────────
function ImageTester({ user }) {
  const [configs,    setConfigs]    = useState([]);
  const [selConfig,  setSelConfig]  = useState('');
  const [imageUrl,   setImageUrl]   = useState('');
  const [imageB64,   setImageB64]   = useState('');
  const [imageMode,  setImageMode]  = useState('url');
  const [jobId,      setJobId]      = useState('');
  const [jobMeta,    setJobMeta]    = useState('');
  const [variables,  setVariables]  = useState({});   // dynamic prompt variables
  const [running,    setRunning]    = useState(false);
  const [result,     setResult]     = useState(null);
  const [history,    setHistory]    = useState([]);
  const [detailRun,  setDetailRun]  = useState(null);
  const [showCfgDetail, setShowCfgDetail] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.configs.list({ limit:200 }).then(r=>setConfigs(r.data||[])).catch(console.error);
  }, []);

  // Reset variables when config changes
  useEffect(() => { setVariables({}); }, [selConfig]);

  const cfg = configs.find(c=>c.id===selConfig);
  const varSchema = cfg?.variable_schema || [];
  const sc = result ? (result.validation_status==='pass'?C.success:result.validation_status==='fail'?C.danger:C.warning) : null;

  const run = async () => {
    if (!selConfig) return alert('Select a validation config first');
    if (!jobId.trim()) return alert('Job ID is required — enter a task or job reference');
    if (imageMode==='url' && !imageUrl) return alert('Enter an image URL');
    if (imageMode==='upload' && !imageB64) return alert('Upload an image first');
    setRunning(true); setResult(null);
    try {
      const body = { configId: selConfig };
      if (imageMode==='url') body.imageUrl = imageUrl;
      else body.imageBase64 = imageB64;
      if (jobId.trim()) body.jobId = jobId.trim();
      if (jobMeta.trim()) { try { body.jobMeta = JSON.parse(jobMeta); } catch {} }
      if (Object.keys(variables).some(k=>variables[k])) body.variables = variables;
      const r = await api.runs.submit(body);
      if (r?.data?.tokenBudget || r?.status === 429) {
        setError('🪙 ' + (r?.data?.message || 'Token budget exhausted'));
        return;
      }
      const run = { ...r.data, validationStatus: r.data.validation_status, parsedResult: r.data.parsed_result };
      setResult(run);
      setHistory(h=>[run,...h].slice(0,20));
    } catch(e) { setResult({ error:e.message, validationStatus:'error' }); }
    setRunning(false);
  };

  return (
    <div>
      <div style={{ marginBottom:20 }}>
        <div style={{ fontSize:20, fontWeight:700, fontFamily:"'DM Serif Display',serif", color:C.text, marginBottom:4 }}>Image Tester</div>
        <div style={{ color:C.textSec, fontSize:13 }}>Pick a param, provide an image, run — results are saved automatically</div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
        {/* Left: config + input */}
        <div>
          <Card style={{ marginBottom:16 }}>
            <div style={{ fontSize:14, fontWeight:700, color:C.text, marginBottom:16 }}>🎯 Config & Job</div>
            <Select label="Validation Config / Param" value={selConfig} onChange={setSelConfig}
              options={[{value:'',label:'-- pick a param --'},...configs.map(c=>({
                value:c.id,
                label:`${CATS[c.category]?.icon||'⚙️'} ${c.workflow_name} → ${c.param_name} [${c.status_trigger}]${user.role==='superadmin'&&c.tenant_name?' ('+c.tenant_name+')':''}`
              }))]} />

            {/* Config detail panel */}
            {cfg && (
              <div style={{ background:C.bg, borderRadius:8, border:`1px solid ${C.border}`, marginBottom:14, overflow:'hidden' }}>
                <div onClick={()=>setShowCfgDetail(s=>!s)} style={{ padding:'10px 14px', cursor:'pointer', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                    <Badge c={C.purple} label={cfg.status_trigger} />
                    <Badge c={C.warning} label={cfg.action} />
                    <span style={{ fontSize:11, color:C.textSec }}>min conf: {(parseFloat(cfg.min_confidence)*100).toFixed(0)}%</span>
                    {cfg.ai_model && <Badge c={C.accent} label={cfg.ai_model} />}
                    {cfg.provider_name && <span style={{ fontSize:11, color:C.textMuted }}>{PROVIDER_ICONS[cfg.ai_provider_type]||''} {cfg.provider_name}</span>}
                  </div>
                  <span style={{ fontSize:11, color:C.textSec }}>{showCfgDetail?'▲':'▼'} details</span>
                </div>
                {showCfgDetail && (
                  <div style={{ padding:'0 14px 14px', borderTop:`1px solid ${C.border}` }}>
                    <div style={{ fontSize:11, color:C.textSec, textTransform:'uppercase', letterSpacing:'0.07em', fontFamily:"'DM Mono',monospace", margin:'10px 0 6px' }}>Prompt</div>
                    <div style={{ fontFamily:"'DM Mono',monospace", fontSize:11, color:C.text, lineHeight:1.6, whiteSpace:'pre-wrap', maxHeight:120, overflowY:'auto' }}>{cfg.prompt}</div>
                    <div style={{ fontSize:11, color:C.textSec, textTransform:'uppercase', letterSpacing:'0.07em', fontFamily:"'DM Mono',monospace", margin:'10px 0 6px' }}>Result Schema</div>
                    <JsonEditor value={JSON.stringify(cfg.result_schema,null,2)} onChange={()=>{}} rows={4} readOnly />
                  </div>
                )}
              </div>
            )}

            <Input label="Job ID / Task ID *" value={jobId} onChange={setJobId} placeholder="e.g. JOB-BR-001-TASK-42" />
            <Textarea label='Job Metadata JSON (optional) — e.g. {"region":"south","product":"ac"}' value={jobMeta} onChange={setJobMeta} rows={2} mono placeholder='{"key":"value"}' />

            {/* Dynamic prompt variables */}
            {varSchema.length > 0 && (
              <div style={{ marginTop:8 }}>
                <div style={{ fontSize:11, color:C.textSec, textTransform:'uppercase', letterSpacing:'0.07em', fontFamily:"'DM Mono',monospace", marginBottom:8 }}>
                  Dynamic Prompt Variables
                </div>
                <div style={{ background:C.bg, borderRadius:8, padding:'8px 12px', border:`1px solid ${C.accent}33`, marginBottom:8 }}>
                  <div style={{ fontSize:11, color:C.textMuted, marginBottom:6 }}>
                    These are injected into the prompt where <code style={{fontFamily:"'DM Mono',monospace",color:C.accent}}>{'{{variable}}'}</code> appears.
                  </div>
                  {varSchema.map(v=>(
                    <div key={v.name} style={{ marginBottom:8 }}>
                      <Input label={`${v.label||v.name}${v.required?' *':' (optional)'}`}
                        value={variables[v.name]||''}
                        onChange={val=>setVariables(prev=>({...prev,[v.name]:val}))}
                        placeholder={v.type==='number'?'0':v.example||`Enter ${v.label||v.name}`} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {varSchema.length === 0 && cfg && (
              <div style={{ fontSize:11, color:C.textMuted, marginBottom:8 }}>
                💡 Tip: to pass dynamic values into the prompt, add variable definitions in Workflow Config → Advanced Settings
              </div>
            )}
          </Card>

          <Card>
            <div style={{ fontSize:14, fontWeight:700, color:C.text, marginBottom:16 }}>📷 Image Source</div>
            <div style={{ display:'flex', gap:8, marginBottom:16 }}>
              {[['url','🔗 URL'],['upload','📁 Upload']].map(([m,l])=>(
                <button key={m} onClick={()=>{ setImageMode(m); setImageUrl(''); setImageB64(''); }} style={{
                  flex:1, padding:'8px', borderRadius:8, border:`1px solid ${imageMode===m?C.accent+'44':C.border}`,
                  background:imageMode===m?C.accentDim:'transparent', color:imageMode===m?C.accent:C.textSec,
                  cursor:'pointer', fontSize:13, fontFamily:"'DM Sans',sans-serif" }}>{l}</button>
              ))}
            </div>
            {imageMode==='url' && (
              <>
                <Input label="Image URL" value={imageUrl} onChange={setImageUrl} placeholder="https://example.com/photo.jpg" />
                {imageUrl && <div style={{ borderRadius:8, overflow:'hidden', border:`1px solid ${C.border}`, marginBottom:8 }}>
                  <img src={imageUrl} alt="" style={{ width:'100%', maxHeight:160, objectFit:'cover', display:'block' }} onError={e=>e.target.style.display='none'} />
                </div>}
              </>
            )}
            {imageMode==='upload' && (
              <div>
                <div onClick={()=>fileRef.current?.click()} style={{
                  border:`2px dashed ${imageB64?C.success+'66':C.border}`, borderRadius:10, padding:20,
                  textAlign:'center', cursor:'pointer', marginBottom:12, background:imageB64?C.successDim:'transparent', transition:'all 0.2s' }}>
                  {imageB64 ? (
                    <div>
                      <img src={`data:image/jpeg;base64,${imageB64}`} alt="" style={{ maxHeight:120, borderRadius:6, marginBottom:6 }} />
                      <div style={{ fontSize:11, color:C.success }}>✓ Image loaded + click to change</div>
                    </div>
                  ) : (<><div style={{ fontSize:32, marginBottom:8 }}>📷</div><div style={{ fontSize:13, color:C.textSec }}>Click or drag an image here</div></>)}
                </div>
                <input ref={fileRef} type="file" accept="image/*" style={{ display:'none' }}
                  onChange={e=>{ const f=e.target.files[0]; if(f){ const r=new FileReader(); r.onload=ev=>setImageB64(ev.target.result.split(',')[1]); r.readAsDataURL(f); }}} />
                {imageB64 && <Btn variant="ghost" size="sm" style={{ width:'100%' }} onClick={()=>setImageB64('')}>Remove image</Btn>}
              </div>
            )}
            <Btn onClick={run} disabled={running||!selConfig} style={{ width:'100%', marginTop:12 }} size="lg">
              {running?'⏳ Running validation...':'▶ Run Validation'}
            </Btn>
          </Card>
        </div>

        {/* Right: result + history */}
        <div>
          <Card style={{ marginBottom:16, minHeight:200 }}>
            <div style={{ fontSize:14, fontWeight:700, color:C.text, marginBottom:12 }}>
              {running?'⏳ Running…':result?'✓ Result':'⬡ Result'}
            </div>
            {running && <Spinner />}
            {!running && result && (
              <>
                {result.image_url && <img src={result.image_url} alt="" style={{ width:'100%', maxHeight:140, objectFit:'cover', borderRadius:8, marginBottom:12, border:`1px solid ${C.border}` }} onError={e=>e.target.style.display='none'} />}
                <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:12 }}>
                  {sc && <Badge c={sc} label={result.validation_status||result.validationStatus} />}
                  {result.confidence!=null && <Badge c={C.purple} label={`${(result.confidence*100).toFixed(0)}%`} />}
                  {result.processing_ms && <span style={{ fontSize:11, color:C.textMuted }}>{result.processing_ms}ms</span>}
                  {(result.tokens_input||result.tokens_output) && (
                    <span style={{ fontSize:11, color:C.textMuted, fontFamily:"'DM Mono',monospace" }}>
                      {result.tokens_input||0}↑ {result.tokens_output||0}↓ tokens
                    </span>
                  )}
                </div>
                {result.parsed_result && (
                  <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                    {Object.entries(result.parsed_result).map(([k,v])=>(
                      <div key={k} style={{ background:C.bg, borderRadius:6, padding:'4px 10px', border:`1px solid ${typeof v==='boolean'?(v?C.success+'44':C.danger+'44'):C.border}` }}>
                        <span style={{ fontSize:10, color:C.textMuted, fontFamily:"'DM Mono',monospace", textTransform:'uppercase' }}>{k}: </span>
                        <span style={{ fontSize:11, fontFamily:"'DM Mono',monospace", color:typeof v==='boolean'?(v?C.success:C.danger):C.text }}>
                          {typeof v==='boolean'?(v?'✓':'✗'):String(v)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {result.error && <div style={{ color:C.danger, fontSize:12, fontFamily:"'DM Mono',monospace" }}>{result.error}</div>}
                {result.id && (
                  <div style={{ marginTop:12, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <span style={{ fontSize:11, color:C.success }}>✓ Saved + {result.id.slice(0,8)}…</span>
                    <Btn size="sm" variant="secondary" onClick={()=>setDetailRun(result)}>View Detail →</Btn>
                  </div>
                )}
              </>
            )}
            {!running && !result && <div style={{ background:C.bg, borderRadius:8, padding:20, border:`1px solid ${C.border}`, textAlign:'center', color:C.textMuted, fontSize:13 }}>Select a config and image, then Run</div>}
          </Card>

          {history.length>0 && (
            <Card>
              <div style={{ fontSize:13, fontWeight:700, color:C.text, marginBottom:12 }}>📋 Session History ({history.length})</div>
              <div style={{ maxHeight:300, overflowY:'auto' }}>
                {history.map((h,i)=>{ const sc=h.validation_status==='pass'?C.success:h.validation_status==='fail'?C.danger:C.warning; return (
                  <div key={h.id||i} onClick={()=>setDetailRun(h)} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 12px',
                    background:C.bg, borderRadius:8, border:`1px solid ${C.border}`, marginBottom:6, cursor:'pointer' }}
                    onMouseEnter={e=>e.currentTarget.style.borderColor=C.accent}
                    onMouseLeave={e=>e.currentTarget.style.borderColor=C.border}>
                    <div style={{ width:6, height:6, borderRadius:'50%', background:sc, flexShrink:0 }} />
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:12, color:C.text }}>{h.param_name}</div>
                      <div style={{ fontSize:10, color:C.textSec, fontFamily:"'DM Mono',monospace" }}>
                        {h.confidence!=null?`${(h.confidence*100).toFixed(0)}% + `:''}
                        {h.processing_ms}ms{h.job_id && ` + ${h.job_id}`}
                      </div>
                    </div>
                    <Badge c={sc} label={h.validation_status} />
                  </div>
                ); })}
              </div>
            </Card>
          )}
        </div>
      </div>

      <RunDetailModal run={detailRun} onClose={()=>setDetailRun(null)} />
    </div>
  );
}


// ── Prompt Library ────────────────────────────────────────────────────────────
function PromptLibrary({ user }) {
  const [templates, setTemplates] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [modal,     setModal]     = useState(false);
  const [editItem,  setEditItem]  = useState(null);
  const [form,      setForm]      = useState({});
  const [catFilter, setCatFilter] = useState('all');
  const [search,    setSearch]    = useState('');
  const [detailTpl, setDetailTpl] = useState(null); // full detail/test modal

  const load = useCallback(async () => {
    setLoading(true);
    const p = {};
    if (catFilter!=='all') p.category = catFilter;
    if (search) p.search = search;
    try { const r = await promptLibApi.list(p); setTemplates(r.data||[]); }
    catch(e) { console.error(e); }
    setLoading(false);
  }, [catFilter, search]);

  useEffect(()=>{ load(); },[load]);

  const openCreate = () => {
    setEditItem(null);
    setForm({ name:'', category:'custom', description:'', promptText:'', resultSchema:'{}', tags:'' });
    setModal(true);
  };

  const openEdit = (t) => {
    setEditItem(t);
    setForm({ name:t.name, category:t.category, description:t.description||'',
      promptText:t.prompt_text, resultSchema:JSON.stringify(t.result_schema,null,2),
      tags:(t.tags||[]).join(', ') });
    setModal(true);
  };

  const openClone = (t) => {
    setEditItem(null); // new template, not editing
    setForm({
      name:`${t.name} (copy)`, category:t.category, description:t.description||'',
      promptText:t.prompt_text, resultSchema:JSON.stringify(t.result_schema,null,2),
      tags:(t.tags||[]).join(', '),
    });
    setModal(true);
  };

  const save = async () => {
    try {
      const body = { name:form.name, category:form.category, description:form.description,
        promptText:form.promptText, resultSchema:JSON.parse(form.resultSchema||'{}'),
        tags:form.tags.split(',').map(t=>t.trim()).filter(Boolean) };
      if (editItem) await promptLibApi.update(editItem.id, body);
      else          await promptLibApi.create(body);
      setModal(false); load();
    } catch(e) { alert(e.message); }
  };

  const del = async (id) => {
    if (!confirm('Delete this template?')) return;
    try { await promptLibApi.delete(id); load(); } catch(e) { alert(e.message); }
  };

  const cats = ['all','ppe','site','serial','odometer','selfie','custom'];
  const globals = templates.filter(t=>t.tenant_id===null);
  const owned   = templates.filter(t=>t.tenant_id!==null);

  const TemplCard = (t) => {
    const cat = CATS[t.category]||CATS.custom;
    const hasContext = t.builder_context && Object.values(t.builder_context).some(v=>v&&v!=='');
    const hasSamples = Array.isArray(t.sample_images) && t.sample_images.length > 0;
    return (
      <div key={t.id} style={{ background:C.bg, borderRadius:12, border:`1px solid ${C.border}`, overflow:'hidden', transition:'border-color 0.2s' }}
        onMouseEnter={e=>e.currentTarget.style.borderColor=cat.color}
        onMouseLeave={e=>e.currentTarget.style.borderColor=C.border}>
        <div style={{ padding:'14px 16px', borderBottom:`1px solid ${C.border}` }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:6 }}>
            <div style={{ display:'flex', gap:8, alignItems:'center' }}>
              <span style={{ fontSize:20 }}>{cat.icon}</span>
              <div style={{ fontSize:13, fontWeight:700, color:C.text }}>{t.name}</div>
            </div>
            <div style={{ display:'flex', gap:4, flexShrink:0 }}>
              {t.is_default && <Badge c={C.success} label="default" />}
              {hasContext && <Badge c={C.purple} label="AI built" />}
            </div>
          </div>
          {t.description && <div style={{ fontSize:12, color:C.textSec, lineHeight:1.4, display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden', marginBottom:6 }}>{t.description}</div>}
          {/* Sample image thumbnails */}
          {hasSamples && (
            <div style={{ display:'flex', gap:4, marginBottom:6 }}>
              {t.sample_images.slice(0,3).map((img,i)=>img.url&&(
                <img key={i} src={img.url} alt="" style={{ width:32, height:24, objectFit:'cover', borderRadius:3, border:`1px solid ${C.border}` }} onError={e=>e.target.style.display='none'} />
              ))}
              {t.sample_images.length > 3 && <span style={{ fontSize:10, color:C.textMuted, lineHeight:'24px' }}>+{t.sample_images.length-3} more</span>}
            </div>
          )}
          <div style={{ display:'flex', gap:4, flexWrap:'wrap' }}>{(t.tags||[]).slice(0,3).map(tag=><Badge key={tag} c={C.textMuted} label={tag} />)}</div>
        </div>
        <div style={{ padding:'8px 12px', display:'flex', gap:5 }}>
          <Btn size="sm" variant="ghost" onClick={()=>setDetailTpl(t)} style={{ flex:1 }}>View & Test</Btn>
          <Btn size="sm" variant="secondary" onClick={()=>openEdit(t)}>Edit</Btn>
          <Btn size="sm" variant="ghost" onClick={()=>openClone(t)} style={{ color:C.purple }}>Clone</Btn>
          {t.tenant_id!==null && <Btn size="sm" variant="danger" onClick={()=>del(t.id)}>Del</Btn>}
        </div>
      </div>
    );
  };

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <div>
          <div style={{ fontSize:20, fontWeight:700, fontFamily:"'DM Serif Display',serif", color:C.text, marginBottom:4 }}>Templates</div>
          <div style={{ color:C.textSec, fontSize:13 }}>Reusable prompt + schema templates + Click "View & Test" to inspect and refine</div>
        </div>
        <Btn onClick={openCreate}>＋ New Template</Btn>
      </div>

      <Card style={{ marginBottom:16, padding:'14px 20px' }}>
        <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'center' }}>
          <div style={{ display:'flex', gap:4 }}>
            {cats.map(c=>(
              <button key={c} onClick={()=>setCatFilter(c)} style={{
                padding:'5px 10px', borderRadius:6, border:'none', cursor:'pointer', fontSize:11, fontWeight:600,
                fontFamily:"'DM Mono',monospace", textTransform:'uppercase',
                background:catFilter===c?C.accent:C.bg, color:catFilter===c?'#000':C.textSec,
                border:catFilter===c?'none':`1px solid ${C.border}` }}>
                {CATS[c]?.icon||'●'} {c}
              </button>
            ))}
          </div>
          <Input value={search} onChange={setSearch} placeholder="Search…" style={{ marginBottom:0, minWidth:180 }} />
        </div>
      </Card>

      {loading ? <Spinner /> : (
        <>
          {globals.length>0 && <>
            <div style={{ fontSize:12, color:C.textMuted, textTransform:'uppercase', letterSpacing:'0.08em', fontFamily:"'DM Mono',monospace", marginBottom:10 }}>🌐 Global Defaults ({globals.length})</div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:12, marginBottom:24 }}>{globals.map(TemplCard)}</div>
          </>}
          {owned.length>0 && <>
            <div style={{ fontSize:12, color:C.textMuted, textTransform:'uppercase', letterSpacing:'0.08em', fontFamily:"'DM Mono',monospace", marginBottom:10 }}>🏢 Your Templates ({owned.length})</div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:12 }}>{owned.map(TemplCard)}</div>
          </>}
          {templates.length===0 && <div style={{ textAlign:'center', padding:60, color:C.textMuted }}><div style={{ fontSize:32, marginBottom:12 }}>📚</div>No templates found.</div>}
        </>
      )}

      {/* ── Template Detail + Test Modal ───────────────────────────────── */}
      <TemplateDetailModal
        template={detailTpl}
        onClose={()=>setDetailTpl(null)}
        onEdit={()=>{ openEdit(detailTpl); setDetailTpl(null); }}
        onClone={()=>{ openClone(detailTpl); setDetailTpl(null); }}
      />

      {/* ── Create / Edit Modal ─────────────────────────────────────────── */}
      <Modal open={modal} onClose={()=>setModal(false)} title={editItem?'Edit Template':form.name?.endsWith('(copy)')?'Clone Template':'New Prompt Template'} width={720}>
        {editItem && editItem.is_default && (
          <div style={{ padding:'10px 14px', background:C.warningDim, border:`1px solid ${C.warning}44`, borderRadius:8, marginBottom:16, fontSize:12, color:C.warning }}>
            ⚠ This is a global default template. Changes will affect all tenants.
          </div>
        )}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
          <Input label="Template Name *" value={form.name||''} onChange={v=>setForm(f=>({...f,name:v}))} placeholder="e.g. PPE Full Check" />
          <Select label="Category" value={form.category||'custom'} onChange={v=>setForm(f=>({...f,category:v}))}
            options={Object.entries(CATS).map(([k,v])=>({value:k,label:`${v.icon} ${v.label}`}))} />
        </div>
        <Input label="Description" value={form.description||''} onChange={v=>setForm(f=>({...f,description:v}))} placeholder="Brief description of what this checks or extracts" />
        <Textarea label="Prompt Text *" value={form.promptText||''} onChange={v=>setForm(f=>({...f,promptText:v}))} rows={7} mono />
        <JsonEditor label="Result Schema (JSON) *" value={form.resultSchema||'{}'} onChange={v=>setForm(f=>({...f,resultSchema:v}))} rows={4} />
        <Input label="Tags (comma-separated)" value={form.tags||''} onChange={v=>setForm(f=>({...f,tags:v}))} placeholder="ppe, safety, mandatory" />
        <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
          <Btn variant="ghost" onClick={()=>setModal(false)}>Cancel</Btn>
          <Btn onClick={save} disabled={!form.name||!form.promptText}>Save Template</Btn>
        </div>
      </Modal>
    </div>
  );
}

// ── TemplateDetailModal — view context, prompt, schema + live test ─────────────
function TemplateDetailModal({ template, onClose, onEdit, onClone }) {
  const [testImageUrl,  setTestImageUrl]  = useState('');
  const [testImageB64,  setTestImageB64]  = useState('');
  const [testImageMode, setTestImageMode] = useState('url');
  const [testing,       setTesting]       = useState(false);
  const [testResult,    setTestResult]    = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    // Reset test state when template changes
    setTestResult(null); setTestImageUrl(''); setTestImageB64('');
  }, [template?.id]);

  if (!template) return null;

  const cat = CATS[template.category]||CATS.custom;
  const ctx = template.builder_context || {};
  const hasCtx = Object.values(ctx).some(v => v && v !== '' && v !== false);
  const samples = template.sample_images || [];

  const runTest = async () => {
    if (testImageMode==='url' && !testImageUrl) return alert('Enter an image URL');
    if (testImageMode==='upload' && !testImageB64) return alert('Upload an image');
    setTesting(true); setTestResult(null);
    try {
      let schema;
      try { schema = JSON.parse(JSON.stringify(template.result_schema)); } catch { schema = {}; }
      const r = await api.prompts.test({
        prompt: template.prompt_text,
        ...(testImageMode==='url'?{imageUrl:testImageUrl}:{imageBase64:testImageB64}),
      });
      setTestResult({ ok:true, data:r.data });
    } catch(e) { setTestResult({ ok:false, err:e.message }); }
    setTesting(false);
  };

  return (
    <Modal open={!!template} onClose={onClose} title={template.name} width={920}>
      {/* Header badges */}
      <div style={{ display:'flex', gap:8, marginBottom:20, flexWrap:'wrap', alignItems:'center' }}>
        <Badge c={cat.color} label={cat.icon + ' ' + template.category} />
        {(template.tags||[]).map(t=><Badge key={t} c={C.textMuted} label={t} />)}
        {template.is_default && <Badge c={C.success} label="global default" />}
        {hasCtx && <Badge c={C.purple} label="AI built" />}
        <div style={{ flex:1 }} />
        <Btn size="sm" variant="secondary" onClick={onEdit}>✏ Edit</Btn>
        <Btn size="sm" variant="purple" onClick={onClone}>⊕ Clone</Btn>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
        {/* Left: prompt + schema + context */}
        <div>
          <div style={{ fontSize:11, color:C.textSec, textTransform:'uppercase', letterSpacing:'0.07em', fontFamily:"'DM Mono',monospace", marginBottom:6 }}>Prompt</div>
          <div style={{ background:C.bg, borderRadius:8, padding:14, border:`1px solid ${C.border}`,
            fontFamily:"'DM Mono',monospace", fontSize:12, color:C.text, lineHeight:1.7,
            whiteSpace:'pre-wrap', maxHeight:220, overflowY:'auto', marginBottom:12 }}>
            {template.prompt_text}
          </div>

          <div style={{ fontSize:11, color:C.textSec, textTransform:'uppercase', letterSpacing:'0.07em', fontFamily:"'DM Mono',monospace", marginBottom:6 }}>Result Schema</div>
          <JsonEditor value={JSON.stringify(template.result_schema,null,2)} onChange={()=>{}} rows={5} readOnly />

          {/* Builder context */}
          {hasCtx && (
            <>
              <div style={{ fontSize:11, color:C.textSec, textTransform:'uppercase', letterSpacing:'0.07em', fontFamily:"'DM Mono',monospace", marginBottom:6 }}>Builder Context</div>
              <div style={{ background:C.purpleDim, borderRadius:8, padding:12, border:`1px solid ${C.purple}33`, fontSize:12, lineHeight:1.8 }}>
                {ctx.industry     && <div><span style={{ color:C.textSec }}>Industry:</span> <span style={{ color:C.text }}>{ctx.industry}</span></div>}
                {ctx.purpose      && <div><span style={{ color:C.textSec }}>Purpose:</span> <span style={{ color:C.text }}>{ctx.purpose}</span></div>}
                {ctx.photoSubject && <div><span style={{ color:C.textSec }}>Photo subject:</span> <span style={{ color:C.text }}>{ctx.photoSubject}</span></div>}
                {ctx.validateItems && <div><span style={{ color:C.textSec }}>Validate:</span> <span style={{ color:C.text }}>{ctx.validateItems}</span></div>}
                {ctx.extractItems  && <div><span style={{ color:C.textSec }}>Extract:</span> <span style={{ color:C.text }}>{ctx.extractItems}</span></div>}
                {ctx.involvesPeople && <div style={{ color:C.warning }}>⚠ Involves people</div>}
                {ctx.notes         && <div><span style={{ color:C.textSec }}>Notes:</span> <span style={{ color:C.text }}>{ctx.notes}</span></div>}
              </div>
            </>
          )}

          {/* Reference images from builder */}
          {samples.length > 0 && (
            <>
              <div style={{ fontSize:11, color:C.textSec, textTransform:'uppercase', letterSpacing:'0.07em', fontFamily:"'DM Mono',monospace", margin:'12px 0 6px' }}>
                Reference Images ({samples.length})
              </div>
              <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                {samples.map((img,i) => img.url ? (
                  <div key={i} style={{ position:'relative' }}>
                    <img src={img.url} alt={img.label||''} style={{ height:52, width:72, objectFit:'cover', borderRadius:6, border:`1px solid ${C.border}` }} onError={e=>e.target.style.display='none'} />
                    {img.label && <div style={{ fontSize:8, color:C.textMuted, textAlign:'center', marginTop:2, maxWidth:72, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{img.label}</div>}
                  </div>
                ) : null)}
              </div>
            </>
          )}
        </div>

        {/* Right: live test */}
        <div>
          <div style={{ fontSize:13, fontWeight:700, color:C.text, marginBottom:12 }}>🧪 Live Test (not saved)</div>
          <div style={{ display:'flex', gap:6, marginBottom:10 }}>
            {[['url','🔗 URL'],['upload','📁 Upload']].map(([m,l])=>(
              <button key={m} onClick={()=>{ setTestImageMode(m); setTestImageUrl(''); setTestImageB64(''); }} style={{
                flex:1, padding:'6px', borderRadius:6, border:`1px solid ${testImageMode===m?C.accent+'44':C.border}`,
                background:testImageMode===m?C.accentDim:'transparent', color:testImageMode===m?C.accent:C.textSec,
                cursor:'pointer', fontSize:12, fontFamily:"'DM Sans',sans-serif" }}>{l}</button>
            ))}
          </div>

          {testImageMode==='url' && (
            <>
              <Input value={testImageUrl} onChange={setTestImageUrl} placeholder="https://example.com/photo.jpg" style={{ marginBottom:6 }} />
              {testImageUrl && <img src={testImageUrl} alt="" style={{ width:'100%', maxHeight:130, objectFit:'cover', borderRadius:8, marginBottom:8, border:`1px solid ${C.border}` }} onError={e=>e.target.style.display='none'} />}
            </>
          )}
          {testImageMode==='upload' && (
            <div style={{ marginBottom:8 }}>
              <div onClick={()=>fileRef.current?.click()} style={{
                border:`2px dashed ${testImageB64?C.success+'66':C.border}`, borderRadius:8, padding:16,
                textAlign:'center', cursor:'pointer', background:testImageB64?C.successDim:'transparent', marginBottom:6 }}>
                {testImageB64
                  ? <img src={`data:image/jpeg;base64,${testImageB64}`} alt="" style={{ maxHeight:110, borderRadius:6 }} />
                  : <><div style={{ fontSize:24, marginBottom:6 }}>📷</div><div style={{ fontSize:12, color:C.textSec }}>Click to upload</div></>}
              </div>
              <input ref={fileRef} type="file" accept="image/*" style={{ display:'none' }}
                onChange={e=>{ const f=e.target.files[0]; if(f){ const r=new FileReader(); r.onload=ev=>setTestImageB64(ev.target.result.split(',')[1]); r.readAsDataURL(f); }}} />
            </div>
          )}

          <Btn onClick={runTest} disabled={testing||(!testImageUrl&&!testImageB64)} style={{ width:'100%', marginBottom:12 }}>
            {testing?'⏳ Running…':'▶ Run Test'}
          </Btn>
          <div style={{ fontSize:10, color:C.textMuted, textAlign:'center', marginBottom:12 }}>Results are NOT saved to DB</div>

          {testing && <Spinner />}
          {!testing && testResult && (
            <>
              <div style={{ fontSize:11, color:C.textSec, textTransform:'uppercase', letterSpacing:'0.07em', fontFamily:"'DM Mono',monospace", marginBottom:6 }}>
                {testResult.ok ? `✓ Result via ${testResult.data?.providerUsed?.name||'AI'}` : '✗ Error'}
              </div>
              {testResult.ok && testResult.data?.tokensInput!=null && (
                <div style={{ fontSize:10, color:C.textMuted, marginBottom:6, fontFamily:"'DM Mono',monospace" }}>
                  {testResult.data.tokensInput}↑ {testResult.data.tokensOutput}↓ tokens + {testResult.data.processingMs}ms
                </div>
              )}
              <div style={{ background:C.bg, borderRadius:8, padding:12, border:`1px solid ${testResult.ok?C.success+'44':C.danger+'44'}`,
                fontFamily:"'DM Mono',monospace", fontSize:11, whiteSpace:'pre-wrap', maxHeight:280, overflowY:'auto', lineHeight:1.8 }}>
                {testResult.ok
                  ? Object.entries(testResult.data?.result||{}).map(([k,v]) => (
                    <div key={k} style={{ display:'flex', justifyContent:'space-between', padding:'2px 0', borderBottom:`1px solid ${C.border}` }}>
                      <span style={{ color:C.textSec }}>{k}</span>
                      <span style={{ color:typeof v==='boolean'?(v?C.success:C.danger):typeof v==='number'?C.accent:C.text, fontWeight:600 }}>
                        {typeof v==='boolean'?(v?'✓ true':'✗ false'):String(v)}
                      </span>
                    </div>
                  ))
                  : <span style={{ color:C.danger }}>{testResult.err}</span>}
              </div>
            </>
          )}
          {!testing && !testResult && (
            <div style={{ background:C.bg, borderRadius:8, padding:20, border:`1px solid ${C.border}`, textAlign:'center', color:C.textMuted, fontSize:12 }}>
              Add an image and click Run Test to try this template
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// SOLUTIONS — SolutionManager, SolutionTester, SolutionTemplates
// These three components are fully self-contained and isolated from all
// existing components. They only use shared helpers (C, Card, Btn, etc.)
// already defined above.
// ─────────────────────────────────────────────────────────────────────────────

// ── SolutionRuns — list/search/filter solution runs with detail modal ──────────
function SolutionRuns({ user }) {
  const [runs,       setRuns]       = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [page,       setPage]       = useState(1);
  const [total,      setTotal]      = useState(0);
  const [viewMode,   setViewMode]   = useState('table');  // 'table' | 'cards'
  const [detailRun,  setDetailRun]  = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [expandRow,  setExpandRow]  = useState(null);    // row id with expanded params
  // Filters
  const [statusFilter,   setStatusFilter]   = useState('all');
  const [tenantFilter,   setTenantFilter]   = useState('');
  const [workflowFilter, setWorkflowFilter] = useState('');
  const [solutionFilter, setSolutionFilter] = useState('');
  const [jobFilter,      setJobFilter]      = useState('');
  const [dateFrom,       setDateFrom]       = useState('');
  const [dateTo,         setDateTo]         = useState('');
  const [solutions,      setSolutions]      = useState([]);
  const [workflows,      setWorkflows]      = useState([]);
  const [tenants,        setTenants]        = useState([]);
  // Dynamic result columns from LLM output
  const [resultKeys, setResultKeys] = useState([]);

  const isSuperAdmin = user.role === 'superadmin';
  const isAccountMgr = user.role === 'account_manager';

  useEffect(() => {
    api.solutions.list({ limit:200 }).then(r => setSolutions(r.data||[])).catch(()=>{});
    api.workflows.list({ limit:200 }).then(r => setWorkflows(r.data||[])).catch(()=>{});
    if (isSuperAdmin || isAccountMgr) api.tenants.list().then(r => setTenants(r.data||[])).catch(()=>{});
  }, []);

  useEffect(() => { load(); }, [page, statusFilter, tenantFilter, workflowFilter,
    solutionFilter, jobFilter, dateFrom, dateTo]);

  const load = () => {
    setLoading(true);
    const p = { page, limit: 20 };
    if (statusFilter !== 'all') p.status     = statusFilter;
    if (tenantFilter)            p.tenantId   = tenantFilter;
    if (workflowFilter)          p.workflowId = workflowFilter;
    if (solutionFilter)          p.solutionId = solutionFilter;
    if (jobFilter)               p.jobId      = jobFilter;
    if (dateFrom)                p.dateFrom   = dateFrom;
    if (dateTo)                  p.dateTo     = dateTo;
    api.solutions.runs(p)
      .then(r => {
        const rows = r.data || [];
        setRuns(rows);
        setTotal(r.meta?.total || 0);
        // Derive dynamic result columns from parsed_result across all rows
        const keys = new Set();
        rows.forEach(run => {
          const pr = run.parsed_result
            ? (typeof run.parsed_result === 'string'
                ? JSON.parse(run.parsed_result) : run.parsed_result)
            : {};
          if (!pr.error) Object.keys(pr).forEach(k => keys.add(k));
        });
        setResultKeys([...keys].filter(k => k !== 'confidence'));
      })
      .catch(()=>{})
      .finally(() => setLoading(false));
  };

  const clearAll = () => {
    setStatusFilter('all'); setTenantFilter(''); setWorkflowFilter('');
    setSolutionFilter(''); setJobFilter(''); setDateFrom(''); setDateTo('');
    setPage(1);
  };

  const openDetail = async run => {
    setDetailRun(run); setDetailData(null);
    try {
      const r = await api.solutions.getRun(run.id);
      setDetailData(r.data);
    } catch(e) {}
  };

  const exportCsv = () => {
    const p = {};
    if (statusFilter !== 'all') p.status     = statusFilter;
    if (tenantFilter)            p.tenantId   = tenantFilter;
    if (workflowFilter)          p.workflowId = workflowFilter;
    if (solutionFilter)          p.solutionId = solutionFilter;
    if (jobFilter)               p.jobId      = jobFilter;
    if (dateFrom)                p.dateFrom   = dateFrom;
    if (dateTo)                  p.dateTo     = dateTo;
    fetch(api.solutions.runsExportUrl(p), {
      headers: { Authorization: `Bearer ${api.getToken()}` },
    }).then(r => r.blob()).then(blob => {
      const a  = document.createElement('a');
      a.href   = URL.createObjectURL(blob);
      a.download = `solution_runs_${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
    }).catch(console.error);
  };

  const sc = v => v==='pass'?C.success:v==='fail'?C.danger:v==='error'?C.danger:C.warning;
  const STATUSES = ['all','pass','fail','review','error'];

  const getParsed = run => {
    if (!run.parsed_result) return {};
    try {
      const p = typeof run.parsed_result === 'string'
        ? JSON.parse(run.parsed_result) : run.parsed_result;
      return p.error ? {} : p;
    } catch { return {}; }
  };

  const renderCellValue = v => {
    if (v === true)  return <span style={{ color:C.success, fontWeight:700 }}>Yes</span>;
    if (v === false) return <span style={{ color:C.danger, fontWeight:700 }}>No</span>;
    if (v === null || v === undefined) return <span style={{ color:C.textMuted }}>—</span>;
    if (typeof v === 'object') return (
      <span style={{ fontFamily:"'DM Mono',monospace", fontSize:9,
        color:C.textMuted }}>{JSON.stringify(v).slice(0,60)}</span>
    );
    const s = String(v);
    const col = s === 'pass' ? C.success : s === 'fail' ? C.danger : null;
    return <span style={{ color: col || C.text }}>{s}</span>;
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
        marginBottom:16, flexWrap:'wrap', gap:10 }}>
        <div>
          <div style={{ fontSize:20, fontWeight:700, fontFamily:"'DM Serif Display',serif",
            color:C.text }}>Solution Runs</div>
          <div style={{ color:C.textSec, fontSize:13 }}>
            {total.toLocaleString()} result{total !== 1 ? 's' : ''}
          </div>
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          {/* View toggle */}
          <div style={{ display:'flex', background:C.bg, borderRadius:8,
            border:`1px solid ${C.border}`, overflow:'hidden' }}>
            {[['table','Table'],['cards','Cards']].map(([m,l]) => (
              <button key={m} onClick={() => setViewMode(m)} style={{
                padding:'5px 14px', fontSize:12, cursor:'pointer', border:'none',
                background: viewMode===m ? C.accent : 'transparent',
                color:      viewMode===m ? '#000'   : C.textSec,
                fontFamily:"'DM Sans',sans-serif", fontWeight: viewMode===m ? 600 : 400,
              }}>{l}</button>
            ))}
          </div>
          <Btn variant="ghost" onClick={exportCsv}>Export CSV</Btn>
        </div>
      </div>

      {/* Filter bar */}
      <Card style={{ padding:'10px 14px', marginBottom:14 }}>
        <div style={{ display:'flex', gap:6, flexWrap:'wrap', alignItems:'center' }}>
          {STATUSES.map(s => (
            <button key={s} onClick={() => { setStatusFilter(s); setPage(1); }} style={{
              padding:'3px 10px', borderRadius:16, fontSize:11, cursor:'pointer', fontWeight:600,
              background: statusFilter===s ? C.accent : C.bg,
              color:      statusFilter===s ? '#000' : C.textSec,
              border:     `1px solid ${statusFilter===s ? C.accent : C.border}`,
            }}>{s}</button>
          ))}
          {(isSuperAdmin || isAccountMgr) && tenants.length > 0 && (
            <select value={tenantFilter} onChange={e=>{setTenantFilter(e.target.value);setPage(1);}}
              style={{ background:C.bg, border:`1px solid ${C.border}`, borderRadius:8,
                padding:'4px 8px', color:tenantFilter?C.text:C.textMuted, fontSize:12,
                fontFamily:"'DM Sans',sans-serif", outline:'none' }}>
              <option value="">All tenants</option>
              {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          )}
          <select value={workflowFilter} onChange={e=>{setWorkflowFilter(e.target.value);setPage(1);}}
            style={{ background:C.bg, border:`1px solid ${C.border}`, borderRadius:8,
              padding:'4px 8px', color:workflowFilter?C.text:C.textMuted, fontSize:12,
              fontFamily:"'DM Sans',sans-serif", outline:'none' }}>
            <option value="">All workflows</option>
            {workflows.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
          <select value={solutionFilter} onChange={e=>{setSolutionFilter(e.target.value);setPage(1);}}
            style={{ background:C.bg, border:`1px solid ${C.border}`, borderRadius:8,
              padding:'4px 8px', color:solutionFilter?C.text:C.textMuted, fontSize:12,
              fontFamily:"'DM Sans',sans-serif", outline:'none' }}>
            <option value="">All solutions</option>
            {solutions.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <input value={jobFilter} onChange={e=>{setJobFilter(e.target.value);setPage(1);}}
            placeholder="Job ID" style={{ background:C.bg, border:`1px solid ${C.border}`,
              borderRadius:8, padding:'4px 8px', color:C.text, fontSize:12, minWidth:100,
              fontFamily:"'DM Mono',monospace", outline:'none' }} />
          <input type="date" value={dateFrom} onChange={e=>{setDateFrom(e.target.value);setPage(1);}}
            style={{ background:C.bg, border:`1px solid ${C.border}`, borderRadius:8,
              padding:'4px 8px', color:C.text, fontSize:11, outline:'none',
              fontFamily:"'DM Mono',monospace" }} />
          <span style={{ color:C.textMuted, fontSize:11 }}>to</span>
          <input type="date" value={dateTo} onChange={e=>{setDateTo(e.target.value);setPage(1);}}
            style={{ background:C.bg, border:`1px solid ${C.border}`, borderRadius:8,
              padding:'4px 8px', color:C.text, fontSize:11, outline:'none',
              fontFamily:"'DM Mono',monospace" }} />
          {(statusFilter!=='all'||tenantFilter||workflowFilter||solutionFilter||jobFilter||dateFrom||dateTo) && (
            <Btn size="sm" variant="ghost" onClick={clearAll}>x Clear</Btn>
          )}
        </div>
      </Card>

      {loading ? <Spinner /> : runs.length === 0 ? (
        <Card style={{ textAlign:'center', padding:40, color:C.textMuted }}>
          No solution runs found.
        </Card>
      ) : viewMode === 'table' ? (
        /* ── Table view: fixed cols + dynamic LLM result columns ── */
        <div>
          {resultKeys.length > 0 && (
            <div style={{ fontSize:11, color:C.textSec, marginBottom:8 }}>
              Showing <strong style={{color:C.accent}}>{resultKeys.length}</strong> result field{resultKeys.length!==1?'s':''} as columns.
              Click any row to see param details.
            </div>
          )}
          <div style={{ overflowX:'auto', borderRadius:10, border:`1px solid ${C.border}` }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
              <thead>
                <tr style={{ background:C.surface }}>
                  {['Date','Solution','Job ID','Status','Conf',
                    ...resultKeys,
                    'Time',''].map(h => (
                    <th key={h} style={{
                      padding:'7px 10px', textAlign:'left', fontSize:10, color:C.textSec,
                      fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em',
                      whiteSpace:'nowrap', fontFamily:"'DM Mono',monospace",
                      borderBottom:`1px solid ${C.border}`,
                      background: resultKeys.includes(h) ? C.accentDim : 'transparent',
                    }}>
                      {resultKeys.includes(h)
                        ? <span style={{color:C.accent}}>{h}</span>
                        : h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {runs.map(r => {
                  const parsed = getParsed(r);
                  const isExpanded = expandRow === r.id;
                  return (
                    <Fragment key={r.id}>
                      <tr style={{ borderBottom:`1px solid ${C.border}`, cursor:'pointer' }}
                        onMouseEnter={e=>e.currentTarget.style.background=C.surfaceHover}
                        onMouseLeave={e=>e.currentTarget.style.background='transparent'}
                        onClick={() => openDetail(r)}>
                        <td style={{ padding:'7px 10px', fontFamily:"'DM Mono',monospace",
                          fontSize:10, color:C.textSec, whiteSpace:'nowrap' }}>
                          {new Date(r.created_at).toLocaleDateString()}
                          <div style={{ fontSize:9, color:C.textMuted }}>
                            {new Date(r.created_at).toLocaleTimeString()}
                          </div>
                        </td>
                        <td style={{ padding:'7px 10px', fontWeight:600, color:C.text,
                          maxWidth:150, overflow:'hidden', textOverflow:'ellipsis',
                          whiteSpace:'nowrap' }}>
                          {r.solution_name}
                          <div style={{ fontSize:10, color:C.textSec, fontWeight:400 }}>
                            {r.workflow_name}
                          </div>
                        </td>
                        <td style={{ padding:'7px 10px', fontFamily:"'DM Mono',monospace",
                          fontSize:11, color:C.accent }}>
                          {r.job_id || '—'}
                        </td>
                        <td style={{ padding:'7px 10px' }}>
                          <Badge c={sc(r.validation_status)} label={r.validation_status} />
                        </td>
                        <td style={{ padding:'7px 10px', fontFamily:"'DM Mono',monospace",
                          color:C.purple, fontSize:11 }}>
                          {r.confidence != null ? `${(r.confidence*100).toFixed(0)}%` : '—'}
                        </td>
                        {resultKeys.map(k => (
                          <td key={k} style={{ padding:'7px 10px', fontSize:11 }}>
                            {renderCellValue(parsed[k])}
                          </td>
                        ))}
                        <td style={{ padding:'7px 10px', color:C.textMuted, fontSize:10,
                          fontFamily:"'DM Mono',monospace", whiteSpace:'nowrap' }}>
                          {r.processing_ms ? `${r.processing_ms}ms` : '—'}
                        </td>
                        <td style={{ padding:'7px 10px' }}>
                          <button
                            onClick={e => { e.stopPropagation(); setExpandRow(isExpanded ? null : r.id); }}
                            style={{ background:'transparent', border:`1px solid ${C.border}`,
                              borderRadius:5, color:C.textSec, fontSize:10, cursor:'pointer',
                              padding:'2px 7px', fontFamily:"'DM Sans',sans-serif" }}>
                            {isExpanded ? 'Hide' : 'Params'}
                          </button>
                        </td>
                      </tr>
                      {/* Expandable param sub-row */}
                      {isExpanded && (
                        <tr style={{ background:C.bg }}>
                          <td colSpan={5 + resultKeys.length + 2}
                            style={{ padding:'8px 12px', borderBottom:`1px solid ${C.border}` }}>
                            <ParamResultsInline runId={r.id} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        /* ── Cards view ── */
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(320px,1fr))',
          gap:10 }}>
          {runs.map(r => {
            const parsed = getParsed(r);
            const keys   = Object.keys(parsed).filter(k => k !== 'confidence');
            return (
              <Card key={r.id} style={{ cursor:'pointer', padding:'14px 16px' }}
                onClick={() => openDetail(r)}>
                <div style={{ display:'flex', justifyContent:'space-between',
                  alignItems:'flex-start', marginBottom:8 }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:13, fontWeight:700, color:C.text,
                      marginBottom:2, overflow:'hidden', textOverflow:'ellipsis',
                      whiteSpace:'nowrap' }}>{r.solution_name}</div>
                    <div style={{ fontSize:11, color:C.textSec }}>{r.workflow_name}</div>
                    {r.job_id && (
                      <div style={{ fontSize:10, color:C.accent,
                        fontFamily:"'DM Mono',monospace", marginTop:2 }}>
                        {r.job_id}
                      </div>
                    )}
                  </div>
                  <Badge c={sc(r.validation_status)} label={r.validation_status} />
                </div>
                {keys.length > 0 && (
                  <div style={{ background:C.bg, borderRadius:6, padding:'8px 10px',
                    marginBottom:8 }}>
                    {keys.slice(0, 5).map(k => (
                      <div key={k} style={{ display:'flex', gap:8, marginBottom:2 }}>
                        <span style={{ fontSize:10, color:C.textMuted, minWidth:100,
                          flexShrink:0 }}>{k}:</span>
                        <span style={{ fontSize:10, fontFamily:"'DM Mono',monospace" }}>
                          {renderCellValue(parsed[k])}
                        </span>
                      </div>
                    ))}
                    {keys.length > 5 && (
                      <div style={{ fontSize:9, color:C.textMuted }}>
                        +{keys.length-5} more fields
                      </div>
                    )}
                  </div>
                )}
                <div style={{ display:'flex', gap:10, fontSize:10, color:C.textMuted }}>
                  {r.confidence != null && (
                    <span style={{ color:C.purple }}>
                      {(r.confidence*100).toFixed(0)}% conf
                    </span>
                  )}
                  {r.processing_ms && <span>{r.processing_ms}ms</span>}
                  <span>{new Date(r.created_at).toLocaleString()}</span>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {total > 20 && (
        <div style={{ display:'flex', justifyContent:'center', gap:8, marginTop:16 }}>
          <Btn variant="ghost" size="sm" disabled={page<=1} onClick={()=>setPage(p=>p-1)}>
            Prev
          </Btn>
          <span style={{ color:C.textSec, fontSize:13, padding:'5px 12px' }}>
            Page {page} of {Math.ceil(total/20)}
          </span>
          <Btn variant="ghost" size="sm" disabled={page*20>=total} onClick={()=>setPage(p=>p+1)}>
            Next
          </Btn>
        </div>
      )}

      <SolutionRunDetailModal
        run={detailRun} data={detailData}
        onClose={() => { setDetailRun(null); setDetailData(null); }} />
    </div>
  );
}

// ── ParamResultsInline — lightweight param sub-table for expanded row ──────────
function ParamResultsInline({ runId }) {
  const [params, setParams] = useState(null);
  useEffect(() => {
    api.solutions.getRun(runId)
      .then(r => setParams(r.data?.param_results || []))
      .catch(() => setParams([]));
  }, [runId]);

  if (params === null) return <Spinner />;
  if (!params.length)  return <div style={{ color:C.textMuted, fontSize:11 }}>No param data.</div>;

  const sc = v => v==='pass'?C.success:v==='fail'?C.danger:C.warning;
  return (
    <div>
      <div style={{ fontSize:10, color:C.textSec, textTransform:'uppercase',
        letterSpacing:'0.07em', marginBottom:6 }}>
        Param Slot Results
      </div>
      <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
        {params.map(pr => {
          const parsed = pr.parsed_result
            ? (typeof pr.parsed_result==='string' ? JSON.parse(pr.parsed_result) : pr.parsed_result)
            : null;
          const psc = sc(pr.validation_status);
          return (
            <div key={pr.position} style={{ background:C.surface, borderRadius:7,
              padding:'8px 10px', border:`1px solid ${psc}44`, minWidth:160 }}>
              <div style={{ display:'flex', justifyContent:'space-between',
                alignItems:'center', marginBottom:parsed?4:0 }}>
                <span style={{ fontSize:10, fontWeight:600, color:C.text }}>
                  {pr.position}. {pr.param_label}
                </span>
                <Badge c={psc} label={pr.validation_status} />
              </div>
              {pr.error_message && (
                <div style={{ fontSize:9, color:C.danger, marginTop:2 }}>
                  {pr.error_message}
                </div>
              )}
              {parsed && !parsed.error && (
                <div style={{ marginTop:4 }}>
                  {Object.entries(parsed).slice(0, 4).map(([k,v]) => (
                    <div key={k} style={{ display:'flex', gap:6, marginBottom:1 }}>
                      <span style={{ fontSize:9, color:C.textMuted, minWidth:70 }}>{k}:</span>
                      <span style={{ fontSize:9, fontFamily:"'DM Mono',monospace",
                        color: v===true?C.success:v===false?C.danger:C.text }}>
                        {typeof v==='object'?JSON.stringify(v).slice(0,30):String(v)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ── SolutionRunDetailModal — full run detail with prompt/schema/params ─────────
function SolutionRunDetailModal({ run, data, onClose }) {
  const [tab, setTab] = useState('result');   // result | params | prompt
  if (!run) return null;

  const sc = run.validation_status==='pass'?C.success:run.validation_status==='fail'?C.danger:C.warning;
  const d  = data || run;   // use detailed data if loaded, else fall back to list row

  const finalResult = d.parsed_result
    ? (typeof d.parsed_result==='string' ? JSON.parse(d.parsed_result) : d.parsed_result)
    : null;
  const resultSchema = d.result_schema
    ? (typeof d.result_schema==='string' ? JSON.parse(d.result_schema) : d.result_schema)
    : null;
  const paramResults = d.param_results || [];

  const TABS = [
    { id:'result',  label:'Result' },
    { id:'params',  label:`Params (${paramResults.length})` },
    { id:'prompt',  label:'Prompt & Schema' },
  ];

  return (
    <Modal open={!!run} onClose={onClose} title="Solution Run Detail" width={720}>
      {/* Header */}
      <div style={{ background:C.bg, borderRadius:10, padding:'12px 16px',
        marginBottom:16, border:`2px solid ${sc}44` }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start',
          marginBottom:8, flexWrap:'wrap', gap:8 }}>
          <div>
            <div style={{ fontSize:14, fontWeight:700, color:C.text, marginBottom:2 }}>
              {d.solution_name}
            </div>
            <div style={{ fontSize:11, color:C.textSec }}>
              {d.workflow_name}
              {d.tenant_name && <span style={{ color:C.textMuted }}> — {d.tenant_name}</span>}
            </div>
          </div>
          <Badge c={sc} label={run.validation_status} />
        </div>
        <div style={{ display:'flex', gap:16, fontSize:11, color:C.textMuted, flexWrap:'wrap' }}>
          {run.job_id && (
            <span>Job: <span style={{ color:C.accent, fontFamily:"'DM Mono',monospace" }}>
              {run.job_id}
            </span></span>
          )}
          {d.confidence!=null && (
            <span>Confidence: <strong style={{color:C.purple}}>
              {(d.confidence*100).toFixed(0)}%
            </strong></span>
          )}
          {d.processing_ms && <span>Time: {d.processing_ms}ms</span>}
          {(d.tokens_input||d.tokens_output) ? (
            <span>Tokens: {((d.tokens_input||0)+(d.tokens_output||0)).toLocaleString()}</span>
          ) : null}
          {d.ai_model && (
            <span style={{ fontFamily:"'DM Mono',monospace", fontSize:10 }}>{d.ai_model}</span>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', gap:4, marginBottom:16, borderBottom:`1px solid ${C.border}`,
        paddingBottom:8 }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding:'4px 14px', borderRadius:16, fontSize:12, cursor:'pointer', fontWeight:600,
            background: tab===t.id ? C.accent : 'transparent',
            color:      tab===t.id ? '#000' : C.textSec,
            border:     `1px solid ${tab===t.id ? C.accent : C.border}`,
          }}>{t.label}</button>
        ))}
      </div>

      {/* Result tab */}
      {tab==='result' && (
        <div>
          {finalResult && !finalResult.error ? (
            <div style={{ background:C.bg, borderRadius:8, padding:'12px 14px',
              marginBottom:12, border:`1px solid ${C.border}` }}>
              <div style={{ fontSize:10, color:C.textSec, textTransform:'uppercase',
                letterSpacing:'0.07em', marginBottom:8 }}>Final Synthesised Result</div>
              {Object.entries(finalResult).map(([k,v]) => (
                <div key={k} style={{ display:'flex', gap:10, marginBottom:5, flexWrap:'wrap' }}>
                  <span style={{ fontSize:11, color:C.textMuted, minWidth:140,
                    flexShrink:0 }}>{k}:</span>
                  <span style={{ fontSize:11, fontFamily:"'DM Mono',monospace",
                    color:v===true?C.success:v===false?C.danger:C.text }}>
                    {typeof v==='object' ? JSON.stringify(v) : String(v)}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ color:C.danger, fontSize:12, padding:'10px 14px', background:C.dangerDim,
              borderRadius:8, marginBottom:12 }}>
              {finalResult?.error || 'No result data'}
            </div>
          )}
          {resultSchema && (
            <div style={{ marginBottom:8 }}>
              <div style={{ fontSize:10, color:C.textSec, textTransform:'uppercase',
                letterSpacing:'0.07em', marginBottom:6 }}>Expected Result Schema</div>
              <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                {Object.entries(resultSchema).map(([k,type]) => (
                  <span key={k} style={{ fontSize:10, background:C.bg, padding:'2px 8px',
                    borderRadius:10, border:`1px solid ${C.border}`, color:C.textMuted,
                    fontFamily:"'DM Mono',monospace" }}>
                    {k}: <span style={{ color:C.accent }}>{String(type)}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Params tab */}
      {tab==='params' && (
        <div>
          {paramResults.length === 0 ? (
            <div style={{ color:C.textMuted, textAlign:'center', padding:24 }}>
              {data ? 'No param results recorded' : '⏳ Loading param details…'}
            </div>
          ) : paramResults.map((pr,i) => {
            const psc  = pr.validation_status==='pass'?C.success:pr.validation_status==='fail'?C.danger:C.warning;
            const prParsed = pr.parsed_result
              ? (typeof pr.parsed_result==='string'?JSON.parse(pr.parsed_result):pr.parsed_result)
              : null;
            return (
              <div key={i} style={{ marginBottom:10, background:C.bg, borderRadius:8,
                padding:'12px 14px', border:`1px solid ${psc}44` }}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6 }}>
                  <span style={{ fontSize:12, fontWeight:600, color:C.text }}>
                    {pr.position}. {pr.param_label}
                  </span>
                  <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                    {pr.confidence!=null && (
                      <span style={{ fontSize:10, color:C.purple,
                        fontFamily:"'DM Mono',monospace" }}>
                        {(pr.confidence*100).toFixed(0)}%
                      </span>
                    )}
                    <Badge c={psc} label={pr.validation_status} />
                  </div>
                </div>
                {pr.error_message && (
                  <div style={{ fontSize:11, color:C.danger, marginBottom:4 }}>
                    {pr.error_message}
                  </div>
                )}
                {pr.ai_model && (
                  <div style={{ fontSize:10, color:C.textMuted, fontFamily:"'DM Mono',monospace",
                    marginBottom:4 }}>
                    Model: {pr.ai_model}
                    {pr.processing_ms ? ` | ${pr.processing_ms}ms` : ''}
                  </div>
                )}
                {prParsed && !prParsed.error && (
                  <div style={{ marginTop:6 }}>
                    {Object.entries(prParsed).map(([k,v]) => (
                      <div key={k} style={{ display:'flex', gap:8, marginBottom:3, flexWrap:'wrap' }}>
                        <span style={{ fontSize:10, color:C.textMuted, minWidth:120,
                          flexShrink:0 }}>{k}:</span>
                        <span style={{ fontSize:10, fontFamily:"'DM Mono',monospace",
                          color:v===true?C.success:v===false?C.danger:C.text }}>
                          {typeof v==='object'?JSON.stringify(v):String(v)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {pr.image_url && (
                  <img src={pr.image_url} alt=""
                    style={{ marginTop:6, height:48, borderRadius:6,
                      border:`1px solid ${C.border}`, objectFit:'cover' }}
                    onError={e=>e.target.style.display='none'} />
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Prompt & Schema tab */}
      {tab==='prompt' && (
        <div>
          <div style={{ marginBottom:14 }}>
            <div style={{ fontSize:10, color:C.textSec, textTransform:'uppercase',
              letterSpacing:'0.07em', marginBottom:6 }}>Solution Synthesis Prompt Used</div>
            <pre style={{ background:C.bg, borderRadius:8, padding:'10px 12px',
              border:`1px solid ${C.border}`, fontSize:10, color:C.textMuted,
              whiteSpace:'pre-wrap', wordBreak:'break-word', maxHeight:220, overflow:'auto',
              fontFamily:"'DM Mono',monospace", margin:0 }}>
              {d.solution_prompt_used || '(prompt not recorded)'}
            </pre>
          </div>
          {resultSchema && (
            <div>
              <div style={{ fontSize:10, color:C.textSec, textTransform:'uppercase',
                letterSpacing:'0.07em', marginBottom:6 }}>Result Schema</div>
              <pre style={{ background:C.bg, borderRadius:8, padding:'10px 12px',
                border:`1px solid ${C.border}`, fontSize:10, color:C.accent,
                whiteSpace:'pre-wrap', fontFamily:"'DM Mono',monospace", margin:0 }}>
                {JSON.stringify(resultSchema, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

// ── SolutionReports — analytics/aggregate view of solution runs ──────────────
function SolutionReports({ user }) {
  const isSuperAdmin = user.role === 'superadmin';
  const isAccountMgr = user.role === 'account_manager';

  const [summary,   setSummary]   = useState([]);
  const [trend,     setTrend]     = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [groupBy,   setGroupBy]   = useState('solution');
  const [dateFrom,  setDateFrom]  = useState('');
  const [dateTo,    setDateTo]    = useState('');
  const [tenants,   setTenants]   = useState([]);
  const [workflows, setWorkflows] = useState([]);
  const [solutions, setSolutions] = useState([]);
  const [tenantF,   setTenantF]   = useState('');
  const [workflowF, setWorkflowF] = useState('');
  const [solutionF, setSolutionF] = useState('');

  useEffect(() => {
    api.workflows.list({ limit:200 }).then(r => setWorkflows(r.data||[])).catch(()=>{});
    api.solutions.list({ limit:200 }).then(r => setSolutions(r.data||[])).catch(()=>{});
    if (isSuperAdmin || isAccountMgr)
      api.tenants.list().then(r => setTenants(r.data||[])).catch(()=>{});
  }, []);

  useEffect(() => { load(); }, [groupBy, dateFrom, dateTo, tenantF, workflowF, solutionF]);

  const load = () => {
    setLoading(true);
    const p = { groupBy };
    if (tenantF)   p.tenantId   = tenantF;
    if (workflowF) p.workflowId = workflowF;
    if (solutionF) p.solutionId = solutionF;
    if (dateFrom)  p.dateFrom   = dateFrom;
    if (dateTo)    p.dateTo     = dateTo;
    api.solutions.runsReport(p)
      .then(r => {
        setSummary(r.data?.summary || []);
        setTrend(r.data?.trend    || []);
      })
      .catch(()=>{})
      .finally(() => setLoading(false));
  };

  const exportCsv = () => {
    const p = { groupBy };
    if (tenantF)   p.tenantId   = tenantF;
    if (workflowF) p.workflowId = workflowF;
    if (solutionF) p.solutionId = solutionF;
    if (dateFrom)  p.dateFrom   = dateFrom;
    if (dateTo)    p.dateTo     = dateTo;
    fetch(api.solutions.runsReportCsvUrl(p), {
      headers: { Authorization: `Bearer ${api.getToken()}` },
    }).then(r => r.blob()).then(blob => {
      const a  = document.createElement('a');
      a.href   = URL.createObjectURL(blob);
      a.download = `solution_report_${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
    }).catch(console.error);
  };

  // Compute totals
  const totals = summary.reduce((acc, r) => ({
    total:   acc.total   + (parseInt(r.total)  || 0),
    passed:  acc.passed  + (parseInt(r.passed) || 0),
    failed:  acc.failed  + (parseInt(r.failed) || 0),
    review:  acc.review  + (parseInt(r.review) || 0),
    errors:  acc.errors  + (parseInt(r.errors) || 0),
  }), { total:0, passed:0, failed:0, review:0, errors:0 });

  const pct = (n, d) => d > 0 ? `${((n/d)*100).toFixed(0)}%` : '—';

  return (
    <div>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
        marginBottom:20, flexWrap:'wrap', gap:10 }}>
        <div>
          <div style={{ fontSize:20, fontWeight:700, fontFamily:"'DM Serif Display',serif",
            color:C.text }}>Solution Reports</div>
          <div style={{ color:C.textSec, fontSize:13 }}>
            Aggregate performance across solution runs
          </div>
        </div>
        <Btn variant="ghost" onClick={exportCsv}>Export CSV</Btn>
      </div>

      {/* Filters */}
      <Card style={{ padding:'10px 14px', marginBottom:16 }}>
        <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
          <div style={{ display:'flex', gap:4 }}>
            {['solution','workflow','status'].map(g => (
              <button key={g} onClick={() => setGroupBy(g)} style={{
                padding:'3px 10px', borderRadius:16, fontSize:11, cursor:'pointer', fontWeight:600,
                background: groupBy===g ? C.accent : C.bg,
                color:      groupBy===g ? '#000'   : C.textSec,
                border:     `1px solid ${groupBy===g ? C.accent : C.border}`,
              }}>Group: {g}</button>
            ))}
          </div>
          {(isSuperAdmin || isAccountMgr) && tenants.length > 0 && (
            <select value={tenantF} onChange={e => setTenantF(e.target.value)}
              style={{ background:C.bg, border:`1px solid ${C.border}`, borderRadius:8,
                padding:'4px 8px', color:tenantF?C.text:C.textMuted, fontSize:12,
                fontFamily:"'DM Sans',sans-serif", outline:'none' }}>
              <option value="">All tenants</option>
              {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          )}
          <select value={workflowF} onChange={e => setWorkflowF(e.target.value)}
            style={{ background:C.bg, border:`1px solid ${C.border}`, borderRadius:8,
              padding:'4px 8px', color:workflowF?C.text:C.textMuted, fontSize:12,
              fontFamily:"'DM Sans',sans-serif", outline:'none' }}>
            <option value="">All workflows</option>
            {workflows.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
          <select value={solutionF} onChange={e => setSolutionF(e.target.value)}
            style={{ background:C.bg, border:`1px solid ${C.border}`, borderRadius:8,
              padding:'4px 8px', color:solutionF?C.text:C.textMuted, fontSize:12,
              fontFamily:"'DM Sans',sans-serif", outline:'none' }}>
            <option value="">All solutions</option>
            {solutions.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            style={{ background:C.bg, border:`1px solid ${C.border}`, borderRadius:8,
              padding:'4px 8px', color:C.text, fontSize:11, outline:'none',
              fontFamily:"'DM Mono',monospace" }} />
          <span style={{ color:C.textMuted, fontSize:11 }}>to</span>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
            style={{ background:C.bg, border:`1px solid ${C.border}`, borderRadius:8,
              padding:'4px 8px', color:C.text, fontSize:11, outline:'none',
              fontFamily:"'DM Mono',monospace" }} />
        </div>
      </Card>

      {loading ? <Spinner /> : (
        <>
          {/* KPI tiles */}
          {totals.total > 0 && (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:10,
              marginBottom:16 }}>
              {[
                { label:'Total Runs', val:totals.total, color:C.accent },
                { label:'Passed',     val:`${totals.passed} (${pct(totals.passed,totals.total)})`,  color:C.success },
                { label:'Failed',     val:`${totals.failed} (${pct(totals.failed,totals.total)})`,  color:C.danger  },
                { label:'Review',     val:`${totals.review} (${pct(totals.review,totals.total)})`,  color:C.warning },
                { label:'Errors',     val:`${totals.errors} (${pct(totals.errors,totals.total)})`,  color:C.textMuted },
              ].map(kpi => (
                <Card key={kpi.label} style={{ textAlign:'center', padding:'12px 8px' }}>
                  <div style={{ fontSize:22, fontWeight:800, color:kpi.color,
                    fontFamily:"'DM Mono',monospace", marginBottom:4 }}>
                    {kpi.val}
                  </div>
                  <div style={{ fontSize:10, color:C.textSec, textTransform:'uppercase',
                    letterSpacing:'0.06em' }}>
                    {kpi.label}
                  </div>
                </Card>
              ))}
            </div>
          )}

          {/* Summary table */}
          {summary.length === 0 ? (
            <Card style={{ textAlign:'center', padding:40, color:C.textMuted }}>
              No solution runs in the selected period.
            </Card>
          ) : (
            <Card style={{ padding:0, overflow:'hidden', marginBottom:16 }}>
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                  <thead>
                    <tr style={{ background:C.surface }}>
                      {['Name','Total','Passed','Failed','Review','Pass Rate',
                        'Avg Conf','Avg Time','Tokens'].map(h => (
                        <th key={h} style={{ padding:'8px 12px', textAlign:'left',
                          fontSize:10, color:C.textSec, fontWeight:700,
                          textTransform:'uppercase', letterSpacing:'0.06em',
                          borderBottom:`1px solid ${C.border}`,
                          fontFamily:"'DM Mono',monospace" }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {summary.map((row, i) => {
                      const total  = parseInt(row.total)  || 0;
                      const passed = parseInt(row.passed) || 0;
                      const failed = parseInt(row.failed) || 0;
                      const passRate = total > 0 ? (passed/total*100).toFixed(0) : 0;
                      const rateColor = passRate >= 90 ? C.success
                                      : passRate >= 70 ? C.warning : C.danger;
                      return (
                        <tr key={i} style={{ borderBottom:`1px solid ${C.border}` }}
                          onMouseEnter={e => e.currentTarget.style.background=C.surfaceHover}
                          onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                          <td style={{ padding:'8px 12px', fontWeight:600, color:C.text }}>
                            {row.group_name}
                          </td>
                          <td style={{ padding:'8px 12px', color:C.text,
                            fontFamily:"'DM Mono',monospace" }}>
                            {total}
                          </td>
                          <td style={{ padding:'8px 12px', color:C.success,
                            fontFamily:"'DM Mono',monospace" }}>
                            {row.passed}
                          </td>
                          <td style={{ padding:'8px 12px', color:C.danger,
                            fontFamily:"'DM Mono',monospace" }}>
                            {row.failed}
                          </td>
                          <td style={{ padding:'8px 12px', color:C.warning,
                            fontFamily:"'DM Mono',monospace" }}>
                            {row.review}
                          </td>
                          <td style={{ padding:'8px 12px' }}>
                            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                              <div style={{ flex:1, background:C.border, borderRadius:4,
                                height:6, minWidth:60 }}>
                                <div style={{ width:`${passRate}%`, height:6,
                                  background:rateColor, borderRadius:4 }} />
                              </div>
                              <span style={{ fontSize:11, color:rateColor, fontWeight:700,
                                fontFamily:"'DM Mono',monospace", minWidth:32 }}>
                                {passRate}%
                              </span>
                            </div>
                          </td>
                          <td style={{ padding:'8px 12px', color:C.purple,
                            fontFamily:"'DM Mono',monospace" }}>
                            {row.avg_confidence_pct != null ? `${row.avg_confidence_pct}%` : '—'}
                          </td>
                          <td style={{ padding:'8px 12px', color:C.textMuted,
                            fontFamily:"'DM Mono',monospace" }}>
                            {row.avg_processing_ms ? `${Math.round(row.avg_processing_ms)}ms` : '—'}
                          </td>
                          <td style={{ padding:'8px 12px', color:C.textMuted,
                            fontFamily:"'DM Mono',monospace" }}>
                            {((parseInt(row.total_tokens_in)||0)+(parseInt(row.total_tokens_out)||0)).toLocaleString()}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {/* Trend table */}
          {trend.length > 0 && (
            <Card style={{ padding:0, overflow:'hidden' }}>
              <div style={{ padding:'12px 16px', fontSize:13, fontWeight:700, color:C.text,
                borderBottom:`1px solid ${C.border}` }}>
                Daily Trend (last 60 days)
              </div>
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:11 }}>
                  <thead>
                    <tr style={{ background:C.surface }}>
                      {['Date','Total','Passed','Failed','Avg Conf'].map(h => (
                        <th key={h} style={{ padding:'6px 12px', textAlign:'left',
                          fontSize:10, color:C.textSec, fontWeight:700,
                          textTransform:'uppercase', letterSpacing:'0.06em',
                          borderBottom:`1px solid ${C.border}`,
                          fontFamily:"'DM Mono',monospace" }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {trend.map((row, i) => (
                      <tr key={i} style={{ borderBottom:`1px solid ${C.border}` }}
                        onMouseEnter={e => e.currentTarget.style.background=C.surfaceHover}
                        onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                        <td style={{ padding:'6px 12px', fontFamily:"'DM Mono',monospace",
                          color:C.textSec }}>
                          {new Date(row.day).toLocaleDateString()}
                        </td>
                        <td style={{ padding:'6px 12px', fontFamily:"'DM Mono',monospace",
                          color:C.text }}>{row.total}</td>
                        <td style={{ padding:'6px 12px', fontFamily:"'DM Mono',monospace",
                          color:C.success }}>{row.passed}</td>
                        <td style={{ padding:'6px 12px', fontFamily:"'DM Mono',monospace",
                          color:C.danger }}>{row.failed}</td>
                        <td style={{ padding:'6px 12px', fontFamily:"'DM Mono',monospace",
                          color:C.purple }}>
                          {row.avg_confidence_pct != null ? `${row.avg_confidence_pct}%` : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

// ── SolutionProgressBar — shows per-slot + synthesis stage progress ───────────
function SolutionProgressBar({ stages, slots, running }) {
  if (!stages.length && !running) return null;
  const PASS_COLOR  = '#22c55e';
  const FAIL_COLOR  = '#ef4444';
  const WARN_COLOR  = '#f59e0b';
  const stageMap = Object.fromEntries(stages.map(s => [String(s.position), s]));
  const allSlots = slots || [];

  const statusIcon = st => {
    if (st === 'pass')    return '✓';
    if (st === 'fail')    return '✗';
    if (st === 'error')   return '!';
    if (st === 'skipped') return '—';
    if (st === 'running') return '⏳';
    if (st === 'aborted') return '⊘';
    return '○';
  };
  const statusColor = st => {
    if (st === 'pass')    return PASS_COLOR;
    if (st === 'fail' || st === 'error') return FAIL_COLOR;
    if (st === 'review')  return WARN_COLOR;
    if (st === 'running') return '#4F8EF7';
    if (st === 'aborted') return FAIL_COLOR;
    return '#555';
  };

  return (
    <div style={{ background:'#0d1117', borderRadius:8, padding:'10px 12px',
      marginBottom:12, border:'1px solid #1e2535' }}>
      <div style={{ fontSize:10, color:'#7891B5', textTransform:'uppercase',
        letterSpacing:'0.07em', marginBottom:8 }}>Run Progress</div>
      <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
        {allSlots.map(slot => {
          const st = stageMap[String(slot.position)];
          const status = st?.status || (running ? 'pending' : 'pending');
          return (
            <div key={slot.position} style={{ display:'flex', alignItems:'center', gap:5,
              background:'#161b26', borderRadius:6, padding:'4px 8px',
              border:`1px solid ${statusColor(status)}44` }}>
              <span style={{ color: statusColor(status), fontSize:11, fontWeight:700 }}>
                {statusIcon(status)}
              </span>
              <span style={{ fontSize:10, color:'#a0b0c8' }}>
                {slot.position}. {slot.param_label}
              </span>
              {st?.ms > 0 && (
                <span style={{ fontSize:9, color:'#555', fontFamily:"'DM Mono',monospace" }}>
                  {st.ms}ms
                </span>
              )}
              {st?.error && (
                <span style={{ fontSize:9, color: FAIL_COLOR, maxWidth:100,
                  overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}
                  title={st.error}>
                  {st.error}
                </span>
              )}
            </div>
          );
        })}
        {/* Synthesis stage */}
        {(() => {
          const synth = stageMap['synthesis'];
          const status = synth?.status || (stages.length >= allSlots.length && running ? 'running' : 'pending');
          return (
            <div style={{ display:'flex', alignItems:'center', gap:5,
              background:'#161b26', borderRadius:6, padding:'4px 8px',
              border:`1px solid ${statusColor(status)}44` }}>
              <span style={{ color: statusColor(status), fontSize:11, fontWeight:700 }}>
                {statusIcon(status)}
              </span>
              <span style={{ fontSize:10, color:'#a0b0c8' }}>Synthesis</span>
              {synth?.error && (
                <span style={{ fontSize:9, color: FAIL_COLOR, maxWidth:100,
                  overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}
                  title={synth.error}>{synth.error}</span>
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
}

// ── SolutionTemplates — full management: browse, edit, clone, create ────────────
function SolutionTemplates({ onApply, standalone = false }) {
  const [templates,  setTemplates]  = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [catFilter,  setCatFilter]  = useState('all');
  const [editItem,   setEditItem]   = useState(null);   // null = closed, {} = new, row = edit
  const [form,       setForm]       = useState({});
  const [saving,     setSaving]     = useState(false);
  const [testTpl,    setTestTpl]    = useState(null);  // template being tested in SolutionsTester
  const [testImages, setTestImgs]   = useState({});
  const [testJobId,  setTestJobIdT] = useState('');
  const [testRunning,setTestRun]    = useState(false);
  const [testResult, setTestResT]   = useState(null);
  const [testError,  setTestErrT]   = useState('');

  useEffect(() => { load(); }, []);

  const load = () => {
    setLoading(true);
    api.solutions.templates()
      .then(r => setTemplates(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  // Client-side dedup by name (guard against duplicate seed rows)
  const unique   = Object.values(Object.fromEntries(templates.map(t => [t.name, t])));
  const cats     = ['all', ...new Set(unique.map(t => t.category))];
  const filtered = unique.filter(t => catFilter === 'all' || t.category === catFilter);

  const CAT_LABELS = { field_service: 'Field Service', field_audit: 'Field Audit' };

  const openNew = () => {
    setForm({ name:'', description:'', category:'field_service',
      solution_prompt:'', result_schema:'{}', param_slots:'[]' });
    setEditItem({});
  };

  const openEdit = t => {
    setForm({
      name:            t.name,
      description:     t.description || '',
      category:        t.category,
      solution_prompt: t.solution_prompt,
      result_schema:   typeof t.result_schema === 'string' ? t.result_schema
                         : JSON.stringify(t.result_schema, null, 2),
      param_slots:     typeof t.param_slots   === 'string' ? t.param_slots
                         : JSON.stringify(t.param_slots, null, 2),
    });
    setEditItem(t);
  };

  const openClone = t => {
    setForm({
      name:            t.name + ' (copy)',
      description:     t.description || '',
      category:        t.category,
      solution_prompt: t.solution_prompt,
      result_schema:   typeof t.result_schema === 'string' ? t.result_schema
                         : JSON.stringify(t.result_schema, null, 2),
      param_slots:     typeof t.param_slots   === 'string' ? t.param_slots
                         : JSON.stringify(t.param_slots, null, 2),
    });
    setEditItem({});   // {} = new (clone is save-as-new)
  };

  const save = async () => {
    if (!form.name.trim())           return alert('Name is required');
    if (!form.solution_prompt.trim())return alert('Solution prompt is required');
    let rs, ps;
    try { rs = JSON.parse(form.result_schema || '{}'); }
    catch { return alert('Result schema must be valid JSON'); }
    try { ps = JSON.parse(form.param_slots   || '[]'); }
    catch { return alert('Param slots must be valid JSON array'); }

    setSaving(true);
    try {
      const body = {
        name:            form.name,
        description:     form.description || null,
        category:        form.category,
        solution_prompt: form.solution_prompt,
        result_schema:   rs,
        param_slots:     ps,
      };
      const isEdit = editItem && editItem.id;
      if (isEdit) {
        await api.req('PATCH', `/solutions/templates/${editItem.id}`, body);
      } else {
        await api.req('POST', '/solutions/templates', body);
      }
      setEditItem(null);
      load();
    } catch(e) { alert(e.message); }
    setSaving(false);
  };

  const remove = async t => {
    if (!confirm(`Delete template "${t.name}"?`)) return;
    try {
      await api.req('DELETE', `/solutions/templates/${t.id}`);
      load();
    } catch(e) { alert(e.message); }
  };

  return (
    <div>
      {/* Category filter + New button */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
        marginBottom:14, flexWrap:'wrap', gap:8 }}>
        <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
          {cats.map(c => (
            <button key={c} onClick={() => setCatFilter(c)} style={{
              padding:'3px 12px', borderRadius:16, fontSize:11, cursor:'pointer', fontWeight:600,
              background: catFilter===c ? C.accent : C.bg,
              color:      catFilter===c ? '#000'   : C.textSec,
              border:    `1px solid ${catFilter===c ? C.accent : C.border}`,
            }}>
              {CAT_LABELS[c] || c}
            </button>
          ))}
        </div>
        <Btn size="sm" onClick={openNew}>+ New Template</Btn>
      </div>

      {loading ? <Spinner /> : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:12 }}>
          {filtered.map(t => {
            const slots = typeof t.param_slots === 'string'
              ? JSON.parse(t.param_slots) : (t.param_slots || []);
            return (
              <Card key={t.id} style={{ padding:'14px 16px' }}>
                <div style={{ fontSize:13, fontWeight:700, color:C.text, marginBottom:2 }}>{t.name}</div>
                <div style={{ fontSize:10, color:C.textMuted, marginBottom:6,
                  textTransform:'uppercase', letterSpacing:'0.06em' }}>
                  {CAT_LABELS[t.category] || t.category}
                </div>
                {t.description && (
                  <div style={{ fontSize:11, color:C.textSec, marginBottom:8, lineHeight:1.5 }}>
                    {t.description}
                  </div>
                )}
                <div style={{ display:'flex', flexWrap:'wrap', gap:4, marginBottom:10 }}>
                  {slots.map((s, i) => (
                    <span key={i} style={{ fontSize:10, color:C.accent, background:C.accentDim,
                      padding:'2px 7px', borderRadius:10, border:`1px solid ${C.accent}33` }}>
                      {s.param_label}
                    </span>
                  ))}
                </div>
                <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
                  {onApply && (
                    <Btn size="sm" onClick={() => onApply(t)}>Use</Btn>
                  )}
                  <Btn size="sm" variant="ghost" onClick={() => { setTestTpl(t); setTestResT(null); setTestErrT(''); setTestImgs({}); }}>🧪 Test</Btn>
                  <Btn size="sm" variant="ghost" onClick={() => openEdit(t)}>Edit</Btn>
                  <Btn size="sm" variant="ghost" onClick={() => openClone(t)}>Clone</Btn>
                  {!t.is_system && (
                    <Btn size="sm" variant="danger" onClick={() => remove(t)}>Delete</Btn>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Edit / Create modal */}
      <Modal open={editItem !== null} onClose={() => setEditItem(null)}
        title={editItem?.id ? 'Edit Template' : 'New Solution Template'} width={660}>
        <Input label="Name *" value={form.name||''} onChange={v => setForm(f=>({...f,name:v}))} />
        <Input label="Description" value={form.description||''} onChange={v=>setForm(f=>({...f,description:v}))} />
        <Select label="Category" value={form.category||'field_service'}
          onChange={v => setForm(f=>({...f,category:v}))}
          options={[
            {value:'field_service', label:'Field Service'},
            {value:'field_audit',   label:'Field Audit'},
          ]} />
        <div style={{ marginBottom:12 }}>
          <div style={{ fontSize:11, color:C.textSec, fontWeight:600, textTransform:'uppercase',
            letterSpacing:'0.07em', marginBottom:4 }}>Solution Prompt *</div>
          <div style={{ fontSize:10, color:C.textMuted, marginBottom:5 }}>
            {'Use {{param_1_result}}, {{param_2_result}} etc. to reference slot results'}
          </div>
          <textarea value={form.solution_prompt||''} rows={5}
            onChange={e => setForm(f=>({...f,solution_prompt:e.target.value}))}
            style={{ width:'100%', background:C.bg, border:`1px solid ${C.border}`, borderRadius:8,
              padding:'10px 12px', color:C.text, fontSize:12, resize:'vertical',
              fontFamily:"'DM Sans',sans-serif", boxSizing:'border-box', outline:'none' }} />
        </div>
        <div style={{ marginBottom:12 }}>
          <div style={{ fontSize:11, color:C.textSec, fontWeight:600, textTransform:'uppercase',
            letterSpacing:'0.07em', marginBottom:4 }}>Result Schema (JSON)</div>
          <textarea value={form.result_schema||'{}'} rows={4}
            onChange={e => setForm(f=>({...f,result_schema:e.target.value}))}
            style={{ width:'100%', background:C.bg, border:`1px solid ${C.border}`, borderRadius:8,
              padding:'10px 12px', color:C.text, fontSize:11, resize:'vertical',
              fontFamily:"'DM Mono',monospace", boxSizing:'border-box', outline:'none' }} />
        </div>
        <div style={{ marginBottom:14 }}>
          <div style={{ fontSize:11, color:C.textSec, fontWeight:600, textTransform:'uppercase',
            letterSpacing:'0.07em', marginBottom:4 }}>Param Slots (JSON array)</div>
          <div style={{ fontSize:10, color:C.textMuted, marginBottom:5 }}>
            {'[{"position":1,"param_label":"Label","hint":"Optional hint"}]'}
          </div>
          <textarea value={form.param_slots||'[]'} rows={4}
            onChange={e => setForm(f=>({...f,param_slots:e.target.value}))}
            style={{ width:'100%', background:C.bg, border:`1px solid ${C.border}`, borderRadius:8,
              padding:'10px 12px', color:C.text, fontSize:11, resize:'vertical',
              fontFamily:"'DM Mono',monospace", boxSizing:'border-box', outline:'none' }} />
        </div>
        <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
          <Btn variant="ghost" onClick={() => setEditItem(null)}>Cancel</Btn>
          <Btn onClick={save} disabled={saving}>
            {saving ? 'Saving…' : (editItem?.id ? 'Save Changes' : 'Create Template')}
          </Btn>
        </div>
      </Modal>

      {/* ── Template test modal ──────────────────────────────────────────────── */}
      <Modal open={!!testTpl} onClose={()=>{setTestTpl(null);setTestResT(null);setTestErrT('');}}
        title={testTpl ? `Test: ${testTpl.name}` : ''} width={680}>
        {testTpl && (
          <div>
            <div style={{ fontSize:12, color:C.textSec, lineHeight:1.6, marginBottom:16 }}>
              {testTpl.description || 'Run this solution template with sample images to preview the result.'}
            </div>
            {/* Slot image inputs */}
            {(()=>{
              const slots = Array.isArray(testTpl.param_slots)
                ? testTpl.param_slots
                : (() => { try { return JSON.parse(testTpl.param_slots||'[]'); } catch{ return []; } })();
              return slots.map((slot, i) => (
                <div key={i} style={{ marginBottom:14 }}>
                  <div style={{ fontSize:11, fontWeight:700, color:C.textSec,
                    textTransform:'uppercase', letterSpacing:'0.07em',
                    fontFamily:"'DM Mono',monospace", marginBottom:5 }}>
                    Slot {slot.position || i+1}: {slot.param_label || slot.label || `Image ${i+1}`}
                    {slot.is_required !== false && <span style={{ color:C.danger }}> *</span>}
                  </div>
                  <Input
                    label=""
                    value={testImages[slot.position || i+1] || ''}
                    onChange={v => setTestImgs(img => ({...img, [slot.position || i+1]: v}))}
                    placeholder="Paste image URL…"
                  />
                </div>
              ));
            })()}
            <Input label="Job ID (optional)" value={testJobId}
              onChange={setTestJobIdT} placeholder="e.g. DEMO-001" />
            {testResult && (
              <div style={{ marginBottom:12, background:C.bg, border:`1px solid ${
                testResult.validation_status==='pass'?C.success:
                testResult.validation_status==='fail'?C.danger:C.warning}44`,
                borderRadius:8, padding:'12px 14px' }}>
                <div style={{ fontSize:12, fontWeight:700, color:C.text, marginBottom:6 }}>
                  Result: <span style={{ color: testResult.validation_status==='pass'?C.success:
                    testResult.validation_status==='fail'?C.danger:C.warning }}>
                    {testResult.validation_status}
                  </span>
                  {testResult.confidence != null &&
                    <span style={{ color:C.textSec, fontWeight:400 }}>
                      {' '}| {Math.round(testResult.confidence*100)}% confidence
                    </span>}
                </div>
                <pre style={{ fontSize:11, color:C.accent, margin:0, whiteSpace:'pre-wrap',
                  fontFamily:"'DM Mono',monospace" }}>
                  {JSON.stringify(testResult.parsed_result || testResult.parsedResult, null, 2)}
                </pre>
              </div>
            )}
            {testError && (
              <div style={{ background:C.dangerDim, border:`1px solid ${C.danger}44`,
                borderRadius:8, padding:'10px 14px', marginBottom:12, fontSize:12,
                color:C.danger }}>✗ {testError}</div>
            )}
            <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
              <Btn variant="ghost" onClick={()=>{setTestTpl(null);setTestResT(null);setTestErrT('');}}>Close</Btn>
              <Btn onClick={async () => {
                setTestRun(true); setTestResT(null); setTestErrT('');
                try {
                  const slots = Array.isArray(testTpl.param_slots)
                    ? testTpl.param_slots
                    : (() => { try { return JSON.parse(testTpl.param_slots||'[]'); } catch{ return []; } })();
                  const images = slots.map((s, i) => {
                    const pos = s.position || i+1;
                    return { position:pos, imageUrl: testImages[pos] || '' };
                  }).filter(im => im.imageUrl);
                  if (!images.length) throw new Error('Add at least one image URL');
                  // Use the param tester for individual slots if only 1 slot
                  // otherwise needs a real solution config ID — show info
                  throw new Error('To run a full solution test, use the Solution Manager → create a solution from this template, then use the built-in tester.');
                } catch(e) { setTestErrT(e.message); }
                setTestRun(false);
              }} disabled={testRunning}>
                {testRunning ? 'Running…' : '▶ Run Test'}
              </Btn>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}


// ── SolutionRunDetail — shows combined result + per-param breakdown ─────────────
function SolutionRunDetail({ run, onClose }) {
  if (!run) return null;
  const sc = run.validation_status === 'pass' ? C.success
           : run.validation_status === 'fail' ? C.danger : C.warning;
  const params = run.param_results || [];
  const finalResult = run.parsed_result
    ? (typeof run.parsed_result === 'string' ? JSON.parse(run.parsed_result) : run.parsed_result)
    : null;

  return (
    <Modal open={!!run} onClose={onClose} title="Solution Run Result" width={680}>
      <div style={{ marginBottom:16, background:C.bg, borderRadius:10, padding:'14px 16px',
        border:`2px solid ${sc}44` }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
          <div style={{ fontSize:14, fontWeight:700, color:C.text }}>{run.solution_name}</div>
          <Badge c={sc} label={run.validation_status} />
        </div>
        {run.job_id && (
          <div style={{ fontSize:11, color:C.textMuted, marginBottom:6 }}>
            Job: <span style={{ color:C.accent, fontFamily:"'DM Mono',monospace" }}>{run.job_id}</span>
          </div>
        )}
        {finalResult && (
          <div style={{ background:C.surface, borderRadius:8, padding:'10px 12px', marginBottom:8 }}>
            <div style={{ fontSize:10, color:C.textSec, textTransform:'uppercase',
              letterSpacing:'0.07em', marginBottom:6 }}>Final Result</div>
            {Object.entries(finalResult).map(([k, v]) => (
              <div key={k} style={{ display:'flex', gap:8, marginBottom:4, flexWrap:'wrap' }}>
                <span style={{ fontSize:11, color:C.textMuted, width:140, flexShrink:0 }}>{k}:</span>
                <span style={{ fontSize:11, fontFamily:"'DM Mono',monospace",
                  color: v===true?C.success:v===false?C.danger:C.text }}>
                  {String(v)}
                </span>
              </div>
            ))}
          </div>
        )}
        <div style={{ display:'flex', gap:16, fontSize:11, color:C.textMuted }}>
          {run.confidence != null && (
            <span>Confidence: <strong style={{color:C.purple}}>{(run.confidence*100).toFixed(0)}%</strong></span>
          )}
          {run.processing_ms && <span>Time: {run.processing_ms}ms</span>}
          {run.tokens_input && (
            <span>Tokens: {(run.tokens_input + (run.tokens_output||0)).toLocaleString()}</span>
          )}
        </div>
      </div>

      {params.length > 0 && (
        <div>
          <div style={{ fontSize:12, fontWeight:600, color:C.textSec, marginBottom:10,
            textTransform:'uppercase', letterSpacing:'0.07em' }}>
            Param Results ({params.length})
          </div>
          {params.map((p, i) => {
            const psc = p.validation_status === 'pass' ? C.success
                      : p.validation_status === 'fail' ? C.danger
                      : p.validation_status === 'error' ? C.danger : C.warning;
            const pr = p.parsed_result
              ? (typeof p.parsed_result === 'string' ? JSON.parse(p.parsed_result) : p.parsed_result)
              : null;
            const prStr = pr ? JSON.stringify(pr) : '';
            return (
              <div key={i} style={{ marginBottom:8, background:C.bg, borderRadius:8,
                padding:'10px 12px', border:`1px solid ${psc}44` }}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:pr?6:0 }}>
                  <span style={{ fontSize:12, fontWeight:600, color:C.text }}>
                    {p.position}. {p.param_label}
                  </span>
                  <Badge c={psc} label={p.validation_status} />
                </div>
                {p.error_message && (
                  <div style={{ fontSize:11, color:C.danger }}>{p.error_message}</div>
                )}
                {pr && (
                  <div style={{ fontSize:10, color:C.textMuted, fontFamily:"'DM Mono',monospace",
                    marginTop:4, wordBreak:'break-all' }}>
                    {prStr.slice(0, 200)}{prStr.length > 200 ? '...' : ''}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
}

// ── SolutionTester — test a solution with multiple images ──────────────────────
function SolutionTester({ user }) {
  const [solutions,  setSolutions]  = useState([]);
  const [solDetail,  setSolDetail]  = useState(null);
  const [selSol,     setSelSol]     = useState('');
  const [jobId,      setJobId]      = useState('');
  const [images,     setImages]     = useState({});
  const [running,    setRunning]    = useState(false);
  const [result,     setResult]     = useState(null);
  const [history,    setHistory]    = useState([]);
  const [detailRun,  setDetailRun]  = useState(null);
  const [error,      setError]      = useState('');
  const [stages,     setStages]     = useState([]);   // per-slot progress
  const fileRefsMap  = useRef({});
  const [zoomSrc,    setZoomSrc]    = useState(null);  // URL being zoomed

  useEffect(() => {
    api.solutions.list({ isActive:'true', limit:200 })
      .then(r => setSolutions(r.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!selSol) { setSolDetail(null); setImages({}); return; }
    api.solutions.get(selSol).then(r => setSolDetail(r.data)).catch(() => {});
    setImages({});
    setResult(null);
    setError('');
  }, [selSol]);

  const slots = solDetail?.params || [];

  const setImage = (pos, field, value) =>
    setImages(prev => ({ ...prev, [pos]: { ...(prev[pos]||{mode:'url'}), [field]: value } }));

  const handleFile = (pos, file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = e => {
      const b64 = e.target.result.split(',')[1];
      setImages(prev => ({ ...prev, [pos]: { mode:'upload', b64 } }));
    };
    reader.readAsDataURL(file);
  };

  const run = async () => {
    if (!selSol)       return alert('Select a solution first');
    if (!jobId.trim()) return alert('Job ID is required');
    const imgArr = slots.map(s => {
      const img = images[s.position] || {};
      const entry = { position: s.position };
      if (img.mode === 'upload') entry.imageBase64 = img.b64 || '';
      else entry.imageUrl = img.url || '';
      return entry;
    }).filter(i => i.imageUrl || i.imageBase64);
    if (!imgArr.length) return alert('Provide at least one image');
    setRunning(true); setResult(null); setError(''); setStages([]);
    try {
      const r = await api.solutions.run(selSol, {
        images: imgArr, jobId: jobId.trim(), sourceMode:'tester',
      });
      if (r?.data?.tokenBudget || r?.status === 429) {
        setError('🪙 ' + (r?.data?.message || 'Token budget exhausted'));
        setRunning(false); return;
      }
      const runData = { ...r.data, solution_name: solDetail?.name };
      setResult(runData);
      setStages(runData.stages || []);
      setHistory(h => [runData, ...h].slice(0, 10));
    } catch(e) { setError(e.message); }
    setRunning(false);
  };

  const sc = result
    ? (result.validation_status==='pass' ? C.success : result.validation_status==='fail' ? C.danger : C.warning)
    : null;

  const finalResult = result?.parsed_result
    ? (typeof result.parsed_result === 'string' ? JSON.parse(result.parsed_result) : result.parsed_result)
    : null;

  return (
    <div style={{ display:'grid', gridTemplateColumns:'1fr 340px', gap:20, alignItems:'start' }}>
      <div>
        <div style={{ marginBottom:20 }}>
          <div style={{ fontSize:20, fontWeight:700, fontFamily:"'DM Serif Display',serif",
            color:C.text }}>Solution Tester</div>
          <div style={{ color:C.textSec, fontSize:13 }}>
            Select a solution, provide images for each param, and run
          </div>
        </div>

        <Card style={{ marginBottom:16 }}>
          <Select label="Solution" value={selSol} onChange={setSelSol}
            options={[{value:'',label:'-- select solution --'},
              ...solutions.map(s => ({
                value: s.id,
                label: s.name + (s.workflow_name ? ' + ' + s.workflow_name : ''),
              }))]} />
          <Input label="Job ID *" value={jobId} onChange={setJobId}
            placeholder="Task or job reference" />
        </Card>

        {/* Solution detail summary shown when a solution is selected */}
        {solDetail && (
          <Card style={{ marginBottom:16, background:C.accentDim, border:`1px solid ${C.accent}33` }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:13, fontWeight:700, color:C.text, marginBottom:4 }}>
                  {solDetail.name}
                </div>
                {solDetail.description && (
                  <div style={{ fontSize:11, color:C.textSec, marginBottom:8, lineHeight:1.5 }}>
                    {solDetail.description}
                  </div>
                )}
                <div style={{ fontSize:11, color:C.textMuted, marginBottom:6 }}>
                  Workflow: <span style={{ color:C.text }}>{solDetail.workflow_name}</span>
                  {solDetail.ai_model && (
                    <span> &nbsp;| Model: <span style={{ fontFamily:"'DM Mono',monospace",
                      color:C.accent, fontSize:10 }}>{solDetail.ai_model}</span></span>
                  )}
                </div>
                <div style={{ fontSize:11, fontWeight:600, color:C.textSec,
                  textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:6 }}>
                  Param Slots ({(solDetail.params||[]).length})
                </div>
                {(solDetail.params||[]).map(p => (
                  <div key={p.position} style={{ display:'flex', alignItems:'center', gap:8,
                    marginBottom:4, fontSize:11, color:C.textSec }}>
                    <span style={{ color:C.accent, fontFamily:"'DM Mono',monospace",
                      fontSize:10, minWidth:16 }}>{p.position}.</span>
                    <span style={{ color:C.text, fontWeight:500 }}>{p.param_label}</span>
                    {p.param_name && (
                      <span style={{ color:C.textMuted, fontSize:10 }}>
                        ({p.param_name} / {p.status_trigger})
                      </span>
                    )}
                    {p.is_required && (
                      <span style={{ color:C.danger, fontSize:9 }}>required</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </Card>
        )}

        {slots.length > 0 && (
          <Card style={{ marginBottom:16 }}>
            <div style={{ fontSize:12, fontWeight:700, color:C.text, marginBottom:14 }}>
              Param Images ({slots.length} slot{slots.length!==1?'s':''})
            </div>
            {/* 2-per-line grid */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              {slots.map(slot => {
                const img = images[slot.position] || { mode:'url' };
                const previewSrc = img.mode === 'upload' && img.b64
                  ? 'data:image/jpeg;base64,' + img.b64
                  : img.mode !== 'upload' && img.url ? img.url : null;
                return (
                  <div key={slot.position} style={{ background:C.bg, borderRadius:8,
                    border:`1px solid ${C.border}`, padding:'10px 12px' }}>
                    {/* Slot label */}
                    <div style={{ fontSize:11, fontWeight:600, color:C.accent, marginBottom:6 }}>
                      {slot.position}. {slot.param_label}
                      {slot.is_required && <span style={{color:C.danger,marginLeft:4}}>*</span>}
                    </div>
                    {slot.hint && (
                      <div style={{ fontSize:10, color:C.textMuted, marginBottom:6, lineHeight:1.4 }}>
                        {slot.hint}
                      </div>
                    )}
                    {/* Mode toggle */}
                    <div style={{ display:'flex', gap:5, marginBottom:8 }}>
                      {['url','upload'].map(m => (
                        <button key={m} onClick={() => setImage(slot.position, 'mode', m)} style={{
                          padding:'2px 10px', borderRadius:6, fontSize:10, cursor:'pointer',
                          background: img.mode===m ? C.accent : 'transparent',
                          color:      img.mode===m ? '#000'   : C.textSec,
                          border:    `1px solid ${img.mode===m ? C.accent : C.border}`,
                        }}>{m==='url' ? '🔗 URL' : '+ Upload'}</button>
                      ))}
                    </div>
                    {/* Input area */}
                    {img.mode !== 'upload' ? (
                      <Input value={img.url||''} onChange={v => setImage(slot.position,'url',v)}
                        placeholder="https://..." style={{ marginBottom:0 }} />
                    ) : (
                      <div>
                        <input type="file" accept="image/*"
                          ref={el => { fileRefsMap.current[slot.position] = el; }}
                          style={{ display:'none' }}
                          onChange={e => handleFile(slot.position, e.target.files[0])} />
                        <Btn size="sm" variant="secondary" style={{ width:'100%' }}
                          onClick={() => fileRefsMap.current[slot.position]?.click()}>
                          {img.b64 ? '↺ Change image' : '+ Choose image'}
                        </Btn>
                      </div>
                    )}
                    {/* Image preview — shown for both URL and upload */}
                    {previewSrc && (
                      <div style={{ marginTop:8, position:'relative' }}>
                        <img
                          src={previewSrc}
                          alt=""
                          onError={e => { e.target.style.display='none'; }}
                          style={{ width:'100%', height:120, objectFit:'cover',
                            borderRadius:6, border:`1px solid ${C.border}`,
                            display:'block', cursor:'zoom-in' }}
                          onClick={() => setZoomSrc(previewSrc)}
                        />
                        <button
                          onClick={() => setZoomSrc(previewSrc)}
                          title="Zoom image"
                          style={{ position:'absolute', top:4, right:4, background:'rgba(0,0,0,0.55)',
                            border:'none', borderRadius:4, color:'#fff', fontSize:12, cursor:'pointer',
                            padding:'2px 6px', lineHeight:1.4 }}>
                          ⤢
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {error && (
          <div style={{ background:C.dangerDim, border:`1px solid ${C.danger}44`, borderRadius:10,
            padding:12, marginBottom:12, fontSize:12, color:C.danger }}>{error}</div>
        )}

        <Btn onClick={run} disabled={running || !selSol || !jobId.trim()} style={{ width:'100%' }}>
          {running ? '⏳ Running...' : '▶ Run Solution'}
        </Btn>

        {/* Stage-by-stage progress */}
        {(running || stages.length > 0) && (
          <div style={{ marginTop:12 }}>
            <SolutionProgressBar
              stages={stages}
              slots={solDetail?.params || []}
              running={running}
            />
          </div>
        )}

        {result && (
          <Card style={{ marginTop:16, border:`2px solid ${sc}44` }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
              marginBottom:10 }}>
              <div style={{ fontSize:13, fontWeight:700, color:C.text }}>Solution Result</div>
              <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                <Badge c={sc} label={result.validation_status} />
                <Btn size="sm" variant="ghost" onClick={() => setDetailRun(result)}>Details</Btn>
              </div>
            </div>
            {finalResult && (
              <div style={{ background:C.bg, borderRadius:8, padding:'10px 12px' }}>
                {Object.entries(finalResult).map(([k, v]) => (
                  <div key={k} style={{ display:'flex', gap:8, marginBottom:4 }}>
                    <span style={{ fontSize:11, color:C.textMuted, width:130, flexShrink:0 }}>{k}:</span>
                    <span style={{ fontSize:11, fontFamily:"'DM Mono',monospace",
                      color: v===true?C.success:v===false?C.danger:C.text }}>
                      {String(v)}
                    </span>
                  </div>
                ))}
              </div>
            )}
            <div style={{ display:'flex', gap:12, fontSize:11, color:C.textMuted, marginTop:8 }}>
              {result.confidence != null && (
                <span>Conf: {(result.confidence*100).toFixed(0)}%</span>
              )}
              {result.processing_ms && <span>{result.processing_ms}ms</span>}
            </div>
          </Card>
        )}
      </div>

      <div>
        <div style={{ fontSize:12, fontWeight:700, color:C.textSec, marginBottom:10,
          textTransform:'uppercase', letterSpacing:'0.07em' }}>Recent Runs</div>
        {history.length === 0 ? (
          <Card style={{ textAlign:'center', padding:24, color:C.textMuted, fontSize:12 }}>
            No runs yet
          </Card>
        ) : history.map((r, i) => {
          const hsc = r.validation_status==='pass' ? C.success
                    : r.validation_status==='fail' ? C.danger : C.warning;
          return (
            <div key={i} onClick={() => setDetailRun(r)}
              style={{ cursor:'pointer', marginBottom:8 }}>
              <Card style={{ padding:'10px 14px', border:`1px solid ${hsc}44` }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <div style={{ fontSize:12, fontWeight:600, color:C.text }}>
                    {r.solution_name || 'Solution'}
                  </div>
                  <Badge c={hsc} label={r.validation_status} />
                </div>
                {r.job_id && (
                  <div style={{ fontSize:10, color:C.textMuted,
                    fontFamily:"'DM Mono',monospace", marginTop:4 }}>{r.job_id}</div>
                )}
              </Card>
            </div>
          );
        })}
      </div>

      <SolutionRunDetail run={detailRun} onClose={() => setDetailRun(null)} />

      {/* Image zoom modal */}
      {zoomSrc && (
        <div onClick={() => setZoomSrc(null)} style={{
          position:'fixed', inset:0, background:'rgba(0,0,0,0.85)',
          display:'flex', alignItems:'center', justifyContent:'center',
          zIndex:9999, cursor:'zoom-out', padding:24
        }}>
          <img src={zoomSrc} alt=""
            style={{ maxWidth:'92vw', maxHeight:'88vh', borderRadius:10,
              objectFit:'contain', boxShadow:'0 8px 48px rgba(0,0,0,0.6)' }}
            onClick={e => e.stopPropagation()} />
          <button onClick={() => setZoomSrc(null)} style={{
            position:'fixed', top:20, right:24, background:'rgba(255,255,255,0.15)',
            border:'none', color:'#fff', fontSize:22, cursor:'pointer',
            borderRadius:'50%', width:36, height:36, lineHeight:'36px', textAlign:'center'
          }}>✕</button>
        </div>
      )}
    </div>
  );
}

// ── SolutionManager — CRUD for solution configs (WorkflowConfig-style layout) ──
function SolutionManager({ user }) {
  const [solutions,   setSolutions]   = useState([]);
  const [viewSol,     setViewSol]     = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [modal,       setModal]       = useState(false);
  const [tmplModal,   setTmplModal]   = useState(false);
  const [form,        setForm]        = useState(null);
  const [workflows,   setWorkflows]   = useState([]);
  const [tenants,     setTenants]     = useState([]);
  const [configs,     setConfigs]     = useState([]);
  const [providers,   setProviders]   = useState([]);
  const [search,      setSearch]      = useState('');
  // Inline tester state
  const [testSolId,   setTestSolId]   = useState(null);
  const [testSolDet,  setTestSolDet]  = useState(null);
  const [testImages,  setTestImages]  = useState({});
  const [testJobId,   setTestJobId]   = useState('');
  const [testRunning, setTestRunning] = useState(false);
  const [testResult,  setTestResult]  = useState(null);
  const [testError,   setTestError]   = useState('');
  const [testDetail,  setTestDetail]  = useState(null);
  const testFileRefs = useRef({});
  const [testZoomSrc, setTestZoomSrc] = useState(null);
  const [testStages,  setTestStages]  = useState([]);

  const isSuperAdmin = user.role === 'superadmin';
  const canWrite     = isSuperAdmin || user.role === 'account_admin' || user.role === 'account_manager';

  useEffect(() => {
    load();
    api.workflows.list({ limit:200 }).then(r => setWorkflows(r.data||[])).catch(()=>{});
    api.configs.list({ limit:500 }).then(r => setConfigs(r.data||[])).catch(()=>{});
    providersApi.list().then(r => setProviders((r.data||[]).filter(p => p.is_active))).catch(()=>{});
    if (isSuperAdmin) api.tenants.list().then(r => setTenants(r.data||[])).catch(()=>{});
  }, []);

  const load = () => {
    setLoading(true);
    api.solutions.list({ limit:200 })
      .then(r => setSolutions(r.data||[]))
      .catch(()=>{})
      .finally(() => setLoading(false));
  };

  // ── Form helpers ────────────────────────────────────────────────────────────
  const openNew = (prefill = null) => {
    const rawSlots = prefill
      ? (typeof prefill.param_slots === 'string'
          ? JSON.parse(prefill.param_slots) : (prefill.param_slots || []))
      : [];
    setForm({
      name: prefill?.name || '', description: prefill?.description || '',
      tenantId: '', workflowId: '',
      solutionPrompt: prefill?.solution_prompt || '',
      resultSchema: prefill?.result_schema
        ? (typeof prefill.result_schema === 'string'
            ? prefill.result_schema
            : JSON.stringify(prefill.result_schema, null, 2))
        : '{}',
      aiModel: '', aiProviderId: '', minConfidence: '0.85',
      params: rawSlots.length
        ? rawSlots.map(s => ({ position:s.position, paramLabel:s.param_label, configId:'', isRequired:true }))
        : [{ position:1, paramLabel:'', configId:'', isRequired:true }],
    });
    setModal(true);
  };

  const openEdit = async sol => {
    // Always fetch full detail (with params) before opening edit
    try {
      const r = await api.solutions.get(sol.id);
      const s = r.data;
      setForm({
        id: s.id, name: s.name, description: s.description || '',
        tenantId: s.tenant_id, workflowId: s.workflow_id,
        solutionPrompt: s.solution_prompt,
        resultSchema: typeof s.result_schema === 'string'
          ? s.result_schema : JSON.stringify(s.result_schema, null, 2),
        aiModel: s.ai_model || '', aiProviderId: s.ai_provider_id || '',
        minConfidence: String(s.min_confidence),
        params: (s.params||[]).map(p => ({
          position: p.position, paramLabel: p.param_label,
          configId: p.config_id, isRequired: p.is_required,
        })),
      });
      setModal(true);
    } catch(e) { alert('Could not load solution: ' + e.message); }
  };

  const addParam    = () => setForm(f => ({
    ...f, params: [...f.params, { position:f.params.length+1, paramLabel:'', configId:'', isRequired:true }],
  }));
  const removeParam = idx => setForm(f => ({
    ...f, params: f.params.filter((_,i)=>i!==idx).map((p,i)=>({...p,position:i+1})),
  }));
  const updateParam = (idx, field, val) => setForm(f => ({
    ...f, params: f.params.map((p,i) => i===idx ? {...p,[field]:val} : p),
  }));

  const save = async () => {
    if (!form.name.trim())           return alert('Name is required');
    if (!form.workflowId)            return alert('Workflow is required');
    if (!form.solutionPrompt.trim()) return alert('Solution prompt is required');
    if (!form.params.length)         return alert('At least one param slot is required');
    if (form.params.some(p => !p.paramLabel.trim())) return alert('All param slots need a label');
    if (form.params.some(p => !p.configId))          return alert('All param slots need a config');
    let rs;
    try { rs = JSON.parse(form.resultSchema || '{}'); } catch { return alert('Result schema must be valid JSON'); }
    const body = {
      name: form.name, description: form.description || undefined,
      workflowId: form.workflowId,
      tenantId:  isSuperAdmin ? (form.tenantId || undefined) : undefined,
      solutionPrompt: form.solutionPrompt, resultSchema: rs,
      aiModel: form.aiModel || undefined, aiProviderId: form.aiProviderId || undefined,
      minConfidence: parseFloat(form.minConfidence) || 0.85,
      params: form.params,
    };
    try {
      if (form.id) await api.solutions.update(form.id, body);
      else         await api.solutions.create(body);
      setModal(false); load();
    } catch(e) { alert(e.message); }
  };

  const toggleActive = async sol => {
    await api.solutions.update(sol.id, { isActive: !sol.is_active }).catch(()=>{});
    load();
  };
  const clone = async sol => {
    if (!confirm(`Clone "${sol.name}"?`)) return;
    await api.solutions.clone(sol.id).catch(()=>{});
    load();
  };
  const remove = async sol => {
    if (!confirm(`Delete solution "${sol.name}"? This cannot be undone.`)) return;
    await api.solutions.remove(sol.id).catch(()=>{});
    load();
  };

  // ── Inline tester ─────────────────────────────────────────────────────────
  const openTest = async sol => {
    setTestSolId(sol.id); setTestResult(null); setTestError(''); setTestImages({}); setTestJobId(''); setTestStages([]);
    try {
      const r = await api.solutions.get(sol.id);
      setTestSolDet(r.data);
    } catch(e) { setTestError(e.message); }
  };

  const setTestImage = (pos, field, val) =>
    setTestImages(prev => ({ ...prev, [pos]: { ...(prev[pos]||{mode:'url'}), [field]: val } }));

  const handleTestFile = (pos, file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = e => {
      const b64 = e.target.result.split(',')[1];
      setTestImages(prev => ({ ...prev, [pos]: { mode:'upload', b64 } }));
    };
    reader.readAsDataURL(file);
  };

  const runInlineTest = async () => {
    if (!testJobId.trim()) return alert('Job ID is required');
    const slots = testSolDet?.params || [];
    const imgArr = slots.map(s => {
      const img = testImages[s.position] || {};
      const entry = { position: s.position };
      if (img.mode === 'upload') entry.imageBase64 = img.b64 || '';
      else entry.imageUrl = img.url || '';
      return entry;
    }).filter(i => i.imageUrl || i.imageBase64);
    if (!imgArr.length) return alert('Provide at least one image');
    setTestRunning(true); setTestResult(null); setTestError('');
    try {
      const r = await api.solutions.run(testSolId, { images:imgArr, jobId:testJobId.trim(), sourceMode:'tester' });
      if (r?.data?.tokenBudget) { setTestError('🪙 ' + (r?.data?.message||'Token budget exhausted')); setTestRunning(false); return; }
      const td = { ...r.data, solution_name: testSolDet?.name };
      setTestResult(td);
      setTestStages(td.stages || []);
    } catch(e) { setTestError(e.message); }
    setTestRunning(false);
  };

  const filtered  = solutions.filter(s => !search || s.name.toLowerCase().includes(search.toLowerCase()));
  const wfOptions = workflows.filter(w => !form?.tenantId || w.tenant_id === form.tenantId);
  const cfgOptions= configs.filter(c => !form?.workflowId || c.workflow_id === form.workflowId);
  const testSlots = testSolDet?.params || [];
  const testSc    = testResult
    ? (testResult.validation_status==='pass'?C.success:testResult.validation_status==='fail'?C.danger:C.warning)
    : null;

  // ── Inline tester panel ────────────────────────────────────────────────────
  const InlineTesterPanel = () => {
    if (!testSolId || !testSolDet) return (
      <div style={{ textAlign:'center', padding:40, color:C.textMuted }}>
        <div style={{ fontSize:32, marginBottom:12 }}>🧪</div>
        <div style={{ fontSize:13 }}>Click Test on a solution to run it here</div>
      </div>
    );
    const finalResult = testResult?.parsed_result
      ? (typeof testResult.parsed_result==='string' ? JSON.parse(testResult.parsed_result) : testResult.parsed_result)
      : null;
    return (
      <div>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:14 }}>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:14, fontWeight:700, color:C.text, marginBottom:2 }}>
              {testSolDet.name}
            </div>
            <div style={{ fontSize:11, color:C.textSec, marginBottom:1 }}>
              ⚙ {testSolDet.workflow_name}
            </div>
            {testSolDet.tenant_name && isSuperAdmin && (
              <div style={{ fontSize:10, color:C.textMuted, marginBottom:1 }}>
                🏢 {testSolDet.tenant_name}
              </div>
            )}
            {(testSolDet.provider_name || testSolDet.ai_model) && (
              <div style={{ fontSize:10, color:C.textMuted, fontFamily:"'DM Mono',monospace" }}>
                {testSolDet.provider_name
                  ? <span>{PROVIDER_ICONS[testSolDet.provider_type]||'🤖'} {testSolDet.provider_name}</span>
                  : null}
                {testSolDet.ai_model
                  ? <span style={{ marginLeft: testSolDet.provider_name ? 4 : 0 }}>
                      {testSolDet.provider_name ? ' | ' : ''}{testSolDet.ai_model}
                    </span>
                  : null}
              </div>
            )}
          </div>
          <Btn size="sm" variant="ghost" onClick={() => { setTestSolId(null); setTestSolDet(null); setTestResult(null); setTestError(''); }}>✕</Btn>
        </div>

        {/* Param slots summary */}
        {testSlots.length > 0 && (
          <div style={{ background:C.bg, borderRadius:8, padding:'8px 12px', marginBottom:12, border:`1px solid ${C.border}` }}>
            {testSlots.map(p => (
              <div key={p.position} style={{ fontSize:11, color:C.textSec, marginBottom:2 }}>
                <span style={{ color:C.accent, fontFamily:"'DM Mono',monospace", marginRight:6 }}>{p.position}.</span>
                {p.param_label}
                {p.param_name && <span style={{ color:C.textMuted }}> — {p.param_name}</span>}
              </div>
            ))}
          </div>
        )}

        <Input label="Job ID *" value={testJobId} onChange={setTestJobId} placeholder="e.g. WO-2025-001" />

        {/* 2-per-line grid for param slots */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:8 }}>
          {testSlots.map(slot => {
            const img = testImages[slot.position] || { mode:'url' };
            const previewSrc = img.mode === 'upload' && img.b64
              ? 'data:image/jpeg;base64,' + img.b64
              : img.mode !== 'upload' && img.url ? img.url : null;
            return (
              <div key={slot.position} style={{ padding:'10px 10px', background:C.bg,
                borderRadius:8, border:`1px solid ${C.border}` }}>
                <div style={{ fontSize:10, fontWeight:600, color:C.accent, marginBottom:5 }}>
                  {slot.position}. {slot.param_label}
                  {slot.is_required && <span style={{ color:C.danger, marginLeft:3 }}>*</span>}
                </div>
                <div style={{ display:'flex', gap:4, marginBottom:5 }}>
                  {['url','upload'].map(m => (
                    <button key={m} onClick={() => setTestImage(slot.position,'mode',m)} style={{
                      padding:'1px 8px', borderRadius:5, fontSize:9, cursor:'pointer',
                      background: img.mode===m?C.accent:'transparent',
                      color: img.mode===m?'#000':C.textSec,
                      border:`1px solid ${img.mode===m?C.accent:C.border}`,
                    }}>{m==='url'?'URL':'Upload'}</button>
                  ))}
                </div>
                {img.mode !== 'upload' ? (
                  <Input value={img.url||''} onChange={v => setTestImage(slot.position,'url',v)}
                    placeholder="https://..." style={{ marginBottom:0, fontSize:10 }} />
                ) : (
                  <div>
                    <input type="file" accept="image/*"
                      ref={el => { testFileRefs.current[slot.position] = el; }}
                      style={{ display:'none' }}
                      onChange={e => handleTestFile(slot.position, e.target.files[0])} />
                    <Btn size="sm" variant="secondary" style={{ width:'100%', fontSize:10 }}
                      onClick={() => testFileRefs.current[slot.position]?.click()}>
                      {img.b64 ? '↺ Change' : '+ Choose image'}
                    </Btn>
                  </div>
                )}
                {/* Preview for both URL and upload — with zoom */}
                {previewSrc && (
                  <div style={{ marginTop:6, position:'relative' }}>
                    <img src={previewSrc} alt=""
                      onError={e => { e.target.style.display='none'; }}
                      style={{ width:'100%', height:90, objectFit:'cover',
                        borderRadius:5, border:`1px solid ${C.border}`,
                        display:'block', cursor:'zoom-in' }}
                      onClick={() => setTestZoomSrc(previewSrc)} />
                    <button
                      onClick={() => setTestZoomSrc(previewSrc)}
                      style={{ position:'absolute', top:3, right:3, background:'rgba(0,0,0,0.55)',
                        border:'none', borderRadius:3, color:'#fff', fontSize:10,
                        cursor:'pointer', padding:'1px 5px', lineHeight:1.4 }}>⤢</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {testError && (
          <div style={{ background:C.dangerDim, border:`1px solid ${C.danger}44`, borderRadius:8,
            padding:10, marginBottom:10, fontSize:11, color:C.danger }}>{testError}</div>
        )}

        <Btn onClick={runInlineTest} disabled={testRunning || !testJobId.trim()} style={{ width:'100%' }}>
          {testRunning ? '⏳ Running...' : '▶ Run Solution'}
        </Btn>

        {/* Stage progress */}
        {(testRunning || testStages.length > 0) && (
          <div style={{ marginTop:8 }}>
            <SolutionProgressBar
              stages={testStages}
              slots={testSlots}
              running={testRunning}
            />
          </div>
        )}

        {testResult && (
          <div style={{ marginTop:12, background:C.bg, borderRadius:10, padding:'12px 14px',
            border:`2px solid ${testSc}44` }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
              <span style={{ fontSize:12, fontWeight:700, color:C.text }}>Result</span>
              <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                <Badge c={testSc} label={testResult.validation_status} />
                <Btn size="sm" variant="ghost" onClick={() => setTestDetail(testResult)}>Details</Btn>
              </div>
            </div>
            {finalResult && Object.entries(finalResult).slice(0,6).map(([k,v]) => (
              <div key={k} style={{ display:'flex', gap:8, marginBottom:3 }}>
                <span style={{ fontSize:10, color:C.textMuted, width:110, flexShrink:0 }}>{k}:</span>
                <span style={{ fontSize:10, fontFamily:"'DM Mono',monospace",
                  color:v===true?C.success:v===false?C.danger:C.text }}>{String(v)}</span>
              </div>
            ))}
            {finalResult && Object.keys(finalResult).length > 6 && (
              <div style={{ fontSize:10, color:C.textMuted, marginTop:4 }}>
                +{Object.keys(finalResult).length-6} more fields
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
        marginBottom:20, flexWrap:'wrap', gap:10 }}>
        <div>
          <div style={{ fontSize:20, fontWeight:700, fontFamily:"'DM Serif Display',serif",
            color:C.text }}>Solutions</div>
          <div style={{ color:C.textSec, fontSize:13 }}>
            Combine multiple param validations into a composite result
          </div>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <Input value={search} onChange={setSearch} placeholder="Search solutions..."
            style={{ marginBottom:0, minWidth:200 }} />
          {canWrite && (
            <>
              <Btn variant="secondary" onClick={() => setTmplModal(true)}>Templates</Btn>
              <Btn onClick={() => openNew()}>+ New Solution</Btn>
            </>
          )}
        </div>
      </div>

      {loading ? <Spinner /> : (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 360px', gap:16, alignItems:'start' }}>
          {/* Left — solutions list */}
          <div>
            {filtered.length === 0 ? (
              <Card style={{ textAlign:'center', padding:60, color:C.textMuted }}>
                <div style={{ fontSize:36, marginBottom:12 }}>🔗</div>
                <div style={{ fontSize:16, fontWeight:600, color:C.textSec, marginBottom:8 }}>No solutions yet</div>
                <div style={{ fontSize:13, marginBottom:20 }}>
                  Solutions combine multiple param validations into a single composite result.
                </div>
                {canWrite && (
                  <div style={{ display:'flex', gap:8, justifyContent:'center' }}>
                    <Btn variant="secondary" onClick={() => setTmplModal(true)}>Browse Templates</Btn>
                    <Btn onClick={() => openNew()}>Create Solution</Btn>
                  </div>
                )}
              </Card>
            ) : filtered.map(s => (
              <Card key={s.id}
                onClick={()=>setViewSol(s)}
                style={{ marginBottom:10, opacity:s.is_active?1:0.7, cursor:'pointer',
                border: testSolId===s.id ? `2px solid ${C.accent}` : `1px solid ${C.border}` }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4, flexWrap:'wrap' }}>
                      <div style={{ fontSize:14, fontWeight:700, color:C.text }}>{s.name}</div>
                      <Badge c={s.is_active?C.success:C.textMuted} label={s.is_active?'active':'inactive'} />
                      <span style={{ fontSize:10, color:C.textMuted }}>v{s.version}</span>
                    </div>
                    {/* Always show tenant for superadmin, workflow for everyone */}
                    {isSuperAdmin && s.tenant_name && (
                      <div style={{ fontSize:11, color:C.textMuted, marginBottom:1 }}>
                        🏢 {s.tenant_name}
                      </div>
                    )}
                    <div style={{ fontSize:11, color:C.textSec, marginBottom:2 }}>
                      ⚙ {s.workflow_name}
                    </div>
                    {(s.provider_name || s.ai_model) && (
                      <div style={{ fontSize:10, color:C.textMuted, marginBottom:4,
                        fontFamily:"'DM Mono',monospace" }}>
                        {s.provider_name && (
                          <span>{PROVIDER_ICONS[s.provider_type]||'🤖'} {s.provider_name}</span>
                        )}
                        {s.ai_model && (
                          <span style={{ marginLeft: s.provider_name ? 6 : 0 }}>
                            {s.provider_name ? ' | ' : ''}{s.ai_model}
                          </span>
                        )}
                      </div>
                    )}
                    {s.description && (
                      <div style={{ fontSize:11, color:C.textSec, marginBottom:6, lineHeight:1.5 }}>
                        {s.description}
                      </div>
                    )}
                    {/* Param slots summary */}
                    {(s.params||[]).length > 0 && (
                      <div style={{ display:'flex', flexWrap:'wrap', gap:4, marginBottom:8 }}>
                        {(s.params||[]).map(p => (
                          <span key={p.position} style={{ fontSize:10, color:C.accent,
                            background:C.accentDim, padding:'2px 8px', borderRadius:10,
                            border:`1px solid ${C.accent}33` }}>
                            {p.position}. {p.param_label}
                          </span>
                        ))}
                      </div>
                    )}
                    <div style={{ display:'flex', gap:6, flexShrink:0 }}>
                      <span style={{ fontSize:10, color:C.textMuted, background:C.bg,
                        padding:'2px 8px', borderRadius:10, border:`1px solid ${C.border}` }}>
                        {s.param_count} params
                      </span>
                      <span style={{ fontSize:10, color:C.textMuted, background:C.bg,
                        padding:'2px 8px', borderRadius:10, border:`1px solid ${C.border}` }}>
                        {s.run_count} runs
                      </span>
                    </div>
                  </div>
                  <div style={{ display:'flex', flexDirection:'column', gap:5, marginLeft:12, flexShrink:0 }}>
                    <Btn size="sm" variant="secondary" onClick={() => openTest(s)}>🧪 Test</Btn>
                    {canWrite && (
                      <>
                        <Btn size="sm" onClick={e=>{e.stopPropagation();openEdit(s);}}>Edit</Btn>
                        <Btn size="sm" variant="ghost" onClick={e=>{e.stopPropagation();clone(s);}}>Clone</Btn>
                        <Btn size="sm" variant="ghost" onClick={() => toggleActive(s)}>
                          {s.is_active?'Deactivate':'Activate'}
                        </Btn>
                        <Btn size="sm" variant="danger" onClick={() => remove(s)}>Delete</Btn>
                      </>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {/* Right — inline tester panel */}
          <div style={{ position:'sticky', top:16 }}>
            <Card style={{ border: testSolId ? `2px solid ${C.accent}44` : `1px solid ${C.border}` }}>
              <InlineTesterPanel />
            </Card>
          </div>
        </div>
      )}

      {/* Create / Edit modal */}
      <Modal open={modal} onClose={() => setModal(false)}
        title={form?.id ? 'Edit Solution' : 'New Solution'} width={680}>
        {form && (
          <div>
            <Input label="Name *" value={form.name} onChange={v => setForm(f => ({...f,name:v}))} />
            <Input label="Description" value={form.description} onChange={v => setForm(f => ({...f,description:v}))} />
            {isSuperAdmin && tenants.length > 0 && (
              <Select label="Tenant" value={form.tenantId}
                onChange={v => setForm(f => ({...f,tenantId:v,workflowId:''}))}
                options={[{value:'',label:'-- select tenant --'},
                  ...tenants.map(t => ({value:t.id,label:t.name}))]} />
            )}
            <Select label="Workflow *" value={form.workflowId}
              onChange={v => setForm(f => ({...f,workflowId:v}))}
              options={[{value:'',label:'-- select workflow --'},
                ...wfOptions.map(w => ({value:w.id,label:w.name}))]} />
            {providers.length > 0 && (
              <Select label="AI Provider (optional)" value={form.aiProviderId||''}
                onChange={v => setForm(f => ({...f,aiProviderId:v,aiModel:''}))}
                options={[{value:'',label:'Default provider'},
                  ...providers.map(p => ({value:p.id,label:(PROVIDER_ICONS[p.provider_type]||'')+' '+p.name}))]} />
            )}
            {(() => {
              const selProv = providers.find(p => p.id === form.aiProviderId);
              const models  = selProv?.settings?.models || [];
              if (selProv && models.length > 0) {
                return (
                  <Select label={'Model (' + selProv.name + ')'}
                    value={form.aiModel||''}
                    onChange={v => setForm(f => ({...f,aiModel:v}))}
                    options={[{value:'',label:'Default ('+( selProv.default_model||'')+')'},
                      ...models.map(m => ({value:m,label:m}))]} />
                );
              }
              if (!selProv) {
                return (
                  <Input label="AI Model (optional — leave blank for system default)"
                    value={form.aiModel||''} onChange={v => setForm(f => ({...f,aiModel:v}))}
                    placeholder="e.g. claude-sonnet-4-20250514" />
                );
              }
              return null;
            })()}

            <div style={{ marginBottom:14 }}>
              <div style={{ fontSize:11, color:C.textSec, marginBottom:4, fontWeight:600,
                textTransform:'uppercase', letterSpacing:'0.07em' }}>Solution Prompt *</div>
              <div style={{ fontSize:10, color:C.textMuted, marginBottom:6 }}>
                {'Use {{param_1_result}}, {{param_2_result}} etc. to reference each param result'}
              </div>
              <textarea value={form.solutionPrompt}
                onChange={e => setForm(f => ({...f,solutionPrompt:e.target.value}))}
                rows={5} style={{
                  width:'100%', background:C.bg, border:`1px solid ${C.border}`, borderRadius:8,
                  padding:'10px 12px', color:C.text, fontSize:12, resize:'vertical',
                  fontFamily:"'DM Sans',sans-serif", boxSizing:'border-box', outline:'none',
                }} />
            </div>

            <div style={{ marginBottom:14 }}>
              <div style={{ fontSize:11, color:C.textSec, marginBottom:4, fontWeight:600,
                textTransform:'uppercase', letterSpacing:'0.07em' }}>Result Schema (JSON)</div>
              <textarea value={form.resultSchema}
                onChange={e => setForm(f => ({...f,resultSchema:e.target.value}))}
                rows={4} style={{
                  width:'100%', background:C.bg, border:`1px solid ${C.border}`, borderRadius:8,
                  padding:'10px 12px', color:C.text, fontSize:11, resize:'vertical',
                  fontFamily:"'DM Mono',monospace", boxSizing:'border-box', outline:'none',
                }} />
            </div>

            <div style={{ marginBottom:14 }}>
              <div style={{ fontSize:11, color:C.textSec, marginBottom:8, fontWeight:600,
                textTransform:'uppercase', letterSpacing:'0.07em',
                display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <span>Param Slots *</span>
                <Btn size="sm" variant="ghost" onClick={addParam}>+ Add Slot</Btn>
              </div>
              {form.params.map((p, i) => (
                <div key={i} style={{ display:'flex', gap:8, marginBottom:8,
                  alignItems:'flex-start', background:C.bg, borderRadius:8,
                  padding:'10px 12px', border:`1px solid ${C.border}` }}>
                  <div style={{ width:22, fontSize:12, color:C.textMuted,
                    fontFamily:"'DM Mono',monospace", paddingTop:6, flexShrink:0 }}>
                    {p.position}.
                  </div>
                  <div style={{ flex:1 }}>
                    <Input value={p.paramLabel}
                      onChange={v => updateParam(i,'paramLabel',v)}
                      placeholder="Label e.g. Odometer Check-In"
                      style={{ marginBottom:6 }} />
                    <Select value={p.configId}
                      onChange={v => updateParam(i,'configId',v)}
                      options={[{value:'',label:'-- select validation config --'},
                        ...cfgOptions.map(c => ({
                          value: c.id,
                          label: c.param_name + ' (' + c.status_trigger + ')',
                        }))]} />
                  </div>
                  {form.params.length > 1 && (
                    <Btn size="sm" variant="danger" onClick={() => removeParam(i)}
                      style={{ marginTop:2, flexShrink:0 }}>x</Btn>
                  )}
                </div>
              ))}
            </div>

            <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:8 }}>
              <Btn variant="ghost" onClick={() => setModal(false)}>Cancel</Btn>
              <Btn onClick={save}>{form.id ? 'Save Changes' : 'Create Solution'}</Btn>
            </div>
          </div>
        )}
      </Modal>

      {/* Template picker modal */}
      <Modal open={tmplModal} onClose={() => setTmplModal(false)}
        title="Solution Templates" width={780}>
        <SolutionTemplates onApply={t => { setTmplModal(false); openNew(t); }} />
      </Modal>

      {/* Inline test result detail modal */}
      <SolutionRunDetail run={testDetail} onClose={() => setTestDetail(null)} />


      {/* ── Solution detail / view modal ────────────────────────────────────── */}
      <Modal open={!!viewSol} onClose={()=>setViewSol(null)}
        title={viewSol ? `${viewSol.name} — Solution Details` : ''} width={640}>
        {viewSol && (
          <div>
            {/* Header info */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:14 }}>
              {[
                ['Workflow',    viewSol.workflow_name || '—'],
                ['Version',     `v${viewSol.version || 1}`],
                ['Status',      viewSol.is_active ? 'Active' : 'Inactive'],
                ['Param Slots', viewSol.param_count || viewSol.params?.length || '—'],
              ].map(([label, val]) => (
                <div key={label} style={{ background:C.bg, border:`1px solid ${C.border}`,
                  borderRadius:8, padding:'8px 12px' }}>
                  <div style={{ fontSize:10, color:C.textSec, fontWeight:700,
                    textTransform:'uppercase', letterSpacing:'0.07em',
                    fontFamily:"'DM Mono',monospace", marginBottom:3 }}>{label}</div>
                  <div style={{ fontSize:13, color:C.text, fontWeight:600 }}>{String(val)}</div>
                </div>
              ))}
            </div>
            {/* Description */}
            {viewSol.description && (
              <div style={{ background:C.accentDim, border:`1px solid ${C.accent}33`,
                borderRadius:8, padding:'10px 14px', marginBottom:12 }}>
                <div style={{ fontSize:10, color:C.accent, fontWeight:700,
                  textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:4 }}>Description</div>
                <div style={{ fontSize:13, color:C.text, lineHeight:1.6 }}>{viewSol.description}</div>
              </div>
            )}
            {/* Param slots */}
            {viewSol.params?.length > 0 && (
              <div style={{ marginBottom:12 }}>
                <div style={{ fontSize:11, fontWeight:700, color:C.textSec,
                  textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:8,
                  fontFamily:"'DM Mono',monospace" }}>Param Slots</div>
                {viewSol.params.map(p => (
                  <div key={p.position} style={{ display:'flex', alignItems:'center', gap:10,
                    padding:'6px 12px', background:C.bg, border:`1px solid ${C.border}`,
                    borderRadius:8, marginBottom:6 }}>
                    <span style={{ width:22, height:22, borderRadius:'50%', background:C.accent,
                      color:'#000', fontSize:11, fontWeight:700, display:'flex',
                      alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                      {p.position}
                    </span>
                    <div>
                      <div style={{ fontSize:12, fontWeight:600, color:C.text }}>
                        {p.param_label}
                      </div>
                      <div style={{ fontSize:10, color:C.textSec }}>
                        {p.param_name || p.config_param_name || ''}
                        {p.is_required ? ' | Required' : ' | Optional'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {/* Solution prompt */}
            {viewSol.solution_prompt && (
              <div style={{ marginBottom:16 }}>
                <div style={{ fontSize:11, fontWeight:700, color:C.textSec,
                  textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:6,
                  fontFamily:"'DM Mono',monospace" }}>Synthesis Prompt</div>
                <div style={{ background:C.bg, border:`1px solid ${C.border}`, borderRadius:8,
                  padding:'12px', fontSize:12, color:C.text, lineHeight:1.7,
                  fontFamily:"'DM Mono',monospace", whiteSpace:'pre-wrap',
                  maxHeight:160, overflowY:'auto' }}>
                  {viewSol.solution_prompt}
                </div>
              </div>
            )}
            {/* Result schema */}
            {viewSol.result_schema && (
              <div style={{ marginBottom:16 }}>
                <div style={{ fontSize:11, fontWeight:700, color:C.textSec,
                  textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:6,
                  fontFamily:"'DM Mono',monospace" }}>Result Schema</div>
                <div style={{ background:C.bg, border:`1px solid ${C.border}`, borderRadius:8,
                  padding:'12px', fontSize:11, color:C.accent, lineHeight:1.7,
                  fontFamily:"'DM Mono',monospace", whiteSpace:'pre-wrap' }}>
                  {JSON.stringify(viewSol.result_schema, null, 2)}
                </div>
              </div>
            )}
            <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
              <Btn variant="ghost" onClick={()=>setViewSol(null)}>Close</Btn>
              {canWrite && (
                <Btn onClick={()=>{ setViewSol(null); openEdit(viewSol); }}>
                  ✏ Edit Solution
                </Btn>
              )}
            </div>
          </div>
        )}
      </Modal>
      {/* Inline tester image zoom overlay */}
      {testZoomSrc && (
        <div onClick={() => setTestZoomSrc(null)} style={{
          position:'fixed', inset:0, background:'rgba(0,0,0,0.87)',
          display:'flex', alignItems:'center', justifyContent:'center',
          zIndex:9999, cursor:'zoom-out', padding:24,
        }}>
          <img src={testZoomSrc} alt=""
            style={{ maxWidth:'92vw', maxHeight:'88vh', borderRadius:10,
              objectFit:'contain', boxShadow:'0 8px 48px rgba(0,0,0,0.6)' }}
            onClick={e => e.stopPropagation()} />
          <button onClick={() => setTestZoomSrc(null)} style={{
            position:'fixed', top:20, right:24, background:'rgba(255,255,255,0.15)',
            border:'none', color:'#fff', fontSize:22, cursor:'pointer',
            borderRadius:'50%', width:36, height:36, lineHeight:'36px', textAlign:'center',
          }}>✕</button>
        </div>
      )}
    </div>
  );
}

// ── Analytics ─────────────────────────────────────────────────────────────────
// Per-param deep analytics: trend, schema-field distributions, provider perf,
// confidence distribution, feedback. Separate nav tab — no impact on existing views.
function Analytics({ user }) {
  const [workflows,  setWorkflows]  = useState([]);
  const [tenants,    setTenants]    = useState([]);
  const [params,     setParams]     = useState([]);
  const [data,       setData]       = useState(null);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState('');

  // Filters
  const [tenantFilter,  setTenantF] = useState('');
  const [workflowFilter,setWorkflowF]=useState('');
  const [paramFilter,   setParamF]  = useState('');
  const [dateFrom,      setDateFrom]= useState('');
  const [dateTo,        setDateTo]  = useState('');
  const [groupBy,       setGroupBy] = useState('day');

  const isSuperAdmin = user.role === 'superadmin';
  const isAccountMgr = user.role === 'account_manager';

  useEffect(() => {
    if (isSuperAdmin || isAccountMgr) api.tenants.list().then(r=>setTenants(r.data||[])).catch(()=>{});
    api.workflows.list({ limit:200 }).then(r=>setWorkflows(r.data||[])).catch(()=>{});
  }, [isSuperAdmin, isAccountMgr]);

  // Load distinct param names for the selected workflow
  useEffect(() => {
    if (!workflowFilter) { setParams([]); return; }
    api.configs.list({ workflowId: workflowFilter, limit:200 })
      .then(r => {
        const ps = [...new Set((r.data||[]).map(c=>c.param_name).filter(Boolean))].sort();
        setParams(ps);
      }).catch(()=>{});
  }, [workflowFilter]);

  const load = async () => {
    if (!workflowFilter && !paramFilter) return;
    setLoading(true); setError(''); setData(null);
    try {
      const p = { groupBy };
      if (tenantFilter)   p.tenantId    = tenantFilter;
      if (workflowFilter) p.workflowId  = workflowFilter;
      if (paramFilter)    p.paramName   = paramFilter;
      if (dateFrom)       p.dateFrom    = dateFrom;
      if (dateTo)         p.dateTo      = dateTo;
      const r = await api.runs.analytics(p);
      setData(r.data);
    } catch(e) { setError(e.message); }
    setLoading(false);
  };

  const fmtNum = n => parseInt(n||0).toLocaleString();
  const fmtPct = n => n != null ? `${parseFloat(n).toFixed(1)}%` : '—';
  const PASS_COLOR = '#22c55e', FAIL_COLOR = '#ef4444', WARN_COLOR = '#f59e0b';

  // Mini horizontal bar chart
  const MiniBar = ({ value, max, color='#4F8EF7', label }) => (
    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
      <div style={{ fontSize:11, color:C.textSec, width:90, textAlign:'right', flexShrink:0,
        fontFamily:"'DM Mono',monospace", overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}
        title={label}>{label}</div>
      <div style={{ flex:1, height:16, background:C.border, borderRadius:3, overflow:'hidden' }}>
        <div style={{ width:`${max>0?Math.round(value/max*100):0}%`, height:'100%',
          background:color, borderRadius:3, transition:'width 0.4s' }} />
      </div>
      <div style={{ fontSize:11, color:C.text, width:40, textAlign:'right', fontFamily:"'DM Mono',monospace",
        flexShrink:0 }}>{fmtNum(value)}</div>
    </div>
  );

  const StatCard = ({ label, value, sub, color=C.text }) => (
    <div style={{ background:C.bg, borderRadius:10, padding:'14px 16px', border:`1px solid ${C.border}` }}>
      <div style={{ fontSize:10, color:C.textSec, textTransform:'uppercase', letterSpacing:'0.08em',
        fontFamily:"'DM Mono',monospace", marginBottom:6 }}>{label}</div>
      <div style={{ fontSize:22, fontWeight:700, color, fontFamily:"'DM Serif Display',serif" }}>{value}</div>
      {sub && <div style={{ fontSize:11, color:C.textMuted, marginTop:4 }}>{sub}</div>}
    </div>
  );

  // Trend sparkline (SVG)
  const Sparkline = ({ rows, width=280, height=60 }) => {
    if (!rows?.length) return null;
    const reversed = [...rows].reverse();
    const vals = reversed.map(r => parseInt(r.total||0));
    const passed = reversed.map(r => parseInt(r.passed||0));
    const max = Math.max(...vals, 1);
    const pts = (arr, yMax) => arr.map((v,i) => {
      const x = (i / (arr.length-1||1)) * (width-10) + 5;
      const y = height - (v/yMax) * (height-10) - 5;
      return `${x},${y}`;
    }).join(' ');
    return (
      <svg width={width} height={height} style={{ overflow:'visible' }}>
        <polyline points={pts(vals, max)} fill="none" stroke={C.accent} strokeWidth="2" />
        <polyline points={pts(passed, max)} fill="none" stroke={PASS_COLOR} strokeWidth="1.5" strokeDasharray="4,2" />
      </svg>
    );
  };

  const wfOptions = workflows.filter(w => !tenantFilter || w.tenant_id === tenantFilter);

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom:20 }}>
        <div style={{ fontSize:20, fontWeight:700, fontFamily:"'DM Serif Display',serif", color:C.text }}>
          Result Analytics
        </div>
        <div style={{ color:C.textSec, fontSize:13 }}>
          Deep analysis per workflow param — trends, distributions, provider performance
        </div>
      </div>

      {/* Filter bar */}
      <Card style={{ padding:'14px 16px', marginBottom:20 }}>
        <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'flex-end' }}>
          {(isSuperAdmin || isAccountMgr) && tenants.length > 0 && (
            <div>
              <div style={{ fontSize:10, color:C.textSec, marginBottom:4, textTransform:'uppercase', letterSpacing:'0.06em' }}>Tenant</div>
              <select value={tenantFilter} onChange={e=>{ setTenantF(e.target.value); setWorkflowF(''); setParamF(''); }}
                style={{ background:C.bg, border:`1px solid ${C.border}`, borderRadius:8, padding:'7px 12px',
                  color:tenantFilter?C.text:C.textMuted, fontSize:13, fontFamily:"'DM Sans',sans-serif" }}>
                <option value="">All tenants</option>
                {tenants.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
          )}
          <div>
            <div style={{ fontSize:10, color:C.textSec, marginBottom:4, textTransform:'uppercase', letterSpacing:'0.06em' }}>Workflow</div>
            <select value={workflowFilter} onChange={e=>{ setWorkflowF(e.target.value); setParamF(''); }}
              style={{ background:C.bg, border:`1px solid ${C.border}`, borderRadius:8, padding:'7px 12px',
                color:workflowFilter?C.text:C.textMuted, fontSize:13, fontFamily:"'DM Sans',sans-serif", minWidth:180 }}>
              <option value="">— select workflow —</option>
              {wfOptions.map(w=><option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
          </div>
          <div>
            <div style={{ fontSize:10, color:C.textSec, marginBottom:4, textTransform:'uppercase', letterSpacing:'0.06em' }}>Param</div>
            <select value={paramFilter} onChange={e=>setParamF(e.target.value)}
              style={{ background:C.bg, border:`1px solid ${C.border}`, borderRadius:8, padding:'7px 12px',
                color:paramFilter?C.text:C.textMuted, fontSize:13, fontFamily:"'DM Sans',sans-serif", minWidth:160 }}>
              <option value="">All params</option>
              {params.map(p=><option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <div style={{ fontSize:10, color:C.textSec, marginBottom:4, textTransform:'uppercase', letterSpacing:'0.06em' }}>From</div>
            <input type="date" value={dateFrom} onChange={e=>setDateFrom(e.target.value)}
              style={{ background:C.bg, border:`1px solid ${C.border}`, borderRadius:8, padding:'7px 10px',
                color:C.text, fontSize:13, fontFamily:"'DM Mono',monospace" }} />
          </div>
          <div>
            <div style={{ fontSize:10, color:C.textSec, marginBottom:4, textTransform:'uppercase', letterSpacing:'0.06em' }}>To</div>
            <input type="date" value={dateTo} onChange={e=>setDateTo(e.target.value)}
              style={{ background:C.bg, border:`1px solid ${C.border}`, borderRadius:8, padding:'7px 10px',
                color:C.text, fontSize:13, fontFamily:"'DM Mono',monospace" }} />
          </div>
          <div>
            <div style={{ fontSize:10, color:C.textSec, marginBottom:4, textTransform:'uppercase', letterSpacing:'0.06em' }}>Group By</div>
            <select value={groupBy} onChange={e=>setGroupBy(e.target.value)}
              style={{ background:C.bg, border:`1px solid ${C.border}`, borderRadius:8, padding:'7px 12px',
                color:C.text, fontSize:13, fontFamily:"'DM Sans',sans-serif" }}>
              <option value="day">Day</option>
              <option value="week">Week</option>
              <option value="month">Month</option>
            </select>
          </div>
          <Btn onClick={load} disabled={loading || (!workflowFilter && !paramFilter)}>
            {loading ? '⏳ Loading…' : '📊 Analyse'}
          </Btn>
        </div>
        {(!workflowFilter && !paramFilter) && (
          <div style={{ fontSize:11, color:C.textMuted, marginTop:10 }}>
            Select a workflow (and optionally a param) then click Analyse.
          </div>
        )}
      </Card>

      {error && (
        <div style={{ background:C.dangerDim, border:`1px solid ${C.danger}44`, borderRadius:10, padding:14,
          marginBottom:16, fontSize:13, color:C.danger }}>✗ {error}</div>
      )}

      {data && (() => {
        const s  = data.summary || {};
        const total = parseInt(s.total||0);
        const passed = parseInt(s.passed||0);
        const failed = parseInt(s.failed||0);
        const passRate = total > 0 ? Math.round(passed/total*100) : 0;

        return (
          <div>
            {/* ── Summary cards ──────────────────────────────────────────── */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(140px,1fr))', gap:12, marginBottom:20 }}>
              <StatCard label="Total Runs"   value={fmtNum(total)}       color={C.accent} />
              <StatCard label="Passed"       value={fmtNum(passed)}      color={PASS_COLOR} sub={`${passRate}% pass rate`} />
              <StatCard label="Failed"       value={fmtNum(failed)}      color={FAIL_COLOR} />
              <StatCard label="Under Review" value={fmtNum(s.review||0)} color={WARN_COLOR} />
              <StatCard label="Avg Confidence" value={fmtPct(s.avg_confidence_pct)} color={C.purple} sub={`min ${fmtPct(s.min_confidence_pct)} + max ${fmtPct(s.max_confidence_pct)}`} />
              <StatCard label="Avg Processing" value={s.avg_processing_ms ? `${Math.round(s.avg_processing_ms)}ms` : '—'} />
              <StatCard label="Tokens Used" value={fmtNum(parseInt(s.total_tokens_in||0) + parseInt(s.total_tokens_out||0))} sub={`${fmtNum(s.total_tokens_in)} in + ${fmtNum(s.total_tokens_out)} out`} color={C.textSec} />
              <StatCard label="Active Days"  value={fmtNum(s.active_days||0)} />
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:20 }}>
              {/* ── Trend chart ──────────────────────────────────────────── */}
              <Card>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
                  <div style={{ fontSize:13, fontWeight:700, color:C.text }}>📈 Trend</div>
                  <div style={{ display:'flex', gap:12, fontSize:11, color:C.textMuted }}>
                    <span style={{ display:'flex', alignItems:'center', gap:4 }}><span style={{color:C.accent}}>─</span> Total</span>
                    <span style={{ display:'flex', alignItems:'center', gap:4 }}><span style={{color:PASS_COLOR}}>- -</span> Passed</span>
                  </div>
                </div>
                {data.trend?.length ? (
                  <>
                    <Sparkline rows={data.trend} width={340} height={80} />
                    <div style={{ display:'flex', justifyContent:'space-between', fontSize:10, color:C.textMuted, marginTop:4 }}>
                      <span>{data.trend[data.trend.length-1]?.period?.toString().slice(0,10)}</span>
                      <span>{data.trend[0]?.period?.toString().slice(0,10)}</span>
                    </div>
                  </>
                ) : <div style={{ color:C.textMuted, fontSize:12, padding:'20px 0', textAlign:'center' }}>No trend data</div>}
              </Card>

              {/* ── Confidence distribution ──────────────────────────────── */}
              <Card>
                <div style={{ fontSize:13, fontWeight:700, color:C.text, marginBottom:12 }}>🎯 Confidence Distribution</div>
                {data.confBuckets?.length ? (
                  data.confBuckets.map(b => (
                    <MiniBar key={b.bucket} label={b.bucket} value={parseInt(b.cnt)}
                      max={Math.max(...data.confBuckets.map(x=>parseInt(x.cnt)))}
                      color={b.bucket.startsWith('9')||b.bucket.startsWith('<5')?
                        (b.bucket.startsWith('9')?PASS_COLOR:FAIL_COLOR):
                        b.bucket==='<50%'?FAIL_COLOR:WARN_COLOR} />
                  ))
                ) : <div style={{ color:C.textMuted, fontSize:12 }}>No confidence data</div>}
              </Card>
            </div>

            {/* ── Schema field distributions ──────────────────────────── */}
            {Object.keys(data.fieldStats||{}).length > 0 && (
              <Card style={{ marginBottom:20 }}>
                <div style={{ fontSize:13, fontWeight:700, color:C.text, marginBottom:16 }}>
                  📋 Result Schema Field Distributions
                  <span style={{ fontSize:11, color:C.textMuted, fontWeight:400, marginLeft:8 }}>
                    — boolean, numeric and enum fields (free-text fields are excluded)
                  </span>
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))', gap:16 }}>
                   {Object.entries(data.fieldStats).map(([field, info]) => {
                     const vals   = info.values || info;
                     const ftype  = info.type || 'enum';
                     const maxCnt = Math.max(...vals.map(v=>parseInt(v.count||0)), 1);
                     const typeColor = ftype==='boolean'?C.success : ftype==='numeric'?C.purple : C.accent;
                     return (
                       <div key={field} style={{ background:C.bg, borderRadius:8, padding:'12px 14px', border:`1px solid ${C.border}` }}>
                         <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
                           marginBottom:10, borderBottom:`1px solid ${C.border}`, paddingBottom:6 }}>
                           <div style={{ fontSize:11, fontWeight:700, color:C.accent, fontFamily:"'DM Mono',monospace" }}>
                             {field}
                           </div>
                           <span style={{ fontSize:9, color:typeColor, textTransform:'uppercase', letterSpacing:'0.08em',
                             border:`1px solid ${typeColor}44`, borderRadius:4, padding:'1px 5px' }}>{ftype}</span>
                         </div>
                         {vals.slice(0, 10).map(v => {
                           const valStr = v.value === null ? '(null)' : String(v.value);
                           const cnt    = parseInt(v.count||0);
                           const barCol = ftype === 'boolean'
                             ? (valStr === 'true' ? PASS_COLOR : valStr === 'false' ? FAIL_COLOR : C.textMuted)
                             : ftype === 'numeric' ? C.purple : C.accent;
                           return (
                             <div key={valStr} style={{ display:'flex', alignItems:'center', gap:6, marginBottom:5 }}>
                               <div style={{ fontSize:11, color:C.textSec, width:80, flexShrink:0, overflow:'hidden',
                                 textOverflow:'ellipsis', whiteSpace:'nowrap', fontFamily:"'DM Mono',monospace" }}
                                 title={valStr}>{valStr}</div>
                               <div style={{ flex:1, height:12, background:C.border, borderRadius:2 }}>
                                 <div style={{ width:`${Math.round(cnt/maxCnt*100)}%`, height:'100%',
                                   background:barCol, borderRadius:2, transition:'width 0.4s' }} />
                               </div>
                               <div style={{ fontSize:10, color:C.textMuted, width:32, textAlign:'right',
                                 fontFamily:"'DM Mono',monospace" }}>{cnt}</div>
                             </div>
                           );
                         })}
                         {vals.length > 10 && (
                           <div style={{ fontSize:10, color:C.textMuted, marginTop:4 }}>+{vals.length-10} more + {ftype} distribution</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </Card>
            )}

            {/* ── Provider / model performance ─────────────────────────── */}
            {data.providers?.length > 0 && (
              <Card style={{ marginBottom:20 }}>
                <div style={{ fontSize:13, fontWeight:700, color:C.text, marginBottom:14 }}>🤖 Provider & Model Performance</div>
                <div style={{ overflowX:'auto' }}>
                  <table style={{ width:'100%', borderCollapse:'collapse', minWidth:600 }}>
                    <thead>
                      <tr style={{ background:C.bg }}>
                        {['Provider','Model','Runs','Pass Rate','Avg Confidence','Avg Speed','Tokens Used'].map(h=>(
                          <th key={h} style={{ padding:'8px 12px', textAlign:'left', fontSize:11, color:C.textSec,
                            fontWeight:600, textTransform:'uppercase', letterSpacing:'0.06em',
                            fontFamily:"'DM Mono',monospace", borderBottom:`1px solid ${C.border}` }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {data.providers.map((p,i)=>(
                        <tr key={i} style={{ borderBottom:`1px solid ${C.border}` }}>
                          <td style={{ padding:'8px 12px', fontSize:12 }}>
                            <span style={{ color:C.accent }}>{PROVIDER_ICONS[p.provider_type]||'🤖'}</span>
                            <span style={{ marginLeft:6, color:C.text }}>{p.provider_name||p.provider_type||'Unknown'}</span>
                          </td>
                          <td style={{ padding:'8px 12px', fontSize:11, color:C.textMuted, fontFamily:"'DM Mono',monospace" }}>
                            {p.model||'—'}
                          </td>
                          <td style={{ padding:'8px 12px', fontSize:12, color:C.text, fontFamily:"'DM Mono',monospace" }}>{fmtNum(p.total)}</td>
                          <td style={{ padding:'8px 12px', fontSize:12, fontWeight:600,
                            color:parseFloat(p.pass_rate||0)>=80?PASS_COLOR:parseFloat(p.pass_rate||0)>=60?WARN_COLOR:FAIL_COLOR }}>
                            {fmtPct(p.pass_rate)}
                          </td>
                          <td style={{ padding:'8px 12px', fontSize:12, color:C.purple }}>{fmtPct(p.avg_confidence)}</td>
                          <td style={{ padding:'8px 12px', fontSize:12, color:C.textSec }}>
                            {p.avg_ms ? `${Math.round(p.avg_ms)}ms` : '—'}
                          </td>
                          <td style={{ padding:'8px 12px', fontSize:12, color:C.textSec, fontFamily:"'DM Mono',monospace" }}>
                            {fmtNum(p.tokens_used)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}

            {/* ── Trend detail table ───────────────────────────────────── */}
            {data.trend?.length > 0 && (
              <Card>
                <div style={{ fontSize:13, fontWeight:700, color:C.text, marginBottom:14 }}>📅 Trend Detail</div>
                <div style={{ overflowX:'auto' }}>
                  <table style={{ width:'100%', borderCollapse:'collapse' }}>
                    <thead>
                      <tr style={{ background:C.bg }}>
                        {['Period','Total','Passed','Failed','Pass Rate','Avg Confidence','Tokens'].map(h=>(
                          <th key={h} style={{ padding:'8px 12px', textAlign:'left', fontSize:11, color:C.textSec,
                            fontWeight:600, textTransform:'uppercase', letterSpacing:'0.06em',
                            fontFamily:"'DM Mono',monospace", borderBottom:`1px solid ${C.border}` }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[...data.trend].reverse().map((r,i)=>{
                        const tot  = parseInt(r.total||0);
                        const pass = parseInt(r.passed||0);
                        const pr   = tot > 0 ? Math.round(pass/tot*100) : 0;
                        return (
                          <tr key={i} style={{ borderBottom:`1px solid ${C.border}` }}>
                            <td style={{ padding:'7px 12px', fontSize:12, color:C.text, fontFamily:"'DM Mono',monospace" }}>
                              {String(r.period).slice(0,10)}
                            </td>
                            <td style={{ padding:'7px 12px', fontSize:12, fontFamily:"'DM Mono',monospace" }}>{fmtNum(tot)}</td>
                            <td style={{ padding:'7px 12px', fontSize:12, color:PASS_COLOR, fontFamily:"'DM Mono',monospace" }}>{fmtNum(pass)}</td>
                            <td style={{ padding:'7px 12px', fontSize:12, color:FAIL_COLOR, fontFamily:"'DM Mono',monospace" }}>{fmtNum(r.failed||0)}</td>
                            <td style={{ padding:'7px 12px', fontSize:12, fontWeight:600,
                              color:pr>=80?PASS_COLOR:pr>=60?WARN_COLOR:FAIL_COLOR }}>{pr}%</td>
                            <td style={{ padding:'7px 12px', fontSize:12, color:C.purple }}>{fmtPct(r.avg_confidence_pct)}</td>
                            <td style={{ padding:'7px 12px', fontSize:12, color:C.textSec, fontFamily:"'DM Mono',monospace" }}>{fmtNum(r.tokens_used)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </div>
        );
      })()}

      {!loading && !data && !error && (
        <div style={{ textAlign:'center', padding:60, color:C.textMuted }}>
          <div style={{ fontSize:48, marginBottom:16 }}>📊</div>
          <div style={{ fontSize:16, fontWeight:600, color:C.textSec, marginBottom:8 }}>Select a workflow and click Analyse</div>
          <div style={{ fontSize:13 }}>Get deep insights into validation trends, schema field distributions, and model performance</div>
        </div>
      )}
    </div>
  );
}

// ── Reports ───────────────────────────────────────────────────────────────────
function Reports({ user }) {
  const isSuperAdmin = user.role === 'superadmin';
  const isAccountMgr = user.role === 'account_manager';
  const [rows,     setRows]     = useState([]);
  const [totals,   setTotals]   = useState({});
  const [loading,  setLoading]  = useState(false);
  const [groupBy,  setGroupBy]  = useState('workflow');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo,   setDateTo]   = useState('');
  const [tenants,  setTenants]  = useState([]);
  const [workflows,setWorkflows]= useState([]);
  const [tenantF,  setTenantF]  = useState('');
  const [workflowF,setWorkflowF]= useState('');

  useEffect(() => {
    if (isSuperAdmin || isAccountMgr) api.tenants.list().then(r=>setTenants(r.data||[])).catch(()=>{});
    api.workflows.list({ limit:200 }).then(r=>setWorkflows(r.data||[])).catch(()=>{});
  }, [isSuperAdmin, isAccountMgr]);

  const load = useCallback(async () => {
    setLoading(true);
    const p = { groupBy };
    if (dateFrom) p.dateFrom = dateFrom;
    if (dateTo)   p.dateTo   = dateTo;
    if (tenantF)  p.tenantId = tenantF;
    if (workflowF)p.workflowId = workflowF;
    try {
      const r = await api.runs.report(p);
      setRows(r.data?.rows||[]); setTotals(r.data?.totals||{});
    } catch(e) { console.error(e); }
    setLoading(false);
  }, [groupBy, dateFrom, dateTo, tenantF, workflowF]);

  useEffect(() => { load(); }, [load]);

  const csvUrl = () => {
    const p = { groupBy };
    if (dateFrom) p.dateFrom = dateFrom;
    if (dateTo)   p.dateTo   = dateTo;
    if (tenantF)  p.tenantId = tenantF;
    if (workflowF)p.workflowId = workflowF;
    const qs = new URLSearchParams({ ...p, format:'csv' });
    return `/api/v1/runs/report?${qs}`;
  };

  // FIX: <a href> doesn't send the Authorization header, so the API returns 401.
  // Instead: fetch with auth header, get blob, create an object URL, click it.
  const downloadCsv = async () => {
    try {
      const token = api.getToken();
      const res = await fetch(csvUrl(), { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) { alert(`Export failed: ${res.status}`); return; }
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `visionai_report_${groupBy}_${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch(e) { alert('Export failed: ' + e.message); }
  };

  const pct = (n,d) => d>0 ? `${Math.round(n/d*100)}%` : '—';
  const bar = (pct, color) => (
    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
      <div style={{ flex:1, height:6, background:C.border, borderRadius:3, overflow:'hidden' }}>
        <div style={{ width:`${Math.min(pct,100)}%`, height:'100%', background:color, borderRadius:3 }} />
      </div>
      <span style={{ fontSize:11, color, fontFamily:"'DM Mono',monospace", width:36, textAlign:'right' }}>{Math.round(pct)||0}%</span>
    </div>
  );

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20, flexWrap:'wrap', gap:12 }}>
        <div>
          <div style={{ fontSize:20, fontWeight:700, fontFamily:"'DM Serif Display',serif", color:C.text, marginBottom:4 }}>Reports</div>
          <div style={{ color:C.textSec, fontSize:13 }}>Aggregate performance by workflow, tenant, param or day + Export to CSV</div>
        </div>
        <Btn variant="secondary" onClick={downloadCsv}>⬇ Export CSV</Btn>
      </div>

      <Card style={{ marginBottom:16, padding:'14px 20px' }}>
        <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'center' }}>
          <Select label="Group By" value={groupBy} onChange={setGroupBy} options={[
            {value:'workflow',label:'Workflow'},
            {value:'param',   label:'Param / Check'},
            {value:'tenant',  label:'Tenant'},
            {value:'day',     label:'Day'},
          ]} />
          {isSuperAdmin && (
            <Select label="Tenant" value={tenantF} onChange={setTenantF}
              options={[{value:'',label:'All tenants'},...tenants.map(t=>({value:t.id,label:t.name}))]} />
          )}
          <Select label="Workflow" value={workflowF} onChange={setWorkflowF}
            options={[{value:'',label:'All workflows'},...workflows.map(w=>({value:w.id,label:w.name}))]} />
          <Input label="From" type="date" value={dateFrom} onChange={setDateFrom} style={{ marginBottom:0, width:140 }} />
          <Input label="To"   type="date" value={dateTo}   onChange={setDateTo}   style={{ marginBottom:0, width:140 }} />
          <Btn variant="ghost" style={{ marginTop:18 }} size="sm" onClick={()=>{ setDateFrom(''); setDateTo(''); setTenantF(''); setWorkflowF(''); }}>Clear</Btn>
        </div>
      </Card>

      {/* Totals strip */}
      {Object.keys(totals).length > 0 && (
        <div style={{ display:'flex', gap:12, flexWrap:'wrap', marginBottom:16 }}>
          {[
            ['Total Runs',  parseInt(totals.total_runs||0).toLocaleString(), C.accent],
            ['Passed',      parseInt(totals.passed||0).toLocaleString(), C.success],
            ['Failed',      parseInt(totals.failed||0).toLocaleString(), C.danger],
            ['Pass Rate',   `${totals.pass_rate_pct||0}%`, C.success],
            ['👍 Feedback', `${totals.thumbs_up||0} / ${totals.feedback_count||0}`, C.purple],
            ...(user.role !== 'viewer' ? [["Tokens", `${parseInt(totals.total_tokens_input||0).toLocaleString()}↑`, C.textSec]] : []),
          ].map(([l,v,c])=>(
            <Card key={l} style={{ flex:1, minWidth:100, padding:'12px 14px' }}>
              <div style={{ fontSize:10, color:C.textSec, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:4, fontFamily:"'DM Mono',monospace" }}>{l}</div>
              <div style={{ fontSize:18, fontWeight:700, color:c, fontFamily:"'DM Serif Display',serif" }}>{v}</div>
            </Card>
          ))}
        </div>
      )}

      {loading ? <Spinner /> : rows.length === 0 ? (
        <Card style={{ textAlign:'center', padding:48, color:C.textMuted }}>
          <div style={{ fontSize:32, marginBottom:12 }}>📊</div>
          <div>No data for the selected filters</div>
        </Card>
      ) : (
        <Card style={{ padding:0, overflow:'hidden' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr style={{ background:C.bg }}>
                  {['Name','Runs','Pass Rate','Avg Conf','Feedback',...(user.role!=='viewer'?['Tokens In']:[]),'Processing'].map(h=>(
                  <th key={h} style={{ padding:'10px 16px', textAlign:'left', fontSize:11, color:C.textSec,
                    fontWeight:600, letterSpacing:'0.06em', textTransform:'uppercase',
                    fontFamily:"'DM Mono',monospace", borderBottom:`1px solid ${C.border}` }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r,i)=>(
                <tr key={i} style={{ borderBottom:`1px solid ${C.border}` }}
                  onMouseEnter={e=>e.currentTarget.style.background=C.surfaceHover}
                  onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                  <td style={{ padding:'10px 16px' }}>
                    <div style={{ fontSize:13, fontWeight:600, color:C.text }}>{r.group_label}</div>
                    {r.category && r.category!=='date' && <div style={{ fontSize:10, color:C.textMuted }}>{r.category}</div>}
                  </td>
                  <td style={{ padding:'10px 16px' }}>
                    <div style={{ fontSize:13, color:C.text, fontFamily:"'DM Mono',monospace" }}>{parseInt(r.total_runs||0).toLocaleString()}</div>
                    <div style={{ fontSize:10, color:C.textSec }}>{r.passed||0}✓ {r.failed||0}✗ {r.review||0}⚠</div>
                  </td>
                  <td style={{ padding:'10px 16px', minWidth:140 }}>
                    {bar(parseFloat(r.pass_rate_pct||0), C.success)}
                  </td>
                  <td style={{ padding:'10px 16px', minWidth:140 }}>
                    {bar(parseFloat(r.avg_confidence_pct||0), C.purple)}
                  </td>
                  <td style={{ padding:'10px 16px' }}>
                    {parseInt(r.feedback_count||0)>0 ? (
                      <div style={{ fontSize:12, color:C.textSec }}>
                        <span style={{ color:C.success }}>👍{r.thumbs_up||0}</span>
                        {' '}<span style={{ color:C.danger }}>👎{r.thumbs_down||0}</span>
                        <span style={{ color:C.textMuted }}> /{r.feedback_count}</span>
                      </div>
                    ) : <span style={{ fontSize:11, color:C.textMuted }}>—</span>}
                  </td>
                   {user.role !== 'viewer' && <td style={{ padding:'10px 16px', fontSize:11, color:C.textSec, fontFamily:"'DM Mono',monospace" }}>
                    {parseInt(r.total_tokens_input||0).toLocaleString()}
                   </td>}
                  <td style={{ padding:'10px 16px', fontSize:11, color:C.textSec, fontFamily:"'DM Mono',monospace" }}>
                    {r.avg_processing_ms ? `${Math.round(r.avg_processing_ms)}ms` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

// ── ResultsDataTable — sortable/filterable/column-hide data table ─────────────
function ResultsDataTable({ data, loading, sortCol, setSortCol, sortDir, setSortDir,
  colSearch, setColSearch, hiddenCols, setHiddenCols, onRefresh, user }) {
  const [showColPicker, setShowColPicker] = useState(false);

  const isViewer = user?.role === 'viewer';

  // All column keys from data, or defaults if no data yet
  const DEFAULT_COLS = ['created_at','validation_status','confidence','param_name',
    'workflow_name','tenant_name','job_id','source_mode','ai_model','processing_ms'];
  const allColKeys = data.length > 0
    ? Object.keys(data[0])
    : DEFAULT_COLS;

  // Default hidden columns for viewer (no token/provider info) and initial set
  const initialHidden = new Set(
    isViewer
      ? ['tokens_in','tokens_out','provider_name','provider_type','ai_model']
      : ['tokens_in','tokens_out','image_source','url_field']
  );
  const effectiveHidden = hiddenCols.size > 0 ? hiddenCols : initialHidden;
  const visibleCols = allColKeys.filter(k => !effectiveHidden.has(k));

  const colLabel = k => k.replace(/^result_/, '★ ').replace(/_/g,' ').replace(/\b\w/g, c => c.toUpperCase());

  // Apply per-column search filters
  const filtered = data.filter(row =>
    Object.entries(colSearch).every(([col, val]) =>
      !val || String(row[col]||'').toLowerCase().includes(val.toLowerCase())
    )
  );

  // Sort
  const sorted = [...filtered].sort((a, b) => {
    const av = a[sortCol]||'', bv = b[sortCol]||'';
    return sortDir === 'asc'
      ? String(av).localeCompare(String(bv), undefined, { numeric: true })
      : String(bv).localeCompare(String(av), undefined, { numeric: true });
  });

  const STATUS_COLORS = { pass: C.success, fail: C.danger, review: C.warning, error: C.textMuted };
  const MONO_COLS = new Set(['id','created_at','job_id','ai_model','processing_ms',
    'tokens_in','tokens_out','confidence','external_ref_id','submitted_by_name']);

  const handleSort = col => {
    if (col === sortCol) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  const toggleCol = k => {
    setHiddenCols(prev => {
      const next = new Set(prev.size > 0 ? prev : initialHidden);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };

  const clearColFilters = () => setColSearch({});
  const hasFilters = Object.values(colSearch).some(Boolean);

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:10, flexWrap:'wrap' }}>
        <div style={{ fontSize:12, color:C.textSec, minWidth:80 }}>
          {sorted.length.toLocaleString()} / {data.length.toLocaleString()} rows
        </div>
        <Btn size="sm" variant="ghost" onClick={onRefresh} disabled={loading}>
          {loading ? '⏳ Loading…' : '↻ Reload'}
        </Btn>
        <Btn size="sm" variant={showColPicker ? 'secondary' : 'ghost'}
          onClick={() => setShowColPicker(v => !v)}>
          ⊟ Columns ({visibleCols.length} / {allColKeys.length})
        </Btn>
        {hasFilters && (
          <Btn size="sm" variant="danger" onClick={clearColFilters}>✕ Clear column filters</Btn>
        )}
        {sorted.length > 500 && (
          <div style={{ fontSize:11, color:C.warning }}>⚠ Showing first 500 of {sorted.length.toLocaleString()}. Use column filters to narrow down.</div>
        )}
      </div>

      {/* Column picker */}
      {showColPicker && (
        <Card style={{ padding:12, marginBottom:12 }}>
          <div style={{ fontSize:11, fontWeight:600, color:C.textSec, marginBottom:8,
            textTransform:'uppercase', letterSpacing:'0.07em' }}>Toggle columns</div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:5 }}>
            {allColKeys.map(k => {
              const hidden = (hiddenCols.size > 0 ? hiddenCols : initialHidden).has(k);
              const isResult = k.startsWith('result_');
              return (
                <button key={k} onClick={() => toggleCol(k)} style={{
                  padding:'3px 10px', borderRadius:20, fontSize:10, cursor:'pointer', transition:'all 0.15s',
                  background: hidden ? 'transparent' : (isResult ? C.warningDim : C.accentDim),
                  border: `1px solid ${hidden ? C.border : (isResult ? C.warning : C.accent)}`,
                  color: hidden ? C.textMuted : (isResult ? C.warning : C.accent),
                  fontFamily:"'DM Sans',sans-serif",
                }}>
                  {colLabel(k)}
                </button>
              );
            })}
          </div>
          <div style={{ fontSize:10, color:C.textMuted, marginTop:8 }}>
            ★ columns are result schema fields. Click to toggle visibility.
          </div>
        </Card>
      )}

      {loading ? (
        <div style={{ textAlign:'center', padding:40 }}><Spinner /></div>
      ) : data.length === 0 ? (
        <Card style={{ textAlign:'center', padding:40, color:C.textMuted }}>
          <div style={{ fontSize:32, marginBottom:12 }}>⊞</div>
          <div style={{ marginBottom:8 }}>No data loaded yet</div>
          <Btn onClick={onRefresh}>↻ Load Table Data</Btn>
        </Card>
      ) : (
        <div style={{ overflowX:'auto', borderRadius:10, border:`1px solid ${C.border}`,
          maxHeight:'72vh', overflowY:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:11 }}>
            <thead style={{ position:'sticky', top:0, zIndex:10 }}>
              {/* Column headers with sort */}
              <tr style={{ background:C.surface, borderBottom:`2px solid ${C.border}` }}>
                {visibleCols.map(col => (
                  <th key={col} style={{ padding:'7px 10px', textAlign:'left', whiteSpace:'nowrap',
                    minWidth:80, cursor:'pointer', userSelect:'none',
                    background: sortCol===col ? C.accentDim : C.surface,
                    borderRight:`1px solid ${C.border}` }}
                    onClick={() => handleSort(col)}>
                    <div style={{ display:'flex', alignItems:'center', gap:4,
                      fontSize:10, fontWeight:700, color: sortCol===col ? C.accent : C.textSec,
                      textTransform:'uppercase', letterSpacing:'0.05em',
                      fontFamily:"'DM Mono',monospace" }}>
                      {colLabel(col)}
                      {sortCol===col
                        ? <span style={{ color:C.accent, fontSize:12 }}>{sortDir==='asc'?'↑':'↓'}</span>
                        : <span style={{ color:C.border, fontSize:10 }}>↕</span>}
                    </div>
                  </th>
                ))}
              </tr>
              {/* Per-column search row */}
              <tr style={{ background:C.bg, borderBottom:`1px solid ${C.border}` }}>
                {visibleCols.map(col => (
                  <th key={col} style={{ padding:'4px 6px', borderRight:`1px solid ${C.border}` }}>
                    <input
                      value={colSearch[col]||''}
                      onChange={e => setColSearch(p => ({ ...p, [col]: e.target.value }))}
                      placeholder="⌕ filter…"
                      style={{ width:'100%', background:C.surface, border:`1px solid ${C.border}`,
                        borderRadius:4, padding:'2px 6px', fontSize:10, color:C.text,
                        outline:'none', boxSizing:'border-box', fontFamily:"'DM Mono',monospace",
                        borderColor: colSearch[col] ? C.accent : C.border }}
                    />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.slice(0, 500).map((row, i) => (
                <tr key={i} style={{ borderBottom:`1px solid ${C.border}`,
                  background: i%2===0 ? 'transparent' : C.surface+'44' }}
                  onMouseEnter={e => e.currentTarget.style.background = C.accentDim}
                  onMouseLeave={e => e.currentTarget.style.background = i%2===0 ? 'transparent' : C.surface+'44'}>
                  {visibleCols.map(col => {
                    const val = row[col];
                    const isStatus = col === 'validation_status';
                    const isConf   = col === 'confidence';
                    const isMono   = MONO_COLS.has(col) || col.startsWith('result_');
                    const confVal  = isConf && val ? parseFloat(val) : null;
                    return (
                      <td key={col} style={{
                        padding:'5px 10px', maxWidth:220, overflow:'hidden',
                        textOverflow:'ellipsis', whiteSpace:'nowrap',
                        borderRight:`1px solid ${C.border}`,
                        fontFamily: isMono ? "'DM Mono',monospace" : "'DM Sans',sans-serif",
                      }} title={String(val||'')}>
                        {isStatus ? (
                          <span style={{ padding:'1px 6px', borderRadius:10, fontSize:10, fontWeight:600,
                            background: (STATUS_COLORS[val]||C.textMuted)+'22',
                            color: STATUS_COLORS[val]||C.textMuted, border:`1px solid ${(STATUS_COLORS[val]||C.textMuted)}44` }}>
                            {val||'—'}
                          </span>
                        ) : isConf && confVal !== null ? (
                          <span style={{ color: confVal>=0.9?C.success : confVal>=0.7?C.warning : C.danger,
                            fontWeight:600 }}>
                            {(confVal*100).toFixed(0)}%
                          </span>
                        ) : (
                          <span style={{ color: val===''||val===null?C.textMuted:C.text }}>
                            {val===''||val===null ? '—' : String(val)}
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
              {sorted.length === 0 && (
                <tr><td colSpan={visibleCols.length} style={{ textAlign:'center', padding:32, color:C.textMuted }}>
                  No rows match the column filters above.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Results ───────────────────────────────────────────────────────────────────
function Results({ user }) {
  const [runs,        setRuns]        = useState([]);
  const [filter,      setFilter]      = useState('all');
  const [page,        setPage]        = useState(1);
  const [total,       setTotal]       = useState(0);
  const [loading,     setLoading]     = useState(true);
  const [dateFrom,    setDateFrom]    = useState('');
  const [dateTo,      setDateTo]      = useState('');
  const [paramFilter, setParamFilter] = useState('');
  const [jobFilter,   setJobFilter]   = useState('');
  const [tenantFilter,setTenantFilter]= useState('');
  const [workflowFilter,setWfFilter]  = useState('');
  const [detailRun,   setDetailRun]   = useState(null);
  const [tenants,     setTenants]     = useState([]);
  const [workflows,   setWorkflows]   = useState([]);
  const [viewMode,    setViewMode]    = useState('card');    // 'card' | 'table'
  const [tableData,   setTableData]   = useState([]);        // flat rows for table view
  const [tableLoading,setTableLoading]= useState(false);
  const [sortCol,     setSortCol]     = useState('created_at');
  const [sortDir,     setSortDir]     = useState('desc');
  const [colSearch,   setColSearch]   = useState({});        // per-column search
  const [hiddenCols,  setHiddenCols]  = useState(new Set()); // hidden column keys
  const isSuperAdmin  = user.role === 'superadmin';
  const isAccountMgr  = user.role === 'account_manager';
  const canFilterTenant = isSuperAdmin || isAccountMgr;

  const exportCsv = async () => {
    try {
      const p = {};
      if (filter !== 'all') p.status = filter;
      if (dateFrom)        p.dateFrom = dateFrom;
      if (dateTo)          p.dateTo   = dateTo;
      if (paramFilter)     p.param_name = paramFilter;
      if (jobFilter)       p.jobId = jobFilter;
      if (tenantFilter)    p.tenantId = tenantFilter;
      if (workflowFilter)  p.workflowId = workflowFilter;
      const qs  = new URLSearchParams(p).toString();
      const url = `/api/v1/runs/export${qs ? '?' + qs : ''}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${api.getToken()}` } });
      if (!res.ok) { alert(`Export failed: ${res.status}`); return; }
      const blob = await res.blob();
      const href = URL.createObjectURL(blob);
      const a = Object.assign(document.createElement('a'), { href, download: `visionai_runs_${new Date().toISOString().slice(0,10)}.csv` });
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(href);
    } catch(e) { alert('Export failed: ' + e.message); }
  };

  useEffect(() => {
    if (isSuperAdmin || isAccountMgr) api.tenants.list().then(r=>setTenants(r.data||[])).catch(()=>{});
    api.workflows.list({ limit:200 }).then(r=>setWorkflows(r.data||[])).catch(()=>{});
  }, [isSuperAdmin, isAccountMgr]);

  // Load all data for table view (uses same /runs/export endpoint)
  const loadTableData = async () => {
    setTableLoading(true);
    try {
      const p = {};
      if (filter !== 'all')  p.status      = filter;
      if (dateFrom)          p.dateFrom     = dateFrom;
      if (dateTo)            p.dateTo       = dateTo;
      if (paramFilter)       p.param_name   = paramFilter;
      if (jobFilter)         p.jobId        = jobFilter;
      if (tenantFilter)      p.tenantId     = tenantFilter;
      if (workflowFilter)    p.workflowId   = workflowFilter;
      p.limit = 5000;
      const qs  = new URLSearchParams(p).toString();
      const url = `/api/v1/runs/export${qs ? '?' + qs : ''}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${api.getToken()}` } });
      if (!res.ok) throw new Error(`Export failed: ${res.status}`);
      const text = await res.text();
      // Parse CSV — handles \r\n and \n line endings, RFC 4180 quoting
      // Normalise line endings then split
      const normalised = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').trim();
      const rows = normalised.split('\n');
      if (rows.length < 2) { setTableData([]); setTableLoading(false); return; }

      // RFC 4180 field parser — correctly handles quoted fields with embedded commas/newlines
      const parseCSVRow = (row) => {
        const fields = [];
        let pos = 0;
        while (pos <= row.length) {
          if (row[pos] === '"') {
            // Quoted field
            let field = '';
            pos++; // skip opening quote
            while (pos < row.length) {
              if (row[pos] === '"' && row[pos + 1] === '"') { field += '"'; pos += 2; }
              else if (row[pos] === '"') { pos++; break; } // closing quote
              else { field += row[pos]; pos++; }
            }
            fields.push(field);
            if (row[pos] === ',') pos++; // skip comma
          } else {
            // Unquoted field
            const end = row.indexOf(',', pos);
            if (end === -1) { fields.push(row.slice(pos)); pos = row.length + 1; }
            else { fields.push(row.slice(pos, end)); pos = end + 1; }
          }
        }
        return fields;
      };

      // Strip BOM if present
      const headerLine = rows[0].replace(/^\uFEFF/, '');
      const headers = parseCSVRow(headerLine);

      const parsed = rows.slice(1).filter(r => r.trim()).map(row => {
        const vals = parseCSVRow(row);
        const obj = {};
        headers.forEach((h, i) => { obj[h.trim()] = vals[i] !== undefined ? vals[i] : ''; });
        return obj;
      });
      setTableData(parsed);
    } catch(e) { console.error(e); }
    setTableLoading(false);
  };

  const load = useCallback(async () => {
    setLoading(true);
    const params = { page, limit:15 };
    if (filter!=='all') params.status = filter;
    if (dateFrom)       params.dateFrom = dateFrom;
    if (dateTo)         params.dateTo   = dateTo;
    if (paramFilter)    params.param_name = paramFilter;
    if (jobFilter)      params.jobId = jobFilter;
    if (tenantFilter)   params.tenantId = tenantFilter;
    if (workflowFilter) params.workflowId = workflowFilter;
    try { const r = await api.runs.list(params); setRuns(r.data||[]); setTotal(r.pagination?.total||0); }
    catch(e) { console.error(e); }
    setLoading(false);
  }, [filter, page, dateFrom, dateTo, paramFilter, jobFilter, tenantFilter, workflowFilter]);

  useEffect(()=>{ load(); },[load]);

  const clearAll = () => {
    setDateFrom(''); setDateTo(''); setParamFilter(''); setJobFilter('');
    setTenantFilter(''); setWfFilter(''); setFilter('all'); setPage(1);
  };

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20, flexWrap:'wrap', gap:10 }}>
        <div>
          <div style={{ fontSize:20, fontWeight:700, fontFamily:"'DM Serif Display',serif", color:C.text, marginBottom:4 }}>Validation Results</div>
          <div style={{ color:C.textSec, fontSize:13 }}>Browse, filter and inspect outcomes + Click a run to view details</div>
        </div>
        <div style={{ display:'flex', gap:6 }}>
          <Btn variant={viewMode==='card'?'secondary':'ghost'} onClick={()=>setViewMode('card')}>⊟ Cards</Btn>
          <Btn variant={viewMode==='table'?'secondary':'ghost'} onClick={()=>{
            setViewMode('table');
            if (!tableData.length) loadTableData();
          }}>⊞ Table</Btn>
          <Btn variant="ghost" onClick={exportCsv}>⬇ Export CSV</Btn>
        </div>
      </div>

      <Card style={{ marginBottom:16, padding:'14px 20px' }}>
        {/* Status filter row */}
        <div style={{ display:'flex', gap:4, marginBottom:12 }}>
          {['all','pass','fail','review','error'].map(f=>(
            <button key={f} onClick={()=>{ setFilter(f); setPage(1); }} style={{
              padding:'5px 12px', borderRadius:6, border:'none', cursor:'pointer', fontSize:11, fontWeight:600,
              fontFamily:"'DM Mono',monospace", textTransform:'uppercase',
              background:filter===f?C.accent:C.bg, color:filter===f?'#000':C.textSec,
              border:filter===f?'none':`1px solid ${C.border}` }}>{f}</button>
          ))}
        </div>
        {/* Filter inputs row — all same alignItems:center, marginBottom:0 to fix alignment */}
        <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'center' }}>
          {canFilterTenant && (
            <Select label="Tenant" value={tenantFilter} onChange={v=>{ setTenantFilter(v); setPage(1); }}
              options={[{value:'',label:'All tenants'},...tenants.map(t=>({value:t.id,label:t.name}))]} />
          )}
          <Select label="Workflow" value={workflowFilter} onChange={v=>{ setWfFilter(v); setPage(1); }}
            options={[{value:'',label:'All workflows'},...workflows.map(w=>({value:w.id,label:w.name}))]} />
          <Input label="From" type="date" value={dateFrom} onChange={setDateFrom} style={{ marginBottom:0, width:140 }} />
          <Input label="To"   type="date" value={dateTo}   onChange={setDateTo}   style={{ marginBottom:0, width:140 }} />
          <Input label="Param" value={paramFilter} onChange={setParamFilter} placeholder="param name" style={{ marginBottom:0, width:140 }} />
          <Input label="Job ID" value={jobFilter} onChange={setJobFilter} placeholder="job id" style={{ marginBottom:0, width:130 }} />
          <Btn variant="ghost" size="sm" style={{ marginTop:18 }} onClick={clearAll}>Clear</Btn>
        </div>
      </Card>

      {viewMode === 'card' && (
      loading ? <Spinner /> : (
        <>
          <div style={{ color:C.textMuted, fontSize:12, marginBottom:12 }}>{total} results</div>
          {runs.map(v=>{
            const cat=CATS[v.workflow_category]||CATS.custom;
            const sc=v.validation_status==='pass'?C.success:v.validation_status==='fail'?C.danger:C.warning;
            return (
              <div key={v.id} onClick={()=>setDetailRun(v)} style={{ marginBottom:10, cursor:'pointer' }}>
                <Card style={{ padding:0, overflow:'hidden' }}
                  onMouseEnter={e=>e.currentTarget.style.borderColor=C.accent}
                  onMouseLeave={e=>e.currentTarget.style.borderColor=C.border}>
                  <div style={{ display:'flex' }}>
                    <div style={{ width:4, background:sc, flexShrink:0 }} />
                    <div style={{ flex:1, padding:'12px 16px' }}>
                      <div style={{ display:'flex', alignItems:'flex-start', gap:12 }}>
                        <div style={{ fontSize:26, lineHeight:1 }}>{cat.icon}</div>
                        <div style={{ flex:1 }}>
                          <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4, flexWrap:'wrap', gap:6 }}>
                            <div style={{ fontSize:14, fontWeight:700, color:C.text }}>
                              {v.workflow_name}
                              {isSuperAdmin && v.tenant_name && <span style={{ fontSize:12, color:C.textMuted, fontWeight:400, marginLeft:8 }}>+ {v.tenant_name}</span>}
                            </div>
                            <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                              <span style={{ fontSize:11, color:C.textSec }}>{fmtDate(v.created_at)}</span>
                              {v.confidence!=null && <span style={{ fontSize:11, fontFamily:"'DM Mono',monospace", color:C.textSec }}>{(v.confidence*100).toFixed(0)}%</span>}
                              {v.submitted_by_name && <span style={{ fontSize:10, color:C.textMuted }}>by {v.submitted_by_name}</span>}
                              {user.role !== 'viewer' && v.ai_provider_type && <span style={{ fontSize:11 }}>{PROVIDER_ICONS[v.ai_provider_type]||''}</span>}
                              <Badge c={sc} label={v.validation_status} />
                            </div>
                          </div>
                          <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                            <Badge c={C.textMuted} label={v.param_name} />
                            {v.job_id && <Badge c={C.purple} label={`job: ${v.job_id}`} />}
                            {v.source_mode && v.source_mode!=='api' && <Badge c={C.textMuted} label={v.source_mode} />}
                          </div>
                          {v.image_url && <img src={v.image_url} alt="" style={{ marginTop:8, height:48, borderRadius:6, border:`1px solid ${C.border}`, objectFit:'cover' }} onError={e=>e.target.style.display='none'} />}
                          {v.parsed_result && (
                            <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginTop:8 }}>
                              {Object.entries(v.parsed_result).slice(0,6).map(([k,val])=>(
                                <div key={k} style={{ background:C.bg, borderRadius:6, padding:'3px 8px', border:`1px solid ${typeof val==='boolean'?(val?C.success+'44':C.danger+'44'):C.border}` }}>
                                  <span style={{ fontSize:10, color:C.textMuted, textTransform:'uppercase' }}>{k}: </span>
                                  <span style={{ fontSize:10, fontFamily:"'DM Mono',monospace", color:typeof val==='boolean'?(val?C.success:C.danger):C.text }}>
                                    {typeof val==='boolean'?(val?'✓':'✗'):String(val).slice(0,20)}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>
            );
          })}
          {total>15 && (
            <div style={{ display:'flex', justifyContent:'center', gap:8, marginTop:16 }}>
              <Btn variant="ghost" size="sm" disabled={page<=1} onClick={()=>setPage(p=>p-1)}>← Prev</Btn>
              <span style={{ color:C.textSec, fontSize:13, padding:'5px 12px' }}>Page {page} of {Math.ceil(total/15)}</span>
              <Btn variant="ghost" size="sm" disabled={page*15>=total} onClick={()=>setPage(p=>p+1)}>Next →</Btn>
            </div>
          )}
        </>
      )
      )}
      {/* ── Table view ─────────────────────────────────────────────────────── */}
      {viewMode === 'table' && (
        <ResultsDataTable
          data={tableData}
          loading={tableLoading}
          sortCol={sortCol} setSortCol={setSortCol}
          sortDir={sortDir} setSortDir={setSortDir}
          colSearch={colSearch} setColSearch={setColSearch}
          hiddenCols={hiddenCols} setHiddenCols={setHiddenCols}
          onRefresh={loadTableData}
          user={user}
        />
      )}
      <RunDetailModal run={detailRun} onClose={()=>setDetailRun(null)} />
    </div>
  );
}



// ── UserManagement ─────────────────────────────────────────────────────────────
function UserManagement({ user: currentUser }) {
  const [users,       setUsers]    = useState([]);
  const [tenants,     setTenants]  = useState([]);
  const [loading,     setLoading]  = useState(true);
  const [search,      setSearch]   = useState('');
  const [roleFilter,  setRole]     = useState('');
  const [tenantFilter,setTenantF]  = useState('');
  const [modal,       setModal]    = useState(false);
  const [form,        setForm]     = useState({});
  const [editUser,    setEditUser] = useState(null);
  const [tempPwd,     setTempPwd]  = useState(null);
  const [mgmtModal,   setMgmtModal]= useState(false);
  const [mgmtUser,    setMgmtUser] = useState(null);
  const [mgmtTenants, setMgmtT]   = useState([]);
  const [mgmtSelected,setMgmtSel] = useState([]);

  const isSuperAdmin = currentUser.role === 'superadmin';

  const load = useCallback(async () => {
    setLoading(true);
    const p = {};
    if (search)      p.search   = search;
    if (roleFilter)  p.role     = roleFilter;
    if (tenantFilter)p.tenantId = tenantFilter;
    try {
      const [ur, tr] = await Promise.all([api.users.list(p), api.tenants.list()]);
      setUsers(ur.data||[]); setTenants(tr.data||[]);
    } catch(e) { console.error(e); }
    setLoading(false);
  }, [search, roleFilter, tenantFilter]);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditUser(null); setTempPwd(null);
    setForm({ name:'', email:'', role:'viewer', tenantId:'' });
    setModal(true);
  };
  const openEdit = (u) => {
    setEditUser(u); setTempPwd(null);
    setForm({ name:u.name, email:u.email, role:u.role, tenantId:u.tenant_id, isActive:u.is_active, externalUserId:u.external_user_id||'' });
    setModal(true);
  };

  const save = async () => {
    try {
      if (editUser) {
        await api.users.update(editUser.id, { name:form.name, role:form.role, isActive:form.isActive, externalUserId:form.externalUserId||undefined });
      } else {
        const r = await api.users.create({ name:form.name, email:form.email, role:form.role, tenantId:form.tenantId||undefined, externalUserId:form.externalUserId||undefined });
        setTempPwd(r.data?.temp_password);
      }
      if (!tempPwd) { setModal(false); load(); }
      else load();
    } catch(e) { alert(e.message); }
  };

  const resetPwd = async (u) => {
    if (!confirm(`Reset password for ${u.name}?`)) return;
    try {
      const r = await api.users.resetPassword(u.id);
      alert(`New temp password for ${u.name}:\n\n${r.data?.temp_password}\n\nShare securely — shown once only.`);
    } catch(e) { alert(e.message); }
  };

  const deactivate = async (u) => {
    if (!confirm(`Deactivate ${u.name}? They will no longer be able to log in.`)) return;
    try { await api.users.deactivate(u.id); load(); } catch(e) { alert(e.message); }
  };

  const openManagedTenants = async (u) => {
    setMgmtUser(u);
    const r = await api.users.getManagedTenants(u.id).catch(()=>({data:[]}));
    setMgmtSel((r.data||[]).map(t=>t.tenant_id));
    setMgmtT(tenants);
    setMgmtModal(true);
  };
  const saveManagedTenants = async () => {
    try { await api.users.setManagedTenants(mgmtUser.id, { tenantIds: mgmtSelected }); setMgmtModal(false); load(); }
    catch(e) { alert(e.message); }
  };

  const ROLE_COLORS = { superadmin:C.purple, account_admin:C.accent, account_manager:C.warning, viewer:C.textSec };

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20, flexWrap:'wrap', gap:10 }}>
        <div>
          <div style={{ fontSize:20, fontWeight:700, fontFamily:"'DM Serif Display',serif", color:C.text }}>User Management</div>
          <div style={{ color:C.textSec, fontSize:13 }}>Create and manage platform users across all tenants</div>
        </div>
        <Btn onClick={openCreate}>＋ New User</Btn>
      </div>

      <Card style={{ marginBottom:16, padding:'12px 16px' }}>
        <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'center' }}>
          <Input value={search} onChange={setSearch} placeholder="Search name / email…" style={{ marginBottom:0, minWidth:200 }} />
          <Select value={roleFilter} onChange={setRole} options={[{value:'',label:'All roles'},{value:'superadmin',label:'Superadmin'},{value:'account_admin',label:'Account Admin'},{value:'account_manager',label:'Account Manager'},{value:'viewer',label:'Viewer'}]} />
          <Select value={tenantFilter} onChange={setTenantF} options={[{value:'',label:'All tenants'},...tenants.map(t=>({value:t.id,label:t.name}))]} />
          <Btn variant="ghost" size="sm" onClick={()=>{ setSearch(''); setRole(''); setTenantF(''); }}>Clear</Btn>
        </div>
      </Card>

      {loading ? <Spinner /> : (
        <Card style={{ padding:0, overflow:'hidden' }}>
          {users.length===0 ? <div style={{ textAlign:'center', padding:40, color:C.textMuted }}>No users found</div> : (
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr style={{ background:C.bg }}>
                {['Name','Email','Role','Tenant','Status','Auth','Created By',''].map(h=>(
                  <th key={h} style={{ padding:'8px 14px', textAlign:'left', fontSize:11, color:C.textSec, fontWeight:600, letterSpacing:'0.06em', textTransform:'uppercase', fontFamily:"'DM Mono',monospace", borderBottom:`1px solid ${C.border}` }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {users.map(u=>(
                  <tr key={u.id} style={{ borderBottom:`1px solid ${C.border}`, opacity:u.is_active===false?0.5:1 }}
                    onMouseEnter={e=>e.currentTarget.style.background=C.surfaceHover}
                    onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                    <td style={{ padding:'9px 14px' }}>
                      <div style={{ fontSize:13, fontWeight:600, color:C.text }}>{u.name}</div>
                      {u.must_change_password && <div style={{ fontSize:10, color:C.warning }}>⚠ Must change password</div>}
                    </td>
                    <td style={{ padding:'9px 14px', fontSize:12, color:C.textSec }}>{u.email}</td>
                    <td style={{ padding:'9px 14px' }}><Badge c={ROLE_COLORS[u.role]||C.textMuted} label={u.role} /></td>
                    <td style={{ padding:'9px 14px', fontSize:12, color:C.textMuted }}>
                      {u.role === 'account_manager' && u.managed_tenants?.length > 0
                        ? <span title={(u.managed_tenants||[]).map(t=>t.name).join(', ')}>
                            {u.managed_tenants.length} tenant{u.managed_tenants.length!==1?'s':''}
                            <span style={{ fontSize:10, color:C.textDim||C.textMuted }}> ({(u.managed_tenants||[]).slice(0,2).map(t=>t.name).join(', ')}{u.managed_tenants.length>2?'…':''}) </span>
                          </span>
                        : u.tenant_name||'—'}
                    </td>
                    <td style={{ padding:'9px 14px' }}><Badge c={u.is_active!==false?C.success:C.danger} label={u.is_active!==false?'active':'inactive'} /></td>
                    <td style={{ padding:'9px 14px' }}><Badge c={u.is_sso?C.accent:C.textMuted} label={u.is_sso?'SSO':'Manual'} /></td>
                    <td style={{ padding:'9px 14px', fontSize:11, color:C.textMuted }}>{u.created_by_name||'—'}</td>
                    <td style={{ padding:'9px 14px' }}>
                      <div style={{ display:'flex', gap:4, flexWrap:'wrap' }}>
                        <Btn size="sm" variant="secondary" onClick={()=>openEdit(u)}>Edit</Btn>
                        {u.role==='account_manager' && <Btn size="sm" variant="ghost" onClick={()=>openManagedTenants(u)}>Tenants</Btn>}
                        {!u.is_sso && u.is_active!==false && (currentUser.role!=='account_manager'||u.role==='viewer') && <Btn size="sm" variant="ghost" onClick={()=>resetPwd(u)}>Reset Pwd</Btn>}
                        {u.is_active!==false && u.id!==currentUser.id && (currentUser.role!=='account_manager'||u.role==='viewer') && <Btn size="sm" variant="ghost" onClick={()=>deactivate(u)}>Deactivate</Btn>}
                        {u.id!==currentUser.id && (currentUser.role!=='account_manager'||u.role==='viewer') && <Btn size="sm" variant="danger" onClick={async ()=>{
                          if(!confirm(`Permanently delete ${u.name}? This removes all their data and cannot be undone.`)) return;
                          try { await api.users.deactivate(u.id, true); load(); } catch(e) { alert(e.message); }
                        }}>Delete</Btn>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}

      {/* Create/Edit modal */}
      <Modal open={modal} onClose={()=>{ setModal(false); setTempPwd(null); load(); }} title={editUser?'Edit User':'New User'} width={520}>
        {tempPwd ? (
          <div>
            <div style={{ background:C.successDim, border:`1px solid ${C.success}44`, borderRadius:10, padding:16, marginBottom:16 }}>
              <div style={{ fontSize:14, fontWeight:700, color:C.success, marginBottom:8 }}>✓ User created successfully</div>
              <div style={{ fontSize:13, color:C.textSec, marginBottom:10 }}>Share this temporary password securely with the user. It is shown only once.</div>
              <div style={{ fontFamily:"'DM Mono',monospace", fontSize:16, color:C.text, background:C.bg, padding:12, borderRadius:8, letterSpacing:'0.1em', textAlign:'center' }}>{tempPwd}</div>
              <Btn variant="success" style={{ marginTop:12, width:'100%' }} onClick={()=>navigator.clipboard?.writeText(tempPwd)}>📋 Copy Password</Btn>
            </div>
            <div style={{ fontSize:12, color:C.textMuted, marginBottom:16 }}>The user will be prompted to change their password on first login.</div>
            <Btn style={{ width:'100%' }} onClick={()=>{ setModal(false); setTempPwd(null); load(); }}>Done</Btn>
          </div>
        ) : (
          <div>
            <Input label="Full Name *" value={form.name||''} onChange={v=>setForm(f=>({...f,name:v}))} placeholder="Jane Smith" />
            {!editUser && <Input label="Email Address *" value={form.email||''} onChange={v=>setForm(f=>({...f,email:v}))} placeholder="jane@company.com" />}
            <Select label="Role *" value={form.role||'viewer'} onChange={v=>setForm(f=>({...f,role:v}))}
              options={[{value:'account_admin',label:'Account Admin'},{value:'account_manager',label:'Account Manager'},{value:'viewer',label:'Viewer'}]} />
            {!editUser && (
              <Select label="Tenant *" value={form.tenantId||''} onChange={v=>setForm(f=>({...f,tenantId:v}))}
                options={[{value:'',label:'-- select tenant --'},...tenants.map(t=>({value:t.id,label:t.name}))]} />
            )}
            <Input label="External User ID (optional)" value={form.externalUserId||''} onChange={v=>setForm(f=>({...f,externalUserId:v}))} placeholder="ID from workflow engine" />
            {editUser && (
              <div onClick={()=>setForm(f=>({...f,isActive:!f.isActive}))} style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', padding:'8px 0' }}>
                <div style={{ width:16, height:16, borderRadius:3, background:form.isActive!==false?C.success:C.bg, border:`2px solid ${form.isActive!==false?C.success:C.border}`, display:'flex', alignItems:'center', justifyContent:'center' }}>
                  {form.isActive!==false && <span style={{ color:'#000', fontSize:10 }}>✓</span>}
                </div>
                <span style={{ fontSize:12, color:C.text }}>Active</span>
              </div>
            )}
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
              <Btn variant="ghost" onClick={()=>setModal(false)}>Cancel</Btn>
              <Btn onClick={save} disabled={!form.name||(!editUser&&(!form.email||!form.tenantId))}>{editUser?'Save Changes':'Create User'}</Btn>
            </div>
          </div>
        )}
      </Modal>

      {/* Account Manager tenant mapping modal */}
      <Modal open={mgmtModal} onClose={()=>setMgmtModal(false)} title={`Managed Tenants — ${mgmtUser?.name||''}`} width={480}>
        <div style={{ fontSize:12, color:C.textSec, marginBottom:14 }}>Select tenants this account manager can access:</div>
        <div style={{ maxHeight:340, overflowY:'auto', marginBottom:16 }}>
          {mgmtTenants.map(t=>(
            <div key={t.id} onClick={()=>setMgmtSel(s=>s.includes(t.id)?s.filter(x=>x!==t.id):[...s,t.id])}
              style={{ display:'flex', alignItems:'center', gap:10, padding:'9px 12px', cursor:'pointer', borderRadius:8, marginBottom:4,
                background:mgmtSelected.includes(t.id)?C.accentDim:C.bg, border:`1px solid ${mgmtSelected.includes(t.id)?C.accent+'44':C.border}` }}>
              <div style={{ width:16, height:16, borderRadius:3, background:mgmtSelected.includes(t.id)?C.accent:C.bg, border:`2px solid ${mgmtSelected.includes(t.id)?C.accent:C.border}`, display:'flex', alignItems:'center', justifyContent:'center' }}>
                {mgmtSelected.includes(t.id) && <span style={{ color:'#000', fontSize:10, fontWeight:700 }}>✓</span>}
              </div>
              <div><div style={{ fontSize:13, fontWeight:600, color:C.text }}>{t.name}</div><div style={{ fontSize:10, color:C.textMuted, fontFamily:"'DM Mono',monospace" }}>{t.slug}</div></div>
            </div>
          ))}
        </div>
        <div style={{ fontSize:12, color:C.textSec, marginBottom:14 }}>{mgmtSelected.length} tenant{mgmtSelected.length!==1?'s':''} selected</div>
        <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
          <Btn variant="ghost" onClick={()=>setMgmtModal(false)}>Cancel</Btn>
          <Btn onClick={saveManagedTenants}>Save Mapping</Btn>
        </div>
      </Modal>
    </div>
  );
}

// ── TokenBudgetViewer — read-only token budget view for account_admin/manager ──
function TokenBudgetViewer({ user }) {
  const [budgets,    setBudgets]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [tenantFilter, setTenantFilter] = useState('');
  const [usageModal,  setUsageModal]  = useState(false);
  const [topUpsModal, setTopUpsModal] = useState(false);
  const [selTid,      setSelTid]      = useState(null);
  const [usageData,   setUsageData]   = useState(null);
  const [topUpsData,  setTopUpsData]  = useState([]);

  const isAccountMgr = user.role === 'account_manager';

  useEffect(() => {
    setLoading(true);
    api.tokenBudgets.list()
      .then(r => setBudgets(r.data||[]))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const viewUsage = async (tid) => {
    setSelTid(tid); setUsageData(null); setUsageModal(true);
    try { const r = await api.tokenBudgets.usage(tid, { groupBy:'day' }); setUsageData(r.data); }
    catch(e) { setUsageData({ error: e.message }); }
  };
  const viewTopUps = async (tid) => {
    setSelTid(tid); setTopUpsData([]); setTopUpsModal(true);
    try { const r = await api.tokenBudgets.topUps(tid); setTopUpsData(r.data||[]); }
    catch(e) { console.error(e); }
  };

  const fmtNum = n => parseInt(n||0).toLocaleString();
  const pct = (used, limit) => limit ? Math.min(Math.round(Number(used)/Number(limit)*100), 100) : 0;
  const barColor = p => p >= 90 ? C.danger : p >= 70 ? C.warning : C.success;

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20, flexWrap:'wrap', gap:10 }}>
        <div>
          <div style={{ fontSize:20, fontWeight:700, fontFamily:"'DM Serif Display',serif", color:C.text }}>Token Budgets</div>
          <div style={{ color:C.textSec, fontSize:13 }}>
            View token pool balances and usage for {isAccountMgr ? 'your mapped accounts' : 'your account'}
          </div>
        </div>
        {budgets.length > 1 && (
          <select value={tenantFilter} onChange={e=>setTenantFilter(e.target.value)} style={{
            background:C.bg, border:`1px solid ${C.border}`, borderRadius:8, padding:'7px 12px',
            color:tenantFilter?C.text:C.textMuted, fontSize:13, fontFamily:"'DM Sans',sans-serif" }}>
            <option value="">All accounts</option>
            {budgets.map(b=><option key={b.tenant_id} value={b.tenant_id}>{b.tenant_name}</option>)}
          </select>
        )}
      </div>

      <div style={{ background:C.bg, border:`1px solid ${C.border}`, borderRadius:10, padding:'10px 16px', marginBottom:20, fontSize:12, color:C.textMuted }}>
        ℹ Read-only view. Contact a superadmin to top up tokens or change rate limits.
      </div>

      {loading ? <Spinner /> : budgets.length === 0 ? (
        <Card style={{ textAlign:'center', padding:40, color:C.textMuted }}>
          No token budgets configured for your account{isAccountMgr ? 's' : ''}. Contact your administrator.
        </Card>
      ) : budgets.filter(b => !tenantFilter || b.tenant_id === tenantFilter).map(b => {
        const dailyPct  = pct(b.daily_used||0,  b.daily_limit);
        const weeklyPct = pct(b.weekly_used||0, b.weekly_limit);
        const poolBal   = Number(b.pool_balance  || 0);
        const totalPur  = Number(b.total_purchased || 0);
        const poolPct   = totalPur > 0 ? pct(totalPur - Math.max(0,poolBal), totalPur) : 0;
        return (
          <Card key={b.tenant_id} style={{ marginBottom:12 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:16, flexWrap:'wrap' }}>
              <div style={{ flex:1 }}>
                <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12 }}>
                  <div style={{ fontSize:14, fontWeight:700, color:C.text }}>{b.tenant_name}</div>
                  <Badge c={b.is_enforced?C.success:C.warning} label={b.is_enforced?'enforced':'tracking'} />
                </div>

                {/* Token Pool */}
                {totalPur > 0 ? (
                  <div style={{ marginBottom:10, padding:'10px 12px', background:C.bg, borderRadius:8, border:`1px solid ${C.border}` }}>
                    <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:C.textSec, marginBottom:6 }}>
                      <span style={{ fontWeight:600 }}>🪙 Token Pool</span>
                      <span style={{ color: poolBal <= 0 ? C.danger : poolBal < 5000 ? C.warning : C.success, fontWeight:700 }}>
                        {fmtNum(Math.max(0, poolBal))} available
                      </span>
                    </div>
                    <div style={{ height:8, background:C.border, borderRadius:4, marginBottom:4 }}>
                      <div style={{ width:`${poolPct}%`, height:'100%', background:barColor(poolPct), borderRadius:4 }} />
                    </div>
                    <div style={{ fontSize:10, color:C.textMuted }}>
                      {fmtNum(b.total_consumed||0)} consumed of {fmtNum(totalPur)} total
                    </div>
                  </div>
                ) : (
                  <div style={{ marginBottom:10, padding:'8px 12px', background:C.bg, borderRadius:8, border:`1px dashed ${C.border}`, fontSize:12, color:C.textMuted }}>
                    🪙 No token pool configured
                  </div>
                )}

                {b.daily_limit && (
                  <div style={{ marginBottom:6 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:C.textSec, marginBottom:3 }}>
                      <span>⏱ Daily: {fmtNum(b.daily_used||0)} / {fmtNum(b.daily_limit)}</span>
                      <span style={{ color:barColor(dailyPct) }}>{dailyPct}%</span>
                    </div>
                    <div style={{ height:6, background:C.border, borderRadius:3 }}>
                      <div style={{ width:`${dailyPct}%`, height:'100%', background:barColor(dailyPct), borderRadius:3 }} />
                    </div>
                  </div>
                )}
                {b.weekly_limit && (
                  <div style={{ marginBottom:4 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:C.textSec, marginBottom:3 }}>
                      <span>📅 Weekly: {fmtNum(b.weekly_used||0)} / {fmtNum(b.weekly_limit)}</span>
                      <span style={{ color:barColor(weeklyPct) }}>{weeklyPct}%</span>
                    </div>
                    <div style={{ height:6, background:C.border, borderRadius:3 }}>
                      <div style={{ width:`${weeklyPct}%`, height:'100%', background:barColor(weeklyPct), borderRadius:3 }} />
                    </div>
                  </div>
                )}
              </div>

              <div style={{ display:'flex', flexDirection:'column', gap:6, flexShrink:0 }}>
                <Btn size="sm" variant="ghost" onClick={()=>viewTopUps(b.tenant_id)}>📋 History</Btn>
                <Btn size="sm" variant="ghost" onClick={()=>viewUsage(b.tenant_id)}>📊 Usage</Btn>
              </div>
            </div>
          </Card>
        );
      })}

      {/* Usage modal */}
      <Modal open={usageModal} onClose={()=>setUsageModal(false)} title="Token Usage" width={620}>
        {!usageData ? <Spinner /> : usageData.error ? <div style={{color:C.danger}}>{usageData.error}</div> : (
          <Card style={{ padding:0, overflow:'hidden' }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr style={{ background:C.bg }}>
                {['Date','Runs','Tokens In','Tokens Out','Total'].map(h=>(
                  <th key={h} style={{ padding:'8px 12px', textAlign:'left', fontSize:11, color:C.textSec, fontWeight:600, textTransform:'uppercase', fontFamily:"'DM Mono',monospace", borderBottom:`1px solid ${C.border}` }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {(usageData.rows||[]).map((r,i)=>(
                  <tr key={i} style={{ borderBottom:`1px solid ${C.border}` }}>
                    <td style={{ padding:'8px 12px', fontSize:12, fontFamily:"'DM Mono',monospace" }}>{r.grp_key}</td>
                    <td style={{ padding:'8px 12px', fontSize:12, color:C.textSec }}>{r.run_count}</td>
                    <td style={{ padding:'8px 12px', fontSize:12, fontFamily:"'DM Mono',monospace", color:C.textSec }}>{parseInt(r.tokens_in||0).toLocaleString()}</td>
                    <td style={{ padding:'8px 12px', fontSize:12, fontFamily:"'DM Mono',monospace", color:C.textSec }}>{parseInt(r.tokens_out||0).toLocaleString()}</td>
                    <td style={{ padding:'8px 12px', fontSize:12, fontWeight:600, color:C.accent, fontFamily:"'DM Mono',monospace" }}>{parseInt(r.total_tokens||0).toLocaleString()}</td>
                  </tr>
                ))}
                {!(usageData.rows||[]).length && <tr><td colSpan={5} style={{textAlign:'center',padding:24,color:C.textMuted}}>No usage data</td></tr>}
              </tbody>
            </table>
          </Card>
        )}
      </Modal>

      {/* Top-ups history modal */}
      <Modal open={topUpsModal} onClose={()=>setTopUpsModal(false)} title="Top-Up History" width={560}>
        <Card style={{ padding:0, overflow:'hidden' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead><tr style={{ background:C.bg }}>
              {['Date','Tokens','Reference','Expires'].map(h=>(
                <th key={h} style={{ padding:'8px 12px', textAlign:'left', fontSize:11, color:C.textSec, fontWeight:600, textTransform:'uppercase', fontFamily:"'DM Mono',monospace", borderBottom:`1px solid ${C.border}` }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {topUpsData.map(r=>(
                <tr key={r.id} style={{ borderBottom:`1px solid ${C.border}` }}>
                  <td style={{ padding:'8px 12px', fontSize:12, fontFamily:"'DM Mono',monospace" }}>{fmtDate(r.created_at)}</td>
                  <td style={{ padding:'8px 12px', fontSize:12, fontWeight:700, color:C.success, fontFamily:"'DM Mono',monospace" }}>+{fmtNum(r.tokens)}</td>
                  <td style={{ padding:'8px 12px', fontSize:12, color:C.accent }}>{r.reference||'—'}</td>
                  <td style={{ padding:'8px 12px', fontSize:11, color: r.expires_at && new Date(r.expires_at) < new Date() ? C.danger : C.textMuted }}>
                    {r.expires_at ? (new Date(r.expires_at) < new Date() ? '⚠ Expired' : fmtDate(r.expires_at)) : 'Never'}
                  </td>
                </tr>
              ))}
              {!topUpsData.length && <tr><td colSpan={4} style={{textAlign:'center',padding:24,color:C.textMuted}}>No top-ups recorded</td></tr>}
            </tbody>
          </table>
        </Card>
      </Modal>
    </div>
  );
}

// ── TokenBudgetManagement ─────────────────────────────────────────────────────
function TokenBudgetManagement() {
  const [budgets,      setBudgets]      = useState([]);
  const [tenants,      setTenants]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [tenantFilter, setTenantFilter] = useState('');
  const [rateModal,  setRateModal]  = useState(false);   // rate-limit edit
  const [topUpModal, setTopUpModal] = useState(false);   // token top-up
  const [usageModal, setUsageModal] = useState(false);
  const [topUpsModal,setTopUpsModal]= useState(false);   // top-up history
  const [selTid,     setSelTid]     = useState(null);    // selected tenant id
  const [rateForm,   setRateForm]   = useState({});
  const [topUpForm,  setTopUpForm]  = useState({});
  const [usageData,  setUsageData]  = useState(null);
  const [topUpsData, setTopUpsData] = useState([]);
  const [editTuModal,setEditTuModal]= useState(false);
  const [editTuItem, setEditTuItem] = useState(null);
  const [editTuForm, setEditTuForm] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [br, tr] = await Promise.all([api.tokenBudgets.list(), api.tenants.list()]);
      setBudgets(br.data||[]);
      // Show all tenants — budget cards will appear for any with a pool or rate config
      setTenants(tr.data||[]);
    } catch(e) { console.error(e); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openRateEdit = (b) => {
    setSelTid(b.tenant_id);
    setRateForm({ tenantId: b.tenant_id, dailyLimit: b.daily_limit||'', weeklyLimit: b.weekly_limit||'', minTokensRequired: b.min_tokens_required||1000, alertThresholdPct: b.alert_threshold_pct||80, isEnforced: b.is_enforced!==false, notes: b.notes||'' });
    setRateModal(true);
  };
  const openNewRate = () => {
    setSelTid(null);
    setRateForm({ tenantId:'', dailyLimit:'', weeklyLimit:'', minTokensRequired:1000, alertThresholdPct:80, isEnforced:true, notes:'' });
    setRateModal(true);
  };
  const saveRate = async () => {
    try {
      const tid = selTid || rateForm.tenantId;
      if (!tid) return alert('Select a tenant');
      await api.tokenBudgets.save(tid, { dailyLimit:rateForm.dailyLimit?parseInt(rateForm.dailyLimit):null, weeklyLimit:rateForm.weeklyLimit?parseInt(rateForm.weeklyLimit):null, minTokensRequired:parseInt(rateForm.minTokensRequired)||1000, alertThresholdPct:parseInt(rateForm.alertThresholdPct)||80, isEnforced:rateForm.isEnforced!==false, notes:rateForm.notes||null });
      setRateModal(false); load();
    } catch(e) { alert(e.message); }
  };

  const openTopUp = (tid) => {
    setSelTid(tid);
    setTopUpForm({ tokens:'', reference:'', notes:'', expiresAt:'' });
    setTopUpModal(true);
  };
  const saveTopUp = async () => {
    try {
      if (!topUpForm.tokens || parseInt(topUpForm.tokens) < 1) return alert('Enter a valid token amount');
      const r = await api.tokenBudgets.topUp(selTid, { tokens: parseInt(topUpForm.tokens), reference: topUpForm.reference||undefined, notes: topUpForm.notes||undefined, expiresAt: topUpForm.expiresAt||undefined });
      setTopUpModal(false);
      const bal = r.data?.balance;
      if (bal) alert(`✓ ${parseInt(topUpForm.tokens).toLocaleString()} tokens added.\nNew pool balance: ${parseInt(bal.pool_balance||0).toLocaleString()} tokens`);
      load();
    } catch(e) { alert(e.message); }
  };

  const openEditTopUp = (r) => {
    setEditTuItem(r);
    setEditTuForm({
      tokens:    r.tokens,
      reference: r.reference||'',
      notes:     r.notes||'',
      expiresAt: r.expires_at ? new Date(r.expires_at).toISOString().slice(0,10) : '',
    });
    setEditTuModal(true);
  };
  const saveEditTopUp = async () => {
    try {
      const body = {
        tokens:    parseInt(editTuForm.tokens)||editTuItem.tokens,
        reference: editTuForm.reference||null,
        notes:     editTuForm.notes||null,
        expiresAt: editTuForm.expiresAt||null,
      };
      await api.tokenBudgets.editTopUp(editTuItem.id, body);
      setEditTuModal(false);
      const r = await api.tokenBudgets.topUps(selTid);
      setTopUpsData(r.data||[]);
      load();
    } catch(e) { alert(e.message); }
  };
  const forceExpireTopUp = async (id) => {
    if (!confirm('Force-expire this top-up immediately? Its remaining tokens will no longer count.')) return;
    try {
      await api.tokenBudgets.expireTopUp(id);
      const r = await api.tokenBudgets.topUps(selTid);
      setTopUpsData(r.data||[]);
      load();
    } catch(e) { alert(e.message); }
  };
  const deleteTopUpRecord = async (id) => {
    if (!confirm('Delete this top-up record? This adjusts the pool balance permanently.')) return;
    try {
      await api.tokenBudgets.deleteTopUp(id);
      const r = await api.tokenBudgets.topUps(selTid);
      setTopUpsData(r.data||[]);
      load();
    } catch(e) { alert(e.message); }
  };

    const viewUsage = async (tid) => {
    setSelTid(tid); setUsageData(null); setUsageModal(true);
    try { const r = await api.tokenBudgets.usage(tid, { groupBy:'day' }); setUsageData(r.data); }
    catch(e) { setUsageData({ error:e.message }); }
  };
  const viewTopUps = async (tid) => {
    setSelTid(tid); setTopUpsData([]); setTopUpsModal(true);
    try { const r = await api.tokenBudgets.topUps(tid); setTopUpsData(r.data||[]); }
    catch(e) { console.error(e); }
  };
  const removeRateLimit = async (tid) => {
    if (!confirm('Remove rate limits for this tenant? The token pool (purchased tokens) will remain.')) return;
    try { await api.tokenBudgets.remove(tid); load(); } catch(e) { alert(e.message); }
  };

  const budgetedIds = new Set(budgets.map(b => b.tenant_id));
  const pct = (used, limit) => limit ? Math.min(Math.round(Number(used)/Number(limit)*100), 100) : 0;
  const barColor = (p) => p >= 90 ? C.danger : p >= 70 ? C.warning : C.success;
  const fmtNum = (n) => parseInt(n||0).toLocaleString();

  // Tenants with a pool top-up OR a rate limit
  const configuredIds = new Set([...budgets.map(b=>b.tenant_id), ...budgets.filter(b=>b.total_purchased>0).map(b=>b.tenant_id)]);

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20, flexWrap:'wrap', gap:10 }}>
        <div>
          <div style={{ fontSize:20, fontWeight:700, fontFamily:"'DM Serif Display',serif", color:C.text }}>Token Management</div>
          <div style={{ color:C.textSec, fontSize:13 }}>Top-up token pools + Set daily/weekly rate limits + Track usage</div>
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          {tenants.length > 0 && (
            <select value={tenantFilter} onChange={e=>setTenantFilter(e.target.value)} style={{
              background:C.bg, border:`1px solid ${C.border}`, borderRadius:8, padding:'7px 12px',
              color:tenantFilter?C.text:C.textMuted, fontSize:13, fontFamily:"'DM Sans',sans-serif" }}>
              <option value="">All tenants</option>
              {tenants.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          )}
          <Btn variant="secondary" onClick={openNewRate}>⚙ Set Rate Limits</Btn>
        </div>
      </div>

      {/* Explanation banner */}
      <Card style={{ marginBottom:16, background:C.accentDim, border:`1px solid ${C.accent}33`, padding:'12px 16px' }}>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
          <div>
            <div style={{ fontSize:12, fontWeight:700, color:C.accent, marginBottom:4 }}>🪙 Token Pool (top-ups)</div>
            <div style={{ fontSize:11, color:C.textSec, lineHeight:1.6 }}>
              Total tokens purchased or granted. Added by superadmin with reference/PO.
              Requests are blocked when pool balance falls below the minimum threshold.
            </div>
          </div>
          <div>
            <div style={{ fontSize:12, fontWeight:700, color:C.purple, marginBottom:4 }}>⏱ Rate Limits (daily/weekly)</div>
            <div style={{ fontSize:11, color:C.textSec, lineHeight:1.6 }}>
              Maximum tokens that can be consumed per day or week.
              Even if pool has tokens, daily cap prevents runaway usage. Resets automatically.
            </div>
          </div>
        </div>
      </Card>

      {loading ? <Spinner /> : (
        <>
          {/* One card per configured tenant */}
          {tenants.filter(t => !tenantFilter || t.id === tenantFilter).map(t => {
            const b = budgets.find(x => x.tenant_id === t.id);
            if (!b) return null;  // only show tenants with a budget or pool configuration
            const dailyPct  = b ? pct(b.daily_used||0,  b.daily_limit)  : 0;
            const weeklyPct = b ? pct(b.weekly_used||0, b.weekly_limit) : 0;
            const poolBal   = Number(b?.pool_balance  || 0);
            const totalPur  = Number(b?.total_purchased || 0);
            const poolPct   = totalPur > 0 ? pct(totalPur - poolBal, totalPur) : 0;
            return (
              <Card key={t.id} style={{ marginBottom:12 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:16, flexWrap:'wrap' }}>
                  <div style={{ flex:1, minWidth:280 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12 }}>
                      <div style={{ fontSize:14, fontWeight:700, color:C.text }}>{t.name}</div>
                      {b && <Badge c={b.is_enforced?C.success:C.warning} label={b.is_enforced?'enforced':'tracking'} />}
                    </div>

                    {/* Token Pool */}
                    {totalPur > 0 ? (
                      <div style={{ marginBottom:10, padding:'10px 12px', background:C.bg, borderRadius:8, border:`1px solid ${C.border}` }}>
                        <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:C.textSec, marginBottom:6 }}>
                          <span style={{ fontWeight:600 }}>🪙 Token Pool</span>
                          <span style={{ color: poolBal <= 0 ? C.danger : poolBal < (b?.min_tokens_required||1000)*5 ? C.warning : C.success, fontWeight:700 }}>
                            {fmtNum(Math.max(0, poolBal))} available
                          </span>
                        </div>
                        <div style={{ height:8, background:C.border, borderRadius:4, marginBottom:4 }}>
                          <div style={{ width:`${poolPct}%`, height:'100%', background:barColor(poolPct), borderRadius:4 }} />
                        </div>
                        <div style={{ fontSize:10, color:C.textMuted }}>
                          {fmtNum(b?.total_consumed||0)} consumed of {fmtNum(totalPur)} total purchased
                        </div>
                      </div>
                    ) : (
                      <div style={{ marginBottom:10, padding:'8px 12px', background:C.bg, borderRadius:8, border:`1px dashed ${C.border}`, fontSize:12, color:C.textMuted }}>
                        🪙 No token pool — click <strong>＋ Top Up</strong> to add tokens
                      </div>
                    )}

                    {/* Rate limits */}
                    {b && b.daily_limit && (
                      <div style={{ marginBottom:6 }}>
                        <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:C.textSec, marginBottom:3 }}>
                          <span>⏱ Daily: {fmtNum(b.daily_used||0)} / {fmtNum(b.daily_limit)}</span>
                          <span style={{ color:barColor(dailyPct) }}>{dailyPct}%</span>
                        </div>
                        <div style={{ height:6, background:C.border, borderRadius:3 }}>
                          <div style={{ width:`${dailyPct}%`, height:'100%', background:barColor(dailyPct), borderRadius:3 }} />
                        </div>
                      </div>
                    )}
                    {b && b.weekly_limit && (
                      <div style={{ marginBottom:6 }}>
                        <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:C.textSec, marginBottom:3 }}>
                          <span>📅 Weekly: {fmtNum(b.weekly_used||0)} / {fmtNum(b.weekly_limit)}</span>
                          <span style={{ color:barColor(weeklyPct) }}>{weeklyPct}%</span>
                        </div>
                        <div style={{ height:6, background:C.border, borderRadius:3 }}>
                          <div style={{ width:`${weeklyPct}%`, height:'100%', background:barColor(weeklyPct), borderRadius:3 }} />
                        </div>
                      </div>
                    )}
                    {b && !b.daily_limit && !b.weekly_limit && (
                      <div style={{ fontSize:11, color:C.textMuted }}>No rate limits configured</div>
                    )}
                    {b && (b.daily_limit || b.weekly_limit) && (
                      <div style={{ fontSize:10, color:C.textMuted, marginTop:4 }}>
                        Min threshold: {fmtNum(b.min_tokens_required||1000)} + Alert at {b.alert_threshold_pct||80}%
                        {b.notes && <span> + {b.notes}</span>}
                      </div>
                    )}
                  </div>

                  <div style={{ display:'flex', flexDirection:'column', gap:6, flexShrink:0 }}>
                    <Btn size="sm" onClick={()=>openTopUp(t.id)}>＋ Top Up</Btn>
                    <Btn size="sm" variant="ghost" onClick={()=>viewTopUps(t.id)}>📋 History</Btn>
                    <Btn size="sm" variant="ghost" onClick={()=>viewUsage(t.id)}>📊 Usage</Btn>
                    {b && <Btn size="sm" variant="secondary" onClick={()=>openRateEdit(b)}>⚙ Limits</Btn>}
                    {b && <Btn size="sm" variant="ghost" onClick={()=>removeRateLimit(t.id)} style={{ color:C.danger }}>✕ Limits</Btn>}
                  </div>
                </div>
              </Card>
            );
          }).filter(Boolean)}

          {/* Add new tenant */}
          {tenants.filter(t => !budgetedIds.has(t.id)).length > 0 && (
            <Card style={{ border:`1px dashed ${C.border}`, textAlign:'center', padding:24, color:C.textMuted }}>
              <div style={{ marginBottom:10 }}>Configure token limits for a tenant</div>
              <div style={{ display:'flex', gap:8, justifyContent:'center', flexWrap:'wrap' }}>
                {tenants.filter(t => !budgetedIds.has(t.id)).map(t=>(
                  <Btn key={t.id} size="sm" variant="ghost" onClick={()=>openTopUp(t.id)}>
                    ＋ {t.name}
                  </Btn>
                ))}
              </div>
            </Card>
          )}
        </>
      )}

      {/* Top-up modal */}
      <Modal open={topUpModal} onClose={()=>setTopUpModal(false)}
        title={`Top Up — ${tenants.find(t=>t.id===selTid)?.name||''}`} width={480}>
        <div style={{ background:C.accentDim, borderRadius:8, padding:12, marginBottom:16, fontSize:12, color:C.textSec, lineHeight:1.6 }}>
          Add tokens to this tenant's pool. Tokens are consumed across all runs until the pool is depleted.
          Each top-up is recorded with a reference for billing/audit.
        </div>
        <Input label="Tokens to Add *" type="number" value={topUpForm.tokens||''} onChange={v=>setTopUpForm(f=>({...f,tokens:v}))} placeholder="e.g. 1000000" />
        <Input label="Reference (PO number / invoice / promo code) *" value={topUpForm.reference||''} onChange={v=>setTopUpForm(f=>({...f,reference:v}))} placeholder="e.g. PO-2024-001 or PROMO-FREE50K" />
        <Input label="Notes (optional)" value={topUpForm.notes||''} onChange={v=>setTopUpForm(f=>({...f,notes:v}))} placeholder="e.g. Annual contract, Q1 2025" />
        <Input label="Expires At (optional — leave blank = never)" type="date"
          value={topUpForm.expiresAt||''} onChange={v=>setTopUpForm(f=>({...f,expiresAt:v}))}
          style={{ marginBottom:14 }}
          min={new Date(Date.now()+86400000).toISOString().slice(0,10)} />
        {topUpForm.tokens && (
          <div style={{ padding:'10px 14px', background:C.bg, borderRadius:8, border:`1px solid ${C.border}`, marginBottom:14, fontSize:13 }}>
            Adding <strong style={{ color:C.accent }}>{parseInt(topUpForm.tokens||0).toLocaleString()}</strong> tokens
            {topUpForm.reference && <> + Ref: <strong>{topUpForm.reference}</strong></>}
          </div>
        )}
        <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
          <Btn variant="ghost" onClick={()=>setTopUpModal(false)}>Cancel</Btn>
          <Btn onClick={saveTopUp} disabled={!topUpForm.tokens||!topUpForm.reference}>＋ Add Tokens</Btn>
        </div>
      </Modal>

      {/* Rate limits modal */}
      <Modal open={rateModal} onClose={()=>setRateModal(false)} title={selTid?'Edit Rate Limits':'New Rate Limits'} width={520}>
        <div style={{ fontSize:12, color:C.textSec, marginBottom:16, lineHeight:1.6 }}>
          Rate limits cap token consumption speed (per day / per week). They are separate from the token pool.
          Leave limits blank for unlimited.
        </div>
        {!selTid && (
          <Select label="Tenant *" value={rateForm.tenantId||''} onChange={v=>setRateForm(f=>({...f,tenantId:v}))}
            options={[{value:'',label:'-- select tenant --'},...tenants.map(t=>({value:t.id,label:t.name}))]} />
        )}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
          <Input label="Daily Limit (blank=unlimited)" type="number" value={rateForm.dailyLimit||''} onChange={v=>setRateForm(f=>({...f,dailyLimit:v}))} placeholder="e.g. 50000" />
          <Input label="Weekly Limit (blank=unlimited)" type="number" value={rateForm.weeklyLimit||''} onChange={v=>setRateForm(f=>({...f,weeklyLimit:v}))} placeholder="e.g. 250000" />
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
          <Input label="Min Tokens Required *" type="number" value={rateForm.minTokensRequired||1000} onChange={v=>setRateForm(f=>({...f,minTokensRequired:v}))} placeholder="1000" />
          <Input label="Alert Threshold %" type="number" value={rateForm.alertThresholdPct||80} onChange={v=>setRateForm(f=>({...f,alertThresholdPct:v}))} placeholder="80" />
        </div>
        <Input label="Notes (optional)" value={rateForm.notes||''} onChange={v=>setRateForm(f=>({...f,notes:v}))} />
        <div onClick={()=>setRateForm(f=>({...f,isEnforced:!f.isEnforced}))} style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', padding:'8px 0', marginBottom:14 }}>
          <div style={{ width:16, height:16, borderRadius:3, background:rateForm.isEnforced!==false?C.accent:C.bg, border:`2px solid ${rateForm.isEnforced!==false?C.accent:C.border}`, display:'flex', alignItems:'center', justifyContent:'center' }}>
            {rateForm.isEnforced!==false && <span style={{ color:'#000', fontSize:10 }}>✓</span>}
          </div>
          <span style={{ fontSize:12, color:C.text }}>Enforce limits (block when exceeded)</span>
        </div>
        <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
          <Btn variant="ghost" onClick={()=>setRateModal(false)}>Cancel</Btn>
          <Btn onClick={saveRate}>Save Rate Limits</Btn>
        </div>
      </Modal>

      {/* Top-up history modal */}
      <Modal open={topUpsModal} onClose={()=>setTopUpsModal(false)}
        title={`Top-Up History — ${tenants.find(t=>t.id===selTid)?.name||''}`} width={640}>
        <Card style={{ padding:0, overflow:'hidden' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead><tr style={{ background:C.bg }}>
              {['Date','Tokens','Reference','Notes','Added By','Expires',''].map(h=>(
                <th key={h} style={{ padding:'8px 12px', textAlign:'left', fontSize:11, color:C.textSec, fontWeight:600, letterSpacing:'0.06em', textTransform:'uppercase', fontFamily:"'DM Mono',monospace", borderBottom:`1px solid ${C.border}` }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {topUpsData.map(r=>(
                <tr key={r.id} style={{ borderBottom:`1px solid ${C.border}` }}>
                  <td style={{ padding:'8px 12px', fontSize:12, color:C.text, fontFamily:"'DM Mono',monospace" }}>{fmtDate(r.created_at)}</td>
                  <td style={{ padding:'8px 12px', fontSize:12, fontWeight:700, color:C.success, fontFamily:"'DM Mono',monospace" }}>+{fmtNum(r.tokens)}</td>
                  <td style={{ padding:'8px 12px', fontSize:12, color:C.accent }}>{r.reference||'—'}</td>
                  <td style={{ padding:'8px 12px', fontSize:11, color:C.textMuted }}>{r.notes||'—'}</td>
                  <td style={{ padding:'8px 12px', fontSize:11, color:C.textMuted }}>{r.added_by_name||'—'}</td>
                  <td style={{ padding:'8px 12px', fontSize:11, color: r.expires_at && new Date(r.expires_at) < new Date() ? C.danger : C.textMuted }}>
                    {r.expires_at ? (new Date(r.expires_at) < new Date() ? '⚠ Expired' : fmtDate(r.expires_at)) : 'Never'}
                  </td>
                  <td style={{ padding:'8px 12px' }}>
                    <div style={{ display:'flex', gap:4 }}>
                      <Btn size="sm" variant="ghost" onClick={()=>openEditTopUp(r)}>Edit</Btn>
                      {(!r.expires_at || new Date(r.expires_at) > new Date()) && (
                        <Btn size="sm" variant="ghost" onClick={()=>forceExpireTopUp(r.id)} style={{ color:C.warning }}>Expire</Btn>
                      )}
                      <Btn size="sm" variant="danger" onClick={()=>deleteTopUpRecord(r.id)}>Delete</Btn>
                    </div>
                  </td>
                </tr>
              ))}
              {topUpsData.length===0 && <tr><td colSpan={7} style={{ textAlign:'center', padding:24, color:C.textMuted, fontSize:13 }}>No top-ups recorded</td></tr>}
            </tbody>
          </table>
        </Card>
      </Modal>

      {/* Edit Top-Up modal */}
      <Modal open={editTuModal} onClose={()=>setEditTuModal(false)} title="Edit Top-Up Record" width={480}>
        {editTuItem && (
          <div>
            <div style={{ background:C.warningDim, border:`1px solid ${C.warning}44`, borderRadius:8, padding:10, marginBottom:16, fontSize:12, color:C.warning }}>
              ⚠ Editing a top-up will affect the pool balance immediately. Use this only for corrections.
            </div>
            <Input label="Tokens" type="number" value={editTuForm.tokens||''} onChange={v=>setEditTuForm(f=>({...f,tokens:v}))} />
            <Input label="Reference" value={editTuForm.reference||''} onChange={v=>setEditTuForm(f=>({...f,reference:v}))} placeholder="PO / invoice / promo code" />
            <Input label="Notes" value={editTuForm.notes||''} onChange={v=>setEditTuForm(f=>({...f,notes:v}))} />
            <Input label="Expires At (leave blank = never)" type="date"
              value={editTuForm.expiresAt||''}
              onChange={v=>setEditTuForm(f=>({...f,expiresAt:v}))}
              min={new Date(Date.now()+86400000).toISOString().slice(0,10)} />
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:8 }}>
              <Btn variant="ghost" onClick={()=>setEditTuModal(false)}>Cancel</Btn>
              <Btn onClick={saveEditTopUp}>Save Changes</Btn>
            </div>
          </div>
        )}
      </Modal>

      {/* Usage detail modal */}
      <Modal open={usageModal} onClose={()=>setUsageModal(false)} title="Token Usage Report" width={680}>
        {!usageData ? <Spinner /> : usageData.error ? <div style={{ color:C.danger }}>{usageData.error}</div> : (
          <div>
            {(usageData.budget || usageData.pool) && (
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10, marginBottom:20 }}>
                {[
                  ['Pool Balance',  fmtNum(usageData.pool?.pool_balance||0), C.accent],
                  ['Total Purchased',fmtNum(usageData.pool?.total_purchased||0), C.success],
                  ['Total Consumed', fmtNum(usageData.pool?.total_consumed||0), C.danger],
                  ['Daily Used',    `${fmtNum(usageData.budget?.daily_used||0)} / ${usageData.budget?.daily_limit?fmtNum(usageData.budget.daily_limit):'∞'}`, C.warning],
                  ['Weekly Used',   `${fmtNum(usageData.budget?.weekly_used||0)} / ${usageData.budget?.weekly_limit?fmtNum(usageData.budget.weekly_limit):'∞'}`, C.purple],
                  ['Enforced',      usageData.budget?.is_enforced?'Yes':'Tracking', usageData.budget?.is_enforced?C.success:C.textSec],
                ].map(([l,v,c])=>(
                  <Card key={l} style={{ padding:'10px 12px' }}>
                    <div style={{ fontSize:10, color:C.textSec, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:4 }}>{l}</div>
                    <div style={{ fontSize:14, fontWeight:700, color:c }}>{v}</div>
                  </Card>
                ))}
              </div>
            )}
            <Card style={{ padding:0, overflow:'hidden' }}>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead><tr style={{ background:C.bg }}>
                  {['Date','Runs','Tokens In','Tokens Out','Total'].map(h=>(
                    <th key={h} style={{ padding:'8px 14px', textAlign:'left', fontSize:11, color:C.textSec, fontWeight:600, letterSpacing:'0.06em', textTransform:'uppercase', fontFamily:"'DM Mono',monospace", borderBottom:`1px solid ${C.border}` }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {(usageData.rows||[]).map((r,i)=>(
                    <tr key={i} style={{ borderBottom:`1px solid ${C.border}` }}>
                      <td style={{ padding:'8px 14px', fontSize:12, color:C.text, fontFamily:"'DM Mono',monospace" }}>{r.grp_key}</td>
                      <td style={{ padding:'8px 14px', fontSize:12, color:C.textSec }}>{r.run_count}</td>
                      <td style={{ padding:'8px 14px', fontSize:12, color:C.textSec, fontFamily:"'DM Mono',monospace" }}>{fmtNum(r.tokens_in)}</td>
                      <td style={{ padding:'8px 14px', fontSize:12, color:C.textSec, fontFamily:"'DM Mono',monospace" }}>{fmtNum(r.tokens_out)}</td>
                      <td style={{ padding:'8px 14px', fontSize:12, fontWeight:600, color:C.accent, fontFamily:"'DM Mono',monospace" }}>{fmtNum(r.total_tokens)}</td>
                    </tr>
                  ))}
                  {(usageData.rows||[]).length===0 && <tr><td colSpan={5} style={{ textAlign:'center', padding:24, color:C.textMuted, fontSize:13 }}>No usage data</td></tr>}
                </tbody>
              </table>
            </Card>
          </div>
        )}
      </Modal>
    </div>
  );
}


// ── Tenant Management (superadmin only) ───────────────────────────────────────
function TenantManagement() {
  const [tenants,  setTenants]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState('');
  const [status,   setStatus]   = useState('');
  const [modal,    setModal]    = useState(false);
  const [form,     setForm]     = useState({});
  const [editId,   setEditId]   = useState(null);
  const [detail,   setDetail]   = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = {};
      if (search) p.search = search;
      if (status) p.status = status;
      const r = await api.tenants.list(p);
      setTenants(r.data||[]);
    } catch(e) { console.error(e); }
    setLoading(false);
  }, [search, status]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditId(null);
    setForm({ name:'', slug:'', plan:'starter', customerId:'', status:'active' });
    setModal(true);
  };

  const openEdit = (t) => {
    setEditId(t.id);
    setForm({ name:t.name, slug:t.slug, plan:t.plan, customerId:t.customer_id||'',
              status:t.status, disabledReason:t.disabled_reason||'' });
    setModal(true);
  };

  const save = async () => {
    try {
      const body = {
        name: form.name, plan: form.plan, customerId: form.customerId||undefined,
        status: form.status,
        ...(form.status === 'suspended' && form.disabledReason ? { disabledReason: form.disabledReason } : {}),
      };
      if (editId) {
        await api.tenants.update(editId, body);
      } else {
        body.slug = form.slug;
        await api.tenants.create(body);
      }
      setModal(false); load();
    } catch(e) { alert(e.message); }
  };

  const del = async (id) => {
    if (!confirm('Delete this tenant? This will cascade-delete all workflows, configs, runs and keys. This cannot be undone.')) return;
    try { await api.tenants.delete(id); load(); } catch(e) { alert(e.message); }
  };

  const viewDetail = async (t) => {
    try {
      const r = await api.tenants.get(t.id);
      setDetail(r.data);
    } catch(e) { alert(e.message); }
  };

  const statusColor = s => s==='active'?C.success:s==='suspended'?C.danger:C.warning;

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <div>
          <div style={{ fontSize:20, fontWeight:700, fontFamily:"'DM Serif Display',serif", color:C.text }}>Tenant Management</div>
          <div style={{ color:C.textSec, fontSize:13 }}>Manage all platform tenants</div>
        </div>
        <Btn onClick={openCreate}>＋ New Tenant</Btn>
      </div>

      <Card style={{ marginBottom:16, padding:'14px 20px' }}>
        <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'flex-end' }}>
          <Input value={search} onChange={setSearch} placeholder="Search name / slug / customer ID…" style={{ marginBottom:0, minWidth:240 }} />
          <Select value={status} onChange={setStatus} options={[{value:'',label:'All statuses'},{value:'active',label:'Active'},{value:'suspended',label:'Suspended'},{value:'pending',label:'Pending'}]} />
          <Btn variant="ghost" onClick={() => { setSearch(''); setStatus(''); }}>Clear</Btn>
        </div>
      </Card>

      {loading ? <Spinner /> : (
        <Card style={{ padding:0, overflow:'hidden' }}>
          {tenants.length===0 ? (
            <div style={{ textAlign:'center', padding:40, color:C.textMuted }}>No tenants found</div>
          ) : (
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead>
                <tr style={{ background:C.bg }}>
                  {['Name','Slug','Customer ID','Plan','Status','Workflows','Configs','Runs','Pool Available',''].map(h=>(
                    <th key={h} style={{ padding:'10px 16px', textAlign:'left', fontSize:11, color:C.textSec,
                      fontWeight:600, letterSpacing:'0.06em', textTransform:'uppercase',
                      fontFamily:"'DM Mono',monospace", borderBottom:`1px solid ${C.border}` }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tenants.map(t => (
                  <tr key={t.id} style={{ borderBottom:`1px solid ${C.border}` }}
                    onMouseEnter={e=>e.currentTarget.style.background=C.surfaceHover}
                    onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                    <td style={{ padding:'10px 16px' }}>
                      <div style={{ fontSize:13, fontWeight:600, color:C.text }}>{t.name}</div>
                      {t.disabled_at && <div style={{ fontSize:10, color:C.danger }}>Disabled {fmtDate(t.disabled_at)}</div>}
                    </td>
                    <td style={{ padding:'10px 16px', fontSize:11, color:C.textSec, fontFamily:"'DM Mono',monospace" }}>{t.slug}</td>
                    <td style={{ padding:'10px 16px', fontSize:11, color:C.textMuted, fontFamily:"'DM Mono',monospace" }}>{t.customer_id||'—'}</td>
                    <td style={{ padding:'10px 16px' }}><Badge c={C.accent} label={t.plan} /></td>
                    <td style={{ padding:'10px 16px' }}><Badge c={statusColor(t.status)} label={t.status} /></td>
                    <td style={{ padding:'10px 16px', fontSize:12, color:C.textSec, fontFamily:"'DM Mono',monospace" }}>{t.workflow_count||0}</td>
                    <td style={{ padding:'10px 16px', fontSize:12, color:C.textSec, fontFamily:"'DM Mono',monospace" }}>{t.config_count||0}</td>
                    <td style={{ padding:'10px 16px', fontSize:12, color:C.textSec, fontFamily:"'DM Mono',monospace" }}>{parseInt(t.total_validations||0).toLocaleString()}</td>
                    <td style={{ padding:'10px 16px', fontSize:11, fontFamily:"'DM Mono',monospace",
                      color: Number(t.pool_balance||0) <= 0 && Number(t.total_purchased||0) > 0 ? C.danger
                           : Number(t.total_purchased||0) > 0 ? C.success : C.textMuted }}>
                      {Number(t.total_purchased||0) > 0
                        ? `${Math.max(0, parseInt(t.pool_balance||0)).toLocaleString()}`
                        : '—'}
                    </td>
                    <td style={{ padding:'10px 16px' }}>
                      <div style={{ display:'flex', gap:5 }}>
                        <Btn size="sm" variant="ghost" onClick={()=>viewDetail(t)}>View</Btn>
                        <Btn size="sm" variant="secondary" onClick={()=>openEdit(t)}>Edit</Btn>
                        {t.slug!=='system' && <Btn size="sm" variant="danger" onClick={()=>del(t.id)}>Del</Btn>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}

      {/* Detail modal */}
      <Modal open={!!detail} onClose={()=>setDetail(null)} title={detail?.name||''} width={600}>
        {detail&&<div>
          <div style={{ display:'flex', gap:8, marginBottom:16, flexWrap:'wrap' }}>
            <Badge c={statusColor(detail.status)} label={detail.status} />
            <Badge c={C.accent} label={detail.plan} />
            {detail.customer_id && <Badge c={C.textMuted} label={`cust: ${detail.customer_id}`} />}
          </div>
          {detail.disabled_at && (
            <div style={{ padding:'10px 14px', background:C.dangerDim, border:`1px solid ${C.danger}44`, borderRadius:8, marginBottom:14, fontSize:12 }}>
              <div style={{ color:C.danger, fontWeight:600 }}>Disabled {fmtDate(detail.disabled_at)}</div>
              {detail.disabled_reason && <div style={{ color:C.textSec, marginTop:4 }}>{detail.disabled_reason}</div>}
            </div>
          )}
          {[
            ['ID',           detail.id],
            ['Slug',         detail.slug],
            ['Created',      fmtDate(detail.created_at)],
            ['Workflows',    detail.workflow_count||0],
            ['Configs',      detail.config_count||0],
            ['Users',        detail.user_count||0],
            ['Active Keys',  detail.active_keys||0],
            ['Total Runs',   parseInt(detail.total_validations||0).toLocaleString()],
            ['Passed',       parseInt(detail.passed||0).toLocaleString()],
            ['Failed',       parseInt(detail.failed||0).toLocaleString()],
          ].map(([l,v])=>(
            <div key={l} style={{ display:'flex', justifyContent:'space-between', padding:'6px 0', borderBottom:`1px solid ${C.border}` }}>
              <span style={{ fontSize:13, color:C.textSec }}>{l}</span>
              <span style={{ fontSize:13, color:C.text, fontFamily:"'DM Mono',monospace" }}>{String(v)}</span>
            </div>
          ))}
          <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:16 }}>
            <Btn variant="secondary" onClick={()=>{ openEdit(detail); setDetail(null); }}>Edit</Btn>
            <Btn variant="ghost" onClick={()=>setDetail(null)}>Close</Btn>
          </div>
        </div>}
      </Modal>

      {/* Create/Edit modal */}
      <Modal open={modal} onClose={()=>setModal(false)} title={editId?'Edit Tenant':'New Tenant'} width={520}>
        <Input label="Tenant Name *" value={form.name||''} onChange={v=>setForm(f=>({...f,name:v}))} placeholder="e.g. Acme Corp" />
        {!editId && (
          <Input label="Slug * (lowercase, hyphens only)" value={form.slug||''} onChange={v=>setForm(f=>({...f,slug:v.toLowerCase().replace(/[^a-z0-9-]/g,'-')}))} placeholder="e.g. acme-corp" />
        )}
        <Input label="Customer ID (workflow engine reference)" value={form.customerId||''} onChange={v=>setForm(f=>({...f,customerId:v}))} placeholder="Optional external reference" />
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
          <Select label="Plan" value={form.plan||'starter'} onChange={v=>setForm(f=>({...f,plan:v}))}
            options={[{value:'starter',label:'Starter'},{value:'pro',label:'Pro'},{value:'enterprise',label:'Enterprise'}]} />
          <Select label="Status" value={form.status||'active'} onChange={v=>setForm(f=>({...f,status:v}))}
            options={[{value:'active',label:'Active'},{value:'suspended',label:'Suspended'},{value:'pending',label:'Pending'}]} />
        </div>
        {form.status==='suspended' && (
          <Input label="Disable Reason *" value={form.disabledReason||''} onChange={v=>setForm(f=>({...f,disabledReason:v}))} placeholder="Reason for suspension" />
        )}
        <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
          <Btn variant="ghost" onClick={()=>setModal(false)}>Cancel</Btn>
          <Btn onClick={save} disabled={!form.name||(form.status==='suspended'&&!form.disabledReason&&!editId)}>
            {editId?'Save Changes':'Create Tenant'}
          </Btn>
        </div>
      </Modal>
    </div>
  );
}

// ── ProfileMappedTenants — shows mapped tenants for account_manager in Profile ─
function ProfileMappedTenants() {
  const [tenants, setTenants] = useState([]);
  useEffect(() => {
    api.tenants.list().then(r => setTenants(r.data||[])).catch(()=>{});
  }, []);
  if (!tenants.length) return null;
  return (
    <div style={{ marginBottom:16 }}>
      <div style={{ fontSize:11, color:C.textSec, textTransform:'uppercase', letterSpacing:'0.07em', fontFamily:"'DM Mono',monospace", marginBottom:8 }}>
        Mapped Accounts ({tenants.length})
      </div>
      <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
        {tenants.map(t => (
          <div key={t.id} style={{ padding:'4px 10px', background:C.accentDim, borderRadius:20,
            border:`1px solid ${C.accent}33`, fontSize:12, color:C.accent }}>
            {t.name}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Settings ──────────────────────────────────────────────────────────────────
function Settings({ user, initialTab }) {
  const [keys,        setKeys]        = useState([]);
  const [tenants,     setTenants]     = useState([]);
  const [newKeyName,  setNewKeyName]  = useState('');
  const [newKeyTenant,setNewKeyTenant]= useState('');
  const [newKey,      setNewKey]      = useState(null);
  const [tab,         setTab]         = useState(initialTab || 'profile');
  const [providers,   setProviders]   = useState([]);
  const [testResult,  setTestResult]  = useState({});
  const [provModal,   setProvModal]   = useState(false);
  const [provForm,    setProvForm]    = useState({});
  const [editProvId,  setEditProvId]  = useState(null);   // null = new, uuid = edit
  const [provSaving,  setProvSaving]  = useState(false);
  const [editProv,    setEditProv]    = useState(null);   // provider being edited
  const isSuperAdmin = user.role === 'superadmin';

  useEffect(()=>{ api.apiKeys.list().then(r=>setKeys(r.data||[])).catch(()=>{}); },[]);
  useEffect(()=>{ if(tab==='providers') providersApi.list().then(r=>setProviders(r.data||[])).catch(()=>{}); },[tab]);
  useEffect(()=>{ if(isSuperAdmin) api.tenants.list().then(r=>setTenants(r.data||[])).catch(()=>{}); },[isSuperAdmin]);

  const openNewProv = (p = null) => {
    // p = null means create new, p = provider obj means edit core fields
    setProvForm(p ? {
      name: p.name, providerType: p.provider_type, baseUrl: p.base_url || '',
      defaultModel: p.default_model || '', apiKeyEnvVar: p.api_key_env_var || '',
    } : {
      name:'', providerType:'anthropic', baseUrl:'https://api.anthropic.com',
      defaultModel:'claude-sonnet-4-20250514', apiKeyEnvVar:'ANTHROPIC_API_KEY',
    });
    setEditProvId(p ? p.id : null);
    setProvModal(true);
  };
  const openEditProv = (p) => {
    setProvForm({
      name: p.name, providerType: p.provider_type, baseUrl: p.base_url,
      defaultModel: p.default_model, apiKeyEnvVar: p.api_key_env_var || '',
      settings: typeof p.settings === 'string' ? p.settings : JSON.stringify(p.settings||{}, null, 2),
    });
    setEditProvId(p.id);
    setProvModal(true);
  };
  const saveProv = async () => {
    if (!provForm.name.trim()) return alert('Name is required');
    if (!provForm.baseUrl.trim()) return alert('Base URL is required');
    let settingsObj;
    try { settingsObj = JSON.parse(provForm.settings || '{}'); }
    catch { return alert('Settings must be valid JSON'); }
    setProvSaving(true);
    try {
      const body = {
        name: provForm.name, providerType: provForm.providerType,
        baseUrl: provForm.baseUrl, defaultModel: provForm.defaultModel,
        apiKeyEnvVar: provForm.apiKeyEnvVar || null,
        settings: settingsObj,
      };
      if (editProvId) {
        // PATCH uses snake_case directly
        await providersApi.update(editProvId, {
          name: body.name, base_url: body.baseUrl, default_model: body.defaultModel,
          api_key_env_var: body.apiKeyEnvVar, settings: settingsObj,
        });
      } else {
        await providersApi.create(body);
      }
      setProvModal(false);
      providersApi.list().then(r => setProviders(r.data||[])).catch(()=>{});
    } catch(e) { alert(e.message); }
    setProvSaving(false);
  };
  const deleteProv = async (p) => {
    if (!confirm(`Delete provider "${p.name}"? This cannot be undone.`)) return;
    try {
      await providersApi.remove(p.id);
      providersApi.list().then(r => setProviders(r.data||[])).catch(()=>{});
    } catch(e) { alert(e.message); }
  };

  const genKey = async () => {
    if (!newKeyName.trim()) return;
    try {
      const body = { name:newKeyName, scopes:['read','write'] };
      if (isSuperAdmin && newKeyTenant) body.tenantId = newKeyTenant;
      const r = await api.apiKeys.create(body);
      setNewKey(r.data); setNewKeyName('');
      const res = await api.apiKeys.list(); setKeys(res.data||[]);
    } catch(e) { alert(e.message); }
  };

  const toggleProvider = async (p) => {
    try { await providersApi.update(p.id, { is_active:!p.is_active }); providersApi.list().then(r=>setProviders(r.data||[])); }
    catch(e) { alert(e.message); }
  };
  const testProvider = async (p) => {
    setTestResult(t=>({...t,[p.id]:'testing'}));
    try { const r = await providersApi.test(p.id); setTestResult(t=>({...t,[p.id]:r.data?.success?'ok':'fail'})); }
    catch { setTestResult(t=>({...t,[p.id]:'fail'})); }
    setTimeout(()=>setTestResult(t=>({...t,[p.id]:null})),4000);
  };
  const openEditProvider = (p) => {
    setEditProv(p);
    setProvForm({
      default_model:  p.default_model || '',
      models:         (p.settings?.models || []).join('\n'),
      base_url:       p.base_url || '',
      endpoint_path:  p.settings?.endpoint_path || '',
      api_format:     p.settings?.api_format    || '',
      force_base64:   p.settings?.force_base64  ?? null,  // null = auto
      image_detail:   p.settings?.image_detail  || 'low',
    });
  };
  const saveProvider = async () => {
    try {
      const modelsArr = provForm.models.split('\n').map(m=>m.trim()).filter(Boolean);
      const newSettings = { ...editProv.settings, models: modelsArr };
      if (provForm.endpoint_path)      newSettings.endpoint_path = provForm.endpoint_path.trim();
      else                             delete newSettings.endpoint_path;
      if (provForm.api_format)         newSettings.api_format    = provForm.api_format;
      else                             delete newSettings.api_format;
      if (provForm.force_base64 === 'true')  newSettings.force_base64 = true;
      else if (provForm.force_base64 === 'false') newSettings.force_base64 = false;
      else                             delete newSettings.force_base64;
      if (provForm.image_detail && provForm.image_detail !== 'low') newSettings.image_detail = provForm.image_detail;
      else                             delete newSettings.image_detail;
      await providersApi.update(editProv.id, {
        default_model: provForm.default_model || undefined,
        base_url:      provForm.base_url      || undefined,
        settings:      newSettings,
      });
      providersApi.list().then(r=>setProviders(r.data||[]));
      setEditProv(null);
    } catch(e) { alert(e.message); }
  };

  const isViewer       = user.role === 'viewer';
  const isAccountMgr   = user.role === 'account_manager';
  const isAccountAdmin = user.role === 'account_admin';
  const tabs = [
    { id:'profile',  label:'Profile' },
    // AI Providers: superadmin ONLY — account_admin and account_manager do NOT see this
    ...(isSuperAdmin ? [{ id:'providers', label:'AI Providers' }] : []),
    ...(!isViewer ? [{ id:'apikeys',   label:'API Keys' }]     : []),
    ...(isSuperAdmin || isAccountMgr ? [{ id:'users', label:'Users' }] : []),
    ...(isSuperAdmin ? [{ id:'tokens',   label:'Token Budgets' }] : (isAccountMgr || isAccountAdmin ? [{ id:'tokens', label:'Token Budgets' }] : [])),
    ...(isSuperAdmin ? [{ id:'tenants',  label:'Tenants' }]    : []),
    ...(isSuperAdmin || isAccountAdmin || isAccountMgr ? [{ id:'external', label:'External API' }] : []),
    ...(!isViewer ? [{ id:'field-links', label:'Field Links' }] : []),
    ...(isSuperAdmin ? [{ id:'system',   label:'System' }] : []),
  ];

  return (
    <div>
      <div style={{ marginBottom:20 }}>
        <div style={{ fontSize:20, fontWeight:700, fontFamily:"'DM Serif Display',serif", color:C.text, marginBottom:4 }}>
          {isSuperAdmin?'System Administration':'Account Settings'}
        </div>
      </div>
      <div style={{ display:'flex', gap:20 }}>
        <div style={{ width:168, flexShrink:0 }}>
          {tabs.map(t=>(
            <button key={t.id} onClick={()=>setTab(t.id)} style={{
              display:'block', width:'100%', padding:'9px 14px', borderRadius:8, border:'none', cursor:'pointer',
              textAlign:'left', marginBottom:4, background:tab===t.id?C.accentDim:'transparent',
              color:tab===t.id?C.accent:C.textSec, fontSize:13, fontWeight:tab===t.id?600:400, fontFamily:"'DM Sans',sans-serif" }}>
              {t.label}
            </button>
          ))}
        </div>

        <div style={{ flex:1 }}>
          {tab==='profile'&&(
            <Card>
              <div style={{ fontSize:14, fontWeight:700, color:C.text, marginBottom:20 }}>
                Profile
                {user.is_sso
                  ? <span style={{ fontSize:11, color:C.textMuted, fontWeight:400, marginLeft:8 }}>(SSO Managed)</span>
                  : <span style={{ fontSize:11, color:C.accent, fontWeight:400, marginLeft:8 }}>(Manual Login)</span>}
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:16, marginBottom:20 }}>
                <div style={{ width:52, height:52, borderRadius:'50%', background:C.accentDim, border:`2px solid ${C.accent}44`,
                  display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, fontWeight:700, color:C.accent }}>
                  {user.name?.[0]?.toUpperCase()}
                </div>
                <div>
                  <div style={{ fontSize:16, fontWeight:700, color:C.text }}>{user.name}</div>
                  <div style={{ fontSize:13, color:C.textSec }}>{user.email}</div>
                  <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap', marginTop:4 }}>
                    <Badge c={user.role==='superadmin'?C.purple:user.role==='account_manager'?C.warning:C.accent} label={user.role} />
                    <Badge c={user.is_sso?C.textSec:C.success} label={user.is_sso?'SSO':'Manual'} />
                  </div>
                  {/* Primary tenant */}
                  {user.tenant_name && (
                    <div style={{ fontSize:12, color:C.textMuted, marginTop:4 }}>
                      {user.role === 'account_manager' ? 'Primary account: ' : 'Account: '}
                      <strong style={{ color:C.text }}>{user.tenant_name}</strong>
                    </div>
                  )}
                </div>
              </div>
              {/* For account_manager: show all mapped tenants */}
              {user.role === 'account_manager' && <ProfileMappedTenants />}
              <div style={{ background:C.bg, borderRadius:8, padding:14, border:`1px solid ${C.border}`, fontSize:12, color:C.textMuted }}>
                {user.is_sso
                  ? 'ℹ Your identity is managed by your SSO provider. Contact your administrator to update your details.'
                  : 'ℹ Your account uses manual password login. Use Settings to change your password, or contact your administrator to reset it.'}
              </div>
            </Card>
          )}

          {tab==='providers'&&(
            <div>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
                <div style={{ fontSize:14, fontWeight:700, color:C.text }}>AI Providers</div>
                <Btn size="sm" onClick={openNewProv}>+ Add Provider</Btn>
              </div>
              {providers.map(p=>{ const tr=testResult[p.id]; return (
                <Card key={p.id} style={{ marginBottom:12 }}>
                  <div style={{ display:'flex', alignItems:'flex-start', gap:14 }}>
                    <div style={{ fontSize:28, flexShrink:0 }}>{PROVIDER_ICONS[p.provider_type]||'🤖'}</div>
                    <div style={{ flex:1 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:4 }}>
                        <div style={{ fontSize:14, fontWeight:700, color:C.text }}>{p.name}</div>
                        <Badge c={p.is_active?C.success:C.textMuted} label={p.is_active?'active':'inactive'} />
                        {p.api_key_configured===false && <Badge c={C.danger} label="no api key" />}
                      </div>
                      {/* Default model */}
                      <div style={{ fontSize:12, color:C.textSec, marginBottom:4 }}>
                        Default: <span style={{ color:C.accent, fontFamily:"'DM Mono',monospace" }}>{p.default_model}</span>
                        {p.api_key_env_var && <span style={{ color:C.textMuted, fontFamily:"'DM Mono',monospace", marginLeft:10 }}>env: {p.api_key_env_var}</span>}
                      </div>
                      {/* Available models */}
                      {p.settings?.models?.length > 0 && (
                        <div style={{ display:'flex', gap:4, flexWrap:'wrap' }}>
                          {p.settings.models.map(m=>(
                            <span key={m} style={{ fontSize:10, fontFamily:"'DM Mono',monospace", padding:'2px 6px',
                              background:m===p.default_model?C.accentDim:C.bg,
                              border:`1px solid ${m===p.default_model?C.accent+'44':C.border}`,
                              borderRadius:4, color:m===p.default_model?C.accent:C.textMuted }}>{m}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div style={{ display:'flex', gap:8, flexShrink:0 }}>
                      {isSuperAdmin && <Btn size="sm" variant="ghost" onClick={()=>openEditProvider(p)}>⚙ Models</Btn>}
                      {isSuperAdmin && <Btn size="sm" variant="ghost" onClick={()=>openNewProv(p)}>✏ Edit</Btn>}
                      {isSuperAdmin && <Btn size="sm" variant="danger" onClick={()=>deleteProv(p)}>Delete</Btn>}
                      {isSuperAdmin && p.is_active && <Btn size="sm" variant={tr==='ok'?'success':tr==='fail'?'danger':'ghost'} onClick={()=>testProvider(p)} disabled={tr==='testing'}>{tr==='testing'?'Testing…':tr==='ok'?'✓ OK':tr==='fail'?'✗ Fail':'Test'}</Btn>}
                      {isSuperAdmin && <Btn size="sm" variant={p.is_active?'danger':'success'} onClick={()=>toggleProvider(p)}>{p.is_active?'Disable':'Enable'}</Btn>}
                      {!isSuperAdmin && isAccountMgr && <span style={{fontSize:11,color:C.textMuted,padding:'4px 0'}}>View only</span>}
                    </div>
                  </div>
                </Card>
              ); })}
              {isSuperAdmin && (
                <div style={{ marginTop:12, background:C.bg, borderRadius:8, padding:14, border:`1px solid ${C.border}`, fontSize:12, color:C.textMuted }}>
                  ℹ Set API keys in your <span style={{ fontFamily:"'DM Mono',monospace", color:C.accent }}>.env</span> file and restart the API container.
                  Model lists here appear as dropdowns in Workflow Config.
                </div>
              )}
              {/* Provider model edit modal */}
              <Modal open={!!editProv} onClose={()=>setEditProv(null)} title={`${editProv?.name||''} — Model Settings`} width={480}>
                {editProv&&<div>
                  <div style={{ fontSize:12, color:C.textSec, marginBottom:16, lineHeight:1.6 }}>
                    Set the default model and list of available models for this provider.
                    Models listed here will appear as dropdown options in Workflow Config.
                  </div>
                  <Select label="Default Model" value={provForm.default_model||''} onChange={v=>setProvForm(f=>({...f,default_model:v}))}
                    options={[
                      {value:'',label:'— type custom below —'},
                      ...(provForm.models||'').split('\n').map(m=>m.trim()).filter(Boolean).map(m=>({value:m,label:m}))
                    ]} />
                  <Input label="Default Model (or type here)" value={provForm.default_model||''} onChange={v=>setProvForm(f=>({...f,default_model:v}))}
                    placeholder="e.g. claude-sonnet-4-6" />
                  <Textarea label="Available Models (one per line)" value={provForm.models||''} onChange={v=>setProvForm(f=>({...f,models:v}))} rows={6} mono
                    placeholder={'claude-opus-4-5\nclaude-sonnet-4-6\nclaude-haiku-4-5-20251001'} />
                  {/* Base URL for ollama and any custom/local provider */}
                  {(editProv.provider_type === 'ollama' || editProv.provider_type === 'openai' || editProv.base_url) && (
                    <Input label="Base URL" value={provForm.base_url||''} onChange={v=>setProvForm(f=>({...f,base_url:v}))}
                      placeholder={editProv.provider_type === 'ollama' ? 'http://ollama:11434 or http://localhost:11434' : editProv.base_url || 'https://api.example.com'} />
                  )}
                  {/* Endpoint path — allows overriding the default API path */}
                  {/* e.g. /api/chat (Ollama native) or /v1/chat/completions (OpenAI-compat, LM Studio) */}
                  <div style={{ marginBottom:14 }}>
                    <Input label="API Endpoint Path (optional)" value={provForm.endpoint_path||''} onChange={v=>setProvForm(f=>({...f,endpoint_path:v}))}
                      placeholder="/api/chat  or  /v1/chat/completions" />
                    <div style={{ fontSize:11, color:C.textMuted, marginTop:4, lineHeight:1.5 }}>
                      Leave empty for default. Use <code style={{fontFamily:"'DM Mono',monospace"}}>&#47;v1&#47;chat&#47;completions</code> for LM Studio or OpenAI-compatible APIs.
                      Use <code style={{fontFamily:"'DM Mono',monospace"}}>&#47;api&#47;chat</code> for Ollama native format.
                    </div>
                  </div>
                  <Select label="API Format" value={provForm.api_format||''} onChange={v=>setProvForm(f=>({...f,api_format:v}))}
                    options={[
                      {value:'',        label:'Auto-detect from endpoint path'},
                      {value:'openai',  label:'OpenAI-compatible (LM Studio, Ollama /v1/, etc.)'},
                      {value:'ollama',  label:'Ollama native (/api/chat)'},
                    ]} />
                  {/* Image detail level — OpenAI/Azure/LM Studio only */}
                  {(editProv.provider_type === 'openai' || editProv.provider_type === 'azure_openai' || provForm.api_format === 'openai') && (
                    <div>
                      <Select label="Image Detail Level (OpenAI)"
                        value={provForm.image_detail||'low'}
                        onChange={v=>setProvForm(f=>({...f,image_detail:v}))}
                        options={[
                          {value:'low',  label:'Low — ~85 tokens, faster, sufficient for most checks'},
                          {value:'high', label:'High — tiles image, higher accuracy, more tokens'},
                          {value:'auto', label:'Auto — OpenAI decides based on image size'},
                        ]} />
                      <div style={{ fontSize:11, color:C.textMuted, marginBottom:14, lineHeight:1.5 }}>
                        <strong>Low</strong> is the default and recommended for most use cases. Use <strong>High</strong> only when fine detail (e.g. serial numbers, small text) is critical.
                      </div>
                    </div>
                  )}
                  <Select label="Image URL → Base64 Conversion"
                    value={provForm.force_base64 === null || provForm.force_base64 === undefined ? '' : String(provForm.force_base64)}
                    onChange={v=>setProvForm(f=>({...f,force_base64:v}))}
                    options={[
                      {value:'',      label:'Auto (Ollama native & Gemini convert; others pass URL)'},
                      {value:'true',  label:'Always convert URL to base64 before sending'},
                      {value:'false', label:'Never convert — always pass URL directly'},
                    ]} />
                  <div style={{ fontSize:11, color:C.textMuted, marginBottom:14, lineHeight:1.6 }}>
                    Ollama native format and Gemini always need base64. Use <strong>Always convert</strong> for any local model
                    that cannot access external URLs. Use <strong>Never convert</strong> for cloud APIs that accept URLs natively.
                  </div>
                  <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
                    <Btn variant="ghost" onClick={()=>setEditProv(null)}>Cancel</Btn>
                    <Btn onClick={saveProvider}>Save</Btn>
                  </div>
                </div>}
              </Modal>
            {/* ── Create / Edit Provider modal ──────────────────────────── */}
            <Modal open={provModal} onClose={()=>setProvModal(false)}
              title={editProvId ? 'Edit Provider' : 'Add AI Provider'} width={520}>
              <Select label="Provider Type" value={provForm.providerType||'anthropic'}
                onChange={v=>setProvForm(f=>({...f,providerType:v}))}
                options={[
                  {value:'anthropic',    label:'Anthropic Claude'},
                  {value:'openai',       label:'OpenAI GPT'},
                  {value:'gemini',       label:'Google Gemini'},
                  {value:'ollama',       label:'Ollama (Local)'},
                  {value:'azure_openai', label:'Azure OpenAI'},
                ]} />
              <Input label="Display Name *" value={provForm.name||''}
                onChange={v=>setProvForm(f=>({...f,name:v}))} placeholder="e.g. Anthropic Claude" />
              <Input label="Base URL *" value={provForm.baseUrl||''}
                onChange={v=>setProvForm(f=>({...f,baseUrl:v}))}
                placeholder="e.g. https://api.anthropic.com" />
              <Input label="Default Model *" value={provForm.defaultModel||''}
                onChange={v=>setProvForm(f=>({...f,defaultModel:v}))}
                placeholder="e.g. claude-sonnet-4-20250514" />
              <Input label="API Key Env Var" value={provForm.apiKeyEnvVar||''}
                onChange={v=>setProvForm(f=>({...f,apiKeyEnvVar:v}))}
                placeholder="e.g. ANTHROPIC_API_KEY (leave blank if no auth)" />
              <div style={{ background:C.accentDim, border:`1px solid ${C.accent}33`,
                borderRadius:8, padding:'10px 14px', fontSize:12, color:C.textSec,
                lineHeight:1.6, marginBottom:14 }}>
                ℹ️ The API key itself is read from the environment variable at runtime —
                never stored in the database. Add it to your <code>.env</code> file.
              </div>
              <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
                <Btn variant="ghost" onClick={()=>setProvModal(false)}>Cancel</Btn>
                <Btn onClick={saveProv} disabled={provSaving}>
                  {provSaving ? 'Saving…' : (editProvId ? 'Save Changes' : 'Add Provider')}
                </Btn>
              </div>
            </Modal>

            </div>
          )}

          {tab==='apikeys'&&(
            <Card>
              <div style={{ fontSize:14, fontWeight:700, color:C.text, marginBottom:isAccountMgr?8:16 }}>API Keys</div>
              {isAccountMgr && (
                <div style={{ fontSize:12, color:C.textMuted, background:C.bg, border:`1px solid ${C.border}`, borderRadius:8, padding:'8px 12px', marginBottom:16 }}>
                  ℹ Account managers can revoke API keys but cannot create new ones. Contact a superadmin to create keys.
                </div>
              )}
              {newKey&&(
                <div style={{ background:C.successDim, border:`1px solid ${C.success}44`, borderRadius:10, padding:16, marginBottom:16 }}>
                  <div style={{ fontSize:13, fontWeight:600, color:C.success, marginBottom:8 }}>✓ New key — copy now, not shown again:</div>
                  <div style={{ fontFamily:"'DM Mono',monospace", fontSize:12, color:C.text, background:C.bg, padding:10, borderRadius:6, wordBreak:'break-all' }}>{newKey.key}</div>
                  <Btn size="sm" variant="success" style={{ marginTop:10 }} onClick={()=>navigator.clipboard?.writeText(newKey.key)}>Copy</Btn>
                </div>
              )}
              {keys.map(k=>(
                <div key={k.id} style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 14px', background:C.bg, borderRadius:8, border:`1px solid ${C.border}`, marginBottom:8 }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:13, fontWeight:600, color:C.text }}>{k.name}{k.tenant_name&&<span style={{ fontSize:11, color:C.textMuted, marginLeft:6 }}>+ {k.tenant_name}</span>}</div>
                    <div style={{ fontSize:11, color:C.textSec, fontFamily:"'DM Mono',monospace" }}>{k.key_prefix} + {k.revoked_at?'❌ revoked':k.last_used_at?`last used ${timeAgo(k.last_used_at)}`:'never used'}</div>
                  </div>
                  {!k.revoked_at && (
                    <Btn size="sm" variant="danger" onClick={async ()=>{
                      if (!confirm(`Revoke API key "${k.name}"? This immediately blocks all requests using this key and cannot be undone.`)) return;
                      await api.apiKeys.revoke(k.id);
                      api.apiKeys.list().then(r=>setKeys(r.data||[]));
                    }}>Revoke</Btn>
                  )}
                </div>
              ))}
              <div style={{ display:'flex', gap:10, marginTop:12, flexWrap:'wrap' }}>
                <Input value={newKeyName} onChange={setNewKeyName} placeholder="Key name e.g. FieldApp" style={{ marginBottom:0, flex:1, minWidth:160 }} />
                {isSuperAdmin && (
                  <select value={newKeyTenant} onChange={e=>setNewKeyTenant(e.target.value)} style={{
                    background:C.bg, border:`1px solid ${C.border}`, borderRadius:8, padding:'9px 12px',
                    color:newKeyTenant?C.text:C.textMuted, fontSize:13, fontFamily:"'DM Sans',sans-serif", minWidth:160 }}>
                    <option value="">System tenant</option>
                    {tenants.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                )}
                {!isAccountMgr ? (
                  <Btn onClick={genKey} disabled={!newKeyName.trim()}>Generate</Btn>
                ) : (
                  <Btn disabled title="Account managers cannot create API keys">Generate</Btn>
                )}
              </div>
            </Card>
          )}

          {tab==='users'&&(isSuperAdmin||isAccountMgr)&&<UserManagement user={user} />}
          {tab==='tokens'&&isSuperAdmin&&<TokenBudgetManagement />}
          {tab==='tokens'&&(isAccountMgr||isAccountAdmin)&&!isSuperAdmin&&<TokenBudgetViewer user={user} />}
          {tab==='tenants'&&isSuperAdmin&&<TenantManagement />}

          {tab==='external'&&(isSuperAdmin||isAccountAdmin||isAccountMgr)&&(
            <div>
              <Card style={{ marginBottom:16 }}>
                <div style={{ fontSize:14, fontWeight:700, color:C.text, marginBottom:16 }}>📱 Field Tester App</div>
                <div style={{ color:C.textSec, fontSize:13, marginBottom:16, lineHeight:1.7 }}>
                  A mobile-friendly public interface for field technicians.
                  Accessible at <span style={{ fontFamily:"'DM Mono',monospace", color:C.accent }}>/field</span>.
                </div>
                <div style={{ background:C.bg, borderRadius:8, padding:14, border:`1px solid ${C.border}`, marginBottom:14 }}>
                  {[
                    ['Basic (user selects)',      `${window.location.origin}/field#key=vai_...`],
                    ['Pre-filled workflow+param', `${window.location.origin}/field?workflow=<id>&param=<n>#key=vai_...`],
                    ['Pre-filled config',         `${window.location.origin}/field?config=<uuid>#key=vai_...`],
                    ['With task ID',              `${window.location.origin}/field?task=JOB-001#key=vai_...`],
                  ].map(([label, url]) => (
                    <div key={label} style={{ marginBottom:8 }}>
                      <div style={{ fontSize:10, color:C.textMuted, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:3 }}>{label}</div>
                      <div style={{ fontFamily:"'DM Mono',monospace", fontSize:11, color:C.text, background:C.surface, padding:'6px 10px', borderRadius:6, wordBreak:'break-all' }}>{url}</div>
                    </div>
                  ))}
                </div>
                <div style={{ background:C.warningDim, border:`1px solid ${C.warning}44`, borderRadius:8, padding:12, fontSize:12, color:C.warning, lineHeight:1.6 }}>
                  🔒 API key goes in the URL <strong>hash</strong> (#key=...) — never the query string. Hash fragments are not sent to the server.
                </div>
              </Card>
              <Card>
                <div style={{ fontSize:14, fontWeight:700, color:C.text, marginBottom:16 }}>External API Reference</div>
                {[
                  { method:'POST', path:'/api/v1/external/validate', desc:'Identify config via any ONE of: configId | workflowId+statusTrigger+paramName | engineWorkflowId+statusTrigger+paramName | paramName',
                    body:'// Option 1 — config UUID\n{ "configId": "<uuid>", "imageUrl": "https://..." }\n\n// Option 2 — workflow triplet\n{ "workflowId": "<uuid>", "statusTrigger": "pre_work_check", "paramName": "ppe_photo", "imageUrl": "..." }\n\n// Option 3 — engine native ID\n{ "engineWorkflowId": "WF-12345", "statusTrigger": "pre_work_check", "paramName": "ppe_photo", "imageUrl": "..." }\n\n// Option 4 — param name only (+ optional extras)\n{ "paramName": "ppe_photo", "imageUrl": "...", "jobId": "JOB-001", "jobMeta": {"region":"south"}, "variables": {"serial":"SN-001"} }' },
                  { method:'POST', path:'/api/v1/external/validate/batch', desc:'Submit up to 10 images.',
                    body:'{ "items": [ { "paramName":"...", "imageUrl":"...", "jobId":"..." } ] }' },
                  { method:'GET',  path:'/api/v1/external/result/:runId', desc:'Fetch a result by run ID.' },
                  { method:'GET',  path:'/api/v1/external/configs', desc:'List active validation params.' },
                  { method:'GET',  path:'/api/v1/external/link?config=<id>', desc:'Generate shareable field tester URL.' },
                ].map(e=>(
                  <div key={e.path} style={{ marginBottom:12, background:C.bg, borderRadius:10, overflow:'hidden', border:`1px solid ${C.border}` }}>
                    <div style={{ padding:'8px 14px', borderBottom:`1px solid ${C.border}`, display:'flex', alignItems:'center', gap:10 }}>
                      <Badge c={e.method==='POST'?C.accent:C.success} label={e.method} />
                      <code style={{ fontSize:13, color:C.text, fontFamily:"'DM Mono',monospace" }}>{e.path}</code>
                    </div>
                    <div style={{ padding:'8px 14px' }}>
                      <div style={{ fontSize:12, color:C.textSec, marginBottom:e.body?6:0 }}>{e.desc}</div>
                      {e.body&&<pre style={{ margin:0, fontFamily:"'DM Mono',monospace", fontSize:11, color:C.textMuted, whiteSpace:'pre-wrap' }}>{e.body}</pre>}
                    </div>
                  </div>
                ))}
              </Card>

              {/* ── Solution API Reference ─────────────────────────────────── */}
              <Card>
                <div style={{ fontSize:14, fontWeight:700, color:C.text, marginBottom:6 }}>
                  🔗 Solution API Reference
                </div>
                <div style={{ fontSize:12, color:C.textSec, marginBottom:16, lineHeight:1.6 }}>
                  Solutions combine multiple param validations into a composite result.
                  Auth: <span style={{ fontFamily:"'DM Mono',monospace", color:C.accent }}>
                    Bearer {'<api_key>'}
                  </span>
                </div>
                {[
                  {
                    method: 'GET',
                    path:   '/api/v1/solutions?isActive=true',
                    desc:   'List available solution configs for your tenant.',
                    body:   null,
                  },
                  {
                    method: 'GET',
                    path:   '/api/v1/solutions/:id',
                    desc:   'Get a solution config with its ordered param slots.',
                    body:   null,
                  },
                  {
                    method: 'POST',
                    path:   '/api/v1/solutions/:id/run',
                    desc:   'Execute a solution. Provide one image per param slot. Returns combined result + per-param breakdown.',
                    body:
`{
  "jobId": "WO-2025-001",
  "images": [
    { "position": 1, "imageUrl": "https://example.com/odometer-checkin.jpg" },
    { "position": 2, "imageUrl": "https://example.com/odometer-checkout.jpg" }
  ]
}`,
                  },
                  {
                    method: 'POST',
                    path:   '/api/v1/solutions/:id/run  (base64 upload)',
                    desc:   'Same as above but supply base64-encoded images instead of URLs.',
                    body:
`{
  "jobId": "WO-2025-001",
  "images": [
    { "position": 1, "imageBase64": "<base64-encoded-image>" },
    { "position": 2, "imageBase64": "<base64-encoded-image>" }
  ]
}`,
                  },
                  {
                    method: 'GET',
                    path:   '/api/v1/solutions/runs/list?solutionId=<id>&jobId=<id>',
                    desc:   'List past solution runs with optional filters.',
                    body:   null,
                  },
                  {
                    method: 'GET',
                    path:   '/api/v1/solutions/runs/:runId',
                    desc:   'Fetch a solution run by ID — includes per-param results and final synthesised JSON.',
                    body:   null,
                  },
                ].map(e => (
                  <div key={e.path} style={{ marginBottom:12, background:C.bg, borderRadius:10,
                    overflow:'hidden', border:`1px solid ${C.border}` }}>
                    <div style={{ padding:'8px 14px', borderBottom:`1px solid ${C.border}`,
                      display:'flex', alignItems:'center', gap:10 }}>
                      <Badge c={e.method==='POST'?C.accent:C.success} label={e.method} />
                      <code style={{ fontSize:12, color:C.text, fontFamily:"'DM Mono',monospace",
                        wordBreak:'break-all' }}>{e.path}</code>
                    </div>
                    <div style={{ padding:'8px 14px' }}>
                      <div style={{ fontSize:12, color:C.textSec, marginBottom:e.body?6:0 }}>
                        {e.desc}
                      </div>
                      {e.body && (
                        <pre style={{ margin:0, fontFamily:"'DM Mono',monospace", fontSize:11,
                          color:C.textMuted, whiteSpace:'pre-wrap' }}>{e.body}</pre>
                      )}
                    </div>
                  </div>
                ))}

                <div style={{ background:C.bg, borderRadius:8, padding:12, marginTop:8,
                  border:`1px solid ${C.border}`, fontSize:12 }}>
                  <div style={{ fontWeight:600, color:C.text, marginBottom:8 }}>
                    Example solution response
                  </div>
                  <pre style={{ margin:0, fontFamily:"'DM Mono',monospace", fontSize:11,
                    color:C.textMuted, whiteSpace:'pre-wrap' }}>{`{
  "id": "uuid",
  "solution_id": "uuid",
  "solution_name": "Odometer Distance Calculation",
  "job_id": "WO-2025-001",
  "validation_status": "pass",
  "confidence": 0.94,
  "parsed_result": {
    "distance_km": 142,
    "check_in_reading": 58210,
    "check_out_reading": 58352,
    "is_valid": true,
    "notes": "Reading difference is consistent with a full day route"
  },
  "processing_ms": 4821,
  "param_results": [
    {
      "position": 1,
      "param_label": "Odometer Check-In",
      "validation_status": "pass",
      "confidence": 0.97,
      "parsed_result": { "reading": 58210, "unit": "km", "visible": true }
    },
    {
      "position": 2,
      "param_label": "Odometer Check-Out",
      "validation_status": "pass",
      "confidence": 0.96,
      "parsed_result": { "reading": 58352, "unit": "km", "visible": true }
    }
  ]
}`}</pre>
                </div>
              </Card>
            </div>
          )}

          {tab==='field-links'&&!isViewer&&(
            <FieldLinksManager user={user} />
          )}

                    {tab==='system'&&isSuperAdmin&&(
            <Card>
              <div style={{ fontSize:14, fontWeight:700, color:C.text, marginBottom:16 }}>System Configuration</div>
              {[['Workflow Engine URL','WORKFLOW_ENGINE_URL'],['SSO Issuer','SSO_ISSUER'],['Sync Interval','WORKFLOW_SYNC_INTERVAL']].map(([l,k])=>(
                <div key={k} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 14px', background:C.bg, borderRadius:8, border:`1px solid ${C.border}`, marginBottom:8 }}>
                  <div><div style={{ fontSize:13, color:C.text }}>{l}</div><div style={{ fontSize:11, color:C.textMuted, fontFamily:"'DM Mono',monospace" }}>env: {k}</div></div>
                  <Badge c={C.success} label="env" />
                </div>
              ))}
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}


// ── Root App ──────────────────────────────────────────────────────────────────
export default function App() {
  const [user,    setUser]    = useState(null);
  const [authed,  setAuthed]  = useState(false);
  const [loading, setLoading] = useState(true);
  const [view,       setView]       = useState('dashboard');
  const [settingsTab, setSettingsTab] = useState(null); // initial tab when navigating to settings

  // Enhanced navigation: supports optional { tab } second argument
  const navigate = (viewId, opts) => {
    if (viewId === 'settings' && opts?.tab) setSettingsTab(opts.tab);
    else setSettingsTab(null);
    setView(viewId);
  };

  useEffect(() => {
    pickupSSOToken();
    if (!isAuthenticated()) { setLoading(false); return; }
    api.auth.me()
      .then(r=>{
        setUser(r.data);
        setAuthed(true);
        // Set a cookie so nginx auth_request can verify docs access
        // Path=/docs ensures it's only sent for docs requests
        const token = localStorage.getItem('vai_token');
        if (token) {
          document.cookie = `vai_docs=${token}; path=/docs; SameSite=Lax; Max-Age=28800`;
        }
      })
      .catch(()=>clearToken())
      .finally(()=>setLoading(false));
    const h = () => { setAuthed(false); setUser(null); };
    window.addEventListener('vai:unauthenticated', h);
    return () => window.removeEventListener('vai:unauthenticated', h);
  }, []);

  if (loading) return <div style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:'100vh' }}><Spinner /></div>;
  if (!authed||!user) return <LoginScreen />;

  const isViewer    = user.role === 'viewer';
  const isAccountMgr = user.role === 'account_manager';
  // Grouped nav — each group has a label + items with role visibility
  const navGroups = [
    {
      label: null,   // no section header for top-level
      items: [
        { id:'dashboard', label:'Dashboard', icon:'◈' },
      ],
    },
    {
      label: 'Validation',
      items: [
        ...(!isViewer && !isAccountMgr ? [{ id:'config-builder',  label:'Config Builder', icon:'🔬' }] : []),
        { id:'workflow-config', label:'Workflow Config', icon:'⚙' },
        ...(!isViewer && !isAccountMgr ? [{ id:'image-tester', label:'Image Tester', icon:'📷' }] : []),
        { id:'templates',       label:'Templates',       icon:'📚' },
        { id:'results',         label:'Results',         icon:'◉' },
        { id:'analytics',       label:'Analytics',       icon:'📈' },
        { id:'reports',         label:'Reports',         icon:'📊' },
      ].filter(Boolean),
    },
    {
      label: 'Solutions',
      items: !isViewer ? [
        { id:'solutions',        label:'Configs',          icon:'🔗' },
        { id:'solution-tester',  label:'Tester',           icon:'🧪' },
        { id:'solution-runs',    label:'Runs',             icon:'◉' },
        { id:'solution-reports', label:'Reports',          icon:'📊' },
      ] : [],
    },
    {
      label: 'Admin',
      items: !isViewer ? [
        { id:'settings', label:'Settings', icon:'◎' },
      ] : [],
    },
  ];
  // Flat list of all accessible items (for view routing — unchanged)
  const navItems = navGroups.flatMap(g => g.items);

  const views = {
    'dashboard':       <Dashboard user={user} onNavigate={navigate} />,
    'config-builder':  <ConfigBuilder user={user} onSaved={() => setView('workflow-config')} />,
    'workflow-config': <WorkflowConfig user={user} onOpenBuilder={() => setView('config-builder')} />,
    'image-tester':    <ImageTester user={user} />,
    'templates':       <PromptLibrary user={user} />,
    'solutions':       <SolutionManager user={user} />,
    'solution-tester': <SolutionTester user={user} />,
    'solution-runs':   <SolutionRuns   user={user} />,
    'solution-reports': <SolutionReports user={user} />,
    'results':         <Results user={user} />,
    'analytics':       <Analytics user={user} />,
    'reports':         <Reports user={user} />,
    'settings':        <Settings user={user} initialTab={settingsTab} />,
  };

  return (
    <div style={{ display:'flex', minHeight:'100vh', background:C.bg }}>
      <style>{`
        *{box-sizing:border-box;margin:0;padding:0}
        ::-webkit-scrollbar{width:4px;height:4px}
        ::-webkit-scrollbar-thumb{background:#2A3450;border-radius:4px}
        input::placeholder,textarea::placeholder{color:#3D4F65}
        select option{background:#10141C}
        @keyframes spin{to{transform:rotate(360deg)}}
        input[type=date]::-webkit-calendar-picker-indicator{filter:invert(0.5)}
        details summary::-webkit-details-marker{display:none}
      `}</style>

      <div style={{ width:214, background:C.surface, borderRight:`1px solid ${C.border}`,
        display:'flex', flexDirection:'column', position:'sticky', top:0, height:'100vh', flexShrink:0 }}>
        <div style={{ padding:'20px 20px 16px', borderBottom:`1px solid ${C.border}` }}>
          <AppLogo size={17} />
          <div style={{ fontSize:10, color:C.textMuted, letterSpacing:'0.1em', textTransform:'uppercase', marginTop:2, fontFamily:"'DM Mono',monospace" }}>{getAppTagline()}</div>
        </div>
        <nav style={{ flex:1, padding:'12px 10px', overflowY:'auto' }}>
          {navGroups.map((group, gi) => (
            <div key={gi}>
              {group.label && (
                <div style={{
                  fontSize:9, fontWeight:700, color:C.textMuted, letterSpacing:'0.1em',
                  textTransform:'uppercase', padding:'10px 12px 4px',
                  marginTop: gi > 0 ? 4 : 0,
                  borderTop: gi > 0 ? `1px solid ${C.border}` : 'none',
                }}>
                  {group.label}
                </div>
              )}
              {group.items.map(item => (
                <button key={item.id} onClick={()=>setView(item.id)} style={{
                  display:'flex', alignItems:'center', gap:10, width:'100%',
                  padding:'8px 12px', borderRadius:8, border:'none', cursor:'pointer',
                  textAlign:'left', marginBottom:1,
                  background: view===item.id ? C.accentDim : 'transparent',
                  color:      view===item.id ? C.accent    : C.textSec,
                  fontSize:13, fontWeight: view===item.id ? 600 : 400,
                  fontFamily:"'DM Sans',sans-serif", transition:'all 0.15s',
                  borderLeft:`2px solid ${view===item.id ? C.accent : 'transparent'}`,
                }}
                onMouseEnter={e=>view!==item.id&&(e.currentTarget.style.background=C.surfaceHover)}
                onMouseLeave={e=>view!==item.id&&(e.currentTarget.style.background='transparent')}>
                  <span style={{ fontSize:14, width:20, textAlign:'center' }}>{item.icon}</span>
                  {item.label}
                </button>
              ))}
            </div>
          ))}
        </nav>
        <div style={{ padding:'12px 16px', borderTop:`1px solid ${C.border}` }}>
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
            <div style={{ width:30, height:30, borderRadius:'50%', background:C.accentDim, border:`1px solid ${C.accent}44`,
              display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, color:C.accent, fontWeight:700 }}>
              {user.name?.[0]?.toUpperCase()}
            </div>
            <div>
              <div style={{ fontSize:12, fontWeight:600, color:C.text }}>{user.name}</div>
              <div style={{ fontSize:10, color:C.textMuted, fontFamily:"'DM Mono',monospace" }}>
                {user.role}{user.tenant_name && ` + ${user.tenant_name}`}
              </div>
            </div>
          </div>
          {/* Docs & support links */}
          <div style={{ display:'flex', gap:6, marginBottom:6 }}>
            {[
              ['📖', 'Docs',   window.__ENV__?.VITE_DOCS_URL || '/docs'],
              ['❓', 'FAQ',    (window.__ENV__?.VITE_DOCS_URL || '/docs') + '/faq'],
              ['🛟', 'Support', 'mailto:platform@medge.app'],
            ].map(([icon, label, href]) => (
              <a key={label} href={href} target="_blank" rel="noreferrer"
                style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center',
                  gap:2, padding:'5px 4px', borderRadius:6, textDecoration:'none',
                  border:`1px solid ${C.border}`, background:C.bg,
                  color:C.textMuted, fontSize:9, cursor:'pointer', transition:'all 0.15s' }}
                onMouseEnter={e=>{e.currentTarget.style.borderColor=C.accent+'66';e.currentTarget.style.color=C.accent;}}
                onMouseLeave={e=>{e.currentTarget.style.borderColor=C.border;e.currentTarget.style.color=C.textMuted;}}>
                <span style={{ fontSize:13 }}>{icon}</span>
                <span>{label}</span>
              </a>
            ))}
          </div>
          <button onClick={()=>{
              if (user.is_sso) {
                api.auth.logout();
              } else {
                api.auth.logoutLocal();
              }
            }} style={{ width:'100%', padding:'6px', borderRadius:6,
            border:`1px solid ${C.border}`, background:'transparent', color:C.textSec, cursor:'pointer',
            fontSize:12, fontFamily:"'DM Sans',sans-serif" }}>Sign out</button>
        </div>
      </div>

      <div style={{ flex:1, padding:28, overflowY:'auto', maxHeight:'100vh' }}>{views[view]}</div>
    </div>
  );
}
